import { useState, useMemo, useEffect, useRef, useCallback, useContext, createContext } from 'react';
/* eslint-disable no-unused-vars */
// Imports consumed across Phases 2-5 (template picker, document editor, AI
// draft panel, settings UI). Surfaced now in Phase 1 so the file references
// the new library and the build verifies the path is correct.
import {
  LINE_ITEM_CATALOG,
  SECTION_TEMPLATES,
  QUOTE_TEMPLATES,
  BUNDLES,
  LINE_ITEM_CATEGORIES,
  PICKER_GROUPS_RESIDENTIAL,
  PICKER_GROUPS_COMMERCIAL,
  DEFAULT_TERMS_RESIDENTIAL,
  DEFAULT_TERMS_COMMERCIAL,
  DEFAULT_EXCLUSIONS_RESIDENTIAL,
  DEFAULT_EXCLUSIONS_COMMERCIAL,
  DEFAULT_INCLUDED_RESIDENTIAL,
  DEFAULT_INCLUDED_COMMERCIAL,
  getAllLineItems,
  getAllSectionTemplates,
  getAllQuoteTemplates,
  getAllBundles,
  loadUserLineItems, saveUserLineItems,
  loadUserSectionTemplates, saveUserSectionTemplates,
  loadUserQuoteTemplates, saveUserQuoteTemplates,
  loadUserBundles, saveUserBundles,
  instantiateSectionFromTemplate,
  instantiateLineItemFromCatalog,
  instantiateBundle,
} from './data/quoteLibraries';
/* eslint-enable no-unused-vars */

// ─── Toast System ─────────────────────────────────────────────────────────────
const ToastCtx = createContext(null);
function useToast() { return useContext(ToastCtx) || (() => {}); }
function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3000);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div style={{ position: 'fixed', bottom: 80, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column-reverse', gap: 8, pointerEvents: 'none' }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            background: t.type === 'error' ? '#450a0a' : '#052e16',
            border: `1px solid ${t.type === 'error' ? 'rgba(239,68,68,0.5)' : 'rgba(34,197,94,0.5)'}`,
            borderRadius: 8, padding: '10px 16px',
            color: '#FFFFFF', fontSize: 13, fontWeight: 500,
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            maxWidth: 300,
          }}>
            {t.type === 'error' ? '✕ ' : '✓ '}{t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ─── Job Types (Roofing-Only) ────────────────────────────────────────────────
const TRADE_LIST = [
  'Full Replacement', 'Repair', 'Inspection', 'Storm Damage',
  'Gutter Install', 'Skylight', 'Flashing Repair', 'Ventilation',
  'Emergency Tarp',
];

const TRADE_COLORS = {
  'Full Replacement': '#E8722A',
  'Repair':           '#3b82f6',
  'Inspection':       '#8b5cf6',
  'Storm Damage':     '#ef4444',
  'Gutter Install':   '#06b6d4',
  'Skylight':         '#f59e0b',
  'Flashing Repair':  '#ec4899',
  'Ventilation':      '#10b981',
  'Emergency Tarp':   '#dc2626',
};

const CUSTOM_TRADE_COLOR = '#A78BFA';
function getTradeColor(trade) {
  if (!trade) return '#8B95A1';
  return TRADE_COLORS[trade] || CUSTOM_TRADE_COLOR;
}

const TRADE_CHECKLISTS = {
  'Full Replacement': [
    { id: 1, label: 'Site inspection & measurements' },
    { id: 2, label: 'Material order & delivery' },
    { id: 3, label: 'Tear off old roof' },
    { id: 4, label: 'Inspect & repair decking' },
    { id: 5, label: 'Install drip edge' },
    { id: 6, label: 'Install ice & water shield' },
    { id: 7, label: 'Install underlayment' },
    { id: 8, label: 'Install shingles' },
    { id: 9, label: 'Install ridge vent & cap' },
    { id: 10, label: 'Flashing & sealing' },
    { id: 11, label: 'Magnetic nail sweep' },
    { id: 12, label: 'Final inspection' },
    { id: 13, label: 'Invoice sent' },
    { id: 14, label: 'Payment received' },
  ],
  'Repair': [
    { id: 1, label: 'Identify damage area' },
    { id: 2, label: 'Material pickup' },
    { id: 3, label: 'Remove damaged shingles' },
    { id: 4, label: 'Inspect decking underneath' },
    { id: 5, label: 'Patch or replace decking' },
    { id: 6, label: 'Install new shingles' },
    { id: 7, label: 'Seal & test' },
    { id: 8, label: 'Final inspection' },
    { id: 9, label: 'Invoice sent' },
    { id: 10, label: 'Payment received' },
  ],
  'Inspection': [
    { id: 1, label: 'Ground-level photos (4 sides)' },
    { id: 2, label: 'Ladder / drone roof access' },
    { id: 3, label: 'Document shingle condition' },
    { id: 4, label: 'Check flashing & penetrations' },
    { id: 5, label: 'Inspect ridge, valleys & eaves' },
    { id: 6, label: 'Check gutters & downspouts' },
    { id: 7, label: 'Attic inspection (if accessible)' },
    { id: 8, label: 'Write inspection report' },
    { id: 9, label: 'Deliver report to customer' },
  ],
  'Storm Damage': [
    { id: 1, label: 'Emergency site assessment' },
    { id: 2, label: 'Document all damage (photos & notes)' },
    { id: 3, label: 'Tarp exposed areas if needed' },
    { id: 4, label: 'File insurance claim with homeowner' },
    { id: 5, label: 'Meet adjuster on site' },
    { id: 6, label: 'Scope agreement & supplement' },
    { id: 7, label: 'Material order & delivery' },
    { id: 8, label: 'Tear off damaged sections' },
    { id: 9, label: 'Install new roofing system' },
    { id: 10, label: 'Final inspection' },
    { id: 11, label: 'Invoice & insurance payment' },
  ],
  'Gutter Install': [
    { id: 1, label: 'Measure & quote' },
    { id: 2, label: 'Material order' },
    { id: 3, label: 'Remove old gutters' },
    { id: 4, label: 'Install fascia board (if needed)' },
    { id: 5, label: 'Install new gutters' },
    { id: 6, label: 'Install downspouts & extensions' },
    { id: 7, label: 'Install gutter guards (if ordered)' },
    { id: 8, label: 'Test water flow' },
    { id: 9, label: 'Final inspection' },
    { id: 10, label: 'Invoice sent' },
    { id: 11, label: 'Payment received' },
  ],
  'Skylight': [
    { id: 1, label: 'Measure opening & select unit' },
    { id: 2, label: 'Order skylight & flashing kit' },
    { id: 3, label: 'Cut roof opening' },
    { id: 4, label: 'Frame curb' },
    { id: 5, label: 'Install skylight unit' },
    { id: 6, label: 'Install step & counter flashing' },
    { id: 7, label: 'Seal & waterproof' },
    { id: 8, label: 'Interior trim (if applicable)' },
    { id: 9, label: 'Final inspection' },
    { id: 10, label: 'Invoice sent' },
    { id: 11, label: 'Payment received' },
  ],
  'Flashing Repair': [
    { id: 1, label: 'Identify leak source' },
    { id: 2, label: 'Remove old flashing & sealant' },
    { id: 3, label: 'Inspect substrate condition' },
    { id: 4, label: 'Install new flashing' },
    { id: 5, label: 'Apply sealant & counterflashing' },
    { id: 6, label: 'Water test' },
    { id: 7, label: 'Final inspection' },
    { id: 8, label: 'Invoice sent' },
    { id: 9, label: 'Payment received' },
  ],
  'Ventilation': [
    { id: 1, label: 'Attic airflow assessment' },
    { id: 2, label: 'Calculate intake/exhaust requirements' },
    { id: 3, label: 'Order vents & materials' },
    { id: 4, label: 'Cut openings' },
    { id: 5, label: 'Install ridge vent / box vents' },
    { id: 6, label: 'Install soffit vents' },
    { id: 7, label: 'Seal & flash around new vents' },
    { id: 8, label: 'Verify balanced airflow' },
    { id: 9, label: 'Final inspection' },
    { id: 10, label: 'Invoice sent' },
    { id: 11, label: 'Payment received' },
  ],
  'Emergency Tarp': [
    { id: 1, label: 'Emergency dispatch' },
    { id: 2, label: 'Assess damage on arrival' },
    { id: 3, label: 'Secure tarp over exposed area' },
    { id: 4, label: 'Anchor with boards & fasteners' },
    { id: 5, label: 'Document damage for insurance' },
    { id: 6, label: 'Schedule follow-up repair' },
    { id: 7, label: 'Invoice sent' },
    { id: 8, label: 'Payment received' },
  ],
};

// ─── Demo Data ───────────────────────────────────────────────────────────────
const DEMO_LEADS = [
  {
    id: 1, name: 'Shumaker Residence', contact: 'Dave Shumaker', role: 'Homeowner',
    phone: '(570) 966-4182', email: 'dshumaker@gmail.com',
    trade: 'Full Replacement', status: 'stalled', value: 14800, stage: 'estimate_sent',
    callbackDate: '2026-03-25', lastContact: '2026-03-10',
    stallReason: 'price_objection', notes: '28 sq, 7/12 pitch. GAF Timberline HDZ Charcoal. 1 layer tear-off over plywood deck. Wants 15% off — comparing 2 other bids from Lewisburg area roofers.',
    industry: 'Residential', dealAge: 22, address: '142 Chestnut St, Mifflinburg PA 17844',
  },
  {
    id: 2, name: 'Susquehanna Valley Mall', contact: 'Denise Rhoads', role: 'Property Manager',
    phone: '(570) 374-8100', email: 'drhoads@svmall.com',
    trade: 'Full Replacement', status: 'stalled', value: 23500, stage: 'contract_signed',
    callbackDate: '2026-03-24', lastContact: '2026-03-08',
    stallReason: 'budget_freeze', notes: 'TPO membrane, 140 sq flat roof on anchor store wing. Q1 budget locked — revisit April per ownership group. 2 HVAC curbs need reflashing.',
    industry: 'Commercial', dealAge: 38, address: '1 Susquehanna Valley Mall Dr, Selinsgrove PA 17870',
  },
  {
    id: 3, name: 'Bowman Family', contact: 'Tina Bowman', role: 'Homeowner',
    phone: '(570) 524-3947', email: 'tinabowman@hotmail.com',
    trade: 'Storm Damage', status: 'active', value: 18700, stage: 'inspection_scheduled',
    callbackDate: '2026-03-23', lastContact: '2026-03-20',
    stallReason: null, notes: 'Wind damage from 3/15 storm. Insurance claim filed with Erie Insurance. Adjuster visit pending. Architectural shingles, 24 sq, 6/12 pitch.',
    industry: 'Residential', dealAge: 9, address: '227 Market St, Lewisburg PA 17837',
  },
  {
    id: 4, name: 'Northumberland Apartments', contact: 'Rick Hess', role: 'Building Manager',
    phone: '(570) 473-3200', email: 'rhess@northumberlandapts.com',
    trade: 'Repair', status: 'stalled', value: 4200, stage: 'estimate_sent',
    callbackDate: '2026-03-19', lastContact: '2026-03-05',
    stallReason: 'no_response', notes: '3 follow-ups, no reply. Active leak in Bldg C — valley flashing failed. 3-tab, 6/12 pitch. Decking shows some staining.',
    industry: 'Commercial', dealAge: 41, address: '350 Front St, Northumberland PA 17857',
  },
  {
    id: 5, name: 'Monroe Township Offices', contact: 'Jeff Weaver', role: 'Facilities Director',
    phone: '(570) 374-4492', email: 'jweaver@monroetwp.org',
    trade: 'Full Replacement', status: 'active', value: 31200, stage: 'contract_signed',
    callbackDate: '2026-03-26', lastContact: '2026-03-21',
    stallReason: null, notes: '42 sq, low-slope modified bitumen on municipal bldg. 2 layers to tear off. Decking likely needs 10-15 sheets. Snyder County permit in hand.',
    industry: 'Commercial', dealAge: 27, address: '88 W Main St, Selinsgrove PA 17870',
  },
  {
    id: 6, name: 'Keller Residence', contact: 'Amy Keller', role: 'Homeowner',
    phone: '(570) 742-8156', email: 'amykeller22@yahoo.com',
    trade: 'Full Replacement', status: 'stalled', value: 22600, stage: 'estimate_sent',
    callbackDate: '2026-03-22', lastContact: '2026-03-12',
    stallReason: 'competitor', notes: '32 sq, 8/12 steep pitch. CertainTeed Landmark Pro Moire Black. 1 layer over OSB deck. Competitor bid $2k less — need to show SureStart warranty value.',
    industry: 'Residential', dealAge: 35, address: '415 Chestnut St, Milton PA 17847',
  },
  {
    id: 7, name: 'Riverwoods HOA', contact: 'Susan Yoder', role: 'HOA President',
    phone: '(570) 524-7200', email: 'syoder@riverwoodshoa.org',
    trade: 'Full Replacement', status: 'active', value: 94000, stage: 'inspection_scheduled',
    callbackDate: '2026-03-27', lastContact: '2026-03-18',
    stallReason: null, notes: '12-building townhome complex, ~280 sq total. Phased replacement. Board vote 3/28. Architectural shingles, color TBD by HOA. Ice & water shield required at eaves. Soffit/fascia repair added to scope after walk-through.',
    industry: 'HOA', dealAge: 18, address: '200 Riverwoods Dr, Lewisburg PA 17837',
    extraScopes: [{ trade: 'Soffit/Fascia Repair', value: 12400 }],
  },
  {
    id: 8, name: 'Market Street Commons', contact: 'Tom Brubaker', role: 'Property Manager',
    phone: '(570) 286-5100', email: 'tbrubaker@marketstcommons.com',
    trade: 'Gutter Install', status: 'stalled', value: 5800, stage: 'estimate_sent',
    callbackDate: '2026-03-15', lastContact: '2026-03-03',
    stallReason: 'technical_fit', notes: '220 LF seamless aluminum 5". K-style vs half-round debate on 1890s building facade. Include leaf guards. Historic district review may apply.',
    industry: 'Commercial', dealAge: 49, address: '118 Market St, Sunbury PA 17801',
  },
  {
    id: 9, name: 'Valley Fitness', contact: 'Marcus Stover', role: 'Owner',
    phone: '(570) 374-6890', email: 'marcus@valleyfitness.com',
    trade: 'Repair', status: 'cold', value: 3400, stage: 'lead',
    callbackDate: '2026-04-05', lastContact: '2026-02-20',
    stallReason: 'timing', notes: 'Ponding water on flat section. TPO seam separation near HVAC curb. Wants to wait until summer. Follow up Q2.',
    industry: 'Commercial', dealAge: 64, address: '1420 N Susquehanna Trail, Selinsgrove PA 17870',
  },
  {
    id: 10, name: 'Christ Lutheran Mifflinburg', contact: 'Pastor Ed Zimmerman', role: 'Facilities Coord',
    phone: '(570) 966-1544', email: 'pezimmerman@christlutheran.org',
    trade: 'Storm Damage', status: 'won', value: 9400, stage: 'paid',
    callbackDate: null, lastContact: '2026-03-17',
    stallReason: null, notes: 'Closed! Wind damage to ridge cap + 3 sq of shingles on sanctuary. Nationwide Insurance covered. CertainTeed Landmark, Weathered Wood. Chimney flashing added during inspection.',
    industry: 'Institutional', dealAge: 55, address: '130 S 5th St, Mifflinburg PA 17844',
    extraScopes: [{ trade: 'Flashing Repair', value: 3200 }],
  },
  {
    id: 11, name: 'Hartman Residence', contact: 'Joe Hartman', role: 'Homeowner',
    phone: '(570) 538-2210', email: 'joehartman@ptd.net',
    trade: 'Skylight', status: 'stalled', value: 4800, stage: 'contract_signed',
    callbackDate: '2026-03-23', lastContact: '2026-03-13',
    stallReason: 'wrong_contact', notes: '2 Velux skylights, curb-mount, bathroom + kitchen. Need to confirm size with wife who picked them out.',
    industry: 'Residential', dealAge: 46, address: '78 Pine Creek Rd, Watsontown PA 17777',
  },
  {
    id: 12, name: 'Buffalo Valley Townhomes', contact: 'Karen Bender', role: 'HOA Director',
    phone: '(570) 524-1180', email: 'kbender@buffvalleyhoa.org',
    trade: 'Full Replacement', status: 'active', value: 34800, stage: 'estimate_sent',
    callbackDate: '2026-03-24', lastContact: '2026-03-22',
    stallReason: null, notes: '6 townhome units, 180 sq total. GAF Timberline architectural, Hickory. Ice & water at all valleys. Proposal well received by board.',
    industry: 'HOA', dealAge: 14, address: '450 Buffalo Rd, Lewisburg PA 17837',
  },
  {
    id: 13, name: 'Klinger Trucking', contact: 'Jim Klinger', role: 'Owner',
    phone: '(570) 742-3400', email: 'jim@klingertrucking.com',
    trade: 'Full Replacement', status: 'lost', value: 19600, stage: 'lost',
    callbackDate: null, lastContact: '2026-03-09',
    stallReason: 'competitor', notes: 'Lost on price. 26 sq standing seam metal on shop. Competitor bid $3k less — no manufacturer warranty.',
    industry: 'Commercial', dealAge: 61, address: '1100 Industrial Park Rd, Milton PA 17847',
  },
  {
    id: 14, name: 'Deimler Residence', contact: 'Scott Deimler', role: 'Homeowner',
    phone: '(570) 966-0247', email: 'scottd@gmail.com',
    trade: 'Full Replacement', status: 'stalled', value: 42000, stage: 'materials_ordered',
    callbackDate: '2026-03-23', lastContact: '2026-03-15',
    stallReason: 'budget_freeze', notes: '48 sq, standing seam metal Burnished Slate. 10/12 pitch, harness required. Materials on order at Boise Cascade Sunbury but homeowner paused — waiting on HELOC.',
    industry: 'Residential', dealAge: 74, address: '22 Mountain Rd, New Berlin PA 17855',
  },
  {
    id: 15, name: 'Blasius Chevrolet', contact: 'Steve Blasius', role: 'Shop Owner',
    phone: '(570) 374-1234', email: 'steve@blasiuschevy.com',
    trade: 'Flashing Repair', status: 'active', value: 2800, stage: 'inspection_complete',
    callbackDate: '2026-03-28', lastContact: '2026-03-23',
    stallReason: null, notes: 'Chimney flashing failed on service bay. Active leak into office below. Counter-flashing pulling away from limestone mortar.',
    industry: 'Commercial', dealAge: 8, address: '400 S Market St, Selinsgrove PA 17870',
  },
  {
    id: 16, name: 'Packwood House Museum', contact: 'Patricia Landis', role: 'Facilities Manager',
    phone: '(570) 524-0323', email: 'plandis@packwoodhouse.org',
    trade: 'Repair', status: 'active', value: 6400, stage: 'estimate_sent',
    callbackDate: '2026-03-26', lastContact: '2026-03-19',
    stallReason: null, notes: 'Slate roof — 8 cracked tiles + ridge mortar repointing. Historic property, must match existing PA slate. 12/12 pitch.',
    industry: 'Commercial', dealAge: 20, address: '15 N Water St, Lewisburg PA 17837',
  },
  {
    id: 17, name: 'Susquehanna Industrial Park', contact: 'Nick Etter', role: 'Operations Mgr',
    phone: '(570) 473-9500', email: 'netter@susqindpark.com',
    trade: 'Full Replacement', status: 'stalled', value: 38000, stage: 'estimate_sent',
    callbackDate: '2026-03-20', lastContact: '2026-03-04',
    stallReason: 'budget_freeze', notes: '52 sq TPO, flat warehouse roof. 2 layers existing. Full board approval pending. Northumberland County permit needed.',
    industry: 'Commercial', dealAge: 53, address: '825 Point Township Dr, Northumberland PA 17857',
  },
  {
    id: 18, name: 'Flickinger Residence', contact: 'Carol Flickinger', role: 'Homeowner',
    phone: '(570) 837-1055', email: 'carolflick@verizon.net',
    trade: 'Emergency Tarp', status: 'active', value: 1200, stage: 'inspection_scheduled',
    callbackDate: '2026-03-25', lastContact: '2026-03-20',
    stallReason: null, notes: 'Oak tree fell on roof during wind storm. Active water intrusion into upstairs bedroom. Tarp ASAP, then scope full replacement.',
    industry: 'Residential', dealAge: 12, address: '56 Maple Ave, Middleburg PA 17842',
  },
  {
    id: 19, name: 'Danville Heritage Villas', contact: 'Dave Moyer', role: 'Board President',
    phone: '(570) 275-4800', email: 'dmoyer@heritagevillas.org',
    trade: 'Inspection', status: 'cold', value: 1800, stage: 'lead',
    callbackDate: '2026-04-10', lastContact: '2026-02-28',
    stallReason: 'timing', notes: '15-building annual roof inspection. Waiting on spring maintenance budget approval. Could lead to multi-phase replacement — most roofs are 18+ years.',
    industry: 'HOA', dealAge: 71, address: '300 Montour Blvd, Danville PA 17821',
  },
  {
    id: 20, name: 'Sheetz #412', contact: 'Brian Lehr', role: 'Facilities Coord',
    phone: '(570) 374-2200', email: 'blehr@sheetz.com',
    trade: 'Repair', status: 'won', value: 4200, stage: 'invoiced',
    callbackDate: null, lastContact: '2026-03-18',
    stallReason: null, notes: 'Closed! Patched 4 sq of wind-damaged 3-tab on flat canopy section. Resealed all pipe boots and HVAC curbs.',
    industry: 'Commercial', dealAge: 29, address: '1250 N Susquehanna Trail, Selinsgrove PA 17870',
  },
  {
    id: 21, name: 'Evangelical Community Hospital', contact: 'Greg Stauffer', role: 'Facilities Manager',
    phone: '(570) 522-2700', email: 'gstauffer@evanhospital.com',
    trade: 'Full Replacement', status: 'stalled', value: 67000, stage: 'contract_signed',
    callbackDate: '2026-03-22', lastContact: '2026-03-11',
    stallReason: 'price_objection', notes: 'Outpatient wing, 320 sq TPO. Hospital procurement wants to value-engineer. Union County permit in hand.',
    industry: 'Institutional', dealAge: 45, address: '1 Hospital Dr, Lewisburg PA 17837',
  },
  {
    id: 22, name: 'Rohrer Residence', contact: 'Mike Rohrer', role: 'Homeowner',
    phone: '(570) 784-6322', email: 'mikerohrer@gmail.com',
    trade: 'Ventilation', status: 'active', value: 3200, stage: 'inspection_complete',
    callbackDate: '2026-03-27', lastContact: '2026-03-21',
    stallReason: null, notes: 'Attic hitting 145°F in summer. No ridge vent, only 2 box vents on 1960s ranch. OSB decking — need full intake/exhaust rebalance + check for moisture damage.',
    industry: 'Residential', dealAge: 17, address: '340 Fairground Rd, Bloomsburg PA 17815',
  },
  {
    id: 23, name: 'Susquehanna Valley Mall — JCPenney Wing', contact: 'Janet Groff', role: 'Facilities Mgr',
    phone: '(570) 374-8105', email: 'jgroff@svmall.com',
    trade: 'Flashing Repair', status: 'stalled', value: 8500, stage: 'estimate_sent',
    callbackDate: '2026-03-21', lastContact: '2026-03-07',
    stallReason: 'no_response', notes: 'Parapet wall flashing failing on 3 sections. Submitted proposal 2 weeks ago. Zero feedback from corporate ownership.',
    industry: 'Commercial', dealAge: 58, address: '1 Susquehanna Valley Mall Dr, Selinsgrove PA 17870',
  },
  {
    id: 24, name: 'RiverWoods Senior Living', contact: 'Andrew Musser', role: 'Maintenance Dir',
    phone: '(570) 524-4600', email: 'amusser@riverwoodssenior.com',
    trade: 'Gutter Install', status: 'active', value: 13200, stage: 'estimate_sent',
    callbackDate: '2026-03-26', lastContact: '2026-03-20',
    stallReason: null, notes: '480 LF 6" commercial gutters + 12 downspouts on main building. Include splash guards at all walkways. On track.',
    industry: 'Institutional', dealAge: 22, address: '150 RiverWoods Dr, Lewisburg PA 17837',
  },
  {
    id: 25, name: 'Brenner Residence', contact: 'Sandra Brenner', role: 'Homeowner',
    phone: '(570) 584-2718', email: 'sbrenner@ptd.net',
    trade: 'Skylight', status: 'stalled', value: 6100, stage: 'scheduled_for_install',
    callbackDate: '2026-03-23', lastContact: '2026-03-14',
    stallReason: 'technical_fit', notes: '3 Velux deck-mount skylights in cathedral ceiling. Flashing kit on backorder at Boise Cascade — ETA pushed 2 weeks. Homeowner frustrated.',
    industry: 'Residential', dealAge: 40, address: '19 Covered Bridge Rd, Hughesville PA 17737',
  },
  {
    id: 26, name: 'Shamokin Creek Condos', contact: 'Bob Reider', role: 'Board Treasurer',
    phone: '(570) 648-3100', email: 'breider@shamokincc.org',
    trade: 'Storm Damage', status: 'stalled', value: 47500, stage: 'estimate_sent',
    callbackDate: '2026-03-24', lastContact: '2026-03-10',
    stallReason: 'wrong_contact', notes: '6 buildings, wind + ice dam damage. Nationwide Insurance adjusting. Need to engage full board, not just treasurer. Mix of 3-tab and architectural.',
    industry: 'HOA', dealAge: 62, address: '500 Shamokin Creek Dr, Shamokin PA 17872',
  },
  // ── Large commercial pipeline ──
  {
    id: 27, name: 'Susquehanna University — Main Hall', contact: 'Dr. Karen Fulton', role: 'VP Facilities',
    phone: '(570) 372-4100', email: 'fultonk@susqu.edu',
    trade: 'Full Replacement', status: 'active', value: 308000, stage: 'estimate_sent',
    callbackDate: '2026-04-28', lastContact: '2026-04-18',
    stallReason: null, notes: '280 sq standing seam metal. Historic Main Hall. University procurement in review.',
    industry: 'Institutional', dealAge: 14, address: '514 University Ave, Selinsgrove PA 17870',
  },
  {
    id: 28, name: 'Geisinger Medical Center — Outpatient Wing', contact: 'Tom Kessler', role: 'Facilities Director',
    phone: '(570) 271-6211', email: 'tkessler@geisinger.edu',
    trade: 'Full Replacement', status: 'active', value: 426000, stage: 'contract_signed',
    callbackDate: '2026-04-25', lastContact: '2026-04-17',
    stallReason: null, notes: '420 sq TPO 60-mil. Outpatient wing. Contract signed — materials ordering imminent.',
    industry: 'Institutional', dealAge: 22, address: '100 N Academy Ave, Danville PA 17822',
  },
  {
    id: 29, name: 'Penn State Extension', contact: 'Jim Hoover', role: 'Regional Facilities',
    phone: '(814) 863-3440', email: 'jhoover@psu.edu',
    trade: 'Full Replacement', status: 'active', value: 87000, stage: 'lead',
    callbackDate: '2026-05-05', lastContact: '2026-04-10',
    stallReason: null, notes: '95 sq architectural shingles. State procurement process. Initial site visit scheduled.',
    industry: 'Institutional', dealAge: 8, address: '340 W Aaron Dr, State College PA 16803',
  },
  {
    id: 30, name: 'Evangelical Community Hospital', contact: 'Greg Stauffer', role: 'Facilities Manager',
    phone: '(570) 522-2700', email: 'gstauffer@evanhospital.com',
    trade: 'Full Replacement', status: 'active', value: 398000, stage: 'materials_ordered',
    callbackDate: '2026-04-22', lastContact: '2026-04-18',
    stallReason: null, notes: '380 sq TPO. Materials ordered from Boise Cascade. Scheduled start May 3.',
    industry: 'Institutional', dealAge: 30, address: '1 Hospital Dr, Lewisburg PA 17837',
  },
  {
    id: 31, name: 'Bucknell University — Bertrand Library', contact: 'Sarah Mitchell', role: 'Dir Capital Projects',
    phone: '(570) 577-3000', email: 'smitchell@bucknell.edu',
    trade: 'Full Replacement', status: 'active', value: 524000, stage: 'estimate_sent',
    callbackDate: '2026-04-30', lastContact: '2026-04-15',
    stallReason: null, notes: '190 sq Peach Bottom slate. Historic library. Must match existing slate exactly. Board approval pending.',
    industry: 'Institutional', dealAge: 18, address: '1 Dent Dr, Lewisburg PA 17837',
  },
  {
    id: 32, name: 'Montour School District — 3 Buildings', contact: 'Dave Marks', role: 'Business Manager',
    phone: '(570) 275-3192', email: 'dmarks@montoursd.org',
    trade: 'Full Replacement', status: 'active', value: 312000, stage: 'lead',
    callbackDate: '2026-05-10', lastContact: '2026-04-12',
    stallReason: null, notes: '310 sq TPO across 3 buildings. School board vote in May. Summer installation window.',
    industry: 'Institutional', dealAge: 6, address: '50 Hornet Dr, Danville PA 17822',
  },
  {
    id: 33, name: 'Sunbury Community Hospital', contact: 'Linda Krebs', role: 'COO',
    phone: '(570) 286-3333', email: 'lkrebs@sunburyhospital.org',
    trade: 'Full Replacement', status: 'active', value: 271000, stage: 'estimate_sent',
    callbackDate: '2026-04-29', lastContact: '2026-04-16',
    stallReason: null, notes: '260 sq TPO. Main building + surgery wing. Competing with 2 other bids.',
    industry: 'Institutional', dealAge: 12, address: '350 N 11th St, Sunbury PA 17801',
  },
  {
    id: 34, name: 'Weis Markets Distribution Center', contact: 'Paul Wentz', role: 'Facilities VP',
    phone: '(570) 286-4571', email: 'pwentz@weismarkets.com',
    trade: 'Full Replacement', status: 'active', value: 495000, stage: 'lead',
    callbackDate: '2026-05-15', lastContact: '2026-04-08',
    stallReason: null, notes: '450 sq standing seam metal. Distribution warehouse. Corporate approval required.',
    industry: 'Commercial', dealAge: 10, address: '1000 S 2nd St, Sunbury PA 17801',
  },
  {
    id: 35, name: 'PA State Police Barracks — Bloomsburg', contact: 'Sgt. Mike Torres', role: 'Facilities Coord',
    phone: '(570) 387-4255', email: 'mtorres@pa.gov',
    trade: 'Full Replacement', status: 'won', value: 94000, stage: 'invoiced',
    callbackDate: null, lastContact: '2026-04-15',
    stallReason: null, notes: '68 sq architectural. Complete — awaiting state payment processing.',
    industry: 'Institutional', dealAge: 45, address: '200 Commerce Blvd, Bloomsburg PA 17815',
  },
  {
    id: 36, name: 'Shikellamy School District — 4 Buildings', contact: 'Ruth Ann Shaffer', role: 'Superintendent',
    phone: '(570) 286-3684', email: 'rshaffer@shiksd.org',
    trade: 'Full Replacement', status: 'active', value: 687000, stage: 'estimate_sent',
    callbackDate: '2026-05-01', lastContact: '2026-04-14',
    stallReason: null, notes: '4 school buildings, mixed TPO + architectural. Summer 2026 installation. Board presentation scheduled.',
    industry: 'Institutional', dealAge: 20, address: '200 Island Blvd, Sunbury PA 17801',
  },
  // ── Stoltzfus: active lead with a draft quote attached (Session 1 demo target) ──
  {
    id: 37, name: 'Amos Stoltzfus', contact: 'Amos Stoltzfus', role: 'Homeowner',
    phone: '(570) 966-2453', email: '',
    trade: 'Full Replacement', status: 'active', value: 18600, stage: 'estimate_sent',
    callbackDate: '2026-04-25', lastContact: '2026-04-12',
    stallReason: null, notes: 'Farmhouse — no Sunday work. Erie Insurance claim. 38 sq, GAF Timberline HDZ Charcoal. Draft quote out.',
    industry: 'Residential', dealAge: 22, address: '1240 Strickler Rd, Mifflinburg PA 17844',
  },
  // ── Yutzy: signed, converted to project (post-conversion demo state) ──
  {
    id: 38, name: 'James & Karen Yutzy', contact: 'James Yutzy', role: 'Homeowner',
    phone: '(570) 524-1962', email: 'kyutzy@frontiernet.net',
    trade: 'Full Replacement', status: 'won', value: 23400, stage: 'contract_signed',
    callbackDate: null, lastContact: '2026-04-08',
    stallReason: null, notes: 'Signed contract 4/8/2026. Erie Insurance claim — wind damage from late winter storm. Project created from signed quote.',
    industry: 'Residential', dealAge: 31, address: '88 Furnace Rd, Lewisburg PA 17837',
    convertedToProjectId: 'p-yutzy-2026-04',
    convertedAt: '2026-04-08T16:32:00Z',
  },
];

const STALL_LABELS = {
  price_objection: 'Price Objection',
  budget_freeze: 'Budget Freeze',
  no_response: 'No Response',
  competitor: 'Competitor',
  timing: 'Bad Timing',
  wrong_contact: 'Wrong Contact',
  technical_fit: 'Technical Fit',
};

const STATUS_COLORS = {
  active: '#16A34A',
  stalled: '#DC2626',
  cold: '#D1D5DB',
  won: '#16A34A',
  lost: '#D1D5DB',
};

const STAGE_ORDER = [
  'lead', 'inspection_scheduled', 'inspection_complete', 'estimate_sent',
  'contract_signed', 'materials_ordered', 'scheduled_for_install',
  'in_progress', 'punch_list', 'completed', 'invoiced', 'paid',
];

const STAGE_LABELS = {
  lead:                  'Lead',
  inspection_scheduled:  'Inspection Scheduled',
  inspection_complete:   'Inspection Complete',
  estimate_sent:         'Estimate Sent',
  contract_signed:       'Contract Signed',
  materials_ordered:     'Materials Ordered',
  scheduled_for_install: 'Scheduled for Install',
  in_progress:           'In Progress',
  punch_list:            'Punch List',
  completed:             'Completed',
  invoiced:              'Invoiced',
  paid:                  'Paid',
  lost:                  'Lost',
};

const STAGE_COLORS = {
  lead:                  '#8B95A1',
  inspection_scheduled:  '#3b82f6',
  inspection_complete:   '#60a5fa',
  estimate_sent:         '#f59e0b',
  contract_signed:       '#10b981',
  materials_ordered:     '#8b5cf6',
  scheduled_for_install: '#06b6d4',
  in_progress:           '#E8722A',
  punch_list:            '#ec4899',
  completed:             '#22c55e',
  invoiced:              '#a855f7',
  paid:                  '#14b8a6',
  lost:                  '#ef4444',
};

const ISSUE_PRESETS = [
  'Wrong Contact', 'Bad Timing', 'No Response', 'Budget Freeze',
  'Price Objection', 'Competitor', 'Weather Delay', 'Material Shortage',
  'Permit Issue', 'HOA Approval Needed',
];

// Operational next-step tips shown inside the job detail modal, keyed by pipeline stage
const STAGE_TIPS = {
  lead: [
    'Call or visit the property to confirm scope and identify any visible damage — many older homes in the valley have multiple layers',
    'Check Google Maps satellite view before the site visit — estimate roof pitch, identify dormers, and spot access issues on rural properties',
    'Ask about insurance claim status before quoting — Erie, State Farm, and Nationwide all handle storm claims differently in PA',
    'Log contact details, job address, and initial scope notes — note if the property is in a township that requires permits (varies by county)',
  ],
  inspection_scheduled: [
    'Confirm the inspection date and time the day before — cell service can be spotty in rural parts of Union and Snyder counties',
    'Prep your inspection kit: drone, ladder, measuring wheel, moisture meter, and camera — PA attics often show ice dam damage from winter',
    'Check the 10-day forecast — central PA spring weather is unpredictable, reschedule early if rain or 20+ mph winds are expected',
    'Review the property on Google Maps satellite view — many valley homes have steep pitches (8/12+) and older slate or metal roofs',
  ],
  inspection_complete: [
    'Upload all photos within 24 hours — four sides, close-ups of damage, ridge, valleys, and all penetrations. Use Drone Shots category for aerials.',
    'Measure total square footage and note roof pitch — steep PA homes (8/12 to 12/12) significantly affect labor and safety costs',
    'Document flashing condition at chimney, skylights, and valleys — limestone chimneys common in the valley are the #1 callback source',
    'Walk the attic if accessible — check for ice dam staining, moisture damage, and inadequate ventilation common in older PA homes',
  ],
  estimate_sent: [
    'Include a line item for each material: shingles, underlayment, ice & water shield (required at eaves in PA), drip edge, flashing, ridge cap',
    'Add a conditional deck repair line — many valley homes have original 1x6 board decking that needs overlay or replacement during tear-off',
    'Send the estimate within 24 hours of inspection — close rate drops significantly after 48 hours, especially during spring roofing season',
    'Follow up by phone 48 hours after sending — in Mennonite communities, a face-to-face visit often works better than phone or email',
  ],
  contract_signed: [
    'Collect the deposit and confirm payment method before scheduling — some rural customers prefer check or cash; confirm before crew day',
    'Check permit requirements — varies by township in Union, Snyder, and Northumberland counties. Some townships don\'t require permits for re-roofs',
    'Confirm material selections: shingle color, brand, and warranty tier. Order from Boise Cascade Sunbury or ABC Supply for fastest delivery.',
    'Send a written confirmation with the start date, scope, and payment schedule — weather delays are common April through November in central PA',
  ],
  materials_ordered: [
    'Order from Boise Cascade Sunbury or ABC Supply — lock pricing now, material costs shift seasonally and specific colors can be backordered',
    'Schedule delivery for the day before or morning of install — confirm the driveway can handle a delivery truck on rural properties',
    'Verify the order includes ice & water shield for eaves, valleys, and around penetrations — PA code requires it in most jurisdictions',
    'Confirm dumpster delivery — check if the township requires a road permit for dumpster placement on rural roads',
  ],
  scheduled_for_install: [
    'Assign the crew lead and confirm start date — spring is peak season in the valley, so lock crew availability early',
    'Send the homeowner a reminder with start date, crew arrival time (usually 7 AM), and what to expect — noise, debris, driveway access',
    'Verify the permit is in hand if required — do not start without it. Check township requirements for Union, Snyder, or Northumberland counties.',
    'Check the 5-day forecast one more time — PA spring storms can push a job mid-week. Have a backup date ready.',
  ],
  in_progress: [
    'Check in with the crew lead at noon — confirm daily progress, flag material shortages, and check weather for afternoon thunderstorms',
    'Take photos at each phase using the photo categories: Before, During Tear-off, Decking, Underlayment, and Final — they protect you on warranty claims',
    'Get written customer approval before adding any scope found during tear-off — rotted board decking and ice dam damage are common surprises in valley homes',
    'Run a magnetic nail sweep at end of each day — especially critical on rural properties where customers mow their own fields nearby',
  ],
  punch_list: [
    'Walk the roof and property with the crew lead — check ridge cap alignment, all flashing seals, and drip edge continuity',
    'Inspect all flashing points carefully — limestone chimneys and older skylights are the most common callback issues in central PA',
    'Verify gutter reattachment, downspout alignment, and soffit/fascia condition — PA freeze-thaw cycles punish any loose connections',
    'Run a full magnetic nail sweep — driveway, lawn, sidewalk, and gravel areas. One nail in a tire costs more in goodwill than the sweep costs in time.',
  ],
  completed: [
    'Walk the completed job with the customer before requesting final payment — let them see the quality up close from the ground',
    'Take final photos from all four sides for your portfolio — drone shots of valley homes with mountain backdrops make great marketing content',
    'File the permit close-out if required by the township — some PA jurisdictions issue lien notices if not closed within 30 days',
    'Ask for a Google review and one referral — word of mouth is everything in the Susquehanna Valley. This is your highest-converting window.',
  ],
  invoiced: [
    'Send the final invoice within 24 hours of the walkthrough — delays signal disorganization and give time for second thoughts',
    'Include before/after photos and a warranty summary — visual proof of the transformation reinforces value, especially for insurance-paid work',
    'Set a 5-business-day follow-up reminder — if payment hasn\'t arrived, call directly. Don\'t rely on email for rural PA customers.',
    'For insurance jobs, confirm the supplement has been filed and the adjuster (Erie, State Farm, Nationwide) has the final documentation',
  ],
  paid: [
    'Record the payment and close the job immediately — open jobs distort your pipeline and make your board look cluttered',
    'Mail or email the warranty certificate and manufacturer registration — this trust-building step costs nothing and protects you on future claims',
    'Schedule a 6-month follow-up call to check on the roof and ask for referrals — valley communities are tight-knit, one good job leads to the next',
    'Archive the job folder with all photos, permits, signed contracts, and payment records — clean documentation protects you for years',
  ],
};
const TODAY = new Date().toISOString().slice(0, 10);

// ─── Job Phases ──────────────────────────────────────────────────────────────
const COMMERCIAL_PHASES = [
  { id: 1, label: 'Pre-Construction' },
  { id: 2, label: 'Tear-Off' },
  { id: 3, label: 'Deck Repair / Inspection' },
  { id: 4, label: 'Insulation' },
  { id: 5, label: 'Membrane / Roofing System' },
  { id: 6, label: 'Flashing & Details' },
  { id: 7, label: 'Final Inspection & Close-Out' },
];

const RESIDENTIAL_PHASES = [
  { id: 1, label: 'Pre-Construction' },
  { id: 2, label: 'Tear-Off' },
  { id: 3, label: 'Install' },
  { id: 4, label: 'Close-Out' },
];

function getDefaultPhases(jobType) {
  return jobType === 'Commercial' ? COMMERCIAL_PHASES : RESIDENTIAL_PHASES;
}


// ─── Jobs Data ────────────────────────────────────────────────────────────────
// ─── Projects (parent: customer relationship at a property) ──────────────────
const DEMO_PROJECTS = [
  { id: 'p-103', customerName: 'Tina & Mark Bowman', address: '227 Market St, Lewisburg PA 17837', customerPhone: '(570) 524-3947', customerEmail: 'tinabowman@hotmail.com', createdDate: '2026-02-22', assignedSalesperson: 'Mark Sensenig', notes: 'Erie Insurance claim — wind damage. Closed out.', recentActivity: [], issues: [] },
  { id: 'p-108', customerName: 'Joe & Barb Hartman', address: '78 Pine Creek Rd, Watsontown PA 17777', customerPhone: '(570) 538-2189', customerEmail: 'hartman.joe@gmail.com', createdDate: '2026-02-18', assignedSalesperson: 'Mark Sensenig', notes: '', recentActivity: [], issues: [] },
  { id: 'p-201', customerName: 'Christ Lutheran Church', address: '130 S 5th St, Mifflinburg PA 17844', customerPhone: '(570) 966-1842', customerEmail: 'office@christlutheranmifflinburg.org', createdDate: '2026-03-10', assignedSalesperson: 'Mark Sensenig', notes: 'Historic sanctuary roof. Nationwide claim.', recentActivity: [], issues: [] },
  { id: 'p-202', customerName: 'Amos Stoltzfus', address: '1240 Strickler Rd, Mifflinburg PA 17844', customerPhone: '(570) 966-2453', customerEmail: '', createdDate: '2026-03-21', assignedSalesperson: 'Mark Sensenig', notes: 'Farmhouse — no Sunday work. Erie Insurance claim.', recentActivity: [], issues: [] },
  { id: 'p-203', customerName: 'Betty Shumaker', address: '142 Chestnut St, Mifflinburg PA 17844', customerPhone: '(570) 966-4182', customerEmail: 'bettyshumaker@gmail.com', createdDate: '2026-03-22', assignedSalesperson: 'Tara Zimmerman', notes: '', recentActivity: [], issues: [] },
  { id: 'p-204', customerName: 'Dan Sensenig', address: '85 Penns Creek Rd, Selinsgrove PA 17870', customerPhone: '(570) 374-2891', customerEmail: 'dsensenig@verizon.net', createdDate: '2026-03-15', assignedSalesperson: 'Mark Sensenig', notes: '', recentActivity: [], issues: [] },
  { id: 'p-205', customerName: 'Hotel Hershey Annex', address: '100 Hotel Rd, Hershey PA 17033', customerPhone: '(717) 533-2171', customerEmail: 'facilities@hotelhershey.com', createdDate: '2026-03-02', assignedSalesperson: 'Mark Sensenig', notes: 'Historic property. Must match existing copper details.', recentActivity: [], issues: [] },
  { id: 'p-206', customerName: 'Earl Yoder', address: '320 Buffalo Rd, Lewisburg PA 17837', customerPhone: '(570) 524-7218', customerEmail: '', createdDate: '2026-03-28', assignedSalesperson: 'Tara Zimmerman', notes: '', recentActivity: [], issues: [] },
  { id: 'p-207', customerName: 'Market Street Commons', address: '118 Market St, Sunbury PA 17801', customerPhone: '(570) 286-3940', customerEmail: 'manager@marketstreetcommons.com', createdDate: '2026-03-08', assignedSalesperson: 'Mark Sensenig', notes: '', recentActivity: [], issues: [] },
  { id: 'p-208', customerName: 'Ray Brubaker', address: '410 N Front St, Milton PA 17847', customerPhone: '(570) 742-3618', customerEmail: 'rbrubaker@windstream.net', createdDate: '2026-03-19', assignedSalesperson: 'Mark Sensenig', notes: 'State Farm claim — storm damage. Harness required.', recentActivity: [], issues: [] },
  { id: 'p-209', customerName: 'Messiah Village', address: '100 Mt Allen Dr, Mechanicsburg PA 17055', customerPhone: '(717) 591-7400', customerEmail: 'facilities@messiahvillage.org', createdDate: '2026-02-26', assignedSalesperson: 'Mark Sensenig', notes: 'Multi-building campus. Phased installation.', recentActivity: [], issues: [] },
  { id: 'p-210', customerName: 'Riverwoods HOA — Phase 2', address: '200 Riverwoods Dr, Lewisburg PA 17837', customerPhone: '(570) 524-9182', customerEmail: 'board@riverwoodshoa.com', createdDate: '2026-03-04', assignedSalesperson: 'Mark Sensenig', notes: 'Buildings 5-8. Waiting on HOA board for start date.', recentActivity: [], issues: [] },
  { id: 'p-211', customerName: 'Eli Beiler', address: '892 Old Turnpike Rd, Mifflinburg PA 17844', customerPhone: '(570) 966-3742', customerEmail: '', createdDate: '2026-04-01', assignedSalesperson: 'Tara Zimmerman', notes: 'Solar reinstall coordinated with homeowner\'s electrician.', recentActivity: [], issues: [] },
  { id: 'p-212', customerName: 'Wengert Residence', address: '54 Buffalo Cross Rd, Lewisburg PA 17837', customerPhone: '(570) 524-6481', customerEmail: 'mwengert@gmail.com', createdDate: '2026-04-03', assignedSalesperson: 'Tara Zimmerman', notes: 'Persistent leak at upslope side of chimney.', recentActivity: [], issues: [] },
  // ── Yutzy: project created by lead→project conversion when their quote signed ──
  { id: 'p-yutzy-2026-04', customerName: 'James & Karen Yutzy', address: '88 Furnace Rd, Lewisburg PA 17837', customerPhone: '(570) 524-1962', customerEmail: 'kyutzy@frontiernet.net', createdDate: '2026-04-08', assignedSalesperson: 'Mark Sensenig', notes: 'Created from signed quote RIDGE-2026-0043. Erie Insurance claim — wind damage.', recentActivity: [{ timestamp: '2026-04-08T16:32:00Z', type: 'conversion', message: 'Lead converted to project from signed quote' }], issues: [] },
];

// ─── Scopes (child: one type of work within a project) ───────────────────────
// scope.id is kept numeric and matches the legacy job id so chats, photos,
// change orders, and other references that store the id as a string remain
// stable across the refactor.
const DEMO_SCOPES = [
  {
    id: 103, projectId: 'p-103',
    scopeType: 'Storm Damage', status: 'Complete', value: 18700,
    scheduledDate: '2026-03-14', completedSteps: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    notes: 'Erie Insurance claim — wind damage. 24 sq architectural. Adjuster approved full replacement. Closed out.',
    phaseTemplate: 'residential-4-phase', currentPhase: 4, completedPhases: [1, 2, 3, 4],
  },
  {
    id: 108, projectId: 'p-108',
    scopeType: 'Skylight', status: 'Complete', value: 4800,
    scheduledDate: '2026-03-11', completedSteps: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    notes: '2 Velux curb-mount skylights installed. Flashing sealed, interior trim complete.',
    phaseTemplate: 'residential-4-phase', currentPhase: 4, completedPhases: [1, 2, 3, 4],
  },
  {
    id: 201, projectId: 'p-201',
    scopeType: 'Full Replacement', status: 'In Progress', value: 80828, hours: 9,
    scheduledDate: '2026-04-14', completedSteps: [1, 2, 3, 4, 5, 6],
    notes: '118 sq Peach Bottom slate, steep pitch, 2 chimneys. Nationwide claim. Historic sanctuary roof.',
    duration: 5,
    phaseTemplate: 'commercial-7-phase', currentPhase: 5, completedPhases: [1, 2, 3, 4],
    changeOrders: [
      { id: 'co-201-1', title: 'Additional Decking — 14 sheets OSB found rotted', description: 'North slope decking rotted through at eave line. 14 sheets OSB replacement required. Discovered during tear-off.', amount: 588, status: 'Approved', date: '2026-04-15', phase: 3 },
    ],
  },
  {
    id: 202, projectId: 'p-202',
    scopeType: 'Full Replacement', status: 'Scheduled', value: 18600, hours: 8,
    scheduledDate: '2026-05-23', completedSteps: [1, 2],
    notes: '38 sq, 6/12 pitch. GAF Timberline HDZ Charcoal. 1 layer tear-off. Storm damage — Erie Insurance claim. Farmhouse — no Sunday work.',
    duration: 2,
    phaseTemplate: 'residential-4-phase', currentPhase: 1, completedPhases: [],
  },
  {
    id: 203, projectId: 'p-203',
    scopeType: 'Gutter Install', status: 'Scheduled', value: 3980, hours: 6,
    scheduledDate: '2026-05-26', completedSteps: [1],
    notes: '180 LF 6" K-style seamless aluminum. 4 downspouts. Old gutter removal included.',
    phaseTemplate: 'residential-4-phase', currentPhase: 1, completedPhases: [],
  },
  {
    id: 204, projectId: 'p-204',
    scopeType: 'Full Replacement', status: 'Scheduled', value: 15110, hours: 4,
    scheduledDate: '2026-06-03', completedSteps: [],
    notes: '34 sq, GAF Timberline HDZ. Standard pitch. Currently on punch list stage.',
    duration: 2,
    phaseTemplate: 'residential-4-phase', currentPhase: 4, completedPhases: [1, 2, 3],
    changeOrders: [
      { id: 'co-204-1', title: 'Ridge Cap Extension — west gable', description: 'West gable ridge cap 12 LF short of full coverage. Extend to match east side.', amount: 210, status: 'Approved', date: '2026-04-19', phase: 3 },
    ],
  },
  {
    id: 205, projectId: 'p-205',
    scopeType: 'Full Replacement', status: 'Scheduled', value: 198000, hours: 10,
    scheduledDate: '2026-06-08', completedSteps: [1, 2],
    notes: '180 sq standing seam metal. Complex roofline, historic property. 4 large chimneys. Must match existing copper details.',
    duration: 8,
    phaseTemplate: 'commercial-7-phase', currentPhase: 1, completedPhases: [],
  },
  {
    id: 206, projectId: 'p-206',
    scopeType: 'Flashing Repair', status: 'Scheduled', value: 1850, hours: 3,
    scheduledDate: '2026-05-29', completedSteps: [],
    notes: 'Chimney valley flashing repair. Step + counter flashing replacement. Match existing architectural shingles.',
    phaseTemplate: 'residential-4-phase', currentPhase: 1, completedPhases: [],
  },
  {
    id: 207, projectId: 'p-207',
    scopeType: 'Full Replacement', status: 'Scheduled', value: 42800, hours: 8,
    scheduledDate: '2026-06-05', completedSteps: [1, 2],
    notes: '85 sq TPO 60-mil commercial flat roof. Insulation board + cover board. Edge metal perimeter.',
    duration: 4,
    phaseTemplate: 'commercial-7-phase', currentPhase: 2, completedPhases: [1],
  },
  {
    id: 208, projectId: 'p-208',
    scopeType: 'Full Replacement', status: 'Scheduled', value: 24800, hours: 7,
    scheduledDate: '2026-05-30', completedSteps: [1, 2],
    notes: '52 sq, 8/12 steep pitch. CertainTeed Landmark Pro Moire Black. Storm damage — State Farm claim. Harness required.',
    duration: 3,
    phaseTemplate: 'residential-4-phase', currentPhase: 1, completedPhases: [],
  },
  {
    id: 211, projectId: 'p-211',
    scopeType: 'Solar Panel Reinstall', status: 'Scheduled', value: 5600, hours: 6,
    scheduledDate: '2026-06-10', completedSteps: [],
    notes: 'Uninstall + reinstall 24-panel grid-tied array for full reroof underneath. Coordinate with homeowner\'s electrician for disconnect/reconnect. Panel removal day 1, shingles days 2-3, panel reinstall day 4.',
    duration: 4,
    phaseTemplate: 'residential-4-phase', currentPhase: 1, completedPhases: [],
  },
  {
    id: 212, projectId: 'p-212',
    scopeType: 'Chimney Cricket', status: 'Scheduled', value: 2200, hours: 4,
    scheduledDate: '2026-05-27', completedSteps: [],
    notes: 'Build 36" cricket behind 5\' wide masonry chimney. Persistent leak at upslope side. Frame, deck, ice & water shield, integrate with existing field. Match Owens Corning Duration Onyx Black.',
    phaseTemplate: 'residential-4-phase', currentPhase: 1, completedPhases: [],
  },
  {
    id: 209, projectId: 'p-209',
    scopeType: 'Full Replacement', status: 'Scheduled', value: 224000,
    completedSteps: [1, 2],
    notes: '240 sq mixed TPO + standing seam metal. Multi-building campus. Phased installation.',
    phaseTemplate: 'commercial-7-phase', currentPhase: 1, completedPhases: [],
  },
  {
    id: 210, projectId: 'p-210',
    scopeType: 'Full Replacement', status: 'Scheduled', value: 94000,
    completedSteps: [1],
    notes: 'Phase 2: buildings 5-8. ~96 sq architectural. Waiting on HOA board to approve start date.',
    phaseTemplate: 'commercial-7-phase', currentPhase: 1, completedPhases: [],
  },
  // ── Yutzy scopes: materialised from signed quote at lead-to-project conversion ──
  {
    id: 2001, projectId: 'p-yutzy-2026-04',
    scopeType: 'Full Replacement', status: 'Scheduled', value: 23400, hours: 9,
    scheduledDate: '2026-05-22', completedSteps: [],
    notes: '48 sq, 7/12 pitch. CertainTeed Landmark Pro Weathered Wood. 1 layer tear-off. Erie Insurance claim — wind damage. Created from signed quote.',
    duration: 3,
    phaseTemplate: 'residential-4-phase', currentPhase: 1, completedPhases: [],
  },
  // ── Multi-scope showcase: secondary scopes on three projects ──
  {
    id: 1001, projectId: 'p-202',
    scopeType: 'Gutter Install', status: 'Scheduled', value: 4800, hours: 5,
    scheduledDate: '2026-05-31', completedSteps: [],
    notes: '210 LF 6" K-style seamless aluminum. 5 downspouts. Coordinate same-week as roof replacement so scaffolding is shared.',
    phaseTemplate: 'residential-4-phase', currentPhase: 1, completedPhases: [],
  },
  {
    id: 1002, projectId: 'p-210',
    scopeType: 'Soffit/Fascia Repair', status: 'Scheduled', value: 12400, hours: 14,
    scheduledDate: '2026-06-09', completedSteps: [],
    notes: 'Buildings 5-8: replace ~340 LF of damaged soffit + 220 LF aluminum fascia wrap. Coordinated with re-roof phase.',
    phaseTemplate: 'commercial-7-phase', currentPhase: 1, completedPhases: [],
  },
  {
    id: 1003, projectId: 'p-201',
    scopeType: 'Flashing Repair', status: 'Scheduled', value: 3200, hours: 6,
    scheduledDate: '2026-06-01', completedSteps: [],
    notes: 'Re-flash both chimneys (north and south transepts). New step + counter flashing in copper to match existing details. Tie in with completed slate field.',
    phaseTemplate: 'commercial-7-phase', currentPhase: 1, completedPhases: [],
  },
];

// ─── Project/Scope helpers ───────────────────────────────────────────────────
function getProjectById(id, projects = DEMO_PROJECTS) {
  return projects.find(p => p.id === id);
}
function getScopesForProject(projectId, scopes = DEMO_SCOPES) {
  return scopes.filter(s => s.projectId === projectId);
}
// eslint-disable-next-line no-unused-vars
function getProjectTotalValue(projectId, scopes = DEMO_SCOPES) {
  return getScopesForProject(projectId, scopes).reduce((sum, s) => sum + (s.value || 0), 0);
}
function getProjectStatus(projectId, scopes = DEMO_SCOPES) {
  const projScopes = getScopesForProject(projectId, scopes);
  if (projScopes.length === 0) return 'Scheduled';
  if (projScopes.some(s => s.status === 'In Progress')) return 'In Progress';
  if (projScopes.every(s => s.status === 'Complete')) return 'Complete';
  // First non-complete scope's status, falling back to first
  return (projScopes.find(s => s.status !== 'Complete') || projScopes[0]).status;
}
// eslint-disable-next-line no-unused-vars
function getProjectsByStage(stage, projects = DEMO_PROJECTS, scopes = DEMO_SCOPES) {
  return projects.filter(p => getProjectStatus(p.id, scopes) === stage);
}

// ─── Quotes (one project can have many quotes; one quote can have many sections) ──
const DEMO_QUOTES = [
  {
    id: 'q-1001',
    quoteNumber: 'RIDGE-2026-0042',
    leadId: null,
    projectId: 'p-202', // Amos Stoltzfus — change-order on an existing project
    mode: 'residential',
    status: 'draft',
    createdDate: '2026-04-12T09:14:00Z',
    sentDate: null,
    viewedDate: null,
    signedDate: null,
    expirationDate: '2026-06-20',
    subtotal: 18600,
    tax: 0,
    total: 18600,
    sections: [
      {
        id: 'qs-1001-a', scopeId: 202, scopeType: 'Full Replacement',
        narrative: 'Full tear-off and replacement of the existing 1-layer composition roof. GAF Timberline HDZ Charcoal. New synthetic underlayment, ice & water shield at eaves and valleys, drip edge all sides, ridge cap. Erie Insurance claim — wind damage from 3/14 storm. Farmhouse — no Sunday work.',
        subtotal: 18600,
        lineItems: [
          { id: 'li-1', description: 'Tear-off, 1 layer composition shingles', category: 'Labor', quantity: 38, unit: 'SQ', unitPrice: 50, extension: 1900, notes: null },
          { id: 'li-2', description: 'Decking inspection + minor patch allowance', category: 'Allowance', quantity: 4, unit: 'EA', unitPrice: 65, extension: 260, notes: 'Per 4x8 OSB sheet replaced — billed only if used' },
          { id: 'li-3', description: 'Synthetic underlayment', category: 'Material', quantity: 38, unit: 'SQ', unitPrice: 25, extension: 950, notes: null },
          { id: 'li-4', description: 'Ice & water shield at eaves and valleys', category: 'Material', quantity: 10, unit: 'SQ', unitPrice: 90, extension: 900, notes: '3ft up from eave + valleys' },
          { id: 'li-5', description: 'Drip edge, aluminum', category: 'Material', quantity: 220, unit: 'LF', unitPrice: 3.5, extension: 770, notes: null },
          { id: 'li-6', description: 'GAF Timberline HDZ Charcoal shingles', category: 'Material', quantity: 38, unit: 'SQ', unitPrice: 130, extension: 4940, notes: '6/12 pitch — within standard waste factor' },
          { id: 'li-7', description: 'Shingle installation labor', category: 'Labor', quantity: 38, unit: 'SQ', unitPrice: 185, extension: 7030, notes: null },
          { id: 'li-8', description: 'Ridge cap shingles', category: 'Material', quantity: 65, unit: 'LF', unitPrice: 9, extension: 585, notes: null },
          { id: 'li-9', description: 'Dumpster — 20 yard, single haul', category: 'Equipment', quantity: 1, unit: 'EA', unitPrice: 525, extension: 525, notes: null },
          { id: 'li-10', description: 'Municipal building permit', category: 'Equipment', quantity: 1, unit: 'EA', unitPrice: 200, extension: 200, notes: null },
          { id: 'li-11', description: 'Magnetic nail sweep + jobsite cleanup', category: 'Labor', quantity: 1, unit: 'LS', unitPrice: 240, extension: 240, notes: null },
          { id: 'li-12', description: 'GAF System Plus 50-year warranty registration', category: 'Adder', quantity: 1, unit: 'LS', unitPrice: 300, extension: 300, notes: 'Eligible — Master Elite certified' },
        ],
      },
    ],
    attachments: [],
    signature: null,
    depositAmount: 0,
    depositPaid: false,
    paymentIntentId: null,
    shareToken: 'qst_a8f3b2c1d9e4f6a2b8c7d3e1f5a9b4c2',
    terms: 'Standard residential terms — payment due net 30 from completion. Workmanship warranted 5 years. Materials carry manufacturer warranty per shingle.',
    exclusions: ['Decking replacement beyond 4-sheet allowance billed at $65/sheet', 'Soffit/fascia repair not included', 'Skylight reflashing not included'],
    alternates: [],
    paymentSchedule: [],
    customerInteractions: [],
    agentConversation: [
      { role: 'user', timestamp: '2026-04-12T09:11:00Z', content: 'Stoltzfus farmhouse — 38 squares, GAF Timberline HDZ Charcoal, tear off 1 layer, 6/12 pitch. Erie Insurance claim.' },
      { role: 'assistant', timestamp: '2026-04-12T09:11:18Z', content: 'Drafted the full replacement scope with HDZ Charcoal — added synthetic underlayment, ice & water at eaves/valleys, ridge cap, dumpster, permit. Decking allowance left at 4 sheets — confirm or correct.' },
      { role: 'user', timestamp: '2026-04-12T09:13:45Z', content: 'Looks right. Save it for now.' },
    ],
  },
  // ── Yutzy: signed quote that converted the lead to a project ──
  {
    id: 'q-1002',
    quoteNumber: 'RIDGE-2026-0043',
    leadId: null,
    projectId: 'p-yutzy-2026-04',
    mode: 'residential',
    status: 'signed',
    createdDate: '2026-04-05T11:20:00Z',
    sentDate: '2026-04-05T13:40:00Z',
    viewedDate: '2026-04-06T08:14:00Z',
    signedDate: '2026-04-08T16:32:00Z',
    expirationDate: '2026-05-05',
    subtotal: 23400, tax: 0, total: 23400,
    sections: [
      {
        id: 'qs-1002-a', scopeId: 2001, scopeType: 'Full Replacement',
        narrative: 'Full tear-off and replacement, 48 sq, 7/12 pitch. CertainTeed Landmark Pro Weathered Wood. 1 layer tear-off. Erie Insurance claim — wind damage. Two-story access included.',
        subtotal: 23400,
        lineItems: [
          { id: 'li-y1', description: 'Tear-off, 1 layer composition shingles', category: 'Labor', quantity: 48, unit: 'SQ', unitPrice: 55, extension: 2640, notes: '2nd story access adder' },
          { id: 'li-y2', description: 'Decking inspection + 4-sheet OSB allowance', category: 'Allowance', quantity: 4, unit: 'EA', unitPrice: 65, extension: 260, notes: 'Billed only if used' },
          { id: 'li-y3', description: 'Synthetic underlayment', category: 'Material', quantity: 48, unit: 'SQ', unitPrice: 25, extension: 1200, notes: null },
          { id: 'li-y4', description: 'Ice & water shield at eaves and valleys', category: 'Material', quantity: 12, unit: 'SQ', unitPrice: 90, extension: 1080, notes: null },
          { id: 'li-y5', description: 'Drip edge, aluminum', category: 'Material', quantity: 260, unit: 'LF', unitPrice: 3.5, extension: 910, notes: null },
          { id: 'li-y6', description: 'CertainTeed Landmark Pro Weathered Wood shingles', category: 'Material', quantity: 48, unit: 'SQ', unitPrice: 145, extension: 6960, notes: 'SBS-modified architectural' },
          { id: 'li-y7', description: 'Shingle installation labor, 2-story', category: 'Labor', quantity: 48, unit: 'SQ', unitPrice: 200, extension: 9600, notes: '+10% 2-story adder applied' },
          { id: 'li-y8', description: 'Ridge cap shingles', category: 'Material', quantity: 80, unit: 'LF', unitPrice: 9, extension: 720, notes: null },
          { id: 'li-y9', description: 'Dumpster — 30 yard, single haul', category: 'Equipment', quantity: 1, unit: 'EA', unitPrice: 650, extension: 650, notes: 'Larger dumpster for 48 sq tear-off' },
          { id: 'li-y10', description: 'Municipal building permit', category: 'Equipment', quantity: 1, unit: 'EA', unitPrice: 250, extension: 250, notes: null },
          { id: 'li-y11', description: 'Magnetic nail sweep + jobsite cleanup', category: 'Labor', quantity: 1, unit: 'LS', unitPrice: 280, extension: 280, notes: null },
          { id: 'li-y12', description: 'CertainTeed Integrity Roof System warranty', category: 'Adder', quantity: 1, unit: 'LS', unitPrice: 350, extension: 350, notes: 'SureStart Plus' },
        ],
      },
    ],
    attachments: [],
    signature: { name: 'James Yutzy', timestamp: '2026-04-08T16:32:00Z', method: 'typed' },
    depositAmount: 7020,
    depositPaid: false,
    paymentIntentId: null,
    shareToken: 'qst_b9e4c3d2a1f0e5b7c8d4f3a9b2c7d1e6',
    terms: 'Standard residential terms — 30% deposit at signing, 30% at material delivery, balance net 15 days from completion. Workmanship warranted 5 years.',
    exclusions: ['Decking replacement beyond 4-sheet allowance billed at $65/sheet', 'Soffit/fascia repair not included', 'Skylight reflashing not included'],
    alternates: [],
    paymentSchedule: [],
    customerInteractions: [
      { timestamp: '2026-04-06T08:14:00Z', type: 'viewed', message: 'Quote opened' },
      { timestamp: '2026-04-06T08:16:30Z', type: 'expanded', message: 'Roof System section expanded' },
      { timestamp: '2026-04-08T16:32:00Z', type: 'signed', message: 'Quote signed' },
    ],
    agentConversation: [],
  },
  // ── Christ Lutheran change order: draft attached to existing project ──
  {
    id: 'q-1003',
    quoteNumber: 'RIDGE-2026-0044',
    leadId: null,
    projectId: 'p-201',
    mode: 'commercial',
    status: 'draft',
    createdDate: '2026-04-18T10:45:00Z',
    sentDate: null, viewedDate: null, signedDate: null,
    expirationDate: '2026-06-20',
    subtotal: 5260, tax: 0, total: 5260,
    sections: [
      {
        id: 'qs-1003-a', scopeId: null, scopeType: 'Flashing Repair',
        narrative: 'Additional chimney flashing work uncovered during tear-off. Re-flash south transept chimney with new copper step + counter flashing. Tie in with completed slate field. Discovered during in-progress slate replacement.',
        subtotal: 5260,
        lineItems: [
          { id: 'li-cl1', description: 'Copper step flashing fabrication', category: 'Material', quantity: 28, unit: 'LF', unitPrice: 38, extension: 1064, notes: 'Match existing detail' },
          { id: 'li-cl2', description: 'Copper counter flashing fabrication', category: 'Material', quantity: 28, unit: 'LF', unitPrice: 42, extension: 1176, notes: null },
          { id: 'li-cl3', description: 'Lead wedge anchors', category: 'Material', quantity: 56, unit: 'EA', unitPrice: 6, extension: 336, notes: null },
          { id: 'li-cl4', description: 'Mason cuts and grinding', category: 'Labor', quantity: 6, unit: 'HR', unitPrice: 95, extension: 570, notes: null },
          { id: 'li-cl5', description: 'Flashing installation labor', category: 'Labor', quantity: 14, unit: 'HR', unitPrice: 110, extension: 1540, notes: 'Includes harness setup + steep-pitch surcharge' },
          { id: 'li-cl6', description: 'Sealant + counter-flashing reglet cement', category: 'Material', quantity: 1, unit: 'LS', unitPrice: 240, extension: 240, notes: null },
          { id: 'li-cl7', description: 'Sealing / cleanup', category: 'Labor', quantity: 1, unit: 'LS', unitPrice: 334, extension: 334, notes: null },
        ],
      },
    ],
    attachments: [],
    signature: null,
    depositAmount: 0,
    depositPaid: false,
    paymentIntentId: null,
    shareToken: 'qst_c0d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9',
    terms: 'Commercial change order — net 30 from completion, ties into existing master agreement.',
    exclusions: ['Chimney repointing not included', 'Chimney cap replacement not included'],
    alternates: [],
    paymentSchedule: [],
    customerInteractions: [],
    agentConversation: [
      { role: 'user', timestamp: '2026-04-18T10:43:00Z', content: 'Christ Lutheran — found the south transept chimney flashing is shot. Need a change order: 28 LF copper step + counter, mason cuts, install.' },
      { role: 'assistant', timestamp: '2026-04-18T10:43:22Z', content: 'Drafted the change order — 28 LF of copper step and counter flashing, lead wedge anchors, mason cuts at 6 hours, install labor at 14 hours with steep-pitch and harness already factored in.' },
    ],
  },
];

// ─── Quote helpers ───────────────────────────────────────────────────────────
// A Quote belongs to EXACTLY ONE of: a Lead (pre-signature) or a Project
// (post-conversion change order, or quote created against an existing
// project). Never both, never neither. createQuote enforces this.
// eslint-disable-next-line no-unused-vars
function getQuotesForProject(projectId, quotes = DEMO_QUOTES) {
  return quotes.filter(q => q.projectId === projectId);
}
// eslint-disable-next-line no-unused-vars
function getQuotesForLead(leadId, quotes = DEMO_QUOTES) {
  return quotes.filter(q => q.leadId === leadId);
}
// eslint-disable-next-line no-unused-vars
function getQuoteById(id, quotes = DEMO_QUOTES) {
  return quotes.find(q => q.id === id);
}
function getQuoteTotal(quote) {
  if (!quote || !Array.isArray(quote.sections)) return 0;
  return quote.sections.reduce((sum, sec) => {
    const secTotal = (sec.lineItems || []).reduce((s, li) => s + (Number(li.extension) || 0), 0);
    return sum + secTotal;
  }, 0);
}
function generateQuoteNumber(existing) {
  const year = new Date().getFullYear();
  const prefix = `RIDGE-${year}-`;
  // Merge: seeded DEMO_QUOTES + any user-saved quotes from localStorage so
  // sequential numbers across the merged set are unique. The caller may also
  // pass in-flight quotes that haven't been persisted yet.
  const pool = [...DEMO_QUOTES];
  try {
    const raw = localStorage.getItem('cl_quotes');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) pool.push(...parsed);
    }
  } catch (_e) { /* ignore */ }
  if (Array.isArray(existing)) pool.push(...existing);
  const sameYear = pool.filter(q => q && q.quoteNumber && q.quoteNumber.startsWith(prefix));
  const maxN = sameYear.reduce((m, q) => {
    const n = parseInt(q.quoteNumber.slice(prefix.length), 10);
    return Number.isFinite(n) ? Math.max(m, n) : m;
  }, 0);
  return `${prefix}${String(maxN + 1).padStart(4, '0')}`;
}
function generateShareToken() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let token = 'qst_';
  for (let i = 0; i < 32; i++) token += chars[Math.floor(Math.random() * chars.length)];
  return token;
}
// eslint-disable-next-line no-unused-vars
function createQuote({ leadId = null, projectId = null, mode = 'residential', sections = [], terms = '' }) {
  // Enforce exactly-one-parent invariant. A quote is anchored either to a
  // pre-conversion lead or to a project (post-conversion change order /
  // additional scope sold to an existing customer). Never both, never neither.
  if ((leadId && projectId) || (!leadId && !projectId)) {
    throw new Error('createQuote: a quote must have exactly one of leadId or projectId set.');
  }
  const now = new Date();
  const exp = new Date(now); exp.setDate(exp.getDate() + 30);
  const subtotal = sections.reduce((sum, sec) => sum + (sec.subtotal || (sec.lineItems || []).reduce((s, li) => s + (li.extension || 0), 0)), 0);
  return {
    id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    quoteNumber: generateQuoteNumber(),
    leadId, projectId,
    mode,
    status: 'draft',
    createdDate: now.toISOString(),
    sentDate: null, viewedDate: null, signedDate: null,
    expirationDate: exp.toISOString().slice(0, 10),
    subtotal, tax: 0, total: subtotal,
    sections,
    attachments: [], signature: null,
    depositAmount: 0, depositPaid: false, paymentIntentId: null,
    shareToken: generateShareToken(),
    terms: terms || 'Standard residential terms — payment due net 30 from completion. Workmanship warranted 5 years.',
    exclusions: [], alternates: [], paymentSchedule: [],
    customerInteractions: [], agentConversation: [],
  };
}
// eslint-disable-next-line no-unused-vars
function updateQuote(quote, patch) {
  const next = { ...quote, ...patch };
  const newSubtotal = getQuoteTotal(next);
  if (next.subtotal !== newSubtotal) next.subtotal = newSubtotal;
  if (next.total !== newSubtotal + (next.tax || 0)) next.total = newSubtotal + (next.tax || 0);
  return next;
}
// eslint-disable-next-line no-unused-vars
function deleteQuote(id, quotes) {
  return (quotes || DEMO_QUOTES).filter(q => q.id !== id);
}

// ─── Lead → Project conversion ───────────────────────────────────────────────
// Called when a quote is signed. Creates a Project from the lead's customer
// info, materialises a single Scope of Work from the signed quote (with all
// quote sections nested as billing/itemization groupings inside it), re-anchors
// the quote from leadId → projectId, and marks the lead as converted
// (preserved in the system with convertedToProjectId set; never deleted, so
// historical pipeline reporting still works).
//
// A roof replacement is one scope of work — the quote's 12 (residential) or
// 14 (commercial) sections are sections within that scope, not separate scopes.
// Future multi-scope jobs (e.g., roof + gutters sold as separate work orders)
// are a future feature; not modeled here.
// eslint-disable-next-line no-unused-vars
function convertLeadToProject(lead, signedQuote) {
  if (!lead || !signedQuote) throw new Error('convertLeadToProject: lead and signedQuote required.');
  if (signedQuote.status !== 'signed') {
    throw new Error('convertLeadToProject: quote must be in signed status to convert.');
  }
  if (!signedQuote.leadId || String(signedQuote.leadId) !== String(lead.id)) {
    throw new Error('convertLeadToProject: quote.leadId must match lead.id.');
  }
  const projectId = `p-l${lead.id}-${Date.now().toString(36)}`;
  const project = {
    id: projectId,
    customerName: lead.name,
    address: lead.address || '',
    customerPhone: lead.phone || '',
    customerEmail: lead.email || '',
    createdDate: new Date().toISOString().slice(0, 10),
    assignedSalesperson: lead.assignedSalesperson || '',
    notes: lead.notes || '',
    recentActivity: [],
    issues: [],
  };
  const isCommercial = signedQuote.mode === 'commercial';
  const nameTag = isCommercial ? lead.name : lastWordOf(lead.name);
  const scopeName = signedQuote.title
    || `${isCommercial ? 'Commercial' : 'Residential'} roof — ${nameTag}`;
  const scopeSections = (signedQuote.sections || []).map(sec => ({
    id: sec.id,
    name: sec.sectionName,
    narrative: sec.narrative || '',
    lineItems: Array.isArray(sec.lineItems) ? sec.lineItems.map(li => ({ ...li })) : [],
    subtotal: sec.subtotal || 0,
    optional: !!sec.optional,
  }));
  const grandTotal = (signedQuote.sections || [])
    .filter(s => !s.optional)
    .reduce((sum, s) => sum + (s.subtotal || 0), 0);
  const scopes = [{
    id: `s-${projectId}-1`,
    projectId,
    scopeType: scopeName,
    status: 'Pending Schedule',
    value: grandTotal || signedQuote.total || 0,
    completedSteps: [],
    notes: '',
    phaseTemplate: isCommercial ? 'commercial-7-phase' : 'residential-4-phase',
    currentPhase: null,
    completedPhases: [],
    sections: scopeSections,
  }];
  const updatedQuote = { ...signedQuote, leadId: null, projectId };
  const updatedLead = {
    ...lead,
    stage: 'contract_signed',
    convertedToProjectId: projectId,
    convertedAt: new Date().toISOString(),
  };
  return { project, scopes, updatedQuote, updatedLead };
}

// ─── Legacy DEMO_JOBS adapter ────────────────────────────────────────────────
// Derived from DEMO_PROJECTS + DEMO_SCOPES so existing UI keeps working
// unchanged while subsequent commits rewire individual tabs to the new
// project-centric model. We attach a template-scaffolded `sections` array
// scaled to each scope's value so the ProjectDetail accordion has realistic
// section names + line items to render.
//
// IMPORTANT — sections is exposed via a memoized getter, NOT computed
// eagerly. scaleScopeSections() → buildResidentialSections/buildCommercialSections
// → reads RESIDENTIAL_CATALOG / COMMERCIAL_CATALOG. Those catalogs are
// `const` declarations later in this file, so they're in TDZ at the point
// where DEMO_JOBS is constructed. Eager evaluation throws "Cannot access
// 'RESIDENTIAL_CATALOG' before initialization" at module load. The getter
// defers the work to first read (App render time), by which time all
// module-level consts are initialized. The Map cache keeps section ids and
// references stable across re-renders.
const _demoScopeSectionsCache = new Map();
function getDemoScopeSections(scope) {
  const cached = _demoScopeSectionsCache.get(scope.id);
  if (cached) return cached;
  const isCommercial = scope.phaseTemplate === 'commercial-7-phase';
  const sections = scaleScopeSections(
    isCommercial ? buildCommercialSections() : buildResidentialSections(),
    scope.value || 0,
  );
  _demoScopeSectionsCache.set(scope.id, sections);
  return sections;
}

const DEMO_JOBS = DEMO_SCOPES.map(s => {
  const project = getProjectById(s.projectId) || {};
  const isCommercial = s.phaseTemplate === 'commercial-7-phase';
  return {
    id: s.id,
    projectId: s.projectId,
    customer: project.customerName,
    address: project.address,
    trade: s.scopeType,
    value: s.value,
    status: s.status,
    scheduledDate: s.scheduledDate,
    hours: s.hours,
    completedSteps: s.completedSteps || [],
    notes: s.notes || project.notes || '',
    duration: s.duration,
    jobType: isCommercial ? 'Commercial' : 'Residential',
    currentPhase: s.currentPhase,
    completedPhases: s.completedPhases || [],
    changeOrders: s.changeOrders || [],
    get sections() { return getDemoScopeSections(s); },
  };
});

const DEMO_CREW = [
  // seedHoursThisWeek / seedHoursAllTime drive the CrewTab cards when there
  // are no live time entries (the demo never has any). User accounts leave the
  // seed fields undefined and the live entry computation drives the display.
  { id: 'dc1', name: 'Jake Stoltzfus', role: 'Foreman', phone: '(570) 555-2341', specialties: ['Full Replacement', 'Storm Damage'], seedHoursThisWeek: 42, seedHoursAllTime: 1847 },
  { id: 'dc2', name: 'Tom Bricker', role: 'Install Crew', phone: '(570) 555-3456', specialties: ['Full Replacement', 'Gutter Install', 'Skylight'], seedHoursThisWeek: 38, seedHoursAllTime: 2103 },
  { id: 'dc3', name: 'Luis Ortiz', role: 'Tear-off Crew', phone: '(570) 555-4567', specialties: ['Full Replacement', 'Repair'], seedHoursThisWeek: 40, seedHoursAllTime: 1512 },
  { id: 'dc4', name: 'Ryan Hess', role: 'Ground Support', phone: '(570) 555-5678', specialties: ['Full Replacement', 'Emergency Tarp'], seedHoursThisWeek: 22, seedHoursAllTime: 968 },
  { id: 'dc5', name: 'Mark Sensenig', role: 'Estimator', phone: '(570) 555-6789', specialties: ['Full Replacement', 'Storm Damage', 'Inspection'], seedHoursThisWeek: 8, seedHoursAllTime: 234 },
  { id: 'dc6', name: 'Tara Zimmerman', role: 'Sales Rep', phone: '(570) 555-7890', specialties: ['Full Replacement', 'Storm Damage', 'Gutter Install'], seedHoursThisWeek: 0, seedHoursAllTime: 1289, offThisWeek: true },
];

const DEMO_MESSAGES = [
  // ── Christ Lutheran Church (201) — active tear-off conversation ──
  { id: 'dm-1', jobId: '201', senderId: 'system', senderName: 'System', text: 'Jake Stoltzfus assigned to this job', timestamp: 1744600000000, type: 'system' },
  { id: 'dm-2', jobId: '201', senderId: 'dc1', senderName: 'Jake Stoltzfus', text: 'On site at Christ Lutheran. Ridge cap removed, starting tear-off on damaged section. Weather is clear.', timestamp: 1744610000000, type: 'user' },
  { id: 'dm-3', jobId: '201', senderId: 'dc2', senderName: 'Tom Bricker', text: 'Shingles matched — CertainTeed Landmark Weathered Wood. Picked up 24 bundles from Boise Cascade Sunbury.', timestamp: 1744620000000, type: 'user' },
  { id: 'dm-4', jobId: '201', senderId: 'owner', senderName: 'Demo Owner', text: 'How does the decking look under the tear-off? Any soft spots?', timestamp: 1744630000000, type: 'user' },
  { id: 'dm-5', jobId: '201', senderId: 'dc1', senderName: 'Jake Stoltzfus', text: 'Found 14 sheets of rotted OSB on the north slope near the eave line. Sending photos now. Gonna need a change order.', timestamp: 1744635000000, type: 'user' },
  { id: 'dm-6', jobId: '201', senderId: 'owner', senderName: 'Demo Owner', text: 'Got it. I\'ll write up the CO for $588 — 14 sheets at $42 each. Get those replaced before underlayment goes down.', timestamp: 1744640000000, type: 'user' },
  { id: 'dm-7', jobId: '201', senderId: 'dc4', senderName: 'Ryan Hess', text: 'Dumpster is full. Called for a swap — should be here by 2 PM. Staging debris on the tarp for now.', timestamp: 1744650000000, type: 'user' },
  { id: 'dm-8', jobId: '201', senderId: 'dc1', senderName: 'Jake Stoltzfus', text: 'Underlayment done on north slope. Ice & water shield at eaves and valleys. Starting south side tomorrow AM.', timestamp: 1744680000000, type: 'user' },
  // ── Amos Stoltzfus (202) — farmhouse crew coordination ──
  { id: 'dm-10', jobId: '202', senderId: 'system', senderName: 'System', text: 'Jake Stoltzfus assigned to Stoltzfus farmhouse', timestamp: 1744700000000, type: 'system' },
  { id: 'dm-11', jobId: '202', senderId: 'dc1', senderName: 'Jake Stoltzfus', text: 'Farmhouse — confirmed no Sunday work with Amos. Materials staged in barn. Starting 7 AM Thursday.', timestamp: 1744710000000, type: 'user' },
  { id: 'dm-12', jobId: '202', senderId: 'owner', senderName: 'Demo Owner', text: 'Good. Make sure crew parks on the gravel, not the lawn. Amos is particular about that.', timestamp: 1744715000000, type: 'user' },
  { id: 'dm-13', jobId: '202', senderId: 'dc1', senderName: 'Jake Stoltzfus', text: 'Copy that. Also — GAF Timberline HDZ Charcoal is confirmed. 38 squares loaded on the trailer at Boise Cascade.', timestamp: 1744720000000, type: 'user' },
  // ── Hotel Hershey (205) — slate matching discussion ──
  { id: 'dm-14', jobId: '205', senderId: 'system', senderName: 'System', text: 'Job scheduled for 2026-04-21', timestamp: 1744800000000, type: 'system' },
  { id: 'dm-15', jobId: '205', senderId: 'dc2', senderName: 'Tom Bricker', text: 'Hotel Hershey — need to match Peach Bottom slate exactly. Supplier in York confirmed they have 180 sq in stock.', timestamp: 1744810000000, type: 'user' },
  { id: 'dm-16', jobId: '205', senderId: 'owner', senderName: 'Demo Owner', text: 'Get a sample tile shipped before we commit. Patricia at the hotel wants to see it against the existing roof first.', timestamp: 1744815000000, type: 'user' },
  { id: 'dm-17', jobId: '205', senderId: 'dc3', senderName: 'Luis Ortiz', text: 'Heads up — the access road behind the annex has a weight limit. Delivery truck needs to come through the main entrance.', timestamp: 1744820000000, type: 'user' },
  // ── Market Street Commons (207) — gutter/fascia discovery ──
  { id: 'dm-18', jobId: '207', senderId: 'system', senderName: 'System', text: 'Jake Stoltzfus assigned to Market St Commons', timestamp: 1744900000000, type: 'system' },
  { id: 'dm-19', jobId: '207', senderId: 'dc1', senderName: 'Jake Stoltzfus', text: 'Fascia on south side is rotted worse than expected. At least 20 LF needs replacing before we can hang gutters.', timestamp: 1744910000000, type: 'user' },
  { id: 'dm-20', jobId: '207', senderId: 'owner', senderName: 'Demo Owner', text: 'Get measurements and I\'ll add fascia board to the materials order. What size — 1x6 or 1x8?', timestamp: 1744915000000, type: 'user' },
  { id: 'dm-21', jobId: '207', senderId: 'dc1', senderName: 'Jake Stoltzfus', text: '1x8 cedar. 20 LF should cover it. Also need 2 tubes of PL Premium for the splice joints.', timestamp: 1744920000000, type: 'user' },
  // ── Dan Sensenig (204) — punch list follow-up ──
  { id: 'dm-22', jobId: '204', senderId: 'system', senderName: 'System', text: 'Tom Bricker assigned to Sensenig punch list', timestamp: 1744950000000, type: 'system' },
  { id: 'dm-23', jobId: '204', senderId: 'dc2', senderName: 'Tom Bricker', text: 'At Sensenig\'s on Penns Creek Rd. Ridge cap looks good. Found one nail pop on the west gable — resealing now.', timestamp: 1744955000000, type: 'user' },
  { id: 'dm-24', jobId: '204', senderId: 'owner', senderName: 'Demo Owner', text: 'Did the magnetic sweep on the driveway and yard? Last thing we need is a nail in someone\'s tire.', timestamp: 1744960000000, type: 'user' },
  { id: 'dm-25', jobId: '204', senderId: 'dc2', senderName: 'Tom Bricker', text: 'Already done. Swept the full perimeter, driveway, and flower beds. All clear. Sensenig signed off — he\'s happy.', timestamp: 1744965000000, type: 'user' },
];

const DEMO_JOB_DURATIONS = {
  103: 2, 108: 1,
  201: 1, 202: 2, 203: 1, 204: 1, 205: 2, 206: 1, 207: 1, 208: 3,
  209: 1, 210: 5,
};

// Demo crew assignments (jobId → [crewId, ...])
const DEMO_ASSIGNMENTS = {
  '103': ['dc1', 'dc2'],                  // Bowman Storm Damage (completed)
  '108': ['dc2'],                          // Hartman Skylight (completed)
  '201': ['dc1', 'dc2'],                  // Christ Lutheran Church
  '202': ['dc1'],                          // Amos Stoltzfus
  '203': ['dc4'],                          // Betty Shumaker
  '204': ['dc2'],                          // Dan Sensenig
  '205': ['dc1', 'dc2', 'dc3'],          // Hotel Hershey
  '206': ['dc4'],                          // Earl Yoder
  '207': ['dc1'],                          // Market Street Commons
  '208': ['dc2'],                          // Ray Brubaker
};

// Demo time-tracking data for Day Detail view (keyed by date → jobId → crewId)
const DEMO_DAY_DETAIL = {
  '2026-04-14': {
    201: [
      { crewId: 'dc1', clockIn: '6:50 AM', status: 'Clocked Out', hoursLogged: 9.0, payRate: 38 },
      { crewId: 'dc2', clockIn: '6:55 AM', status: 'Clocked Out', hoursLogged: 9.0, payRate: 32 },
    ],
  },
  '2026-04-17': {
    202: [
      { crewId: 'dc1', clockIn: '6:45 AM', status: 'On Site', hoursLogged: 8.0, payRate: 38 },
    ],
    203: [
      { crewId: 'dc4', clockIn: '7:00 AM', status: 'On Site', hoursLogged: 6.0, payRate: 22 },
    ],
  },
  '2026-04-18': {
    204: [
      { crewId: 'dc2', clockIn: '7:30 AM', status: 'On Site', hoursLogged: 4.0, payRate: 32 },
    ],
  },
  '2026-04-21': {
    205: [
      { crewId: 'dc1', clockIn: '6:30 AM', status: 'On Site', hoursLogged: 10.0, payRate: 38 },
      { crewId: 'dc2', clockIn: '6:35 AM', status: 'On Site', hoursLogged: 10.0, payRate: 32 },
      { crewId: 'dc3', clockIn: '6:45 AM', status: 'On Site', hoursLogged: 9.5, payRate: 26 },
    ],
  },
  '2026-04-22': {
    206: [
      { crewId: 'dc4', clockIn: '8:00 AM', status: 'On Site', hoursLogged: 3.0, payRate: 22 },
    ],
  },
  '2026-04-23': {
    207: [
      { crewId: 'dc1', clockIn: '7:00 AM', status: 'On Site', hoursLogged: 8.0, payRate: 38 },
    ],
  },
  '2026-04-28': {
    208: [
      { crewId: 'dc2', clockIn: '7:00 AM', status: 'On Site', hoursLogged: 7.0, payRate: 32 },
    ],
  },
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const S = {
  app: {
    height: '100dvh',
    minHeight: '100vh',
    background: '#1E2329',
    color: '#FFFFFF',
    fontFamily: "'Inter', -apple-system, sans-serif",
    fontSize: 14,
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: '#161B22',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    height: 56,
    flexShrink: 0,
    zIndex: 100,
  },
  logo: { fontSize: 22, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.5px' },
  logoSub: { fontSize: 13, color: '#D1D5DB', fontWeight: 500, marginLeft: 4 },
  tabs: { display: 'flex', gap: 2, marginLeft: 'auto', flexShrink: 0 },
  tab: (active) => ({
    padding: '8px 16px',
    borderRadius: 0,
    border: 'none',
    borderBottom: active ? '2px solid #E8722A' : '2px solid transparent',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: active ? 600 : 500,
    background: 'transparent',
    color: active ? '#E8722A' : '#D1D5DB',
    transition: 'all 0.15s',
    boxShadow: 'none',
  }),
  body: { padding: 24, maxWidth: 1400, margin: '0 auto' },

  filterRow: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' },
  tradeFilterRow: {
    display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center',
    paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  filterBtn: (active) => ({
    padding: '8px 14px',
    borderRadius: 20,
    border: `1px solid ${active ? '#E8722A' : 'rgba(255,255,255,0.12)'}`,
    background: active ? 'rgba(232,114,42,0.12)' : 'transparent',
    color: active ? '#E8722A' : '#D1D5DB',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    minHeight: 44,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitTapHighlightColor: 'transparent',
  }),
  tradeFilterBtn: (active, color) => ({
    padding: '8px 14px', minHeight: 44,
    borderRadius: 20,
    border: `1px solid ${active ? color : 'rgba(255,255,255,0.08)'}`,
    background: active ? color + '22' : 'transparent',
    color: active ? color : '#D1D5DB',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
    gap: 16,
  },
  card: (hovered) => ({
    background: '#2A3140',
    border: `1px solid ${hovered ? 'rgba(232,114,42,0.6)' : 'rgba(255,255,255,0.08)'}`,
    borderRadius: 12,
    padding: 16,
    cursor: 'pointer',
    transition: 'all 0.15s',
    transform: hovered ? 'translateY(-2px)' : 'none',
    boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.6)' : '0 2px 8px rgba(0,0,0,0.3)',
    overflow: 'hidden',
    minWidth: 0,
  }),
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 8, minWidth: 0 },
  cardName: { fontSize: 15, fontWeight: 600, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardContact: { fontSize: 12, color: '#D1D5DB', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  statusBadge: (status) => ({
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 10,
    background: STATUS_COLORS[status],
    color: '#2A3140',
    border: 'none',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  }),
  tradeBadge: (trade) => {
    const c = getTradeColor(trade);
    return {
      display: 'inline-block',
      fontSize: 10,
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: 10,
      background: c + '2e',
      color: c,
      border: `1px solid ${c}44`,
      letterSpacing: '0.3px',
      whiteSpace: 'nowrap',
      marginTop: 6,
    };
  },
  stageBadge: (stage) => ({
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 10,
    background: (STAGE_COLORS[stage] || '#8B95A1') + '28',
    color: STAGE_COLORS[stage] || '#8B95A1',
    border: `1px solid ${(STAGE_COLORS[stage] || '#8B95A1')}44`,
    letterSpacing: '0.2px',
    whiteSpace: 'nowrap',
  }),
  cardMeta: { display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' },
  metaItem: { fontSize: 12, color: '#D1D5DB' },
  metaValue: { color: '#D1D5DB', fontWeight: 500 },
  stallTag: {
    marginTop: 10,
    display: 'inline-block',
    fontSize: 10,
    padding: '2px 8px',
    borderRadius: 4,
    background: 'rgba(249,115,22,0.1)',
    color: '#E8722A',
    border: '1px solid rgba(249,115,22,0.2)',
  },

  // Callbacks
  cbSection: { marginBottom: 28 },
  cbSectionHeader: {
    fontSize: 13, fontWeight: 700, color: '#b0c0d8',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
  },
  cbBadge: (color) => ({
    background: color + '22', color, padding: '1px 8px', borderRadius: 10, fontSize: 11,
  }),
  cbRow: (hovered) => ({
    background: hovered ? '#2A3140' : '#2A3140',
    border: `1px solid ${hovered ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 8,
    padding: '12px 16px', marginBottom: 8,
    display: 'flex', alignItems: 'center', gap: 16,
    cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s',
  }),
  cbDate: (overdue) => ({
    fontSize: 13, fontWeight: 600,
    color: overdue ? '#ef4444' : '#E8722A', minWidth: 90,
  }),
  cbName: { fontSize: 14, fontWeight: 600, color: '#FFFFFF', flex: 1 },
  cbContact: { fontSize: 12, color: '#D1D5DB' },
  cbValue: { fontSize: 13, fontWeight: 600, color: '#22c55e' },

  // Analytics
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16, marginBottom: 28,
  },
  statCard: {
    background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderLeft: '3px solid #E8722A',
    borderRadius: 12, padding: 20, textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  statVal: { fontSize: 28, fontWeight: 800, color: '#FFFFFF' },
  statLabel: { fontSize: 12, color: '#D1D5DB', fontWeight: 600, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.06em' },
  chartSection: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  chartCard: {
    background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10, padding: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  chartTitle: { fontSize: 14, fontWeight: 600, color: '#D1D5DB', marginBottom: 16 },
  barRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  barLabel: { fontSize: 12, color: '#D1D5DB', width: 120, flexShrink: 0 },
  barTrack: { flex: 1, height: 8, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' },
  barFill: (pct, color) => ({
    height: '100%', width: `${pct}%`, background: color,
    borderRadius: 4, transition: 'width 0.5s ease',
  }),
  barCount: { fontSize: 12, color: '#D1D5DB', width: 28, textAlign: 'right' },

  // Jobs
  progressTrack: { height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden', marginTop: 10 },
  progressFill: (pct, color) => ({
    height: '100%', width: `${pct}%`, background: color,
    borderRadius: 3, transition: 'width 0.4s ease',
  }),

  // Checklist modal
  checklistItem: () => ({
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 4px', borderBottom: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer',
    borderRadius: 4, transition: 'background 0.1s',
  }),
  checkbox: (done) => ({
    width: 20, height: 20, borderRadius: 4, flexShrink: 0,
    border: `2px solid ${done ? '#E8722A' : 'rgba(255,255,255,0.12)'}`,
    background: done ? '#E8722A' : 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.15s',
    boxShadow: done ? '0 0 6px rgba(249,115,22,0.35)' : 'none',
  }),
  checkLabel: (done) => ({
    fontSize: 13, color: done ? '#8B95A1' : '#D1D5DB',
    textDecoration: done ? 'line-through' : 'none', flex: 1,
  }),
  checkTs: { fontSize: 11, color: '#D1D5DB', whiteSpace: 'nowrap' },

  // Modal
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 20,
    overscrollBehavior: 'contain',
  },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 20,
    overscrollBehavior: 'contain',
  },
  modal: {
    background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14, width: '100%', maxWidth: 560,
    maxHeight: '85dvh', overflow: 'auto', padding: 28, position: 'relative',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
  },
  modalTitle: { fontSize: 18, fontWeight: 700, color: '#FFFFFF', marginBottom: 4 },
  modalSub: { fontSize: 13, color: '#D1D5DB', marginBottom: 20 },
  closeBtn: {
    position: 'absolute', top: 12, right: 12,
    background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 8,
    color: '#D1D5DB', cursor: 'pointer', fontSize: 20, lineHeight: 1,
    width: 40, height: 40,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    WebkitTapHighlightColor: 'transparent',
    transition: 'color 0.15s, background 0.15s',
  },
  sectionLabel: {
    fontSize: 11, fontWeight: 600, color: '#D1D5DB',
    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10, marginTop: 0,
  },
  playbookList: { listStyle: 'none', padding: 0, margin: '0 0 20px 0' },
  playbookItem: {
    display: 'flex', gap: 10, padding: '8px 0',
    borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 13, color: '#D1D5DB',
    alignItems: 'flex-start',
  },
  playbookNum: {
    background: 'rgba(249,115,22,0.15)', color: '#E8722A',
    borderRadius: 4, width: 22, height: 22, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1,
  },
  aiBtn: (loading) => ({
    width: '100%', padding: '10px 16px',
    background: loading ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #E8722A, #ea580c)',
    border: 'none', borderRadius: 8,
    color: loading ? '#8B95A1' : '#fff',
    fontWeight: 600, fontSize: 14,
    cursor: loading ? 'not-allowed' : 'pointer',
    marginBottom: 16, transition: 'opacity 0.15s',
  }),
  aiResponse: {
    background: '#2A3140', border: '1px solid rgba(249,115,22,0.2)',
    borderRadius: 8, padding: 16, fontSize: 13,
    lineHeight: 1.75, color: '#D1D5DB', whiteSpace: 'pre-wrap',
  },
  apiKeyNote: { fontSize: 11, color: '#D1D5DB', marginTop: 10, textAlign: 'center' },
};

// ─── Mobile Hook ─────────────────────────────────────────────────────────────
function useMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

// True at phone-sized viewports where a 5-column table can't fit. Stacked-card
// layouts trigger from this; useMobile (<768) still drives broader responsive
// decisions like bottom-sheet vs modal.
function useNarrowMobile() {
  const [narrow, setNarrow] = useState(() => window.innerWidth < 480);
  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 480);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return narrow;
}

// Lock background scroll while a modal is open WITHOUT perturbing
// the app's 100dvh flex-column layout (header → main → BottomNav).
//
// Critical: do NOT touch <main>'s overflow property. Changing
// overflow on a flex-1 child triggers an iOS Safari layout
// recalculation that visibly re-flows the BottomNav out of its
// pinned position. Instead we lock scroll with touch-action and
// body-overflow, which don't disturb the flex layout at all.
//
// Pass `active` for inline modals rendered conditionally in their
// parent.
function useScrollLock(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    const mainEl = document.querySelector('main');
    const prevTouch = mainEl ? mainEl.style.touchAction : '';
    const prevBody = document.body.style.overflow;
    if (mainEl) mainEl.style.touchAction = 'none';
    document.body.style.overflow = 'hidden';
    return () => {
      if (mainEl) mainEl.style.touchAction = prevTouch;
      document.body.style.overflow = prevBody;
    };
  }, [active]);
}

// Shared tab definitions for bottom nav
const NAV_TABS = [
  { key: 'pipeline',  label: 'Pipeline',  icon: '📋' },
  { key: 'quotes',    label: 'Quotes',    icon: '📄' },
  { key: 'callbacks', label: 'Callbacks', icon: '📞' },
  { key: 'analytics', label: 'Analytics', icon: '📊' },
  { key: 'jobs',      label: 'Projects',  icon: '🔨' },
  { key: 'calendar',  label: 'Calendar',  icon: '📅' },
  { key: 'crew',      label: 'Crew',      icon: '👷' },
  { key: 'chat',      label: 'Chat',      icon: '💬' },
  { key: 'photos',    label: 'Photos',    icon: '📸' },
  { key: 'team',      label: 'Team',      icon: '👥' },
];

// ─── Section Color Map ──────────────────────────────────────────────────────
// Central color constants for tab accents — easy to tweak in one place.
const SECTION_COLORS = {
  // Main nav tabs
  pipeline:  '#E8722A',  // orange
  quotes:    '#E8722A',  // orange (same hue as pipeline — quotes live in the sales surface)
  callbacks: '#ef4444',  // red
  analytics: '#8b5cf6',  // purple
  jobs:      '#3b82f6',  // blue
  calendar:  '#7c3aed',  // violet
  crew:      '#eab308',  // yellow
  chat:      '#10b981',  // green
  photos:    '#06b6d4',  // cyan
  team:      '#22c55e',  // green
  // Job detail modal tabs
  checklist: '#E8722A',  // orange
  crewpay:   '#14b8a6',  // teal (Cost Manager)
};

// Cost Manager sub-tab teal variants
const CM_SUB_COLORS = {
  materials: '#14b8a6',  // teal
  labor:     '#0d9488',  // teal-600
  summary:   '#0f766e',  // teal-700
  signoff:   '#2dd4bf',  // teal-300
};

// ─── Demo Role Definitions ────────────────────────────────────────────────────
const DEMO_ROLES = {
  owner: {
    label: 'Owner/Admin', color: '#22c55e',
    tabs: ['pipeline','quotes','callbacks','analytics','jobs','calendar','crew','chat','photos','team'],
    seeDollars: true, seeRates: true, seeProfitability: true,
    seeCostManager: true, seeCrewPay: true, seeAnalyticsRevenue: true,
    seeChangeOrders: true, seeTeamMgmt: true,
  },
  sales: {
    label: 'Sales/Estimator', color: '#6366f1',
    tabs: ['pipeline','quotes','callbacks','jobs','analytics','chat'],
    seeDollars: true, seeRates: false, seeProfitability: false,
    seeCostManager: false, seeCrewPay: false, seeAnalyticsRevenue: true,
    seeChangeOrders: false, seeTeamMgmt: false,
  },
  foreman: {
    label: 'Foreman', color: '#E8722A',
    tabs: ['jobs','calendar','crew','chat','photos'],
    seeDollars: false, seeRates: false, seeProfitability: false,
    seeCostManager: true, seeCrewPay: false, seeAnalyticsRevenue: false,
    seeChangeOrders: false, seeTeamMgmt: false,
  },
  crew: {
    label: 'Crew/Installer', color: '#D1D5DB',
    tabs: ['jobs','chat','photos'],
    seeDollars: false, seeRates: false, seeProfitability: false,
    seeCostManager: false, seeCrewPay: false, seeAnalyticsRevenue: false,
    seeChangeOrders: false, seeTeamMgmt: false,
  },
};

function GlobalStyles() {
  return (
    <style>{`
      .ri-btn { transition: filter 0.15s, transform 0.12s, box-shadow 0.15s; cursor: pointer; }
      .ri-btn-primary { background: linear-gradient(135deg,#E8722A,#e8640c) !important; color:#fff !important; border:none !important; font-weight:700 !important; box-shadow:0 2px 10px rgba(249,115,22,0.3); }
      .ri-btn-primary:hover:not(:disabled) { filter:brightness(1.12); transform:translateY(-1px); box-shadow:0 4px 18px rgba(249,115,22,0.5) !important; }
      .ri-btn-primary:active:not(:disabled) { transform:translateY(0); filter:brightness(0.96); }
      .ri-btn-secondary { background:transparent !important; border:1px solid rgba(255,255,255,0.12) !important; color:#8B95A1 !important; }
      .ri-btn-secondary:hover:not(:disabled) { border-color:#E8722A !important; color:#F0F2F5 !important; background:rgba(232,114,42,0.1) !important; }
      .ri-btn-danger { background:rgba(239,68,68,0.12) !important; border:1px solid rgba(239,68,68,0.4) !important; color:#f87171 !important; }
      .ri-btn-danger:hover:not(:disabled) { background:rgba(239,68,68,0.24) !important; border-color:rgba(239,68,68,0.7) !important; }
      .ri-del { transition:color 0.12s, background 0.12s; cursor:pointer; }
      .ri-del:hover { color:#ef4444 !important; }
      input:focus, select:focus, textarea:focus { border-color:#E8722A !important; box-shadow:0 0 0 3px rgba(249,115,22,0.14) !important; outline:none !important; }
      .ri-nav-tab { transition:color 0.15s, background 0.15s; }
      .ri-nav-tab:hover { color:#F0F2F5 !important; background:rgba(255,255,255,0.08) !important; border-radius:6px; }
      .ri-modal-tab { transition:all 0.15s; }
      .ri-modal-tab:hover { background:rgba(255,255,255,0.05) !important; border-radius:6px 6px 0 0; }
      .ri-bnav-btn { transition:color 0.15s, background 0.15s; }
      .ri-bnav-btn:hover { background:rgba(255,255,255,0.05) !important; }
      .ri-cb-row { transition:background 0.12s, border-color 0.12s; }
      .ri-cb-row:hover { background:rgba(255,255,255,0.04) !important; border-color:rgba(255,255,255,0.12) !important; }
      .ri-sec-hdr { transition:background 0.12s; }
      .ri-sec-hdr:hover { background:rgba(255,255,255,0.04) !important; }
      .ri-sub-tab { transition:color 0.15s, border-color 0.15s; cursor:pointer; }
      .ri-sub-tab:hover { color:#F0F2F5 !important; }
      .ri-card-btn:hover { color:#F0F2F5 !important; background:rgba(232,114,42,0.1) !important; }
      .ri-close-btn:hover { color:#F0F2F5 !important; background:rgba(255,255,255,0.1) !important; }
      .ri-add-item:hover { color:#8B95A1 !important; border-color:rgba(255,255,255,0.15) !important; }
      @keyframes ri-pulse { 0%,100%{opacity:0.4} 50%{opacity:0.8} }
      .ri-skel { animation:ri-pulse 1.5s ease-in-out infinite; background:#2A3140; border-radius:6px; }
      @keyframes ri-fadein { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
      @keyframes coSlideIn { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
      @keyframes ri-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      .ri-fadein { animation:ri-fadein 0.2s ease-out; }
      ::-webkit-scrollbar { width:5px; height:5px; }
      ::-webkit-scrollbar-track { background:transparent; }
      ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.15); border-radius:3px; }
      @media (max-width:768px) {
        .ri-btn { min-height:48px; }
        .ri-btn-primary { font-size:15px !important; }
        .ri-btn-secondary { font-size:15px !important; }
      }
    `}</style>
  );
}

function BottomNav({ tab, setTab, tabs }) {
  const tabList = tabs || NAV_TABS;
  // 10 visible tabs (Owner role) ÷ 390px viewport = ~39px per tab. Shrink the
  // basis + label font + horizontal padding so labels stay legible.
  const tight = tabList.length >= 10;
  return (
    <div style={{
      flexShrink: 0,
      background: '#161B22', borderTop: '1px solid rgba(255,255,255,0.08)',
      display: 'flex', height: 64, zIndex: 200,
      boxShadow: '0 -4px 12px rgba(0,0,0,0.3)',
    }}>
      {tabList.map(({ key, label, icon, locked }) => {
        const c = SECTION_COLORS[key] || '#E8722A';
        const isActive = tab === key && !locked;
        return (
          <button
            key={key}
            className={isActive ? '' : 'ri-bnav-btn'}
            onClick={() => !locked && setTab(key)}
            style={{
              flex: tight ? '1 1 38px' : '1 1 42px', minWidth: 0,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              background: isActive ? c + '10' : 'transparent', border: 'none',
              color: locked ? 'rgba(255,255,255,0.12)' : isActive ? '#E8722A' : '#D1D5DB',
              fontWeight: isActive ? 700 : 500,
              cursor: locked ? 'not-allowed' : 'pointer',
              gap: 2, minHeight: 64, padding: tight ? '0 1px' : '0 2px',
              borderTop: isActive ? `2px solid ${c}` : '2px solid transparent',
              transition: 'color 0.15s, background 0.15s',
              WebkitTapHighlightColor: 'transparent',
              overflow: 'hidden',
            }}
          >
            <span style={{
              fontSize: isActive ? (tight ? 18 : 20) : (tight ? 16 : 18),
              lineHeight: 1, transition: 'font-size 0.15s',
            }}>
              {locked ? '🔒' : icon}
            </span>
            <span style={{
              fontSize: tight ? 9 : 10, lineHeight: 1.1, maxWidth: '100%',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (n) => '$' + n.toLocaleString();

// Stages at which a lead also appears as an active job
const JOB_STAGES = [
  'materials_ordered', 'scheduled_for_install', 'in_progress',
  'punch_list', 'completed', 'invoiced', 'paid',
];

function leadToJob(lead) {
  const s = lead.stage;
  let status = 'Scheduled';
  if (s === 'paid' || s === 'invoiced' || s === 'completed') status = 'Complete';
  else if (s === 'in_progress' || s === 'punch_list') status = 'In Progress';

  return {
    id: lead.id,
    customer: lead.name,
    address: lead.address || '',
    trade: lead.trade,
    value: lead.value,
    status,
    scheduledDate: lead.scheduledDate || lead.callbackDate || lead.lastContact || TODAY,
    explicitlyScheduled: !!lead.scheduledDate,
    completedSteps: lead.completedSteps || [],
    notes: lead.notes || '',
    duration: lead.duration || 1,
  };
}

const diffDays = (dateStr) => {
  if (!dateStr) return null;
  return Math.round((new Date(dateStr) - new Date(TODAY)) / 86400000);
};



// Operational demo AI responses keyed by stage — used when demoMode is true in CoachPanel
function getStageAiDemo(lead) {
  const stage = lead.stage || 'lead';
  const name = lead.name;
  const val = fmt(lead.value);
  const contact = lead.contact;

  const responses = {
    lead: `1. Call ${contact} today to schedule a site visit for the ${name} job — leads that get a same-day visit convert 3× higher than those that wait a week. Ask about insurance coverage (Erie, State Farm, Nationwide are the big three in the valley) before the call ends.\n\n2. Pull the ${name} property on Google Maps satellite view before visiting — estimate roof pitch, check for dormers or additions, and spot access issues. Many valley homes have steep pitches and tight driveways. Arriving prepared on a ${val} lead signals professionalism.\n\n3. Check the township requirements for the ${name} address — permit rules vary across Union, Snyder, and Northumberland counties. Some townships don't require permits for re-roofs, but verify before quoting.\n\n4. Create the job folder today: address, ${contact}'s contact details, satellite screenshot, and your initial scope notes. Note if this is a referral — word of mouth is everything in the Susquehanna Valley.`,

    inspection_scheduled: `1. Confirm the inspection appointment with ${contact} at ${name} — send a text with the date, time, and what you'll need access to (roof, attic, all sides). Cell service can be spotty in parts of Union and Snyder counties, so confirm by text, not just voicemail.\n\n2. Prep your inspection kit: drone (charged), ladder, measuring wheel, moisture meter, camera. Many valley homes have steep pitches (8/12+) and older slate or metal that require extra caution. Missing one tool on a ${val} job means a return trip.\n\n3. Check the 3-day forecast — central PA spring weather is unpredictable. Rain or 20+ mph winds mean a reschedule. Notify ${contact} early rather than canceling on arrival. Rescheduling proactively signals professionalism.\n\n4. Review the ${name} property on Google Maps satellite view — estimate total roof area, count slopes, and identify penetrations. Older valley homes often have multiple additions with complex tie-ins that aren't obvious from the street.`,

    inspection_complete: `1. Upload all ${name} inspection photos within 24 hours using the Damage Documentation and Drone Shots categories — four sides from ground level, close-ups of each damage area, the ridge, both valleys, and all penetrations. ${contact} will use these to understand exactly what they're approving at the ${val} estimate.\n\n2. Measure the roof in sections: ridge length, each slope's dimensions, and pitch. Many valley homes have 8/12 to 12/12 pitches that significantly increase labor cost. Accurate square footage on a ${val} job prevents under-ordering from Boise Cascade or ABC Supply.\n\n3. Walk the attic if accessible — check for ice dam staining, moisture damage from freeze-thaw cycles, and prior patching with mismatched materials. Damaged decking at ${name} must be priced as a conditional line item, not discovered mid-job.\n\n4. Document flashing condition at every penetration — limestone chimneys common in the Susquehanna Valley are the #1 source of callbacks. Note what needs replacing now so it's in the ${val} estimate, not an afterthought after ${contact} has signed.`,

    estimate_sent: `1. The ${name} estimate should have gone out within 24 hours of inspection — close rate drops sharply after 48 hours, especially during spring storm season when competitors are knocking the same doors. The ${val} estimate should include materials, labor, disposal, and a conditional deck repair line item.\n\n2. Break the estimate into specific line items: shingles (GAF, CertainTeed, or Owens Corning — specify brand and warranty tier), underlayment, ice & water shield at eaves and valleys (PA code), drip edge, flashing, ridge cap, tear-off, and disposal. ${contact} will compare your bid — being itemized makes ${val} impossible to compare against a lump-sum competitor.\n\n3. Include your workmanship warranty terms in the estimate. Most storm chasers passing through the valley carry no written warranty. Putting yours in writing is the differentiator at the ${val} level — and protects you if Erie or State Farm questions workmanship later.\n\n4. Follow up with a call to ${contact} 48 hours after sending. For Mennonite or rural customers, a face-to-face visit often works better than phone or email. Ask: "Did you have a chance to look over the line items? Any questions on the material spec?"`,

    contract_signed: `1. Collect the deposit from ${contact} for ${name} immediately — a signed contract without a deposit is just a letter of intent. Some rural valley customers prefer check or cash; confirm the method and amount before moving to material ordering on this ${val} job.\n\n2. Check township permit requirements for the ${name} address — rules vary across Union, Snyder, Northumberland, and Columbia counties. Some townships don't require permits for re-roofs, but pulling one anyway protects you on insurance work.\n\n3. Confirm material selections with ${contact}: shingle color and brand (GAF Timberline HDZ, CertainTeed Landmark, Owens Corning Duration), warranty tier, and any upgrades. Order from Boise Cascade Sunbury or ABC Supply now — popular colors like Charcoal and Weathered Wood can be 2-3 weeks out in peak season.\n\n4. Send ${contact} a written confirmation: start date, scope summary, payment schedule, and your cancellation/change-order policy. Note any Sunday work restrictions if the customer or neighbors have requested it.`,

    materials_ordered: `1. Order all materials for ${name} from Boise Cascade Sunbury or ABC Supply — shingles, underlayment, ice & water shield, drip edge, flashing, and ridge cap. Lock pricing now. Spring is peak season in the valley and popular colors go fast.\n\n2. Schedule delivery for the day before or morning of the install start date at ${name}. Verify the supplier has the correct address and driveway access — many rural properties in the valley have narrow lanes or gravel drives that limit truck size.\n\n3. Verify the order matches the ${val} estimate exactly: shingle count by color/type, underlayment rolls, ice & water shield for eaves and valleys (PA code), and ridge cap bundles. A shortage on day one costs a half-day of crew time.\n\n4. Confirm dumpster delivery and placement with ${contact}. Check if the township requires a road permit for dumpster placement — this varies across Union, Snyder, and Northumberland counties.`,

    scheduled_for_install: `1. Assign the crew lead for ${name} and confirm their availability for the start date in writing. Spring is peak season in the Susquehanna Valley — every crew is booked. For a ${val} job, get the foreman's name and 7 AM start time committed.\n\n2. Send ${contact} a reminder with the start date, expected crew arrival time, and what to expect: noise starting early, driveway access for the dumpster, and approximate duration. Note any gravel drive or narrow lane access issues common on rural valley properties.\n\n3. Check the 5-day forecast one more time — PA spring storms can push a job mid-week. Shingles can't be installed below 40°F (GAF and CertainTeed both void warranties). Have a backup date ready and communicate it to ${contact} proactively.\n\n4. Verify the permit is in hand (if required by the township) and confirm all materials + dumpster are scheduled for delivery before the crew arrives at ${name}. Run through the Boise Cascade or ABC Supply order one final time.`,

    in_progress: `1. Check in with the crew lead at ${name} at noon — confirm progress, flag any material shortages, and check the afternoon forecast. Summer thunderstorms in the valley can roll in fast. Daily visibility on a ${val} job prevents small delays from compounding.\n\n2. Take progress photos using the During Tear-off, Decking, and Underlayment categories — these protect you on warranty claims with GAF or CertainTeed, and give ${contact} confidence the ${val} job is moving as expected. Erie Insurance and State Farm both require documentation if this is a claim.\n\n3. If the crew finds rotted board decking or ice dam damage at ${name}, stop and call ${contact} before proceeding — show them a photo, explain the issue, and get written approval. Old valley homes often have 1x6 board decking that needs full OSB overlay. Never absorb deck repair on a ${val} job.\n\n4. Run a magnetic nail sweep at end of each day — especially critical on rural properties where ${contact}'s family mows the yard or kids play outside. Nail injuries and neighbor complaints are avoidable with a 10-minute sweep.`,

    punch_list: `1. Walk the ${name} property with the crew lead — check ridge cap alignment, all flashing seals at limestone chimneys, drip edge continuity, and any visible nail pops. Every item gets a line item and a deadline. Callbacks cost 3x more than catching it now.\n\n2. Verify gutter reattachment and downspout alignment — these are the items ${contact} will notice first from the ground. A loose gutter on a ${val} job undermines the entire quality impression, especially if neighbors are watching (word travels fast in the valley).\n\n3. Run a full magnetic nail sweep — driveway, lawn, sidewalk, gravel areas, and flower beds. One nail in a tire generates a callback that costs more in goodwill than the sweep takes in time.\n\n4. Clear all debris, leftover shingle bundles, and protective tarps from the ${name} property. The site should look better than when you arrived. Take Final category photos before leaving — ${contact} is evaluating the whole experience.`,

    completed: `1. Schedule the final walkthrough with ${contact} at ${name} — walk the perimeter together, check the ridge line, and inspect all flashing points. A face-to-face walkthrough closes the ${val} job cleanly and sets up the referral ask. Valley communities are tight-knit — this moment matters.\n\n2. File the permit close-out if required by the township. Some PA jurisdictions issue lien notices if the final isn't filed within 30 days — don't leave this open on a ${val} job, especially if it's insurance-funded.\n\n3. Take Final category photos and Drone Shots from all four sides. Valley homes with mountain backdrops make great portfolio content. These photos drive future sales more than any ad spend — Susquehanna Valley homeowners check local roofers' work online before calling.\n\n4. Ask ${contact} for a Google review and one referral within 48 hours. Say it directly: "If you're happy with the work, a Google review and one name are the best ways to help us." Word of mouth is everything in communities like Mifflinburg, Lewisburg, and Selinsgrove.`,

    invoiced: `1. The final invoice for ${name} should have gone out within 24 hours of the walkthrough — delays signal disorganization and give ${contact} time to find objections to the ${val} balance.\n\n2. Include before/after photos and a warranty summary (GAF, CertainTeed, or Owens Corning registration details) with the invoice. Visual proof of the transformation reinforces value and reduces pushback.\n\n3. Set a 5-business-day follow-up reminder — if ${contact} hasn't paid, call directly. Don't rely on email for payment in rural PA. Some valley customers prefer to drop off a check or pay in person — accommodate that.\n\n4. If this is an insurance job, confirm the supplement has been filed and the adjuster (Erie, State Farm, Nationwide, or Allstate) has the final documentation including the permit close-out. Insurance delays are the #1 reason roofing invoices go past 30 days in the valley.`,

    paid: `1. Record the payment from ${contact} for ${name} and close the job immediately — open jobs distort your pipeline and make your dispatch board look cluttered heading into the next season push.\n\n2. Mail or email the warranty certificate and manufacturer registration to ${contact}. For GAF Golden Pledge or CertainTeed SureStart Plus, the registration must be filed within 60 days. This protects you and the homeowner.\n\n3. Schedule a 6-month follow-up call with ${contact} to check on the ${name} roof after the first winter freeze-thaw cycle. Ask about ice dams, attic moisture, and any concerns. Then ask for referrals — a proactive check-in after a ${val} job converts past customers into your best lead source in the valley.\n\n4. Archive the ${name} job folder with all photos (Before through Final + Drone Shots), permits, signed contracts, and payment records. Clean documentation protects you on warranty claims for decades — and builds your portfolio for future bids across Union, Snyder, and Northumberland counties.`,
  };

  return responses[stage] || responses.lead;
}

// ─── Add / Edit Lead Modal ────────────────────────────────────────────────────
const LEAD_SOURCES = ['Referral', 'Door knock', 'Online', 'Phone call', 'Repeat customer', 'Other'];
const LEAD_STAGES = [
  'lead', 'inspection_scheduled', 'inspection_complete', 'estimate_sent',
  'contract_signed', 'materials_ordered', 'scheduled_for_install',
  'in_progress', 'punch_list', 'completed', 'invoiced', 'paid',
];

const FI = { // form input base
  width: '100%', padding: '10px 12px', background: '#2A3140',
  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#FFFFFF',
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
  fontFamily: "'Inter', -apple-system, sans-serif",
  transition: 'border-color 0.15s',
};
const FLbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: '#D1D5DB',
  marginBottom: 5, marginTop: 14, textTransform: 'uppercase', letterSpacing: '0.4px',
};
const FRow = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };

function AddLeadModal({ lead, defaultTrade, customTrade, onSave, onClose }) {
  const isMobile = useMobile();
  useScrollLock();
  const isEdit = !!lead;
  const initialTrade = lead?.trade || defaultTrade || 'Full Replacement';
  const initialIsOther = !!initialTrade && !TRADE_LIST.includes(initialTrade) && initialTrade !== customTrade;
  const [form, setForm] = useState({
    name: lead?.name || '',
    contact: lead?.contact || '',
    phone: lead?.phone || '',
    email: lead?.email || '',
    address: lead?.address || '',
    value: lead?.value ? String(lead.value) : '',
    trade: initialIsOther ? '__OTHER__' : initialTrade,
    tradeCustom: initialIsOther ? initialTrade : '',
    stage: lead?.stage || 'lead',
    source: lead?.source || 'Referral',
    notes: lead?.notes || '',
    callbackDate: lead?.callbackDate || '',
  });
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const fi = (field) => ({
    ...FI,
    border: errors[field] ? '1px solid #ef4444'
      : focused === field ? '1px solid #E8722A'
      : '1px solid rgba(255,255,255,0.08)',
  });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = true;
    if (!form.phone.trim()) e.phone = true;
    if (!form.value || isNaN(Number(form.value)) || Number(form.value) <= 0) e.value = true;
    if (!form.trade) e.trade = true;
    if (form.trade === '__OTHER__' && !form.tradeCustom.trim()) e.trade = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const resolvedTrade = form.trade === '__OTHER__' ? form.tradeCustom.trim() : form.trade;

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      ...(isEdit ? lead : {}),
      id: lead?.id || Date.now(),
      name: form.name.trim(),
      contact: form.contact.trim(),
      role: lead?.role || '',
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      trade: resolvedTrade,
      status: lead?.status || 'active',
      value: Math.round(Number(form.value)),
      stage: form.stage,
      source: form.source,
      callbackDate: form.callbackDate || null,
      lastContact: lead?.lastContact || TODAY,
      stallReason: lead?.stallReason || null,
      notes: form.notes.trim(),
      industry: lead?.industry || 'Residential',
      dealAge: lead?.dealAge || 0,
    });
  };

  const mobileOverlay = { ...S.overlay, padding: 0, alignItems: 'flex-end' };
  const mobileModal = {
    ...S.modal, maxWidth: '100vw', width: '100vw',
    maxHeight: '95dvh', borderRadius: '16px 16px 0 0',
    margin: 0,
  };
  const mobileInput = { ...FI, padding: '13px 14px', fontSize: 16 }; // 16px prevents iOS zoom
  const mRow = isMobile
    ? { display: 'grid', gridTemplateColumns: '1fr', gap: 0 }
    : FRow;

  return (
    <div style={isMobile ? mobileOverlay : { ...S.overlay }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={isMobile ? mobileModal : { ...S.modal, maxWidth: 560 }}>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        <div style={{ ...S.modalTitle, paddingRight: 48 }}>{isEdit ? 'Edit Lead' : 'Add New Lead'}</div>
        <div style={{ ...S.modalSub, marginBottom: 4 }}>
          {isEdit ? `Editing: ${lead.name}` : 'Required fields are marked with *'}
        </div>

        <div style={mRow}>
          <div>
            <label style={FLbl}>Customer / Company *</label>
            <input style={isMobile ? { ...mobileInput, ...(errors.name ? { border: '1px solid #ef4444' } : {}) } : fi('name')} value={form.name} placeholder="Apex Roofing LLC"
              onChange={e => set('name', e.target.value)}
              onFocus={() => setFocused('name')} onBlur={() => setFocused(null)} />
          </div>
          <div>
            <label style={FLbl}>Contact Name</label>
            <input style={isMobile ? mobileInput : fi('contact')} value={form.contact} placeholder="Jane Smith"
              onChange={e => set('contact', e.target.value)}
              onFocus={() => setFocused('contact')} onBlur={() => setFocused(null)} />
          </div>
        </div>

        <div style={mRow}>
          <div>
            <label style={FLbl}>Phone *</label>
            <input style={isMobile ? { ...mobileInput, ...(errors.phone ? { border: '1px solid #ef4444' } : {}) } : fi('phone')} value={form.phone} placeholder="(512) 555-0100"
              onChange={e => set('phone', e.target.value)}
              onFocus={() => setFocused('phone')} onBlur={() => setFocused(null)} />
          </div>
          <div>
            <label style={FLbl}>Email</label>
            <input style={isMobile ? mobileInput : fi('email')} type="email" value={form.email} placeholder="jane@company.com"
              onChange={e => set('email', e.target.value)}
              onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} />
          </div>
        </div>

        <label style={FLbl}>Job Address</label>
        <input style={isMobile ? mobileInput : fi('address')} value={form.address} placeholder="1234 Oak St, Mifflinburg PA 17844"
          onChange={e => set('address', e.target.value)}
          onFocus={() => setFocused('address')} onBlur={() => setFocused(null)} />

        <div style={mRow}>
          <div>
            <label style={FLbl}>Estimated Value ($) *</label>
            <input style={isMobile ? { ...mobileInput, ...(errors.value ? { border: '1px solid #ef4444' } : {}) } : fi('value')} value={form.value} placeholder="15000" type="number" min="0"
              onChange={e => set('value', e.target.value)}
              onFocus={() => setFocused('value')} onBlur={() => setFocused(null)} />
          </div>
          <div>
            <label style={FLbl}>Callback Date</label>
            <input style={isMobile ? mobileInput : fi('callbackDate')} type="date" value={form.callbackDate}
              onChange={e => set('callbackDate', e.target.value)}
              onFocus={() => setFocused('callbackDate')} onBlur={() => setFocused(null)} />
          </div>
        </div>

        <div style={mRow}>
          <div>
            <label style={FLbl}>Job Type *</label>
            <select style={isMobile ? { ...mobileInput, ...(errors.trade ? { border: '1px solid #ef4444' } : {}) } : fi('trade')} value={form.trade}
              onChange={e => set('trade', e.target.value)}
              onFocus={() => setFocused('trade')} onBlur={() => setFocused(null)}>
              {customTrade && !TRADE_LIST.includes(customTrade) && (
                <option value={customTrade}>{customTrade} ✨</option>
              )}
              {TRADE_LIST.map(t => <option key={t} value={t}>{t}</option>)}
              <option value="__OTHER__">Other…</option>
            </select>
            {form.trade === '__OTHER__' && (
              <input
                style={{ ...(isMobile ? mobileInput : fi('trade')), marginTop: 8 }}
                value={form.tradeCustom}
                onChange={e => set('tradeCustom', e.target.value)}
                placeholder="Describe job type (e.g. Solar reinstall, Chimney cricket, Snow guards)"
                autoFocus
              />
            )}
          </div>
          <div>
            <label style={FLbl}>Lead Stage</label>
            <select style={isMobile ? mobileInput : fi('stage')} value={form.stage}
              onChange={e => set('stage', e.target.value)}
              onFocus={() => setFocused('stage')} onBlur={() => setFocused(null)}>
              {LEAD_STAGES.map(s => (
                <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>
              ))}
            </select>
          </div>
        </div>

        <label style={FLbl}>Lead Source</label>
        <select style={isMobile ? mobileInput : fi('source')} value={form.source}
          onChange={e => set('source', e.target.value)}
          onFocus={() => setFocused('source')} onBlur={() => setFocused(null)}>
          {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <label style={FLbl}>Notes</label>
        <textarea
          style={isMobile
            ? { ...mobileInput, minHeight: 80, resize: 'vertical', lineHeight: 1.5 }
            : { ...FI, minHeight: 72, resize: 'vertical', lineHeight: 1.5 }}
          value={form.notes} placeholder="Any relevant details about this lead..."
          onChange={e => set('notes', e.target.value)}
          onFocus={() => setFocused('notes')} onBlur={() => setFocused(null)}
        />

        {Object.keys(errors).length > 0 && (
          <div style={{ fontSize: 13, color: '#ef4444', marginTop: 10 }}>
            Please fill in all required fields marked with *.
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 20, paddingBottom: isMobile ? 8 : 0 }}>
          <button
            className="ri-btn ri-btn-primary"
            style={{
              flex: 1, padding: isMobile ? '14px 16px' : '10px 16px',
              background: 'linear-gradient(135deg, #E8722A, #e8640c)',
              border: 'none', borderRadius: 8, color: '#fff',
              fontWeight: 700, fontSize: 15, cursor: 'pointer',
              minHeight: 48, WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 2px 12px rgba(249,115,22,0.35)',
            }}
            onClick={handleSave}
          >
            {isEdit ? 'Save Changes' : 'Add Lead'}
          </button>
          <button
            className="ri-btn ri-btn-secondary"
            style={{
              padding: isMobile ? '14px 20px' : '10px 20px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
              color: '#D1D5DB', cursor: 'pointer', fontSize: 14,
              minHeight: 48, WebkitTapHighlightColor: 'transparent',
            }}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── IndexedDB Photo Storage ──────────────────────────────────────────────────
const photoDB = {
  _db: null,
  open() {
    if (this._db) return Promise.resolve(this._db);
    return new Promise((resolve, reject) => {
      const req = indexedDB.open('ridgeos-photos', 1);
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains('photos')) {
          const store = db.createObjectStore('photos', { keyPath: 'id' });
          store.createIndex('jobId', 'jobId', { unique: false });
        }
      };
      req.onsuccess = (e) => { this._db = e.target.result; resolve(this._db); };
      req.onerror = () => reject(req.error);
    });
  },
  getAll() {
    return this.open().then(db => new Promise((resolve, reject) => {
      const req = db.transaction('photos', 'readonly').objectStore('photos').getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  },
  getByJob(jobId) {
    return this.open().then(db => new Promise((resolve, reject) => {
      const req = db.transaction('photos', 'readonly').objectStore('photos').index('jobId').getAll(jobId);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  },
  add(photo) {
    return this.open().then(db => new Promise((resolve, reject) => {
      const req = db.transaction('photos', 'readwrite').objectStore('photos').add(photo);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  },
  remove(id) {
    return this.open().then(db => new Promise((resolve, reject) => {
      const req = db.transaction('photos', 'readwrite').objectStore('photos').delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    }));
  },
};

// ─── IndexedDB Time Tracking ─────────────────────────────────────────────────
const timeDB = {
  _db: null,
  open() {
    if (this._db) return Promise.resolve(this._db);
    return new Promise((resolve, reject) => {
      const req = indexedDB.open('ridgeos-time', 1);
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains('entries')) {
          const store = db.createObjectStore('entries', { keyPath: 'id' });
          store.createIndex('jobId', 'jobId', { unique: false });
          store.createIndex('crewMemberId', 'crewMemberId', { unique: false });
        }
      };
      req.onsuccess = (e) => { this._db = e.target.result; resolve(this._db); };
      req.onerror = () => reject(req.error);
    });
  },
  getAll() {
    return this.open().then(db => new Promise((resolve, reject) => {
      const req = db.transaction('entries', 'readonly').objectStore('entries').getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  },
  add(entry) {
    return this.open().then(db => new Promise((resolve, reject) => {
      const req = db.transaction('entries', 'readwrite').objectStore('entries').add(entry);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  },
  put(entry) {
    return this.open().then(db => new Promise((resolve, reject) => {
      const req = db.transaction('entries', 'readwrite').objectStore('entries').put(entry);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  },
};

function fmtDuration(ms) {
  if (!ms || ms < 0) return '0m';
  const totalMins = Math.floor(ms / 60000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function weekStartMs() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d.getTime();
}

function computeTotalMs(entries) {
  const now = Date.now();
  return entries.reduce((sum, e) => sum + Math.max(0, (e.clockOut || now) - e.clockIn), 0);
}

// ─── Calendar Helpers ─────────────────────────────────────────────────────────
function toDateStr(d) {
  return d.toISOString().slice(0, 10);
}

function dateAdd(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return toDateStr(d);
}

function jobDuration(job) {
  return job.duration || DEMO_JOB_DURATIONS[job.id] || 1;
}

function jobEndDate(job) {
  if (!job.scheduledDate) return null;
  return dateAdd(job.scheduledDate, jobDuration(job) - 1);
}

function jobOccupiesDate(job, dateStr) {
  if (!job.scheduledDate) return false;
  return dateStr >= job.scheduledDate && dateStr <= jobEndDate(job);
}

function buildMonthDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const start = new Date(firstDay);
  start.setDate(start.getDate() - start.getDay());
  const days = [];
  const d = new Date(start);
  for (let i = 0; i < 42; i++) {
    days.push(toDateStr(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function buildWeekDays(refDateStr) {
  const start = new Date(refDateStr + 'T12:00:00');
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return toDateStr(d);
  });
}

// ─── IndexedDB Chat Storage ───────────────────────────────────────────────────
const chatDB = {
  _db: null,
  open() {
    if (this._db) return Promise.resolve(this._db);
    return new Promise((resolve, reject) => {
      const req = indexedDB.open('ridgeos-chat', 1);
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains('messages')) {
          const store = db.createObjectStore('messages', { keyPath: 'id' });
          store.createIndex('jobId', 'jobId', { unique: false });
        }
      };
      req.onsuccess = (e) => { this._db = e.target.result; resolve(this._db); };
      req.onerror = () => reject(req.error);
    });
  },
  getAll() {
    return this.open().then(db => new Promise((resolve, reject) => {
      const req = db.transaction('messages', 'readonly').objectStore('messages').getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  },
  getByJob(jobId) {
    return this.open().then(db => new Promise((resolve, reject) => {
      const req = db.transaction('messages', 'readonly').objectStore('messages').index('jobId').getAll(jobId);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  },
  add(msg) {
    return this.open().then(db => new Promise((resolve, reject) => {
      const req = db.transaction('messages', 'readwrite').objectStore('messages').add(msg);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  },
};

async function compressImage(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const MAX = 1200;
      let { width, height } = img;
      if (width > MAX) { height = Math.round(height * MAX / width); width = MAX; }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    img.src = url;
  });
}

// ─── Photo Categories ────────────────────────────────────────────────────────
const PHOTO_CATEGORIES = [
  'Before', 'During Tear-off', 'Decking', 'Underlayment',
  'Final', 'Damage Documentation', 'Drone Shots',
];

// ─── Job Photos Panel ─────────────────────────────────────────────────────────
function JobPhotosPanel({ lead, onCountChange, phases }) {
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  useScrollLock(!!lightbox);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [photoCategory, setPhotoCategory] = useState('Before');
  const [photoPhase, setPhotoPhase] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const isMobile = useMobile();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const refresh = useCallback(() => {
    photoDB.getByJob(String(lead.id)).then(ps => {
      const sorted = ps.sort((a, b) => b.timestamp - a.timestamp);
      setPhotos(sorted);
      if (onCountChange) onCountChange(sorted.length);
    }).catch(() => {});
  }, [lead.id, onCountChange]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        const imageData = await compressImage(file);
        await photoDB.add({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          jobId: String(lead.id),
          jobName: lead.name,
          imageData,
          timestamp: Date.now(),
          caption: caption.trim(),
          category: photoCategory,
          stage: lead.stage || 'lead',
          trade: lead.trade || '',
          ...(photoPhase ? { phase: parseInt(photoPhase, 10) } : {}),
        });
      }
      setCaption('');
      setPhotoPhase('');
    } finally {
      setUploading(false);
      refresh();
    }
  };

  const handleDelete = async (id) => {
    await photoDB.remove(id);
    if (lightbox && lightbox.id === id) setLightbox(null);
    refresh();
  };

  const formatTs = (ts) => new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  const gridCols = isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(150px, 1fr))';

  return (
    <div>
      {/* Upload controls */}
      <div style={{ marginBottom: 14 }}>
        <input
          style={{
            width: '100%', padding: '8px 12px', background: '#2A3140',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, color: '#D1D5DB',
            fontSize: 13, outline: 'none', boxSizing: 'border-box',
            fontFamily: 'inherit', marginBottom: 8,
          }}
          placeholder="Caption / note (optional)"
          value={caption}
          onChange={e => setCaption(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <select
            style={{
              flex: 1, padding: '8px 12px', background: '#2A3140',
              border: '1px solid #164e63', borderRadius: 7, color: '#22d3ee',
              fontSize: 13, outline: 'none', fontFamily: 'inherit', fontWeight: 600,
            }}
            value={photoCategory}
            onChange={e => setPhotoCategory(e.target.value)}
          >
            {PHOTO_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {phases && phases.length > 0 && (
            <select
              style={{
                flex: 1, padding: '8px 12px', background: '#2A3140',
                border: '1px solid rgba(45,80,22,0.4)', borderRadius: 7,
                color: photoPhase ? '#2D5016' : '#D1D5DB',
                fontSize: 13, outline: 'none', fontFamily: 'inherit', fontWeight: 600,
              }}
              value={photoPhase}
              onChange={e => setPhotoPhase(e.target.value)}
            >
              <option value="">Phase (optional)</option>
              {phases.map(p => <option key={p.id} value={p.id}>Phase {p.id}: {p.label}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
            onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
          />
          <input
            ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
            onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
          />
          <button
            onClick={() => cameraRef.current && cameraRef.current.click()}
            disabled={uploading}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 8,
              background: '#06b6d4', border: 'none', color: '#fff',
              fontSize: 13, fontWeight: 700,
              cursor: uploading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              opacity: uploading ? 0.6 : 1,
            }}
          >
            📷 {isMobile ? 'Camera' : 'Take Photo'}
          </button>
          <button
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={uploading}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 8,
              background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
              color: uploading ? '#8B95A1' : '#D1D5DB',
              fontSize: 13, fontWeight: 600,
              cursor: uploading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            🖼 {uploading ? 'Saving…' : 'Upload'}
          </button>
        </div>
      </div>

      {/* Category filter */}
      {photos.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          <button
            onClick={() => setCategoryFilter('all')}
            style={{
              padding: '3px 10px', borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              border: categoryFilter === 'all' ? '1px solid #06b6d4' : '1px solid rgba(255,255,255,0.08)',
              background: categoryFilter === 'all' ? 'rgba(6,182,212,0.15)' : 'transparent',
              color: categoryFilter === 'all' ? '#06b6d4' : '#D1D5DB',
            }}
          >All</button>
          {PHOTO_CATEGORIES.filter(c => photos.some(p => p.category === c)).map(c => (
            <button
              key={c}
              onClick={() => setCategoryFilter(categoryFilter === c ? 'all' : c)}
              style={{
                padding: '3px 10px', borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                border: categoryFilter === c ? '1px solid #06b6d4' : '1px solid rgba(255,255,255,0.08)',
                background: categoryFilter === c ? 'rgba(6,182,212,0.15)' : 'transparent',
                color: categoryFilter === c ? '#06b6d4' : '#D1D5DB',
              }}
            >{c}</button>
          ))}
        </div>
      )}

      {/* Photo grid */}
      {photos.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '28px 16px',
          border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 10,
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📷</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#D1D5DB', marginBottom: 4 }}>No photos yet</div>
          <div style={{ fontSize: 12, color: '#D1D5DB' }}>
            {isMobile
              ? 'Tap Camera to shoot or Upload to pick from library.'
              : 'Use Take Photo or Upload to start documenting this job.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 8 }}>
          {photos.filter(p => categoryFilter === 'all' || p.category === categoryFilter).map(photo => (
            <div
              key={photo.id}
              onClick={() => setLightbox(photo)}
              style={{
                position: 'relative', borderRadius: 8, overflow: 'hidden',
                cursor: 'pointer', border: '1px solid rgba(255,255,255,0.08)',
                aspectRatio: '4/3', background: '#2A3140',
              }}
            >
              <img
                src={photo.imageData}
                alt={photo.caption || 'Job photo'}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'rgba(0,0,0,0.65)', padding: '4px 7px',
                fontSize: 10, color: '#D1D5DB',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {photo.category && <span style={{ background: 'rgba(6,182,212,0.3)', color: '#22d3ee', padding: '1px 5px', borderRadius: 4, fontSize: 9, fontWeight: 600, marginRight: 4 }}>{photo.category}</span>}
                {photo.phase && <span style={{ background: 'rgba(45,80,22,0.3)', color: '#6abf40', padding: '1px 5px', borderRadius: 4, fontSize: 9, fontWeight: 600, marginRight: 4 }}>P{photo.phase}</span>}
                {photo.caption || ''}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.93)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
          onClick={(e) => e.target === e.currentTarget && setLightbox(null)}
        >
          <img
            src={lightbox.imageData}
            alt={lightbox.caption || ''}
            style={{ maxWidth: '100%', maxHeight: '72vh', objectFit: 'contain', borderRadius: 8 }}
          />
          <div style={{
            marginTop: 12, display: 'flex', gap: 10, alignItems: 'center',
            flexWrap: 'wrap', justifyContent: 'center', maxWidth: 480,
          }}>
            {lightbox.category && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                background: 'rgba(6,182,212,0.15)', color: '#06b6d4',
                border: '1px solid rgba(6,182,212,0.3)',
              }}>
                {lightbox.category}
              </span>
            )}
            {lightbox.caption && (
              <span style={{ fontSize: 13, color: '#D1D5DB' }}>{lightbox.caption}</span>
            )}
            <span style={{ fontSize: 11, color: '#D1D5DB' }}>{formatTs(lightbox.timestamp)}</span>
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
              background: 'rgba(59,130,246,0.15)', color: '#3b82f6',
              border: '1px solid rgba(59,130,246,0.3)',
            }}>
              {STAGE_LABELS[lightbox.stage] || lightbox.stage}
            </span>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button
              onClick={() => setLightbox(null)}
              style={{
                padding: '8px 20px', borderRadius: 7,
                background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
                color: '#D1D5DB', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Close
            </button>
            <button
              onClick={() => handleDelete(lightbox.id)}
              style={{
                padding: '8px 20px', borderRadius: 7,
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                color: '#ef4444', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CoachPanel ──────────────────────────────────────────────────────────────
function CoachPanel({ lead, onClose, demoMode, tier, onStageChange }) {
  useScrollLock();
  const [aiText, setAiText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [localStage, setLocalStage] = useState(lead.stage || 'lead');
  const [activeTab, setActiveTab] = useState('info');
  const [photoCount, setPhotoCount] = useState(0);
  const isMobile = useMobile();
  const goToSignup = () => { window.location.href = '/'; };

  const stageTips = STAGE_TIPS[localStage] || STAGE_TIPS.lead;
  const isStarterLocked = demoMode && tier === 'starter';

  useEffect(() => {
    photoDB.getByJob(String(lead.id)).then(ps => setPhotoCount(ps.length)).catch(() => {});
  }, [lead.id]);

  const handleStageChange = (newStage) => {
    setLocalStage(newStage);
    setAiText('');
    setError('');
    if (onStageChange) onStageChange(newStage);
  };

  const getAiAdvice = async () => {
    setLoading(true);
    setError('');
    setAiText('');

    if (demoMode) {
      const text = getStageAiDemo({ ...lead, stage: localStage });
      const words = text.split(' ');
      for (let i = 0; i < words.length; i++) {
        await new Promise(r => setTimeout(r, 28));
        setAiText(prev => prev + (i === 0 ? '' : ' ') + words[i]);
      }
      setLoading(false);
      return;
    }

    const currentStageLabel = STAGE_LABELS[localStage] || localStage;

    try {
      const res = await fetch('/api/ai-coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trade: lead.trade,
          stage: currentStageLabel,
          notes: lead.notes,
          value: fmt(lead.value),
          name: lead.name,
          contact: lead.contact,
          role: lead.role,
          industry: lead.industry,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError('AI advice is temporarily unavailable. Please try again in a moment.');
      } else {
        setAiText(data.text);
      }
    } catch (e) {
      setError('AI advice is temporarily unavailable. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const coachOverlay = isMobile
    ? { ...S.overlay, padding: 0, alignItems: 'flex-end' }
    : S.overlay;
  const coachModal = isMobile
    ? { ...S.modal, maxWidth: '100vw', width: '100vw', maxHeight: '95dvh', borderRadius: '16px 16px 0 0', margin: 0 }
    : S.modal;

  return (
    <div style={coachOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={coachModal}>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        <div style={{ ...S.modalTitle, paddingRight: 52 }}>{lead.name}</div>
        <div style={S.modalSub}>
          {lead.contact} · {lead.role} · {fmt(lead.value)} · {STAGE_LABELS[lead.stage] || lead.stage}
          {lead.stallReason && ` · ${STALL_LABELS[lead.stallReason]}`}
        </div>
        <div style={{ marginBottom: 16 }}>
          <span style={S.tradeBadge(lead.trade)}>{lead.trade}</span>
        </div>

        {/* Tab switcher */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 20 }}>
          {[
            { key: 'info', label: 'Details' },
            { key: 'photos', label: photoCount > 0 ? `Photos (${photoCount})` : 'Photos' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                padding: '8px 16px', background: 'transparent', border: 'none',
                borderBottom: activeTab === key ? '2px solid #E8722A' : '2px solid transparent',
                color: activeTab === key ? '#E8722A' : '#D1D5DB',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                marginBottom: -1, transition: 'color 0.15s', fontFamily: 'inherit',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Details tab */}
        {activeTab === 'info' && (
          <>
            {lead.notes && (
              <>
                <div style={S.sectionLabel}>Job Notes</div>
                <div style={{
                  fontSize: 13, color: '#D1D5DB', marginBottom: 20,
                  padding: '10px 12px', background: '#2A3140', borderRadius: 6,
                }}>
                  {lead.notes}
                </div>
              </>
            )}

            <div style={S.sectionLabel}>Stage</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
              {STAGE_ORDER.map((s) => (
                <button
                  key={s}
                  onClick={() => handleStageChange(s)}
                  style={{
                    padding: '5px 12px', borderRadius: 20, fontSize: 11,
                    fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                    border: localStage === s ? 'none' : '1px solid rgba(255,255,255,0.08)',
                    background: localStage === s ? '#E8722A' : '#2A3140',
                    color: localStage === s ? '#fff' : '#D1D5DB',
                  }}
                >
                  {STAGE_LABELS[s]}
                </button>
              ))}
            </div>

            <div style={S.sectionLabel}>What to do at this stage</div>
            <ul style={S.playbookList}>
              {stageTips.map((tip, i) => (
                <li key={i} style={S.playbookItem}>
                  <span style={S.playbookNum}>{i + 1}</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>

            <div style={S.sectionLabel}>AI Coach — Job Advice</div>
            {isStarterLocked ? (
              <div>
                <button
                  style={{
                    width: '100%', padding: '11px 16px', borderRadius: 8,
                    background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
                    color: '#D1D5DB', fontSize: 13, fontWeight: 600,
                    cursor: 'not-allowed', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                  disabled
                >
                  <span style={{ fontSize: 16 }}>🔒</span>
                  AI Job Advice — Pro feature
                </button>
                <div style={{
                  marginTop: 10, padding: '10px 14px',
                  background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.18)',
                  borderRadius: 7, fontSize: 12, color: '#D1D5DB',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                }}>
                  <span>AI job advice with stage-specific next steps is available on Pro and Business plans.</span>
                  <button
                    onClick={goToSignup}
                    style={{
                      padding: '5px 14px', background: '#E8722A', border: 'none',
                      borderRadius: 6, color: '#fff', fontWeight: 700,
                      fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    Upgrade
                  </button>
                </div>
              </div>
            ) : (
              <button style={S.aiBtn(loading)} onClick={getAiAdvice} disabled={loading}>
                {loading ? '⟳  Generating job advice...' : '✦  Get AI Job Advice'}
              </button>
            )}

            {error && (
              <div style={{
                fontSize: 12, color: '#ef4444', marginBottom: 12,
                padding: '8px 12px', background: 'rgba(239,68,68,0.08)',
                borderRadius: 6, border: '1px solid rgba(239,68,68,0.2)',
              }}>
                {error}
              </div>
            )}

            {aiText && <div style={S.aiResponse}>{aiText}</div>}

            {!demoMode && <div style={S.apiKeyNote}>Powered by Claude</div>}
          </>
        )}

        {/* Photos tab */}
        {activeTab === 'photos' && (
          <JobPhotosPanel lead={lead} onCountChange={setPhotoCount} />
        )}
      </div>
    </div>
  );
}

// ─── Disabled Tooltip ────────────────────────────────────────────────────────
function DisabledTooltip({ active, label, children }) {
  const [show, setShow] = useState(false);
  if (!active) return children;
  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div style={{
          position: 'absolute', bottom: '110%', left: '50%',
          transform: 'translateX(-50%)',
          background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 6, padding: '5px 10px',
          fontSize: 11, color: '#D1D5DB', whiteSpace: 'nowrap',
          zIndex: 200, pointerEvents: 'none',
        }}>
          {label}
          <div style={{
            position: 'absolute', top: '100%', left: '50%',
            transform: 'translateX(-50%)',
            borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: '5px solid rgba(255,255,255,0.08)',
          }} />
        </div>
      )}
    </div>
  );
}

// ─── Lead Card ───────────────────────────────────────────────────────────────

// ─── Pipeline Tab ─────────────────────────────────────────────────────────────
// ─── Kanban Card ──────────────────────────────────────────────────────────────
function KanbanCard({ lead, urgencyBorder, staleDays, onQuickEdit, onEdit, onDelete, demoMode, rolePerms, quotes }) {
  const [hovered, setHovered] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [dragging, setDragging] = useState(false);
  const isMobile = useMobile();

  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', String(lead.id));
    e.dataTransfer.effectAllowed = 'move';
    setDragging(true);
  };
  const handleDragEnd = () => setDragging(false);

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setConfirmDelete(false); }}
      onClick={() => !dragging && onQuickEdit && onQuickEdit(lead)}
      style={{
        background: '#2A3140',
        border: hovered ? '1px solid #E8722A' : '1px solid rgba(255,255,255,0.08)',
        borderRadius: 10, padding: '10px 12px',
        cursor: 'pointer', position: 'relative',
        transition: 'all 0.15s',
        userSelect: 'none',
        overflow: 'hidden',
        boxShadow: hovered ? '0 4px 12px rgba(0,0,0,0.25)' : '0 1px 3px rgba(0,0,0,0.2)',
      }}
    >
      {(() => {
        const scopes = [{ trade: lead.trade, value: lead.value }, ...(lead.extraScopes || [])];
        const totalValue = scopes.reduce((sum, s) => sum + (s.value || 0), 0);
        const scopeCount = scopes.length;
        return (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.name}</div>
                <div style={{ fontSize: 11, color: '#D1D5DB', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.contact}{lead.role ? ` · ${lead.role}` : ''}</div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#22c55e', flexShrink: 0, whiteSpace: 'nowrap' }}>{rolePerms?.seeDollars !== false ? fmt(totalValue) : '—'}</span>
            </div>

            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4, minWidth: 0, alignItems: 'center' }}>
              {scopeCount > 1 && (
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                  background: 'rgba(232,114,42,0.12)', color: '#E8722A',
                  border: '1px solid rgba(232,114,42,0.3)',
                  letterSpacing: '0.3px', whiteSpace: 'nowrap',
                }}>{scopeCount} scopes</span>
              )}
              {scopes.map((s, i) => (
                <span key={`${s.trade}-${i}`} style={{ ...S.tradeBadge(s.trade), marginTop: 0, fontSize: 9, padding: '1px 6px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.trade}</span>
              ))}
              <span style={{ ...S.statusBadge(lead.status), fontSize: 9 }}>{lead.status}</span>
              {(() => {
                const leadQuotes = getQuotesForLead(lead.id, quotes || DEMO_QUOTES);
                if (leadQuotes.length === 0) return null;
                // Pick the most advanced quote status to display (signed > viewed > sent > draft).
                const order = { signed: 4, viewed: 3, sent: 2, draft: 1, rejected: 0, expired: 0 };
                const best = leadQuotes.slice().sort((a, b) => (order[b.status] || 0) - (order[a.status] || 0))[0];
                const c = { draft: '#9CA3AF', sent: '#3b82f6', viewed: '#eab308', signed: '#22c55e', rejected: '#ef4444', expired: '#f59e0b' }[best.status] || '#9CA3AF';
                return (
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                    background: c + '22', color: c, border: `1px solid ${c}44`,
                    letterSpacing: '0.3px', whiteSpace: 'nowrap',
                  }}>Quote · {best.status}</span>
                );
              })()}
            </div>
          </>
        );
      })()}

      {staleDays > 7 && (
        <div style={{ fontSize: 10, color: staleDays > 14 ? '#ef4444' : '#eab308', marginTop: 3 }}>
          {staleDays > 14 ? '🔴' : '🟡'} {staleDays}d no contact
        </div>
      )}

      {lead.stallReason && (
        <div style={{ fontSize: 10, color: '#E8722A', marginTop: 2 }}>⚠ {STALL_LABELS[lead.stallReason]}</div>
      )}

      {/* Issues badges */}
      {lead.issues && lead.issues.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
          {lead.issues.slice(0, 2).map(issue => (
            <span key={issue} style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>{issue}</span>
          ))}
          {lead.issues.length > 2 && <span style={{ fontSize: 9, color: '#D1D5DB' }}>+{lead.issues.length - 2}</span>}
        </div>
      )}

      {/* Last update preview */}
      {lead.lastUpdate && (
        <div style={{ fontSize: 10, color: '#D1D5DB', marginTop: 5, paddingTop: 5, borderTop: '1px solid #1a2035', lineHeight: 1.4 }}>
          <span style={{ color: '#D1D5DB' }}>↳ </span>
          {lead.lastUpdate.length > 60 ? lead.lastUpdate.slice(0, 60) + '…' : lead.lastUpdate}
        </div>
      )}

      {/* Card actions — always visible on mobile, hover-only on desktop */}
      {(isMobile || hovered) && !demoMode && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
            {!confirmDelete ? (
              <button
                style={{ minWidth: 44, minHeight: 44, padding: '8px 12px', fontSize: 14, background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#D1D5DB', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={() => setConfirmDelete(true)}
              >
                🗑
              </button>
            ) : (
              <>
                <button style={{ minWidth: 44, minHeight: 44, padding: '8px 14px', fontSize: 13, fontWeight: 700, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', cursor: 'pointer' }} onClick={() => onDelete && onDelete(lead.id)}>Delete</button>
                <button style={{ minWidth: 44, minHeight: 44, padding: '8px 14px', fontSize: 13, fontWeight: 600, background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#D1D5DB', cursor: 'pointer' }} onClick={() => setConfirmDelete(false)}>Cancel</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Card Quick Edit Modal ─────────────────────────────────────────────────────
function CardQuickEdit({ lead, onClose, onUpdate, onOpenDetail, currentUser, rolePerms, quotes, onNewQuote }) {
  useScrollLock();
  const [stage, setStage] = useState(lead.stage);
  const [status, setStatus] = useState(lead.status || 'active');
  const [updateText, setUpdateText] = useState('');
  const [issues, setIssues] = useState(lead.issues || []);
  const [showIssueMenu, setShowIssueMenu] = useState(false);
  const [customIssue, setCustomIssue] = useState('');
  const showToast = useToast();
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) textareaRef.current.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const user = currentUser || 'You';
  const now = () => new Date().toISOString();

  const addEntry = (log, type, message) => [...(log || []), { timestamp: now(), type, message, user }];

  const postUpdate = () => {
    if (!updateText.trim()) return;
    const newLog = addEntry(lead.activityLog, 'update', updateText.trim());
    onUpdate({ ...lead, issues, activityLog: newLog, lastUpdate: updateText.trim() });
    setUpdateText('');
    showToast('Update posted');
  };

  const logContact = () => {
    const today = new Date().toISOString().slice(0, 10);
    const newLog = addEntry(lead.activityLog, 'contact', `Contact logged on ${today}`);
    onUpdate({ ...lead, issues, lastContact: today, activityLog: newLog });
    showToast('Contact logged');
  };

  const addIssue = (issue) => {
    const trimmed = issue.trim();
    if (!trimmed || issues.includes(trimmed)) { setShowIssueMenu(false); setCustomIssue(''); return; }
    const newIssues = [...issues, trimmed];
    setIssues(newIssues);
    const newLog = addEntry(lead.activityLog, 'issue', `Issue added: ${trimmed}`);
    onUpdate({ ...lead, issues: newIssues, activityLog: newLog });
    setShowIssueMenu(false);
    setCustomIssue('');
  };

  const removeIssue = (issue) => {
    const newIssues = issues.filter(i => i !== issue);
    setIssues(newIssues);
    const newLog = addEntry(lead.activityLog, 'issue', `Issue removed: ${issue}`);
    onUpdate({ ...lead, issues: newIssues, activityLog: newLog });
  };

  const save = () => {
    let log = [...(lead.activityLog || [])];
    if (stage !== lead.stage) log = addEntry(log, 'stage', `Stage changed from ${STAGE_LABELS[lead.stage] || lead.stage} to ${STAGE_LABELS[stage] || stage}`);
    if (status !== lead.status) log = addEntry(log, 'status', `Status changed to ${status}`);
    onUpdate({ ...lead, stage, status, issues, activityLog: log });
    onClose();
    showToast('Saved');
  };

  const recentActivity = [...(lead.activityLog || [])].reverse().slice(0, 4);

  const ACTIVITY_ICONS = { stage: '→', status: '◉', update: '✎', contact: '📞', issue: '⚑' };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, width: '100%', maxWidth: 500, maxHeight: '92dvh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.name}</div>
            <div style={{ fontSize: 11, color: '#D1D5DB', marginTop: 2 }}>
              {lead.contact}{lead.trade ? ` · ${lead.trade}` : ''} · <span style={{ color: '#22c55e', fontWeight: 600 }}>{rolePerms?.seeDollars !== false ? fmt(lead.value) : '—'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {onOpenDetail && (
              <button
                onClick={onOpenDetail}
                className="ri-btn ri-btn-secondary"
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 11, padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                AI Coach ↗
              </button>
            )}
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#D1D5DB', fontSize: 20, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* ── Post Update (most prominent) ── */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#E8722A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.6px' }}>✎ Post Update</div>
            <textarea
              ref={textareaRef}
              value={updateText}
              onChange={e => setUpdateText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postUpdate(); }}
              placeholder="e.g. Spoke with owner, waiting on HOA approval…&#10;Materials ordered from ABC Supply…&#10;Client wants to wait until spring."
              rows={4}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: '#060a12', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8,
                color: '#FFFFFF', fontSize: 13, padding: '10px 12px',
                resize: 'vertical', outline: 'none', fontFamily: 'inherit', lineHeight: 1.6,
              }}
            />
            <button
              onClick={postUpdate}
              disabled={!updateText.trim()}
              style={{
                marginTop: 8, width: '100%', padding: '10px 0',
                background: updateText.trim() ? 'linear-gradient(135deg, #E8722A, #e8640c)' : '#2A3140',
                border: updateText.trim() ? 'none' : '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8, color: updateText.trim() ? '#fff' : '#3d4f63',
                fontWeight: 700, fontSize: 14, cursor: updateText.trim() ? 'pointer' : 'not-allowed',
                transition: 'background 0.15s',
                boxShadow: updateText.trim() ? '0 2px 10px rgba(249,115,22,0.3)' : 'none',
              }}
            >
              Post Update {updateText.trim() ? '(Ctrl+Enter)' : ''}
            </button>
          </div>

          {/* ── Stage + Status ── */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Stage</div>
              <select
                value={stage}
                onChange={e => setStage(e.target.value)}
                style={{ width: '100%', ...FI, padding: '8px 10px', fontSize: 13 }}
              >
                {Object.entries(STAGE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</div>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                style={{ width: '100%', ...FI, padding: '8px 10px', fontSize: 13 }}
              >
                <option value="active">Active</option>
                <option value="stalled">Stalled</option>
                <option value="cold">Cold</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
              </select>
            </div>
          </div>

          {/* ── Log Contact ── */}
          <button
            onClick={logContact}
            style={{
              width: '100%', padding: '10px 0',
              background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)',
              borderRadius: 8, color: '#22c55e', fontWeight: 600, fontSize: 13, cursor: 'pointer',
            }}
          >
            📞 Log Contact — resets "no contact" timer
          </button>

          {/* ── Issues ── */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Issues</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {issues.map(issue => (
                <span key={issue} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, padding: '4px 10px', fontSize: 11, color: '#fca5a5' }}>
                  {issue}
                  <button onClick={() => removeIssue(issue)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1, marginLeft: 1 }}>✕</button>
                </span>
              ))}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowIssueMenu(v => !v)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 12, padding: '4px 10px', fontSize: 11, color: '#D1D5DB', cursor: 'pointer' }}
                >
                  + Add Issue
                </button>
                {showIssueMenu && (
                  <div
                    style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200, background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '6px 0', minWidth: 210, boxShadow: '0 12px 32px rgba(0,0,0,0.1)' }}
                    onClick={e => e.stopPropagation()}
                  >
                    {ISSUE_PRESETS.filter(p => !issues.includes(p)).map(p => (
                      <div
                        key={p}
                        onClick={() => addIssue(p)}
                        style={{ padding: '7px 14px', fontSize: 12, color: '#D1D5DB', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#1a2035'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {p}
                      </div>
                    ))}
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '4px 0', padding: '4px 8px' }}>
                      <input
                        value={customIssue}
                        onChange={e => setCustomIssue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && customIssue.trim()) addIssue(customIssue); }}
                        placeholder="Custom issue... (Enter)"
                        autoFocus
                        style={{ width: '100%', background: 'transparent', border: 'none', color: '#FFFFFF', fontSize: 12, padding: '4px 6px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Quotes ── */}
          {(() => {
            const leadQuotes = getQuotesForLead(lead.id, quotes || DEMO_QUOTES);
            const qsColor = (s) => ({
              draft: '#9CA3AF', sent: '#3b82f6', viewed: '#06b6d4',
              signed: '#22c55e', rejected: '#ef4444', expired: '#f59e0b',
            }[s] || '#9CA3AF');
            const qsBg = (s) => ({
              draft: 'rgba(156,163,175,0.12)', sent: 'rgba(59,130,246,0.15)', viewed: 'rgba(6,182,212,0.15)',
              signed: 'rgba(34,197,94,0.15)', rejected: 'rgba(239,68,68,0.15)', expired: 'rgba(245,158,11,0.15)',
            }[s] || 'rgba(156,163,175,0.12)');
            return (
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>Quotes</span>
                    {leadQuotes.length > 0 && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
                        background: 'rgba(255,255,255,0.06)', color: '#D1D5DB',
                      }}>{leadQuotes.length}</span>
                    )}
                  </div>
                  <button
                    onClick={() => onNewQuote && onNewQuote(lead)}
                    style={{
                      padding: '6px 12px', minHeight: 36,
                      background: 'linear-gradient(135deg, #2D5016, #3d6b1e)',
                      border: 'none', borderRadius: 6, color: '#fff', fontSize: 11, fontWeight: 700,
                      cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                      boxShadow: '0 2px 8px rgba(45,80,22,0.35)',
                    }}
                  >+ New Quote</button>
                </div>
                {leadQuotes.length === 0 ? (
                  <div style={{
                    padding: '14px 14px', borderRadius: 8,
                    background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.08)',
                    fontSize: 12, color: '#D1D5DB', textAlign: 'center',
                  }}>
                    No quotes yet. Tap <strong style={{ color: '#FFFFFF' }}>+ New Quote</strong> to describe the job — the agent will draft it.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {leadQuotes.map(q => (
                      <div
                        key={q.id}
                        onClick={() => onNewQuote && onNewQuote(lead, q)}
                        style={{
                          background: '#1E2329', border: '1px solid rgba(255,255,255,0.08)',
                          borderLeft: `3px solid ${qsColor(q.status)}`,
                          borderRadius: 8, padding: '10px 12px',
                          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                          cursor: 'pointer', transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#252b36'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#1E2329'; }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF', whiteSpace: 'nowrap' }}>{q.quoteNumber}</span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                          background: qsBg(q.status), color: qsColor(q.status),
                          textTransform: 'uppercase', letterSpacing: '0.5px',
                        }}>{q.status}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: '#22c55e', whiteSpace: 'nowrap' }}>
                          {rolePerms?.seeDollars !== false ? fmt(q.total || 0) : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Recent Activity ── */}
          {recentActivity.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Recent Activity</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {recentActivity.map((entry, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                    <span style={{ color: '#D1D5DB', flexShrink: 0 }}>{ACTIVITY_ICONS[entry.type] || '·'}</span>
                    <div style={{ minWidth: 0 }}>
                      <span style={{ color: '#D1D5DB' }}>{new Date(entry.timestamp).toLocaleDateString()} </span>
                      <span style={{ color: '#D1D5DB' }}>{entry.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Save & Close ── */}
          <button
            onClick={save}
            style={{
              width: '100%', padding: '12px 0',
              background: 'linear-gradient(135deg, #1d3461, #1a2d4a)',
              border: '1px solid rgba(59,130,246,0.25)', borderRadius: 9,
              color: '#93c5fd', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}
          >
            Save & Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Pipeline Tab (Kanban) ─────────────────────────────────────────────────────
function PipelineTab({ leads, onSelectLead, onAddLead, onEditLead, onDeleteLead, demoMode, onStageChange, onUpdateLead, currentUser, rolePerms, quotes, onSaveQuote, onConvertLead }) {
  const [search, setSearch] = useState('');
  const [tradeFilter, setTradeFilter] = useState('all');
  const [dragOver, setDragOver] = useState(null);
  const [quickEditLead, setQuickEditLead] = useState(null);
  const [quoteEditLead, setQuoteEditLead] = useState(null);
  const showToast = useToast();
  const isMobile = useMobile();

  // Sync horizontal scroll between the stats summary row and the kanban
  // columns row. Proportional sync (scrollLeft / scrollMax).
  //
  // iOS momentum-scroll bug: assigning `.scrollLeft` while momentum is in
  // flight aborts the momentum (snap-back to wherever the sync wrote). We
  // fix this by giving the *user-touched* container exclusive ownership of
  // the scroll until ~350ms after pointer release (covers the momentum tail).
  // The passive container is mirrored from the active one, but its synthetic
  // scroll events never trigger a reverse-sync.
  const statsRef = useRef(null);
  const kanbanRef = useRef(null);
  const activeScrollerRef = useRef(null); // 'stats' | 'kanban' | null
  const releaseTimerRef = useRef(null);
  const lastKanbanLeftRef = useRef(0);
  const lastStatsLeftRef = useRef(0);

  const markActive = (which) => {
    activeScrollerRef.current = which;
    if (releaseTimerRef.current) { clearTimeout(releaseTimerRef.current); releaseTimerRef.current = null; }
  };
  const scheduleRelease = () => {
    if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
    releaseTimerRef.current = setTimeout(() => {
      activeScrollerRef.current = null;
      releaseTimerRef.current = null;
    }, 350);
  };

  useEffect(() => () => {
    if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
  }, []);

  const handleStatsScroll = () => {
    const s = statsRef.current, k = kanbanRef.current;
    if (!s || !k) return;
    const prev = lastStatsLeftRef.current;
    const cur = s.scrollLeft;
    lastStatsLeftRef.current = cur;
    // Defensive: ignore a sudden snap to 0 when we were significantly scrolled —
    // almost always a layout reset on re-render, not a real user scroll.
    if (cur === 0 && prev > 50) return;
    // Only sync when the user is actively driving the stats row. The kanban
    // row's momentum scroll must not be interrupted by reverse-sync writes.
    if (activeScrollerRef.current !== 'stats') return;
    const sMax = s.scrollWidth - s.clientWidth;
    const kMax = k.scrollWidth - k.clientWidth;
    if (sMax <= 0 || kMax <= 0) return;
    k.scrollLeft = (cur / sMax) * kMax;
    lastKanbanLeftRef.current = k.scrollLeft;
  };
  const handleKanbanScroll = () => {
    const s = statsRef.current, k = kanbanRef.current;
    if (!s || !k) return;
    const prev = lastKanbanLeftRef.current;
    const cur = k.scrollLeft;
    lastKanbanLeftRef.current = cur;
    if (cur === 0 && prev > 50) return;
    if (activeScrollerRef.current !== 'kanban') return;
    const sMax = s.scrollWidth - s.clientWidth;
    const kMax = k.scrollWidth - k.clientWidth;
    if (sMax <= 0 || kMax <= 0) return;
    s.scrollLeft = (cur / kMax) * sMax;
    lastStatsLeftRef.current = s.scrollLeft;
  };

  const KANBAN_STAGES = [
    { key: 'lead', label: 'Lead' },
    { key: 'inspection_scheduled', label: 'Insp Scheduled' },
    { key: 'inspection_complete', label: 'Insp Complete' },
    { key: 'estimate_sent', label: 'Estimate Sent' },
    { key: 'contract_signed', label: 'Contract Signed' },
    { key: 'materials_ordered', label: 'Materials Ordered' },
    { key: 'scheduled_for_install', label: 'Sched for Install' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'punch_list', label: 'Punch List' },
    { key: 'completed', label: 'Completed' },
    { key: 'invoiced', label: 'Invoiced' },
    { key: 'paid', label: 'Paid' },
  ];

  const today = new Date(TODAY);
  const staleDays = (lead) => {
    if (!lead.lastContact) return 0;
    return Math.round((today - new Date(lead.lastContact)) / 86400000);
  };

  const urgencyBorder = () => {
    return '1px solid rgba(255,255,255,0.08)';
  };

  const filteredLeads = useMemo(() => {
    let r = leads.filter(l => l.stage !== 'lost');
    if (tradeFilter !== 'all') r = r.filter(l => l.trade === tradeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(l =>
        l.name.toLowerCase().includes(q) ||
        (l.contact && l.contact.toLowerCase().includes(q)) ||
        (l.address && l.address.toLowerCase().includes(q)) ||
        (l.trade && l.trade.toLowerCase().includes(q))
      );
    }
    return r;
  }, [leads, tradeFilter, search]);

  const byStage = useMemo(() => {
    const map = {};
    KANBAN_STAGES.forEach(s => { map[s.key] = filteredLeads.filter(l => l.stage === s.key); });
    return map;
  }, [filteredLeads]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-lead total value sums the lead's primary scope (lead.trade/value)
  // plus any extra scopes attached to multi-scope projects.
  const leadTotalValue = (lead) => {
    const extras = (lead.extraScopes || []).reduce((sum, s) => sum + (s.value || 0), 0);
    return (lead.value || 0) + extras;
  };

  const stageStats = KANBAN_STAGES.map(s => ({
    ...s,
    count: (byStage[s.key] || []).length,
    value: (byStage[s.key] || []).reduce((sum, l) => sum + leadTotalValue(l), 0),
  }));

  const totalPipeline = filteredLeads.reduce((s, l) => s + leadTotalValue(l), 0);

  const handleDrop = (e, stage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('text/plain');
    if (!leadId || !onStageChange) { setDragOver(null); return; }
    const lead = leads.find(l => String(l.id) === leadId);
    if (lead && lead.stage !== stage) {
      onStageChange(leadId, stage);
      // Also add activity log entry via onUpdateLead if available
      if (onUpdateLead) {
        const entry = { timestamp: new Date().toISOString(), type: 'stage', message: `Stage changed from ${STAGE_LABELS[lead.stage] || lead.stage} to ${STAGE_LABELS[stage] || stage}`, user: currentUser || 'You' };
        onUpdateLead({ ...lead, stage, activityLog: [...(lead.activityLog || []), entry] });
      }
      showToast(`"${lead.name}" → ${STAGE_LABELS[stage]}`);
    }
    setDragOver(null);
  };

  return (
    <div>
      {/* Summary bar — scrolls in sync with kanban below */}
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <div
          ref={statsRef}
          onScroll={handleStatsScroll}
          onPointerDown={() => markActive('stats')}
          onPointerUp={scheduleRelease}
          onPointerCancel={scheduleRelease}
          onTouchStart={() => markActive('stats')}
          onTouchEnd={scheduleRelease}
          style={{ display: 'flex', gap: 0, background: '#2A3140', borderBottom: '1px solid rgba(255,255,255,0.08)', borderRadius: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}
        >
          {stageStats.map((s) => (
            <div key={s.key} style={{ flex: '0 0 auto', minWidth: 96, padding: '10px 8px', borderRight: '1px solid rgba(255,255,255,0.08)', textAlign: 'center', overflow: 'hidden' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#FFFFFF' }}>{s.count}</div>
              <div style={{ fontSize: 11, color: '#D1D5DB', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</div>
              {s.value > 0 && rolePerms?.seeDollars !== false && <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmt(s.value)}</div>}
            </div>
          ))}
          <div style={{ flex: '0 0 auto', minWidth: 96, padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #E8722A', overflow: 'hidden' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#E8722A', whiteSpace: 'nowrap' }}>{rolePerms?.seeDollars !== false ? fmt(totalPipeline) : '—'}</div>
            <div style={{ fontSize: 11, color: '#D1D5DB', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Pipeline</div>
          </div>
        </div>
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 1, width: 32, background: 'linear-gradient(to right, transparent, #1E2329)', pointerEvents: 'none' }} />
      </div>

      {/* Search + trade filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          placeholder="Search client, address, job type..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...FI, flex: '1 1 200px', padding: '8px 12px', fontSize: 13 }}
        />
        <select
          value={tradeFilter}
          onChange={e => setTradeFilter(e.target.value)}
          style={{ ...FI, flex: '0 0 auto', padding: '8px 12px', fontSize: 13 }}
        >
          <option value="all">All Job Types</option>
          {TRADE_LIST.filter(t => leads.some(l => l.trade === t)).map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
          {Array.from(new Set(leads.map(l => l.trade).filter(t => t && !TRADE_LIST.includes(t)))).sort().map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        {onAddLead && (
          <button
            style={{
              padding: '10px 16px', minHeight: 44,
              background: 'linear-gradient(135deg, #E8722A, #e8640c)',
              border: 'none', borderRadius: 8,
              color: '#fff', fontWeight: 700, fontSize: 14,
              cursor: 'pointer', whiteSpace: 'nowrap',
              boxShadow: '0 2px 10px rgba(249,115,22,0.3)',
            }}
            onClick={onAddLead}
          >
            + Add Lead
          </button>
        )}
      </div>

      {/* Kanban board */}
      <div style={{ position: 'relative' }}>
        {isMobile && (
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 16, width: 40, background: 'linear-gradient(to right, transparent, #1E2329)', zIndex: 2, pointerEvents: 'none', borderRadius: '0 10px 10px 0' }} />
        )}
        {isMobile && (
          <div style={{ textAlign: 'center', fontSize: 11, color: '#D1D5DB', marginBottom: 6, letterSpacing: '0.3px' }}>
            ← Swipe to see all stages →
          </div>
        )}
      <div
        ref={kanbanRef}
        onScroll={handleKanbanScroll}
        onPointerDown={() => markActive('kanban')}
        onPointerUp={scheduleRelease}
        onPointerCancel={scheduleRelease}
        onTouchStart={() => markActive('kanban')}
        onTouchEnd={scheduleRelease}
        style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16, WebkitOverflowScrolling: 'touch', alignItems: 'flex-start' }}
      >
        {KANBAN_STAGES.map(stg => {
          const cards = byStage[stg.key] || [];
          const colValue = cards.reduce((s, l) => s + leadTotalValue(l), 0);
          const isOver = dragOver === stg.key;

          return (
            <div
              key={stg.key}
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(stg.key); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => handleDrop(e, stg.key)}
              style={{
                minWidth: isMobile ? 230 : 220,
                flex: isMobile ? '0 0 230px' : '1 1 0',
                background: isOver ? 'rgba(232,114,42,0.1)' : '#1E2329',
                border: `1px solid ${isOver ? '#E8722A' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 10,
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              {/* Column header */}
              <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', borderLeft: `4px solid ${STAGE_COLORS[stg.key] || '#E8722A'}`, background: '#2A3140', borderRadius: '10px 10px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{stg.label}</span>
                  <span style={{ fontSize: 11, color: '#D1D5DB', marginLeft: 6 }}>{cards.length}</span>
                  {colValue > 0 && rolePerms?.seeDollars !== false && <div style={{ fontSize: 10, color: '#D1D5DB' }}>{fmt(colValue)}</div>}
                </div>
                {stg.key === 'lead' && onAddLead && (
                  <button
                    style={{
                      padding: '6px 12px', minHeight: 44,
                      background: 'linear-gradient(135deg, #E8722A, #e8640c)',
                      border: 'none', borderRadius: 8,
                      color: '#fff', fontWeight: 700, fontSize: 12,
                      cursor: 'pointer', flexShrink: 0,
                    }}
                    onClick={onAddLead}
                  >
                    + New
                  </button>
                )}
              </div>

              {/* Cards */}
              <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 120 }}>
                {cards.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 8px', color: '#9CA3AF', fontSize: 11 }}>
                    Drop here
                  </div>
                ) : cards.map(lead => (
                  <KanbanCard
                    key={lead.id}
                    lead={lead}
                    urgencyBorder={urgencyBorder(lead)}
                    staleDays={staleDays(lead)}
                    onQuickEdit={setQuickEditLead}
                    onEdit={onEditLead}
                    onDelete={onDeleteLead}
                    demoMode={demoMode}
                    rolePerms={rolePerms}
                    quotes={quotes}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      </div>

      {/* Quick Edit Modal */}
      {quickEditLead && (
        <CardQuickEdit
          lead={quickEditLead}
          onClose={() => setQuickEditLead(null)}
          onUpdate={(updated) => {
            if (onUpdateLead) onUpdateLead(updated);
            // keep quickEditLead in sync so the panel reflects immediate changes
            setQuickEditLead(updated);
          }}
          onOpenDetail={onSelectLead ? () => { setQuickEditLead(null); onSelectLead(quickEditLead); } : null}
          currentUser={currentUser}
          rolePerms={rolePerms}
          quotes={quotes}
          onNewQuote={(lead, existingQuote) => {
            setQuickEditLead(null);
            if (existingQuote) {
              // Opening an existing draft → go straight to the editor
              setQuoteEditLead({ lead, quote: existingQuote, picker: false });
            } else {
              // New quote → show template picker first
              setQuoteEditLead({ lead, quote: null, picker: true });
            }
          }}
        />
      )}

      {quoteEditLead && quoteEditLead.picker && (
        <QuoteModePickerModal
          target={{
            kind: 'lead', id: quoteEditLead.lead.id,
            customerName: quoteEditLead.lead.name,
            address: quoteEditLead.lead.address || '',
            lead: quoteEditLead.lead,
          }}
          quotes={quotes}
          onPick={(quote) => setQuoteEditLead({ ...quoteEditLead, quote, picker: false })}
          onClose={() => setQuoteEditLead(null)}
        />
      )}

      {quoteEditLead && !quoteEditLead.picker && quoteEditLead.quote && (
        <QuoteDocumentEditor
          target={{
            kind: 'lead', id: quoteEditLead.lead.id,
            customerName: quoteEditLead.lead.name,
            address: quoteEditLead.lead.address || '',
            lead: quoteEditLead.lead,
          }}
          initialQuote={quoteEditLead.quote}
          onSave={(quote) => {
            if (onSaveQuote) onSaveQuote(quote);
            // Keep editor open after auto-save; just refresh local quote
            setQuoteEditLead(prev => prev ? { ...prev, quote } : prev);
          }}
          onMarkSigned={(quote) => { if (onSaveQuote) onSaveQuote(quote); }}
          onConvertToProject={(lead, signedQuote) => {
            if (onConvertLead) {
              onConvertLead(lead, signedQuote);
              setQuoteEditLead(null);
            }
          }}
          onClose={() => setQuoteEditLead(null)}
        />
      )}
    </div>
  );
}

// ─── Callbacks Tab ────────────────────────────────────────────────────────────
function CallbacksTab({ leads, onSelectLead, onUpdateLead, rolePerms }) {
  const [hovered, setHovered] = useState(null);
  const [snoozeModal, setSnoozeModal] = useState(null);
  const [snoozeDate, setSnoozeDate] = useState('');
  const showToast = useToast();
  useScrollLock(!!snoozeModal);

  const withCallbacks = leads
    .filter(l => l.callbackDate && l.status !== 'won' && l.status !== 'lost')
    .sort((a, b) => new Date(a.callbackDate) - new Date(b.callbackDate));

  const overdue = withCallbacks.filter(l => diffDays(l.callbackDate) < 0);
  const today = withCallbacks.filter(l => diffDays(l.callbackDate) === 0);
  const upcoming = withCallbacks.filter(l => diffDays(l.callbackDate) > 0);

  const handleMarkCalled = (lead, e) => {
    e.stopPropagation();
    if (!onUpdateLead) return;
    onUpdateLead({ ...lead, lastContact: TODAY, callbackDate: null });
    showToast(`Marked "${lead.name}" as called`);
  };

  const handleSnooze = (lead, e) => {
    e.stopPropagation();
    if (!onUpdateLead) return;
    const tomorrow = new Date(TODAY);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().slice(0, 10);
    setSnoozeDate(tomorrowStr);
    setSnoozeModal(lead);
  };

  const confirmSnooze = () => {
    if (!snoozeModal || !snoozeDate) return;
    onUpdateLead({ ...snoozeModal, callbackDate: snoozeDate });
    showToast(`Snoozed "${snoozeModal.name}" to ${snoozeDate}`);
    setSnoozeModal(null);
  };

  const CbSection = ({ title, items, color }) => {
    if (!items.length) return null;
    return (
      <div style={S.cbSection}>
        <div style={S.cbSectionHeader}>
          {title}
          <span style={S.cbBadge(color)}>{items.length}</span>
        </div>
        {items.map(lead => {
          const days = diffDays(lead.callbackDate);
          const isOverdue = days < 0;
          return (
            <div
              key={lead.id}
              style={{ ...S.cbRow(hovered === lead.id), border: isOverdue ? '1px solid rgba(239,68,68,0.25)' : '1px solid rgba(255,255,255,0.08)' }}
              onMouseEnter={() => setHovered(lead.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSelectLead(lead)}
            >
              <div style={S.cbDate(isOverdue)}>
                {days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? 'Today' : `in ${days}d`}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.cbName}>{lead.name}</div>
                <div style={S.cbContact}>
                  {lead.contact} · {STAGE_LABELS[lead.stage] || lead.stage}
                  {lead.phone && <span style={{ marginLeft: 8, color: '#D1D5DB' }}>{lead.phone}</span>}
                </div>
                {lead.stallReason && (
                  <div style={{ fontSize: 11, color: '#E8722A', marginTop: 3 }}>⚠ {STALL_LABELS[lead.stallReason]}</div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={S.cbValue}>{rolePerms?.seeDollars !== false ? fmt(lead.value) : '—'}</div>
                <div style={{ fontSize: 11, color: '#D1D5DB', marginBottom: 6 }}>{lead.callbackDate}</div>
                {onUpdateLead && (
                  <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                    <button
                      style={{ padding: '5px 10px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)', borderRadius: 5, color: '#4ade80', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'background 0.15s' }}
                      onClick={e => handleMarkCalled(lead, e)}
                    >
                      ✓ Called
                    </button>
                    <button
                      style={{ padding: '5px 10px', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.4)', borderRadius: 5, color: '#a5b4fc', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'background 0.15s' }}
                      onClick={e => handleSnooze(lead, e)}
                    >
                      Snooze
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <CbSection title="🔴  Overdue" items={overdue} color="#ef4444" />
      <CbSection title="🟡  Due Today" items={today} color="#E8722A" />
      <CbSection title="🟢  Upcoming" items={upcoming} color="#22c55e" />
      {!overdue.length && !today.length && !upcoming.length && (
        <div style={{ textAlign: 'center', padding: '80px 24px' }}>
          <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.4 }}>📞</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#D1D5DB', marginBottom: 8 }}>No callbacks scheduled</div>
          <div style={{ fontSize: 13, color: '#D1D5DB', maxWidth: 320, margin: '0 auto' }}>Jobs with follow-up reminders will appear here. Set a callback date when editing a lead.</div>
        </div>
      )}

      {/* Snooze modal */}
      {snoozeModal && (
        <div style={S.modalOverlay} onClick={() => setSnoozeModal(null)}>
          <div style={{ ...S.modal, maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <button style={S.closeBtn} onClick={() => setSnoozeModal(null)}>×</button>
            <div style={{ ...S.modalTitle, paddingRight: 48 }}>Snooze Callback</div>
            <div style={{ ...S.modalSub }}>{snoozeModal.name}</div>
            <label style={FLbl}>New Callback Date</label>
            <input type="date" style={{ ...FI, marginBottom: 20 }} value={snoozeDate} onChange={e => setSnoozeDate(e.target.value)} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ri-btn ri-btn-secondary" style={{ flex: 1, padding: '8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, color: '#D1D5DB', cursor: 'pointer' }} onClick={() => setSnoozeModal(null)}>Cancel</button>
              <button style={{ flex: 2, padding: '8px', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', border: 'none', borderRadius: 7, color: '#fff', fontWeight: 700, cursor: 'pointer' }} onClick={confirmSnooze}>Set Snooze Date</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Analytics Tab ────────────────────────────────────────────────────────────
function AnalyticsTab({ leads, jobs, tier, rolePerms }) {
  const isMobile = useMobile();

  const stats = useMemo(() => {
    const active = leads.filter(l => ['active', 'stalled', 'cold'].includes(l.status));
    const stalled = leads.filter(l => l.status === 'stalled');
    const won = leads.filter(l => l.status === 'won');
    const lost = leads.filter(l => l.status === 'lost');

    // Total Pipeline Value — all jobs not in paid stage
    const allJobs = jobs || [];
    const pipelineJobs = allJobs.filter(j => j.status !== 'Complete' || !['paid'].includes(j.stage));
    const totalPipeline = pipelineJobs.reduce((s, j) => s + (j.value || 0), 0)
      + active.reduce((s, l) => s + (l.value || 0), 0);

    // Revenue Closed YTD — leads in invoiced/paid stage
    const revenueLeads = leads.filter(l => ['invoiced', 'paid'].includes(l.stage));
    const revenueJobs = allJobs.filter(j => j.status === 'Complete');
    const revenueClosed = revenueLeads.reduce((s, l) => s + (l.value || 0), 0)
      + revenueJobs.reduce((s, j) => s + (j.value || 0), 0);

    // Average Job Value — across all leads + jobs
    const allValues = [...leads.map(l => l.value || 0), ...allJobs.map(j => j.value || 0)].filter(v => v > 0);
    const avgJobValue = allValues.length ? Math.round(allValues.reduce((s, v) => s + v, 0) / allValues.length) : 0;

    // Win Rate
    const closedDeals = leads.filter(l => ['won', 'lost'].includes(l.status));
    const winRate = closedDeals.length ? Math.round(won.length / closedDeals.length * 100) : 0;

    // Scheduled Hours This Week
    const now = new Date();
    const dayOfWeek = now.getDay();
    const weekStart = new Date(now); weekStart.setDate(now.getDate() - dayOfWeek);
    const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
    const ws = weekStart.toISOString().slice(0, 10);
    const we = weekEnd.toISOString().slice(0, 10);
    const weekJobs = allJobs.filter(j => j.scheduledDate && j.scheduledDate >= ws && j.scheduledDate <= we);
    const weekHours = weekJobs.reduce((s, j) => s + (j.hours || 0), 0);

    const stalledValue = stalled.reduce((s, l) => s + l.value, 0);
    const avgDealAge = active.length ? Math.round(active.reduce((s, l) => s + (l.dealAge || 0), 0) / active.length) : 0;

    // Stage funnel from leads
    const stageFunnel = STAGE_ORDER.map(stage => ({
      stage,
      count: leads.filter(l => l.stage === stage).length,
      value: leads.filter(l => l.stage === stage).reduce((s, l) => s + l.value, 0),
      avgAge: (() => {
        const sl = active.filter(l => l.stage === stage);
        return sl.length ? Math.round(sl.reduce((s, l) => s + (l.dealAge || 0), 0) / sl.length) : 0;
      })(),
    })).filter(s => s.count > 0);

    // Stall breakdown
    const stallBreakdown = Object.entries(
      stalled.reduce((acc, l) => { if (l.stallReason) acc[l.stallReason] = (acc[l.stallReason] || 0) + 1; return acc; }, {})
    ).sort((a, b) => b[1] - a[1]);

    // Average Margin — estimate from job values vs DEMO_COST_DATA labor totals
    const marginJobs = allJobs.filter(j => j.value > 0 && DEMO_COST_DATA[j.id]);
    const avgMargin = marginJobs.length ? Math.round(marginJobs.reduce((s, j) => {
      const fills = DEMO_COST_DATA[j.id];
      const labFills = fills?.lab || {};
      const laborCost = Object.values(labFills).reduce((t, item) => t + (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0), 0);
      const matFills = fills?.mat || {};
      const matCost = Object.values(matFills).reduce((t, item) => t + (parseFloat(item.qty) || 0) * (parseFloat(item.costPerUnit) || 0), 0);
      const totalCost = laborCost + matCost;
      const margin = j.value > 0 ? ((j.value - totalCost) / j.value) * 100 : 0;
      return s + margin;
    }, 0) / marginJobs.length) : 0;

    // Jobs by stage (combining leads + jobs)
    const jobsByStage = STAGE_ORDER.map(stage => {
      const leadCount = leads.filter(l => l.stage === stage).length;
      const jobCount = allJobs.filter(j => {
        if (stage === 'in_progress') return j.status === 'In Progress';
        if (stage === 'completed' || stage === 'paid') return j.status === 'Complete';
        return false;
      }).length;
      return { stage, count: leadCount + jobCount };
    }).filter(s => s.count > 0);

    // Avg scopes per project — count unique customer+address pairs across all
    // jobs (each pair = one project) and divide total scopes by project count.
    const projectKeys = new Set((jobs || []).map(j => `${j.customer}|${j.address}`));
    const projectCount = projectKeys.size;
    const totalScopes = (jobs || []).length;
    const avgScopesPerProject = projectCount > 0
      ? Math.round((totalScopes / projectCount) * 10) / 10
      : 0;

    return { active, stalled, won, lost, totalPipeline, revenueClosed, avgJobValue, winRate, weekHours, stalledValue, avgDealAge, stageFunnel, stallBreakdown, avgMargin, jobsByStage, avgScopesPerProject };
  }, [leads, jobs]);

  const { active, stalled, won, lost, totalPipeline, revenueClosed, avgJobValue, winRate, weekHours, stalledValue, avgDealAge, stageFunnel, stallBreakdown, avgMargin, jobsByStage, avgScopesPerProject } = stats;
  const maxStageValue = Math.max(...stageFunnel.map(s => s.value), 1);
  const maxStallCount = stallBreakdown.length ? Math.max(...stallBreakdown.map(s => s[1]), 1) : 1;

  const STALL_COLORS = {
    price_objection: '#E8722A', budget_freeze: '#6366f1', no_response: '#ef4444',
    competitor: '#f59e0b', timing: '#D1D5DB', wrong_contact: '#8b5cf6', technical_fit: '#06b6d4',
  };

  const D = rolePerms?.seeDollars !== false;
  const kpis = [
    { val: D ? fmt(totalPipeline) : '—', label: 'Total Pipeline Value' },
    { val: D ? fmt(revenueClosed) : '—', label: 'Revenue Closed YTD' },
    { val: D ? fmt(avgJobValue) : '—', label: 'Avg Project Value' },
    { val: `${avgMargin}%`, label: 'Avg Margin' },
    { val: `${winRate}%`, label: 'Win / Close Rate' },
    { val: `${avgScopesPerProject}`, label: 'Avg Scopes / Project' },
    { val: `${weekHours}h`, label: 'Scheduled Hrs This Week' },
    { val: `${avgDealAge}d`, label: 'Avg Deal Age' },
    { val: D ? fmt(stalledValue) : '—', label: 'Value at Risk' },
  ];

  return (
    <div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: isMobile ? 10 : 14,
        marginBottom: 24,
      }}>
        {kpis.map(({ val, label }) => (
          <div key={label} style={{ ...S.statCard, ...(isMobile ? { padding: 12 } : {}) }}>
            <div style={{ ...S.statVal, ...(isMobile ? { fontSize: 20 } : {}), color: '#FFFFFF' }}>{val}</div>
            <div style={S.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ ...S.chartSection, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
        {/* Revenue pipeline by stage */}
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Revenue by Stage</div>
          {stageFunnel.length === 0 ? (
            <div style={{ color: '#D1D5DB', fontSize: 13 }}>No active leads</div>
          ) : stageFunnel.map(({ stage, count, value, avgAge }) => (
            <div key={stage} style={S.barRow}>
              <div style={{ ...S.barLabel, width: 110 }}>{STAGE_LABELS[stage] || stage}</div>
              <div style={S.barTrack}>
                <div style={S.barFill(value / maxStageValue * 100, STAGE_COLORS[stage] || '#E8722A')} />
              </div>
              <div style={{ fontSize: 11, color: '#D1D5DB', width: 24, textAlign: 'right' }}>{count}</div>
              <div style={{ fontSize: 11, color: '#D1D5DB', width: 72, textAlign: 'right' }}>{D ? fmt(value) : '—'}</div>
              {avgAge > 0 && <div style={{ fontSize: 10, color: '#D1D5DB', width: 36, textAlign: 'right' }}>{avgAge}d</div>}
            </div>
          ))}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ fontSize: 11, color: '#D1D5DB' }}>Total pipeline value</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#E8722A', marginTop: 4 }}>{D ? fmt(totalPipeline) : '—'}</div>
          </div>
        </div>

        {/* Stall reason breakdown */}
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Stall Reason Breakdown</div>
          {stallBreakdown.length === 0 && (
            <div style={{ color: '#D1D5DB', fontSize: 13 }}>No stalled deals</div>
          )}
          {stallBreakdown.map(([reason, count]) => (
            <div key={reason} style={S.barRow}>
              <div style={S.barLabel}>{STALL_LABELS[reason]}</div>
              <div style={S.barTrack}>
                <div style={S.barFill(count / maxStallCount * 100, STALL_COLORS[reason] || '#E8722A')} />
              </div>
              <div style={S.barCount}>{count}</div>
              <div style={{ fontSize: 11, color: '#D1D5DB', width: 72, textAlign: 'right' }}>
                {fmt(stalled.filter(l => l.stallReason === reason).reduce((s, l) => s + l.value, 0))}
              </div>
            </div>
          ))}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ fontSize: 12, color: '#D1D5DB', marginBottom: 6 }}>Total value at risk</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#E8722A' }}>{fmt(stalledValue)}</div>
          </div>
        </div>

        {/* Win/Loss funnel */}
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Win / Loss Analysis</div>
          {[
            { label: 'Total Leads', count: leads.length, color: '#D1D5DB' },
            { label: 'Active', count: active.length, color: '#3b82f6' },
            { label: 'Won', count: won.length, color: '#22c55e' },
            { label: 'Lost', count: lost.length, color: '#ef4444' },
            { label: 'Stalled', count: stalled.length, color: '#f59e0b' },
          ].map(({ label, count, color }) => (
            <div key={label} style={S.barRow}>
              <div style={{ ...S.barLabel, width: 80 }}>{label}</div>
              <div style={S.barTrack}>
                <div style={S.barFill(leads.length > 0 ? count / leads.length * 100 : 0, color)} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color, width: 28, textAlign: 'right' }}>{count}</div>
            </div>
          ))}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ display: 'flex', gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: '#D1D5DB' }}>Win Rate</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: winRate >= 60 ? '#22c55e' : '#E8722A' }}>{winRate}%</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#D1D5DB' }}>Avg Job Value</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#6366f1' }}>{fmt(avgJobValue)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Trade distribution */}
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Pipeline by Scope Type</div>
          {(() => {
            const tradeCounts = leads.reduce((acc, l) => {
              if (!['won', 'lost'].includes(l.status)) {
                if (!acc[l.trade]) acc[l.trade] = { count: 0, value: 0 };
                acc[l.trade].count++;
                acc[l.trade].value += l.value;
              }
              return acc;
            }, {});
            const sorted = Object.entries(tradeCounts).sort((a, b) => b[1].value - a[1].value).slice(0, 7);
            const maxVal = Math.max(...sorted.map(s => s[1].value), 1);
            return sorted.map(([trade, data]) => (
              <div key={trade} style={S.barRow}>
                <div style={{ ...S.barLabel, width: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{trade}</div>
                <div style={S.barTrack}>
                  <div style={S.barFill(data.value / maxVal * 100, TRADE_COLORS[trade] || '#E8722A')} />
                </div>
                <div style={{ fontSize: 11, color: '#D1D5DB', width: 24, textAlign: 'right' }}>{data.count}</div>
                <div style={{ fontSize: 11, color: '#D1D5DB', width: 72, textAlign: 'right' }}>{fmt(data.value)}</div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Projects by Stage funnel */}
      {jobsByStage.length > 0 && (
        <div style={{ ...S.chartCard, marginTop: 20, marginBottom: 20 }}>
          <div style={S.chartTitle}>Projects by Stage</div>
          {jobsByStage.map(({ stage, count }) => {
            const maxCount = Math.max(...jobsByStage.map(s => s.count), 1);
            return (
              <div key={stage} style={S.barRow}>
                <div style={{ ...S.barLabel, width: 130 }}>{STAGE_LABELS[stage] || stage}</div>
                <div style={S.barTrack}>
                  <div style={S.barFill(count / maxCount * 100, STAGE_COLORS[stage] || '#E8722A')} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: STAGE_COLORS[stage] || '#8B95A1', width: 28, textAlign: 'right' }}>{count}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Margin stats */}
      {avgMargin > 0 && D && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
          <div style={S.statCard}>
            <div style={{ ...S.statVal, color: avgMargin >= 30 ? '#22c55e' : avgMargin >= 15 ? '#f59e0b' : '#ef4444' }}>{avgMargin}%</div>
            <div style={S.statLabel}>Avg Margin (All Jobs)</div>
          </div>
          <div style={S.statCard}>
            <div style={{ ...S.statVal, color: '#14b8a6' }}>{fmt(Math.round(totalPipeline * avgMargin / 100))}</div>
            <div style={S.statLabel}>Est. Gross Profit (Pipeline)</div>
          </div>
          <div style={S.statCard}>
            <div style={{ ...S.statVal, color: '#22c55e' }}>{fmt(Math.round(revenueClosed * avgMargin / 100))}</div>
            <div style={S.statLabel}>Est. Gross Profit (Closed YTD)</div>
          </div>
        </div>
      )}

      {tier !== undefined && (
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
          <DisabledTooltip active={tier !== 'business'} label={tier === 'business' ? '' : 'Export — Business feature.'}>
            <button
              onClick={() => {
                if (tier !== 'business') return;
                const rows = [['Name','Stage','Status','Value','Trade','Deal Age'].join(',')];
                leads.forEach(l => rows.push([`"${l.name}"`,STAGE_LABELS[l.stage]||l.stage,l.status,l.value,l.trade,l.dealAge||''].join(',')));
                const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `ridgeos-pipeline-${TODAY}.csv`; a.click();
                URL.revokeObjectURL(url);
              }}
              style={{
                padding: '8px 20px',
                background: tier === 'business' ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : '#2A3140',
                border: tier === 'business' ? 'none' : '1px solid rgba(255,255,255,0.08)',
                borderRadius: 7, color: tier === 'business' ? '#fff' : '#D1D5DB',
                fontWeight: 600, fontSize: 13, cursor: tier === 'business' ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 7,
              }}
            >
              {tier !== 'business' && <span>🔒</span>}
              Export Pipeline Report
            </button>
          </DisabledTooltip>
        </div>
      )}
    </div>
  );
}

// ─── Cost Manager ─────────────────────────────────────────────────────────────
const CP_UNITS = ['EA', 'SQ', 'BDL', 'LF', 'SF', 'FT', 'PC', 'GAL', 'BOX', 'ROLL', 'HR', 'CY', 'TON', 'LB', 'SY', 'PK', 'FLAT', 'LOAD'];
const CP_TPL_KEY = 'cl_costmgr_tpls';

const CP_DEF_MATS = [
  { id: 'ms1', title: 'Shingles & Roofing', items: [
    { id: 'ma1', item: '3-Tab Shingles', description: '', unit: 'BDL' },
    { id: 'ma2', item: 'Architectural Shingles', description: '', unit: 'BDL' },
    { id: 'ma3', item: 'Premium/Designer Shingles', description: '', unit: 'BDL' },
    { id: 'ma4', item: 'Ridge Cap Shingles', description: '', unit: 'BDL' },
    { id: 'ma5', item: 'Starter Strip', description: '', unit: 'LF' },
    { id: 'ma6', item: 'Hip & Ridge', description: '', unit: 'LF' },
  ]},
  { id: 'ms2', title: 'Underlayment & Barriers', items: [
    { id: 'mb1', item: 'Synthetic Underlayment', description: '', unit: 'ROLL' },
    { id: 'mb2', item: 'Felt Paper 15#', description: '', unit: 'ROLL' },
    { id: 'mb3', item: 'Felt Paper 30#', description: '', unit: 'ROLL' },
    { id: 'mb4', item: 'Ice & Water Shield', description: '', unit: 'ROLL' },
  ]},
  { id: 'ms3', title: 'Flashing & Metal', items: [
    { id: 'mc1', item: 'Drip Edge', description: '', unit: 'LF' },
    { id: 'mc2', item: 'Step Flashing', description: '', unit: 'EA' },
    { id: 'mc3', item: 'Valley Metal', description: '', unit: 'LF' },
    { id: 'mc4', item: 'Counter Flashing', description: '', unit: 'LF' },
    { id: 'mc5', item: 'Pipe Boot/Jack', description: '', unit: 'EA' },
    { id: 'mc6', item: 'Small Chimney Flashing', description: '', unit: 'EA' },
    { id: 'mc7', item: 'Large Chimney Flashing', description: '', unit: 'EA' },
    { id: 'mc8', item: 'Skylight Flashing', description: '', unit: 'EA' },
    { id: 'mc9', item: 'Wall Flashing', description: '', unit: 'LF' },
  ]},
  { id: 'ms4', title: 'Decking & Structural', items: [
    { id: 'md1', item: 'Plywood 4x8 Sheet', description: '', unit: 'PC' },
    { id: 'md2', item: 'OSB 4x8 Sheet', description: '', unit: 'PC' },
    { id: 'md3', item: 'Boards/Lumber', description: '', unit: 'FT' },
    { id: 'md4', item: '2x4 Lumber', description: '', unit: 'FT' },
    { id: 'md5', item: '2x6 Lumber', description: '', unit: 'FT' },
  ]},
  { id: 'ms5', title: 'Ventilation', items: [
    { id: 'me1', item: 'Ridge Vent', description: '', unit: 'LF' },
    { id: 'me2', item: 'Box Vent', description: '', unit: 'EA' },
    { id: 'me3', item: 'Soffit Vent', description: '', unit: 'EA' },
    { id: 'me4', item: 'Power Vent', description: '', unit: 'EA' },
    { id: 'me5', item: 'Turbine Vent', description: '', unit: 'EA' },
  ]},
  { id: 'ms6', title: 'Gutters & Drainage', items: [
    { id: 'mf1', item: 'Gutter 5"', description: '', unit: 'LF' },
    { id: 'mf2', item: 'Gutter 6"', description: '', unit: 'LF' },
    { id: 'mf3', item: 'Downspout', description: '', unit: 'LF' },
    { id: 'mf4', item: 'Gutter Guard', description: '', unit: 'LF' },
    { id: 'mf5', item: 'End Cap', description: '', unit: 'EA' },
    { id: 'mf6', item: 'Elbow', description: '', unit: 'EA' },
  ]},
  { id: 'ms7', title: 'Sealants & Fasteners', items: [
    { id: 'mg1', item: 'Roofing Nails', description: '', unit: 'BOX' },
    { id: 'mg2', item: 'Coil Nails', description: '', unit: 'BOX' },
    { id: 'mg3', item: 'Roofing Cement/Tar', description: '', unit: 'GAL' },
    { id: 'mg4', item: 'Caulk/Sealant', description: '', unit: 'EA' },
  ]},
  { id: 'ms8', title: 'Miscellaneous', items: [
    { id: 'mh1', item: 'Dumpster/Disposal', description: '', unit: 'EA' },
    { id: 'mh2', item: 'Tarps/Protection', description: '', unit: 'EA' },
  ]},
];

const CP_DEF_LABOR = [
  { id: 'ls1', title: 'Shingle Installation by Slope', isWaste: false, items: [
    { id: 'la1', label: 'Standard Slope (4/12\u20137/12)', unit: 'SQ' },
    { id: 'la2', label: 'Steep Slope (8/12\u20139/12)', unit: 'SQ' },
    { id: 'la3', label: 'Very Steep (10/12\u201311/12)', unit: 'SQ' },
    { id: 'la4', label: 'Extreme (12/12)', unit: 'SQ' },
    { id: 'la5', label: 'Extreme+ (13/12+)', unit: 'SQ' },
    { id: 'la6', label: 'Second Layer Add-on', unit: 'SQ' },
    { id: 'la7', label: 'Harness Required Add-on', unit: 'SQ' },
  ]},
  { id: 'ls2', title: 'Tear-Off', isWaste: false, items: [
    { id: 'lb1', label: 'Tear-off Single Layer', unit: 'SQ' },
    { id: 'lb2', label: 'Tear-off Double Layer', unit: 'SQ' },
    { id: 'lb3', label: 'Tear-off Flat/Built-up', unit: 'SQ' },
  ]},
  { id: 'ls3', title: 'Additional Labor', isWaste: false, items: [
    { id: 'lc1', label: 'Flashing Work', unit: 'HR' },
    { id: 'lc2', label: 'Chimney Flashing', unit: 'EA' },
    { id: 'lc3', label: 'Skylight Install', unit: 'EA' },
    { id: 'lc4', label: 'Skylight Reflashing', unit: 'EA' },
    { id: 'lc5', label: 'Gutter Install', unit: 'LF' },
    { id: 'lc6', label: 'Gutter Repair', unit: 'LF' },
    { id: 'lc7', label: 'Fascia Repair/Replace', unit: 'LF' },
    { id: 'lc8', label: 'Soffit Repair/Replace', unit: 'SF' },
    { id: 'lc9', label: 'Decking Repair', unit: 'PC' },
    { id: 'lc10', label: 'EPDM/Flat Roof', unit: 'SF' },
  ]},
  { id: 'ls4', title: 'Waste Tracking', isWaste: true, items: [
    { id: 'lw1', label: 'Shingles Ordered', unit: 'BDL', note: '' },
    { id: 'lw2', label: 'Additional Shingles Purchased', unit: 'BDL', note: 'Extra bundles bought to finish' },
    { id: 'lw3', label: 'Leftover Shingles', unit: 'BDL', note: 'Unused bundles' },
  ]},
];

const CP_TRADE_TEMPLATES = {
  'Full Replacement': { materials: CP_DEF_MATS, labor: CP_DEF_LABOR },
  'Repair': { materials: CP_DEF_MATS, labor: CP_DEF_LABOR },
  'Inspection': { materials: CP_DEF_MATS, labor: CP_DEF_LABOR },
  'Storm Damage': { materials: CP_DEF_MATS, labor: CP_DEF_LABOR },
  'Gutter Install': { materials: CP_DEF_MATS, labor: CP_DEF_LABOR },
  'Skylight': { materials: CP_DEF_MATS, labor: CP_DEF_LABOR },
  'Flashing Repair': { materials: CP_DEF_MATS, labor: CP_DEF_LABOR },
  'Ventilation': { materials: CP_DEF_MATS, labor: CP_DEF_LABOR },
  'Emergency Tarp': { materials: CP_DEF_MATS, labor: CP_DEF_LABOR },
};

function cpNewMakeInit(job, crewNames) {
  const tradeName = (job.trade && CP_TRADE_TEMPLATES[job.trade]) ? job.trade : 'Full Replacement';
  const tpl = CP_TRADE_TEMPLATES[tradeName];
  return {
    jobInfo: {
      contractPrice: job.value ? String(job.value) : '',
      overhead: '15',
      tax: '0',
      subContractorName: crewNames,
      notes: '',
      templateName: tradeName,
    },
    materials: {
      sections: tpl.materials.map(sec => ({
        ...sec,
        collapsed: false,
        items: sec.items.map(item => ({ ...item, qty: '', costPerUnit: '' })),
      })),
    },
    labor: {
      sections: tpl.labor.map(sec => ({
        ...sec,
        collapsed: false,
        items: sec.items.map(item => ({ ...item, qty: '', rate: '' })),
      })),
    },
    signOff: {
      inspectionPass: '',
      subName: crewNames,
      supervisor: '',
      dateCompleted: '',
      notes: '',
      submitted: false,
    },
  };
}

// Demo pre-filled Cost Manager data: jobId → { mat: {id→{qty,costPerUnit}}, lab: {id→{qty,rate}}, signOff: {...} }
const DEMO_COST_DATA = {
  201: { // Christ Lutheran Church — 118 sq, large commercial full replacement
    signOff: { inspectionPass: 'yes', subName: 'Jake Stoltzfus', supervisor: 'Demo Owner', dateCompleted: '', notes: 'North slope decking replacement complete. Underlayment installed. South side in progress.', submitted: false },
    mat: {
      ma2: { qty: '354', costPerUnit: '38.50' },  // Architectural Shingles (118 sq × 3 bdl/sq)
      ma4: { qty: '24', costPerUnit: '42' },       // Ridge Cap Shingles
      ma5: { qty: '360', costPerUnit: '0.85' },    // Starter Strip LF
      mb1: { qty: '12', costPerUnit: '76' },       // Synthetic Underlayment
      mb4: { qty: '8', costPerUnit: '98' },        // Ice & Water Shield
      mc1: { qty: '480', costPerUnit: '2.80' },    // Drip Edge LF
      mc2: { qty: '280', costPerUnit: '2.10' },    // Step Flashing
      mc7: { qty: '2', costPerUnit: '420' },       // Large Chimney Flashing
      mc6: { qty: '1', costPerUnit: '285' },       // Small Chimney Flashing
      md2: { qty: '18', costPerUnit: '42' },       // OSB 4x8 Sheet
      me1: { qty: '96', costPerUnit: '4.25' },     // Ridge Vent LF
      mg1: { qty: '12', costPerUnit: '32' },       // Roofing Nails (boxes)
      mg2: { qty: '6', costPerUnit: '45' },        // Coil Nails
      mh1: { qty: '2', costPerUnit: '425' },       // Dumpster
      mf2: { qty: '320', costPerUnit: '8.40' },    // Gutter 6" (full system)
      mf3: { qty: '8', costPerUnit: '48' },        // Downspouts
    },
    lab: {
      la1: { qty: '118', rate: '225' },            // Shingle Installation
      lb1: { qty: '118', rate: '65' },             // Tearoff & Disposal
      la7: { qty: '118', rate: '28' },             // Steep Pitch Surcharge
    },
  },
  202: { // Amos Stoltzfus — 38 sq farmhouse, standard residential
    signOff: { inspectionPass: '', subName: 'Jake Stoltzfus', supervisor: '', dateCompleted: '', notes: '', submitted: false },
    mat: {
      ma2: { qty: '114', costPerUnit: '38.50' },   // Architectural Shingles (38 sq × 3 bdl)
      ma4: { qty: '8', costPerUnit: '42' },         // Ridge Cap Shingles
      ma5: { qty: '180', costPerUnit: '0.85' },     // Starter Strip
      mb1: { qty: '4', costPerUnit: '76' },         // Synthetic Underlayment
      mb4: { qty: '3', costPerUnit: '98' },         // Ice & Water Shield
      mc1: { qty: '200', costPerUnit: '2.80' },     // Drip Edge
      mc2: { qty: '140', costPerUnit: '2.10' },     // Step Flashing
      mc6: { qty: '1', costPerUnit: '285' },        // Small Chimney Flashing
      md2: { qty: '6', costPerUnit: '42' },         // OSB 4x8 Sheet
      me1: { qty: '42', costPerUnit: '4.25' },      // Ridge Vent
      mg1: { qty: '6', costPerUnit: '32' },         // Roofing Nails
      mh1: { qty: '1', costPerUnit: '425' },        // Dumpster
    },
    lab: {
      la1: { qty: '38', rate: '225' },              // Shingle Installation (Standard)
      lb1: { qty: '38', rate: '65' },               // Tearoff & Disposal
    },
  },
  203: { // Betty Shumaker — 180 LF Gutter, $3,980
    signOff: { inspectionPass: '', subName: 'Tom Bricker', supervisor: '', dateCompleted: '', notes: '', submitted: false },
    mat: {
      mf2: { qty: '180', costPerUnit: '8.40' },   // Gutter 6" (K-style aluminum)
      mf5: { qty: '8', costPerUnit: '12' },        // End Caps & Outlets
      mf4: { qty: '36', costPerUnit: '3.20' },     // Gutter Guard (hangers sub)
      mf3: { qty: '4', costPerUnit: '48' },         // Downspout
    },
    lab: {
      lc5: { qty: '180', rate: '6.80' },           // Gutter Installation
      lc6: { qty: '180', rate: '1.20' },           // Old Gutter Removal
    },
  },
  204: { // Dan Sensenig — small residential punch list, minimal materials
    signOff: { inspectionPass: 'yes', subName: 'Tom Bricker', supervisor: 'Demo Owner', dateCompleted: '2026-04-19', notes: 'Ridge cap extension complete. One nail pop on west gable resealed. Customer satisfied.', submitted: true },
    mat: {
      ma4: { qty: '4', costPerUnit: '42' },        // Ridge Cap Shingles
      ma6: { qty: '28', costPerUnit: '1.85' },     // Hip & Ridge LF
      mg3: { qty: '3', costPerUnit: '18' },        // Roofing Cement/Tar
      mg4: { qty: '4', costPerUnit: '6.50' },      // Caulk/Sealant
      mc5: { qty: '2', costPerUnit: '12' },        // Pipe Boot/Jack
    },
    lab: {
      lc1: { qty: '4', rate: '95' },              // Flashing Work (hours)
    },
  },
  205: { // Hotel Hershey — slate matching, York supplier, copper accents
    signOff: { inspectionPass: '', subName: 'Tom Bricker', supervisor: '', dateCompleted: '', notes: '', submitted: false },
    mat: {
      ma3: { qty: '12', costPerUnit: '685' },     // Peach Bottom Slate tiles (from York, low qty high cost)
      mc7: { qty: '2', costPerUnit: '520' },      // Large Chimney Flashing (copper)
      mc2: { qty: '48', costPerUnit: '4.80' },    // Step Flashing (copper)
      mc4: { qty: '32', costPerUnit: '6.40' },    // Counter Flashing (copper)
      mg3: { qty: '4', costPerUnit: '24' },       // Roofing Cement (slate grade)
      mg4: { qty: '6', costPerUnit: '8.50' },     // Sealant (polyurethane)
      md3: { qty: '16', costPerUnit: '4.20' },    // Boards/Lumber (nailer boards)
    },
    lab: {
      la4: { qty: '12', rate: '285' },            // Slate Installation (Extreme pitch, specialty)
      lc1: { qty: '8', rate: '95' },              // Flashing Work (hours)
      lc2: { qty: '2', rate: '180' },             // Chimney Flashing (copper, each)
    },
  },
  206: { // Earl Yoder — Flashing Repair, $1,850
    signOff: { inspectionPass: '', subName: '', supervisor: '', dateCompleted: '', notes: '', submitted: false },
    mat: {
      mc2: { qty: '40', costPerUnit: '2.10' },    // Step Flashing
      mc4: { qty: '24', costPerUnit: '3.40' },    // Counter Flashing
      mg3: { qty: '2', costPerUnit: '18' },       // Roofing Cement
      ma2: { qty: '1', costPerUnit: '112' },      // Matching Shingles
    },
    lab: {
      lc1: { qty: '4', rate: '95' },              // Flashing Repair Labor
      lc9: { qty: '1', rate: '85' },              // Sealant & Cleanup (Decking Repair sub)
    },
  },
  207: { // Market Street Commons — gutter + fascia focused
    signOff: { inspectionPass: '', subName: 'Jake Stoltzfus', supervisor: '', dateCompleted: '', notes: 'Fascia repair needed on south side before gutter install.', submitted: false },
    mat: {
      mf2: { qty: '220', costPerUnit: '8.40' },   // Gutter 6" K-style seamless
      mf3: { qty: '6', costPerUnit: '48' },        // Downspouts
      mf4: { qty: '220', costPerUnit: '3.20' },    // Gutter Guards
      mf5: { qty: '12', costPerUnit: '12' },       // End Caps & Outlets
      md3: { qty: '20', costPerUnit: '6.80' },     // 1x8 Cedar Fascia board (FT)
      mg4: { qty: '6', costPerUnit: '8.50' },      // PL Premium sealant
      mg3: { qty: '2', costPerUnit: '18' },        // Roofing Cement (touchup)
    },
    lab: {
      lc5: { qty: '220', rate: '6.80' },           // Gutter Installation LF
      lc6: { qty: '220', rate: '1.20' },           // Old Gutter Removal LF
      lc7: { qty: '20', rate: '4.50' },            // Fascia Repair/Replace LF
    },
  },
  208: { // Ray Brubaker — 52 sq CertainTeed Landmark Pro, $24,800
    signOff: { inspectionPass: '', subName: '', supervisor: '', dateCompleted: '', notes: '', submitted: false },
    mat: {
      ma2: { qty: '52', costPerUnit: '128' },     // CertainTeed Landmark Pro
      mb1: { qty: '6', costPerUnit: '76' },       // Synthetic Underlayment
      mb4: { qty: '4', costPerUnit: '98' },       // Ice & Water Shield
      mc2: { qty: '165', costPerUnit: '2.10' },   // Step Flashing
      ma6: { qty: '140', costPerUnit: '1.85' },   // Ridge Cap
      mc7: { qty: '1', costPerUnit: '420' },      // Large Chimney Flashing
      md2: { qty: '8', costPerUnit: '42' },       // OSB 4x8 Sheet
    },
    lab: {
      la2: { qty: '52', rate: '225' },            // Shingle Install (Steep Slope)
      lb1: { qty: '52', rate: '65' },             // Tearoff & Disposal
    },
  },
};

function applyDemoCostData(initData, jobId) {
  const fills = DEMO_COST_DATA[jobId];
  if (!fills) return initData;
  const matFills = fills.mat || {};
  const labFills = fills.lab || {};
  return {
    ...initData,
    materials: {
      ...initData.materials,
      sections: initData.materials.sections.map(sec => ({
        ...sec,
        items: sec.items.map(item => matFills[item.id] ? { ...item, qty: matFills[item.id].qty, costPerUnit: matFills[item.id].costPerUnit } : item),
      })),
    },
    labor: {
      ...initData.labor,
      sections: initData.labor.sections.map(sec => ({
        ...sec,
        items: sec.items.map(item => labFills[item.id] ? { ...item, qty: labFills[item.id].qty, rate: labFills[item.id].rate } : item),
      })),
    },
    signOff: fills.signOff ? { ...initData.signOff, ...fills.signOff } : initData.signOff,
  };
}

function CostManagerPanel({ job, crew, assignments, rolePerms, isDemo }) {
  const cpKey = `cl_crewpay_${job.id}`;
  const assignedIds = assignments[String(job.id)] || [];
  const assignedCrew = (crew || []).filter(m => assignedIds.includes(m.id));

  const [subTab, setSubTab] = useState('materials');
  const [data, setData] = useState(() => {
    // In demo mode with demo cost data, always use demo fills (ignore stale localStorage)
    if (isDemo && DEMO_COST_DATA[job.id]) {
      return applyDemoCostData(cpNewMakeInit(job, assignedCrew.map(m => m.name).join(', ')), job.id);
    }
    try {
      const s = localStorage.getItem(cpKey);
      if (s) {
        const parsed = JSON.parse(s);
        if (
          parsed &&
          parsed.materials && Array.isArray(parsed.materials.sections) &&
          parsed.labor && Array.isArray(parsed.labor.sections) &&
          parsed.jobInfo && parsed.signOff
        ) return parsed;
      }
    } catch (e) { /* ignore */ }
    return applyDemoCostData(cpNewMakeInit(job, assignedCrew.map(m => m.name).join(', ')), job.id);
  });
  const [jobTpls, setJobTpls] = useState(() => {
    try { const s = localStorage.getItem(CP_TPL_KEY); return s ? JSON.parse(s) : []; } catch (e) { return []; }
  });
  const [showTplSave, setShowTplSave] = useState(false);
  const [tplName, setTplName] = useState('');
  const [pendingTpl, setPendingTpl] = useState(null);

  useEffect(() => {
    try { localStorage.setItem(cpKey, JSON.stringify(data)); } catch (e) { /* ignore */ }
  }, [data, cpKey]);

  useEffect(() => {
    try { localStorage.setItem(CP_TPL_KEY, JSON.stringify(jobTpls)); } catch (e) { /* ignore */ }
  }, [jobTpls]);

  // ── material handlers ───────────────────────────────────────────────────────
  const updMat = (secId, itemId, field, val) => setData(d => ({
    ...d,
    materials: {
      ...d.materials,
      sections: d.materials.sections.map(sec =>
        sec.id !== secId ? sec : {
          ...sec,
          items: sec.items.map(item => item.id !== itemId ? item : { ...item, [field]: val }),
        }
      ),
    },
  }));

  const addMatItem = (secId) => setData(d => ({
    ...d,
    materials: {
      ...d.materials,
      sections: d.materials.sections.map(sec =>
        sec.id !== secId ? sec : {
          ...sec,
          items: [...sec.items, { id: `mi${Date.now()}`, item: '', description: '', unit: 'EA', qty: '', costPerUnit: '' }],
        }
      ),
    },
  }));

  const removeMatItem = (secId, itemId) => setData(d => ({
    ...d,
    materials: {
      ...d.materials,
      sections: d.materials.sections.map(sec =>
        sec.id !== secId ? sec : { ...sec, items: sec.items.filter(item => item.id !== itemId) }
      ),
    },
  }));

  const toggleMatSec = (secId) => setData(d => ({
    ...d,
    materials: {
      ...d.materials,
      sections: d.materials.sections.map(sec =>
        sec.id !== secId ? sec : { ...sec, collapsed: !sec.collapsed }
      ),
    },
  }));

  const updMatSecTitle = (secId, val) => setData(d => ({
    ...d,
    materials: {
      ...d.materials,
      sections: d.materials.sections.map(sec =>
        sec.id !== secId ? sec : { ...sec, title: val }
      ),
    },
  }));

  const addMatSec = () => setData(d => ({
    ...d,
    materials: {
      ...d.materials,
      sections: [...d.materials.sections, {
        id: `msc${Date.now()}`,
        title: 'New Section',
        collapsed: false,
        items: [],
      }],
    },
  }));

  // ── labor handlers ──────────────────────────────────────────────────────────
  const updLabor = (secId, itemId, field, val) => setData(d => ({
    ...d,
    labor: {
      ...d.labor,
      sections: d.labor.sections.map(sec =>
        sec.id !== secId ? sec : {
          ...sec,
          items: sec.items.map(item => item.id !== itemId ? item : { ...item, [field]: val }),
        }
      ),
    },
  }));

  const addLaborItem = (secId) => setData(d => ({
    ...d,
    labor: {
      ...d.labor,
      sections: d.labor.sections.map(sec =>
        sec.id !== secId ? sec : {
          ...sec,
          items: [...sec.items, { id: `li${Date.now()}`, label: '', unit: 'EA', qty: '', rate: '' }],
        }
      ),
    },
  }));

  const removeLaborItem = (secId, itemId) => setData(d => ({
    ...d,
    labor: {
      ...d.labor,
      sections: d.labor.sections.map(sec =>
        sec.id !== secId ? sec : { ...sec, items: sec.items.filter(item => item.id !== itemId) }
      ),
    },
  }));

  const toggleLaborSec = (secId) => setData(d => ({
    ...d,
    labor: {
      ...d.labor,
      sections: d.labor.sections.map(sec =>
        sec.id !== secId ? sec : { ...sec, collapsed: !sec.collapsed }
      ),
    },
  }));

  const updLaborSecTitle = (secId, val) => setData(d => ({
    ...d,
    labor: {
      ...d.labor,
      sections: d.labor.sections.map(sec =>
        sec.id !== secId ? sec : { ...sec, title: val }
      ),
    },
  }));

  const addLaborSec = () => setData(d => ({
    ...d,
    labor: {
      ...d.labor,
      sections: [...d.labor.sections, {
        id: `lsc${Date.now()}`,
        title: 'New Section',
        isWaste: false,
        collapsed: false,
        items: [],
      }],
    },
  }));

  const updInfo = (field, val) => setData(d => ({ ...d, jobInfo: { ...d.jobInfo, [field]: val } }));
  const updSignOff = (field, val) => setData(d => ({ ...d, signOff: { ...d.signOff, [field]: val } }));

  // ── template handlers ───────────────────────────────────────────────────────
  const saveJobTpl = () => {
    if (!tplName.trim()) return;
    const materials = data.materials.sections.map(sec => ({
      ...sec, items: sec.items.map(item => { const c = { ...item }; delete c.qty; delete c.costPerUnit; return c; }),
    }));
    const labor = data.labor.sections.map(sec => ({
      ...sec, items: sec.items.map(item => { const c = { ...item }; delete c.qty; delete c.rate; return c; }),
    }));
    setJobTpls(prev => [...prev.filter(t => t.name !== tplName.trim()), { name: tplName.trim(), materials, labor }]);
    setTplName('');
    setShowTplSave(false);
  };

  const applyTpl = (name) => {
    const builtIn = CP_TRADE_TEMPLATES[name];
    const custom = jobTpls.find(t => t.name === name);
    if (!builtIn && !custom) return;
    const matSecs = builtIn ? builtIn.materials : custom.materials;
    const labSecs = builtIn ? builtIn.labor : custom.labor;
    setData(d => ({
      ...d,
      jobInfo: { ...d.jobInfo, templateName: name },
      materials: { sections: matSecs.map(sec => ({ ...sec, collapsed: false, items: sec.items.map(item => ({ ...item, qty: '', costPerUnit: '' })) })) },
      labor: { sections: labSecs.map(sec => ({ ...sec, collapsed: false, items: sec.items.map(item => ({ ...item, qty: '', rate: '' })) })) },
    }));
    setPendingTpl(null);
  };

  // ── calculations ────────────────────────────────────────────────────────────
  const matItemTot = (item) => (parseFloat(item.qty) || 0) * (parseFloat(item.costPerUnit) || 0);
  const matSecTot = (sec) => sec.items.reduce((s, item) => s + matItemTot(item), 0);
  const matTotal = data.materials.sections.reduce((s, sec) => s + matSecTot(sec), 0);

  const laborItemTot = (item) => (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0);
  const laborSecTot = (sec) => sec.isWaste ? 0 : sec.items.reduce((s, item) => s + laborItemTot(item), 0);
  const laborTotal = data.labor.sections.reduce((s, sec) => s + laborSecTot(sec), 0);

  const subtotal = matTotal + laborTotal;
  const overheadPct = parseFloat(data.jobInfo.overhead) || 0;
  const taxPct = parseFloat(data.jobInfo.tax) || 0;
  const overheadAmt = subtotal * overheadPct / 100;
  const taxAmt = (subtotal + overheadAmt) * taxPct / 100;
  const totalJobCost = subtotal + overheadAmt + taxAmt;

  const contractPrice = parseFloat(data.jobInfo.contractPrice) || 0;
  const grossProfit = contractPrice - totalJobCost;
  const profitMargin = contractPrice > 0 ? (grossProfit / contractPrice * 100) : 0;

  const wasteSection = data.labor.sections.find(s => s.isWaste);
  const wasteOrdered = parseFloat((wasteSection && wasteSection.items[0] && wasteSection.items[0].qty) || 0);
  const wasteExtra = parseFloat((wasteSection && wasteSection.items[1] && wasteSection.items[1].qty) || 0);
  const wasteLeftover = parseFloat((wasteSection && wasteSection.items[2] && wasteSection.items[2].qty) || 0);
  const netWaste = wasteOrdered + wasteExtra - wasteLeftover;

  const installSec = data.labor.sections.find(s => s.title === 'Shingle Installation by Slope');
  const totalSq = installSec ? installSec.items.reduce((s, item) => s + (parseFloat(item.qty) || 0), 0) : 0;
  const costPerSq = totalSq > 0 ? totalJobCost / totalSq : 0;
  const revenuePerSq = totalSq > 0 ? contractPrice / totalSq : 0;
  const profitPerSq = totalSq > 0 ? grossProfit / totalSq : 0;

  const fmtC = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const profitColor = profitMargin >= 20 ? '#22c55e' : profitMargin >= 10 ? '#f59e0b' : '#ef4444';

  const isMobile = useMobile();

  // ── shared cell styles ───────────────────────────────────────────────────────
  const NINP = { background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, color: '#D1D5DB', fontSize: 13, fontFamily: "'Courier New', monospace", textAlign: 'right', outline: 'none', padding: '4px 6px', width: '100%', boxSizing: 'border-box', transition: 'border-color 0.15s' };
  const UINP = { background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, color: '#D1D5DB', fontSize: 11, outline: 'none', textAlign: 'center', padding: '4px 2px', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s' };
  const CHEAD = { fontSize: 10, fontWeight: 700, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px' };
  const seeRates = rolePerms?.seeRates !== false;
  const MAT_COLS = seeRates ? '1fr 44px 58px 72px 72px 20px' : '1fr 44px 58px 20px';
  const LAB_COLS = seeRates ? '1fr 44px 58px 72px 72px 20px' : '1fr 44px 58px 20px';
  const WASTE_COLS = '1fr 44px 64px 20px';

  // ── sub-tab nav ──────────────────────────────────────────────────────────────
  const SUB_TABS = [
    { key: 'materials', label: 'Materials' },
    { key: 'labor', label: 'Labor' },
    ...(seeRates ? [{ key: 'summary', label: 'Summary' }] : []),
    { key: 'signoff', label: 'Sign Off' },
  ];

  // ── materials grid ───────────────────────────────────────────────────────────
  const renderMatSection = (sec) => {
    const secTot = matSecTot(sec);
    const secHdr = (
      <div
        className="ri-sec-hdr"
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2A3140', borderLeft: '3px solid #14b8a6', borderRadius: sec.collapsed ? 6 : '6px 6px 0 0', padding: '9px 10px', cursor: 'pointer', minHeight: 44 }}
        onClick={() => toggleMatSec(sec.id)}
      >
        <span style={{ color: '#14b8a6', fontSize: 10, flexShrink: 0 }}>{sec.collapsed ? '▶' : '▼'}</span>
        <input
          value={sec.title}
          onChange={e => { e.stopPropagation(); updMatSecTitle(sec.id, e.target.value); }}
          onClick={e => e.stopPropagation()}
          style={{ flex: 1, background: 'transparent', border: 'none', color: '#D1D5DB', fontSize: 11, fontWeight: 700, outline: 'none', fontFamily: "'Inter', -apple-system, sans-serif", textTransform: 'uppercase', letterSpacing: '0.6px', cursor: 'text' }}
        />
        <span style={{ fontFamily: "'Courier New', monospace", fontSize: 13, fontWeight: 700, color: secTot > 0 ? '#14b8a6' : 'rgba(255,255,255,0.12)', flexShrink: 0 }}>
          {secTot > 0 ? fmtC(secTot) : '—'}
        </span>
      </div>
    );
    if (isMobile) {
      return (
        <div key={sec.id} style={{ marginBottom: 4 }}>
          {secHdr}
          {!sec.collapsed && (
            <div style={{ background: '#2A3140', border: '1px solid #1a2035', borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
              {sec.items.map(item => {
                const tot = matItemTot(item);
                const filled = tot > 0;
                return (
                  <div key={item.id} style={{ padding: '8px 10px', borderBottom: '1px solid #0a0e18', background: filled ? 'rgba(249,115,22,0.04)' : 'transparent', borderLeft: filled ? '3px solid rgba(249,115,22,0.4)' : '3px solid transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
                      <input value={item.item} onChange={e => updMat(sec.id, item.id, 'item', e.target.value)} placeholder="Item name" style={{ flex: 1, background: 'transparent', border: 'none', color: '#D1D5DB', fontSize: 14, fontWeight: 600, outline: 'none', fontFamily: "'Inter', -apple-system, sans-serif", minHeight: 44, padding: '0 4px' }} />
                      <button onClick={() => removeMatItem(sec.id, item.id)} className="ri-del" style={{ background: 'transparent', border: 'none', color: '#D1D5DB', cursor: 'pointer', fontSize: 20, padding: '0 4px', minHeight: 44, minWidth: 36, flexShrink: 0 }}>×</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: seeRates ? '52px 1fr 1fr 1fr' : '52px 1fr', gap: 8, alignItems: 'end' }}>
                      <div>
                        <div style={{ ...CHEAD, marginBottom: 4 }}>Unit</div>
                        <input list="cp_unit_dl" value={item.unit} onChange={e => updMat(sec.id, item.id, 'unit', e.target.value)} style={{ ...UINP, minHeight: 44 }} />
                      </div>
                      <div>
                        <div style={{ ...CHEAD, marginBottom: 4 }}>Qty</div>
                        <input type="text" inputMode="decimal" value={item.qty} onChange={e => updMat(sec.id, item.id, 'qty', e.target.value)} placeholder="0" style={{ ...NINP, minHeight: 44, fontSize: 15 }} />
                      </div>
                      {seeRates && <div>
                        <div style={{ ...CHEAD, marginBottom: 4 }}>$/Unit</div>
                        <input type="text" inputMode="decimal" value={item.costPerUnit} onChange={e => updMat(sec.id, item.id, 'costPerUnit', e.target.value)} placeholder="0.00" style={{ ...NINP, minHeight: 44, fontSize: 15 }} />
                      </div>}
                      {seeRates && <div>
                        <div style={{ ...CHEAD, marginBottom: 4 }}>Total</div>
                        <div style={{ minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontFamily: "'Courier New', monospace", fontSize: 14, fontWeight: 700, color: filled ? '#E8722A' : 'rgba(255,255,255,0.12)' }}>{filled ? fmtC(tot) : '—'}</div>
                      </div>}
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', alignItems: 'center', borderTop: '1px dashed rgba(255,255,255,0.08)' }}>
                <button onClick={() => addMatItem(sec.id)} style={{ flex: 1, padding: '10px 10px', background: 'transparent', border: 'none', color: '#D1D5DB', cursor: 'pointer', fontSize: 13, textAlign: 'left', minHeight: 44, WebkitTapHighlightColor: 'transparent' }}>+ Add Item</button>
                {secTot > 0 && <div style={{ padding: '6px 10px', fontSize: 12, color: '#D1D5DB', fontFamily: "'Courier New', monospace", whiteSpace: 'nowrap' }}>Sub: <strong style={{ color: '#E8722A' }}>{fmtC(secTot)}</strong></div>}
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      <div key={sec.id} style={{ marginBottom: 4 }}>
        {secHdr}
        {!sec.collapsed && (
          <div style={{ background: '#2A3140', border: '1px solid #1a2035', borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: MAT_COLS, gap: 4, padding: '4px 8px', background: '#1E2329', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ ...CHEAD }}>Item</div>
              <div style={{ ...CHEAD, textAlign: 'center' }}>Unit</div>
              <div style={{ ...CHEAD, textAlign: 'right' }}>Qty</div>
              {seeRates && <div style={{ ...CHEAD, textAlign: 'right' }}>$/Unit</div>}
              {seeRates && <div style={{ ...CHEAD, textAlign: 'right' }}>Total</div>}
              <div />
            </div>
            {sec.items.map(item => {
              const tot = matItemTot(item);
              const filled = tot > 0;
              return (
                <div key={item.id} style={{ display: 'grid', gridTemplateColumns: MAT_COLS, gap: 4, alignItems: 'center', padding: '3px 8px', borderBottom: '1px solid #0a0e18', background: filled ? 'rgba(249,115,22,0.03)' : 'transparent', borderLeft: filled ? '2px solid rgba(249,115,22,0.35)' : '2px solid transparent' }}>
                  <input value={item.item} onChange={e => updMat(sec.id, item.id, 'item', e.target.value)} placeholder="Item name" style={{ background: 'transparent', border: 'none', color: '#D1D5DB', fontSize: 13, fontWeight: 500, outline: 'none', width: '100%', fontFamily: "'Inter', -apple-system, sans-serif", padding: '4px 0' }} />
                  <input list="cp_unit_dl" value={item.unit} onChange={e => updMat(sec.id, item.id, 'unit', e.target.value)} style={{ ...UINP }} />
                  <input type="text" inputMode="decimal" value={item.qty} onChange={e => updMat(sec.id, item.id, 'qty', e.target.value)} placeholder="0" style={{ ...NINP }} />
                  {seeRates && <input type="text" inputMode="decimal" value={item.costPerUnit} onChange={e => updMat(sec.id, item.id, 'costPerUnit', e.target.value)} placeholder="0.00" style={{ ...NINP }} />}
                  {seeRates && <div style={{ fontFamily: "'Courier New', monospace", fontSize: 13, fontWeight: 700, textAlign: 'right', color: filled ? '#1E2329' : 'rgba(255,255,255,0.12)' }}>{filled ? fmtC(tot) : '—'}</div>}
                  <button onClick={() => removeMatItem(sec.id, item.id)} className="ri-del" style={{ background: 'transparent', border: 'none', color: '#D1D5DB', cursor: 'pointer', fontSize: 15, padding: 0, lineHeight: 1 }}>×</button>
                </div>
              );
            })}
            <div style={{ display: 'flex', alignItems: 'center', borderTop: '1px dashed rgba(255,255,255,0.08)' }}>
              <button onClick={() => addMatItem(sec.id)} style={{ flex: 1, padding: '6px 8px', background: 'transparent', border: 'none', color: '#D1D5DB', cursor: 'pointer', fontSize: 12, textAlign: 'left', WebkitTapHighlightColor: 'transparent' }}>+ Add Item</button>
              {seeRates && secTot > 0 && <div style={{ padding: '6px 10px', fontSize: 12, color: '#D1D5DB', fontFamily: "'Courier New', monospace", whiteSpace: 'nowrap' }}>Subtotal: <strong style={{ color: '#E8722A' }}>{fmtC(secTot)}</strong></div>}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── labor grid ───────────────────────────────────────────────────────────────
  const renderLaborSection = (sec) => {
    const secTot = laborSecTot(sec);
    const cols = sec.isWaste ? WASTE_COLS : LAB_COLS;
    const labHdr = (
      <div
        className="ri-sec-hdr"
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2A3140', borderLeft: '3px solid #0d9488', borderRadius: sec.collapsed ? 6 : '6px 6px 0 0', padding: '9px 10px', cursor: 'pointer', minHeight: 44 }}
        onClick={() => toggleLaborSec(sec.id)}
      >
        <span style={{ color: '#0d9488', fontSize: 10, flexShrink: 0 }}>{sec.collapsed ? '▶' : '▼'}</span>
        <input
          value={sec.title}
          onChange={e => { e.stopPropagation(); updLaborSecTitle(sec.id, e.target.value); }}
          onClick={e => e.stopPropagation()}
          style={{ flex: 1, background: 'transparent', border: 'none', color: '#D1D5DB', fontSize: 11, fontWeight: 700, outline: 'none', fontFamily: "'Inter', -apple-system, sans-serif", textTransform: 'uppercase', letterSpacing: '0.6px', cursor: 'text' }}
        />
        {!sec.isWaste && seeRates && (
          <span style={{ fontFamily: "'Courier New', monospace", fontSize: 13, fontWeight: 700, color: secTot > 0 ? '#0d9488' : 'rgba(255,255,255,0.12)', flexShrink: 0 }}>
            {secTot > 0 ? fmtC(secTot) : '—'}
          </span>
        )}
      </div>
    );
    if (isMobile) {
      return (
        <div key={sec.id} style={{ marginBottom: 4 }}>
          {labHdr}
          {!sec.collapsed && (
            <div style={{ background: '#2A3140', border: '1px solid #1a2035', borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
              {sec.items.map(item => {
                const tot = sec.isWaste ? 0 : laborItemTot(item);
                const filled = !sec.isWaste && tot > 0;
                return (
                  <div key={item.id} style={{ padding: '8px 10px', borderBottom: '1px solid #0a0e18', background: filled ? 'rgba(99,102,241,0.04)' : 'transparent', borderLeft: filled ? '3px solid rgba(99,102,241,0.4)' : '3px solid transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: sec.isWaste ? 7 : 7 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#D1D5DB', fontSize: 14, fontWeight: 600, fontFamily: "'Inter', -apple-system, sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '4px 0' }}>{item.label || '—'}</div>
                        {item.note && <div style={{ fontSize: 10, color: '#D1D5DB', fontStyle: 'italic' }}>{item.note}</div>}
                      </div>
                      <button onClick={() => removeLaborItem(sec.id, item.id)} className="ri-del" style={{ background: 'transparent', border: 'none', color: '#D1D5DB', cursor: 'pointer', fontSize: 20, padding: '0 4px', minHeight: 44, minWidth: 36, flexShrink: 0 }}>×</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: sec.isWaste ? '52px 1fr' : (seeRates ? '52px 1fr 1fr 1fr' : '52px 1fr'), gap: 8, alignItems: 'end' }}>
                      <div>
                        <div style={{ ...CHEAD, marginBottom: 4 }}>Unit</div>
                        <input list="cp_unit_dl" value={item.unit} onChange={e => updLabor(sec.id, item.id, 'unit', e.target.value)} style={{ ...UINP, minHeight: 44 }} />
                      </div>
                      <div>
                        <div style={{ ...CHEAD, marginBottom: 4 }}>Qty</div>
                        <input type="text" inputMode="decimal" value={item.qty} onChange={e => updLabor(sec.id, item.id, 'qty', e.target.value)} placeholder="0" style={{ ...NINP, minHeight: 44, fontSize: 15 }} />
                      </div>
                      {!sec.isWaste && seeRates && (
                        <>
                          <div>
                            <div style={{ ...CHEAD, marginBottom: 4 }}>Rate</div>
                            <input type="text" inputMode="decimal" value={item.rate} onChange={e => updLabor(sec.id, item.id, 'rate', e.target.value)} placeholder="0.00" style={{ ...NINP, minHeight: 44, fontSize: 15 }} />
                          </div>
                          <div>
                            <div style={{ ...CHEAD, marginBottom: 4 }}>Total</div>
                            <div style={{ minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontFamily: "'Courier New', monospace", fontSize: 14, fontWeight: 700, color: filled ? '#6366f1' : 'rgba(255,255,255,0.12)' }}>{filled ? fmtC(tot) : '—'}</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {!sec.isWaste && (
                <div style={{ display: 'flex', alignItems: 'center', borderTop: '1px dashed rgba(255,255,255,0.08)' }}>
                  <button onClick={() => addLaborItem(sec.id)} style={{ flex: 1, padding: '10px', background: 'transparent', border: 'none', color: '#D1D5DB', cursor: 'pointer', fontSize: 13, textAlign: 'left', minHeight: 44, WebkitTapHighlightColor: 'transparent' }}>+ Add Item</button>
                  {seeRates && secTot > 0 && <div style={{ padding: '6px 10px', fontSize: 12, color: '#D1D5DB', fontFamily: "'Courier New', monospace", whiteSpace: 'nowrap' }}>Sub: <strong style={{ color: '#6366f1' }}>{fmtC(secTot)}</strong></div>}
                </div>
              )}
              {sec.isWaste && (
                <div style={{ padding: '8px 10px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: '#D1D5DB' }}>Net Waste</span>
                  <span style={{ fontFamily: "'Courier New', monospace", fontSize: 14, fontWeight: 700, color: netWaste > 0 ? '#f59e0b' : '#D1D5DB' }}>{netWaste.toFixed(1)} BDL</span>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    return (
      <div key={sec.id} style={{ marginBottom: 4 }}>
        {labHdr}
        {!sec.collapsed && (
          <div style={{ background: '#2A3140', border: '1px solid #1a2035', borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 4, padding: '4px 8px', background: '#1E2329', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ ...CHEAD }}>Item</div>
              <div style={{ ...CHEAD, textAlign: 'center' }}>Unit</div>
              <div style={{ ...CHEAD, textAlign: 'right' }}>Qty</div>
              {!sec.isWaste && seeRates && <div style={{ ...CHEAD, textAlign: 'right' }}>Rate</div>}
              {!sec.isWaste && seeRates && <div style={{ ...CHEAD, textAlign: 'right' }}>Total</div>}
              <div />
            </div>
            {sec.items.map(item => {
              const tot = sec.isWaste ? 0 : laborItemTot(item);
              const filled = !sec.isWaste && tot > 0;
              return (
                <div key={item.id} style={{ display: 'grid', gridTemplateColumns: cols, gap: 4, alignItems: 'center', padding: '3px 8px', borderBottom: '1px solid #0a0e18', background: filled ? 'rgba(99,102,241,0.04)' : 'transparent', borderLeft: filled ? '2px solid rgba(99,102,241,0.35)' : '2px solid transparent' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: '#D1D5DB', fontSize: 13, fontWeight: 500, fontFamily: "'Inter', -apple-system, sans-serif", padding: '4px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label || <span style={{ color: '#9CA3AF' }}>—</span>}</div>
                    {item.note && <div style={{ fontSize: 10, color: '#D1D5DB', fontStyle: 'italic' }}>{item.note}</div>}
                  </div>
                  <input list="cp_unit_dl" value={item.unit} onChange={e => updLabor(sec.id, item.id, 'unit', e.target.value)} style={{ ...UINP }} />
                  <input type="text" inputMode="decimal" value={item.qty} onChange={e => updLabor(sec.id, item.id, 'qty', e.target.value)} placeholder="0" style={{ ...NINP }} />
                  {!sec.isWaste && seeRates && (
                    <>
                      <input type="text" inputMode="decimal" value={item.rate} onChange={e => updLabor(sec.id, item.id, 'rate', e.target.value)} placeholder="0.00" style={{ ...NINP }} />
                      <div style={{ fontFamily: "'Courier New', monospace", fontSize: 13, fontWeight: 700, textAlign: 'right', color: filled ? '#1E2329' : 'rgba(255,255,255,0.12)' }}>{filled ? fmtC(tot) : '—'}</div>
                    </>
                  )}
                  <button onClick={() => removeLaborItem(sec.id, item.id)} className="ri-del" style={{ background: 'transparent', border: 'none', color: '#D1D5DB', cursor: 'pointer', fontSize: 15, padding: 0, lineHeight: 1 }}>×</button>
                </div>
              );
            })}
            {!sec.isWaste && (
              <div style={{ display: 'flex', alignItems: 'center', borderTop: '1px dashed rgba(255,255,255,0.08)' }}>
                <button onClick={() => addLaborItem(sec.id)} style={{ flex: 1, padding: '6px 8px', background: 'transparent', border: 'none', color: '#D1D5DB', cursor: 'pointer', fontSize: 12, textAlign: 'left', WebkitTapHighlightColor: 'transparent' }}>+ Add Item</button>
                {seeRates && secTot > 0 && <div style={{ padding: '6px 10px', fontSize: 12, color: '#D1D5DB', fontFamily: "'Courier New', monospace", whiteSpace: 'nowrap' }}>Subtotal: <strong style={{ color: '#6366f1' }}>{fmtC(secTot)}</strong></div>}
              </div>
            )}
            {sec.isWaste && (
              <div style={{ padding: '6px 8px 8px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#D1D5DB' }}>Net Waste</span>
                <span style={{ fontFamily: "'Courier New', monospace", fontSize: 13, fontWeight: 700, color: netWaste > 0 ? '#f59e0b' : '#D1D5DB' }}>{netWaste.toFixed(1)} BDL</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <datalist id="cp_unit_dl">
        {CP_UNITS.map(u => <option key={u} value={u} />)}
      </datalist>

      {/* ── Template bar ── */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', flexShrink: 0 }}>Template:</span>
        <select
          value={data.jobInfo.templateName || ''}
          onChange={e => {
            const name = e.target.value;
            if (!name) return;
            const hasData = data.materials.sections.some(sec => sec.items.some(item => item.qty || item.costPerUnit));
            if (hasData) { setPendingTpl(name); } else { applyTpl(name); }
          }}
          style={{ flex: 1, minWidth: 140, padding: '6px 8px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 13, outline: 'none', transition: 'border-color 0.15s' }}
        >
          <option value="">Select template…</option>
          <optgroup label="Built-in">
            {Object.keys(CP_TRADE_TEMPLATES).map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </optgroup>
          {jobTpls.length > 0 && (
            <optgroup label="Custom">
              {jobTpls.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
            </optgroup>
          )}
        </select>
        <button
          onClick={() => setShowTplSave(s => !s)}
          className="ri-btn ri-btn-secondary"
          style={{ padding: '6px 10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', WebkitTapHighlightColor: 'transparent' }}
        >
          Save as Template
        </button>
      </div>
      {showTplSave && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input value={tplName} onChange={e => setTplName(e.target.value)} placeholder="Template name" style={{ flex: 1, padding: '7px 10px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 13, outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.15s' }} />
          <button onClick={saveJobTpl} style={{ padding: '7px 14px', background: '#E8722A', border: 'none', borderRadius: 6, color: '#fff', fontWeight: 700, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>Save</button>
        </div>
      )}
      {pendingTpl && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 7, padding: '9px 12px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#fca5a5', flex: 1 }}>Replace all line items with the <strong>{pendingTpl}</strong> template?</span>
          <button onClick={() => applyTpl(pendingTpl)} style={{ padding: '5px 12px', background: '#ef4444', border: 'none', borderRadius: 6, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13, WebkitTapHighlightColor: 'transparent' }}>Yes, Replace</button>
          <button onClick={() => setPendingTpl(null)} style={{ padding: '5px 10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', cursor: 'pointer', fontSize: 13, WebkitTapHighlightColor: 'transparent' }}>Cancel</button>
        </div>
      )}

      {/* Sub-tab navigation — sticky within scroll container */}
      <div style={{ position: 'sticky', top: -20, zIndex: 20, display: 'flex', background: '#2A3140', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 16, marginLeft: -28, marginRight: -28, paddingLeft: 28, paddingRight: 28, paddingTop: 8, paddingBottom: 0, overflowX: 'auto', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
        {SUB_TABS.map(({ key, label }) => {
          const cmColor = CM_SUB_COLORS[key] || '#14b8a6';
          const isActive = subTab === key;
          return (
          <button
            key={key}
            className={isActive ? '' : 'ri-modal-tab'}
            onClick={() => setSubTab(key)}
            style={{
              padding: '9px 14px', border: 'none',
              borderBottom: `2px solid ${isActive ? cmColor : 'transparent'}`,
              background: isActive ? cmColor + '18' : 'transparent',
              borderRadius: isActive ? '6px 6px 0 0' : 0,
              color: isActive ? cmColor : '#D1D5DB',
              cursor: 'pointer', fontSize: 13, fontWeight: isActive ? 700 : 600,
              marginBottom: -2, whiteSpace: 'nowrap', WebkitTapHighlightColor: 'transparent',
            }}
          >
            {label}
          </button>
          );
        })}
      </div>

      {/* ── Materials Tab ── */}
      {subTab === 'materials' && (
        <div style={{ paddingBottom: 52 }}>
          {/* Sections */}
          {data.materials.sections.map(sec => renderMatSection(sec))}
          <button
            onClick={addMatSec}
            className="ri-add-item"
            style={{ width: '100%', padding: '10px', marginTop: 8, background: 'transparent', border: '1px dashed rgba(255,255,255,0.12)', borderRadius: 7, color: '#D1D5DB', cursor: 'pointer', fontSize: 12, WebkitTapHighlightColor: 'transparent', transition: 'color 0.15s, border-color 0.15s' }}
          >
            + Add Section
          </button>
          {/* Materials Grand Total — sticky */}
          {seeRates && (
          <div style={{ position: 'sticky', bottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 14px', marginTop: 8, background: '#2A3140', borderTop: '2px solid rgba(249,115,22,0.4)', boxShadow: '0 -6px 20px rgba(0,0,0,0.1)' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#E8722A', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Materials Total</span>
            <span style={{ fontFamily: "'Courier New', monospace", fontSize: 22, fontWeight: 700, color: '#14b8a6' }}>{fmtC(matTotal)}</span>
          </div>
          )}
        </div>
      )}

      {/* ── Labor Tab ── */}
      {subTab === 'labor' && (
        <div>
          {/* Crew info */}
          <div style={{ background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Crew Assigned</div>
            <input value={data.jobInfo.subContractorName} onChange={e => updInfo('subContractorName', e.target.value)} style={{ width: '100%', padding: '8px 10px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s' }} />
          </div>
          {/* Sections */}
          {data.labor.sections.map(sec => renderLaborSection(sec))}
          <button
            onClick={addLaborSec}
            className="ri-add-item"
            style={{ width: '100%', padding: '10px', marginTop: 8, background: 'transparent', border: '1px dashed rgba(255,255,255,0.12)', borderRadius: 7, color: '#D1D5DB', cursor: 'pointer', fontSize: 12, WebkitTapHighlightColor: 'transparent', transition: 'color 0.15s, border-color 0.15s' }}
          >
            + Add Section
          </button>
          {/* Labor Grand Total — sticky */}
          {seeRates && (
          <div style={{ position: 'sticky', bottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 14px', marginTop: 8, background: '#2A3140', borderTop: '2px solid rgba(99,102,241,0.4)', boxShadow: '0 -6px 20px rgba(0,0,0,0.1)' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Labor Total</span>
            <span style={{ fontFamily: "'Courier New', monospace", fontSize: 22, fontWeight: 700, color: '#0d9488' }}>{fmtC(laborTotal)}</span>
          </div>
          )}
        </div>
      )}

      {/* ── Summary Tab ── */}
      {subTab === 'summary' && (
        <div>
          {/* Job Cost Breakdown */}
          <div style={{ background: '#2A3140', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Job Cost Breakdown</div>
            {[
              { label: 'Materials', val: matTotal, color: '#14b8a6' },
              { label: 'Labor', val: laborTotal, color: '#0d9488' },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#D1D5DB' }}>{label}</span>
                <span style={{ fontFamily: "'Courier New', monospace", fontSize: 14, color }}>{fmtC(val)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid rgba(255,255,255,0.08)', marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#D1D5DB' }}>Subtotal</span>
              <span style={{ fontFamily: "'Courier New', monospace", fontSize: 15, fontWeight: 700, color: '#FFFFFF' }}>{fmtC(subtotal)}</span>
            </div>
            {/* Overhead & Tax */}
            {[
              { label: 'Overhead (%)', field: 'overhead', pct: overheadPct, amt: overheadAmt },
              { label: 'Tax (%)', field: 'tax', pct: taxPct, amt: taxAmt },
            ].map(({ label, field, pct, amt }) => (
              <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#D1D5DB', flex: 1 }}>{label}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={data.jobInfo[field]}
                  onChange={e => updInfo(field, e.target.value)}
                  placeholder="0"
                  style={{ width: 60, padding: '6px 8px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 13, fontFamily: "'Courier New', monospace", textAlign: 'right', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                />
                <span style={{ fontSize: 11, color: '#D1D5DB', width: 14 }}>%</span>
                <span style={{ fontFamily: "'Courier New', monospace", fontSize: 13, color: '#D1D5DB', width: 80, textAlign: 'right' }}>{fmtC(amt)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', marginTop: 8, background: '#2A3140', borderRadius: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#FFFFFF' }}>Total Job Cost</span>
              <span style={{ fontFamily: "'Courier New', monospace", fontSize: 20, fontWeight: 700, color: '#FFFFFF' }}>{fmtC(totalJobCost)}</span>
            </div>
          </div>

          {/* Profitability */}
          <div style={{ background: '#2A3140', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Job Profitability</div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Contract / Sale Price</div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#D1D5DB', fontSize: 15 }}>$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={data.jobInfo.contractPrice}
                  onChange={e => updInfo('contractPrice', e.target.value)}
                  placeholder="0.00"
                  style={{ width: '100%', padding: '10px 10px 10px 24px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, color: '#D1D5DB', fontSize: 18, fontFamily: "'Courier New', monospace", textAlign: 'right', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                />
              </div>
            </div>
            {[
              { label: 'Total Cost', val: totalJobCost, color: '#D1D5DB' },
              { label: 'Gross Profit', val: grossProfit, color: profitColor },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#D1D5DB' }}>{label}</span>
                <span style={{ fontFamily: "'Courier New', monospace", fontSize: 15, fontWeight: 700, color }}>{fmtC(val)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: `${profitColor}14`, border: `1px solid ${profitColor}44`, borderRadius: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: profitColor }}>Profit Margin</span>
              <span style={{ fontFamily: "'Courier New', monospace", fontSize: 22, fontWeight: 700, color: profitColor }}>{profitMargin.toFixed(1)}%</span>
            </div>
          </div>

          {/* Per-Square Metrics */}
          {totalSq > 0 && (
            <div style={{ background: '#2A3140', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Per-Square Metrics</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: '#D1D5DB' }}>Total Squares</span>
                <span style={{ fontFamily: "'Courier New', monospace", fontSize: 14, color: '#D1D5DB', fontWeight: 700 }}>{totalSq.toFixed(1)} SQ</span>
              </div>
              {[
                { label: 'Cost / SQ', val: costPerSq },
                { label: 'Revenue / SQ', val: revenuePerSq },
                { label: 'Profit / SQ', val: profitPerSq },
              ].map(({ label, val }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: '#D1D5DB' }}>{label}</span>
                  <span style={{ fontFamily: "'Courier New', monospace", fontSize: 14, color: '#D1D5DB' }}>{fmtC(val)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Crew Pay */}
          <div style={{ background: '#2A3140', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Crew Pay</div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Crew Assigned</div>
              <input value={data.jobInfo.subContractorName} onChange={e => updInfo('subContractorName', e.target.value)} style={{ width: '100%', padding: '9px 10px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#6366f1' }}>Total Labor Pay</span>
              <span style={{ fontFamily: "'Courier New', monospace", fontSize: 18, fontWeight: 700, color: '#0d9488' }}>{fmtC(laborTotal)}</span>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Notes / Pay Adjustments</div>
              <textarea value={data.jobInfo.notes} onChange={e => updInfo('notes', e.target.value)} rows={3} placeholder="Any adjustments or notes…" style={{ width: '100%', padding: '9px 10px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 13, outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s' }} />
            </div>
          </div>
        </div>
      )}

      {/* ── Sign Off Tab ── */}
      {subTab === 'signoff' && (
        <div>
          {data.signOff.submitted ? (
            <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, padding: '24px 18px', textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#22c55e', marginBottom: 4 }}>Submitted & Locked</div>
              <div style={{ fontSize: 12, color: '#D1D5DB' }}>This form has been submitted and locked.</div>
            </div>
          ) : (
            <div style={{ background: '#2A3140', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>Inspection Pass?</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {['yes', 'no'].map(v => (
                    <button
                      key={v}
                      onClick={() => updSignOff('inspectionPass', v)}
                      style={{ flex: 1, padding: '13px', background: data.signOff.inspectionPass === v ? (v === 'yes' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)') : 'transparent', border: `1px solid ${data.signOff.inspectionPass === v ? (v === 'yes' ? '#22c55e' : '#ef4444') : 'rgba(255,255,255,0.08)'}`, borderRadius: 8, color: data.signOff.inspectionPass === v ? (v === 'yes' ? '#22c55e' : '#ef4444') : '#D1D5DB', fontWeight: 700, fontSize: 16, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                    >
                      {v === 'yes' ? 'Yes ✓' : 'No ✗'}
                    </button>
                  ))}
                </div>
              </div>
              {[
                { field: 'subName', label: 'Crew Name', type: 'text' },
                { field: 'supervisor', label: 'Site Supervisor Name', type: 'text' },
                { field: 'dateCompleted', label: 'Date Completed', type: 'date' },
              ].map(({ field, label, type }) => (
                <div key={field} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>{label}</div>
                  <input type={type} value={data.signOff[field]} onChange={e => updSignOff(field, e.target.value)} style={{ width: '100%', padding: '11px 12px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, color: '#D1D5DB', fontSize: 15, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s' }} />
                </div>
              ))}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Notes / Comments</div>
                <textarea value={data.signOff.notes} onChange={e => updSignOff('notes', e.target.value)} rows={4} placeholder="Any notes or comments…" style={{ width: '100%', padding: '11px 12px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, color: '#D1D5DB', fontSize: 14, outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s' }} />
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            {!data.signOff.submitted && (
              <button
                onClick={() => updSignOff('submitted', true)}
                style={{ flex: 3, padding: '14px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
              >
                Submit & Lock
              </button>
            )}
            <button
              onClick={() => window.print()}
              className="ri-btn ri-btn-secondary"
              style={{ flex: 1, padding: '14px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, color: '#D1D5DB', fontWeight: 600, fontSize: 14, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
            >
              Print
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Job Modal ────────────────────────────────────────────────────────────────
function JobModal({ job, onClose, customChecklist, crew, assignments, onAssign, onUnassign, onAddCrew, currentUser, demoMessages, onComplete, onUpdateSteps, onUpdateSchedule, rolePerms, isDemo }) {
  useScrollLock();
  const steps = (job.taskList && job.taskList.length ? job.taskList.map((label, i) => ({ id: i + 1, label })) : null)
    || TRADE_CHECKLISTS[job.trade]
    || (customChecklist ? customChecklist.map((label, i) => ({ id: i + 1, label })) : null)
    || TRADE_CHECKLISTS['Full Replacement'];
  const tradeColor = getTradeColor(job.trade);

  const [checks, setChecks] = useState(() => {
    const init = {};
    steps.forEach(s => {
      init[s.id] = job.completedSteps.includes(s.id)
        ? { done: true, ts: '2026-03-' + String(10 + s.id).padStart(2, '0') + ' 09:00' }
        : { done: false, ts: null };
    });
    return init;
  });

  const [modalTab, setModalTab] = useState('checklist');
  const [msgCount, setMsgCount] = useState(0);
  const [photoCount, setPhotoCount] = useState(0);
  const [hasCostMgr, setHasCostMgr] = useState(() => {
    try { return !!localStorage.getItem(`cl_crewpay_${job.id}`); } catch (e) { return false; }
  });

  useEffect(() => {
    if (modalTab !== 'crewpay') {
      try { setHasCostMgr(!!localStorage.getItem(`cl_crewpay_${job.id}`)); } catch (e) { /* ignore */ }
    }
  }, [modalTab, job.id]);
  const [changeOrders, setChangeOrders] = useState(job.changeOrders || []);
  const [showCOPanel, setShowCOPanel] = useState(false);
  const [coDesc, setCODesc] = useState('');
  const [coAmount, setCOAmount] = useState('');
  const [coReason, setCOReason] = useState('');
  const [coPhase, setCOPhase] = useState('');
  const [coPhoto, setCOPhoto] = useState(null);
  const [coNotes, setCONotes] = useState('');
  const [undoCO, setUndoCO] = useState(null);
  const undoTimerRef = useRef(null);
  const coDescRef = useRef(null);
  const coPhotoRef = useRef(null);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [schedDate, setSchedDate] = useState(job.scheduledDate || '');
  const [schedDur, setSchedDur] = useState(jobDuration(job));
  const [localCurrentPhase, setLocalCurrentPhase] = useState(job.currentPhase || 1);
  const [localCompletedPhases, setLocalCompletedPhases] = useState(job.completedPhases || []);

  useEffect(() => {
    if (demoMessages) {
      setMsgCount(demoMessages.filter(m => m.type === 'user').length);
      return;
    }
    chatDB.getByJob(String(job.id)).then(msgs => setMsgCount(msgs.length)).catch(() => {});
  }, [job.id, demoMessages]);

  const toggle = (id) => {
    setChecks(prev => {
      const wasDone = prev[id].done;
      const next = {
        ...prev,
        [id]: {
          done: !wasDone,
          ts: wasDone ? null : new Date().toISOString().slice(0, 16).replace('T', ' '),
        },
      };
      if (onUpdateSteps) {
        const doneIds = Object.entries(next).filter(([, v]) => v.done).map(([k]) => Number(k));
        onUpdateSteps(job.id, doneIds);
      }
      return next;
    });
  };

  const isMobile = useMobile();
  const doneCount = Object.values(checks).filter(c => c.done).length;
  const total = steps.length;
  const pct = Math.round(doneCount / total * 100);
  const statusColor = pct === 100 ? '#22c55e' : pct > 0 ? '#E8722A' : '#6366f1';

  // Full-screen overlay + flex-column modal: header is pinned, only content scrolls
  const jobOverlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
    zIndex: 1000, padding: isMobile ? 0 : 20,
  };
  const jobModal = {
    background: '#2A3140', border: isMobile ? 'none' : '1px solid rgba(255,255,255,0.08)',
    borderRadius: isMobile ? '16px 16px 0 0' : 14,
    width: '100%', maxWidth: isMobile ? '100vw' : 600,
    height: isMobile ? '96dvh' : '90vh',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  };

  return (
    <div style={jobOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={jobModal} onClick={e => e.stopPropagation()}>

        {/* ── Pinned header: never scrolls ── */}
        <div style={{ flexShrink: 0, padding: isMobile ? '16px 14px 0' : '24px 28px 0', position: 'relative' }}>
          <button className="ri-close-btn" style={{ ...S.closeBtn, zIndex: 12 }} onClick={onClose}>×</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={S.modalTitle}>{job.customer}</div>
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
              background: statusColor + '22', color: statusColor,
              textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              {pct === 100 ? 'Complete' : pct > 0 ? 'In Progress' : 'Scheduled'}
            </span>
          </div>
          <div style={S.modalSub}>
            {job.address}{rolePerms?.seeDollars !== false ? ` · ${fmt(job.value)}` : ''}
          </div>
          <div style={{ marginBottom: 12 }}>
            <span style={{
              display: 'inline-block', fontSize: 11, fontWeight: 600,
              padding: '3px 10px', borderRadius: 10,
              background: tradeColor + '22', color: tradeColor,
              letterSpacing: '0.3px',
            }}>
              {job.trade}
            </span>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: '#D1D5DB' }}>Progress</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: statusColor }}>{doneCount}/{total} steps — {pct}%</span>
            </div>
            <div style={S.progressTrack}>
              <div style={S.progressFill(pct, statusColor)} />
            </div>
          </div>

          {/* ── Phase Stepper ── */}
          {(() => {
            const phases = getDefaultPhases(job.jobType);
            const cp = localCurrentPhase;
            const done = localCompletedPhases;
            const allDone = done.length >= phases.length;
            return (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    {job.jobType || 'Residential'} Phases
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#2D5016' }}>
                    {allDone ? 'All Phases Complete' : `Phase ${cp} of ${phases.length}`}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  {phases.map((phase, i) => {
                    const isComplete = done.includes(phase.id);
                    const isActive = phase.id === cp && !isComplete;
                    return (
                      <div key={phase.id} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <div
                          title={phase.label}
                          style={{
                            width: 36, height: 36, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                            background: isComplete ? '#2D5016' : isActive ? 'rgba(45,80,22,0.15)' : 'rgba(255,255,255,0.06)',
                            border: isActive ? '2px solid #2D5016' : '2px solid transparent',
                            color: isComplete ? '#fff' : isActive ? '#2D5016' : '#D1D5DB',
                            transition: 'all 0.15s',
                            padding: 4,
                          }}
                        >
                          {isComplete ? '✓' : phase.id}
                        </div>
                        {i < phases.length - 1 && (
                          <div style={{
                            flex: 1, height: 2, minWidth: 8,
                            background: done.includes(phases[i + 1]?.id) || phases[i + 1]?.id === cp ? '#2D5016' : 'rgba(255,255,255,0.08)',
                          }} />
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                  <div style={{ fontSize: 11, color: '#D1D5DB' }}>
                    Current: <span style={{ color: '#C9D1D9', fontWeight: 600 }}>{phases.find(p => p.id === cp)?.label || 'N/A'}</span>
                  </div>
                  {!allDone && (
                    <button
                      onClick={() => {
                        const newCompleted = [...done, cp];
                        const nextPhase = cp < phases.length ? cp + 1 : cp;
                        setLocalCompletedPhases(newCompleted);
                        setLocalCurrentPhase(nextPhase);
                      }}
                      style={{
                        padding: '8px 14px', fontSize: 12, fontWeight: 700,
                        background: 'linear-gradient(135deg, #2D5016, #3d6b1e)',
                        border: 'none', borderRadius: 8, color: '#fff',
                        cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 44,
                      }}
                    >
                      Mark Phase Complete
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Modal tab bar */}
          <div style={{ display: 'flex', background: '#2A3140', borderBottom: '2px solid rgba(255,255,255,0.08)', borderRadius: '8px 8px 0 0', marginBottom: 0, overflowX: 'auto', padding: '4px 4px 0' }}>
          {[
            { key: 'checklist', label: 'Checklist' },
            { key: 'crew', label: 'Crew' },
            { key: 'photos', label: photoCount > 0 ? `Photos (${photoCount})` : 'Photos' },
            { key: 'chat', label: msgCount > 0 ? `Chat (${msgCount})` : 'Chat' },
            ...(rolePerms?.seeCostManager !== false ? [{ key: 'crewpay', label: hasCostMgr ? 'Cost Manager ●' : 'Cost Manager' }] : []),
          ].map(({ key, label }) => {
            const accent = SECTION_COLORS[key] || '#E8722A';
            const isActive = modalTab === key;
            return (
            <button
              key={key}
              className={isActive ? '' : 'ri-modal-tab'}
              onClick={() => setModalTab(key)}
              style={{
                padding: '9px 14px', border: 'none',
                borderBottom: `2px solid ${isActive ? accent : 'transparent'}`,
                background: isActive ? accent + '18' : 'transparent',
                borderRadius: isActive ? '6px 6px 0 0' : 0,
                color: isActive ? accent : '#D1D5DB',
                cursor: 'pointer', fontSize: 13, fontWeight: isActive ? 700 : 600,
                marginBottom: -2, whiteSpace: 'nowrap', flexShrink: 0,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {label}
            </button>
            );
          })}
          </div>
        </div>
        {/* ── End pinned header ── */}

        {/* ── Scrollable content area ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 14px 24px' : '20px 28px 28px' }}>

        {modalTab === 'checklist' && (
          <>
            <div style={S.sectionLabel}>Job Notes</div>
            <div style={{
              fontSize: 13, color: '#D1D5DB', marginBottom: 20,
              padding: '10px 12px', background: '#2A3140', borderRadius: 6,
            }}>
              {job.notes}
            </div>

            <div style={S.sectionLabel}>{job.trade} Job Checklist</div>
            <div>
              {steps.map((step) => {
                const c = checks[step.id];
                return (
                  <div key={step.id} style={S.checklistItem(c.done)} onClick={() => toggle(step.id)}>
                    <div style={S.checkbox(c.done)}>
                      {c.done && (
                        <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                          <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <span style={S.checkLabel(c.done)}>
                      <span style={{ color: '#D1D5DB', marginRight: 6, fontSize: 11 }}>{step.id}.</span>
                      {step.label}
                    </span>
                    {c.ts && <span style={S.checkTs}>{c.ts}</span>}
                  </div>
                );
              })}
            </div>

            {/* Schedule / reschedule row */}
            <div style={{ marginTop: 16, background: '#2A3140', borderRadius: 7, padding: '10px 12px' }}>
              {editingSchedule ? (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Schedule</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                      type="date"
                      value={schedDate}
                      onChange={e => setSchedDate(e.target.value)}
                      style={{ ...FI, fontSize: 13, padding: '7px 10px', flex: 1, minWidth: 130 }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <span style={{ fontSize: 12, color: '#D1D5DB' }}>Duration</span>
                      <input
                        type="number"
                        min="1"
                        max="60"
                        value={schedDur}
                        onChange={e => setSchedDur(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{ ...FI, fontSize: 13, padding: '7px 8px', width: 56 }}
                      />
                      <span style={{ fontSize: 12, color: '#D1D5DB' }}>days</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button
                      className="ri-btn ri-btn-secondary"
                      style={{ flex: 1, padding: '8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', cursor: 'pointer', fontSize: 12 }}
                      onClick={() => setEditingSchedule(false)}
                    >
                      Cancel
                    </button>
                    <button
                      style={{ flex: 2, padding: '8px', background: 'linear-gradient(135deg, #E8722A, #ea580c)', border: 'none', borderRadius: 6, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                      onClick={() => {
                        if (onUpdateSchedule) onUpdateSchedule(job.id, schedDate, schedDur);
                        setEditingSchedule(false);
                      }}
                    >
                      Save Schedule
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, color: '#D1D5DB' }}>📅</span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 12, color: '#D1D5DB', fontWeight: 500 }}>
                      {job.scheduledDate || 'Not scheduled'}
                    </span>
                    {job.scheduledDate && (
                      <span style={{ fontSize: 11, color: '#D1D5DB', marginLeft: 8 }}>
                        → {jobEndDate(job)} · {jobDuration(job)}d
                      </span>
                    )}
                  </div>
                  {onUpdateSchedule && (
                    <button
                      className="ri-btn ri-btn-secondary"
                      style={{ padding: '4px 10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}
                      onClick={() => setEditingSchedule(true)}
                    >
                      {job.scheduledDate ? 'Reschedule' : 'Schedule'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Mark Job Complete */}
            {onComplete && job.status !== 'Complete' && (
              <div style={{ marginTop: 20 }}>
                {pct === 100 ? (
                  <button
                    style={{ width: '100%', padding: '12px 16px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                    onClick={() => onComplete(job.id)}
                  >
                    ✓ Mark Job Complete
                  </button>
                ) : confirmComplete ? (
                  <div style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.25)', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 13, color: '#E8722A', marginBottom: 10 }}>Not all checklist items are done. Complete anyway?</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="ri-btn ri-btn-secondary" style={{ flex: 1, padding: '8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, color: '#D1D5DB', cursor: 'pointer', fontSize: 13 }} onClick={() => setConfirmComplete(false)}>Cancel</button>
                      <button style={{ flex: 2, padding: '8px', background: '#22c55e', border: 'none', borderRadius: 7, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }} onClick={() => onComplete(job.id)}>Complete Anyway</button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="ri-btn ri-btn-secondary"
                    style={{ width: '100%', padding: '10px 16px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#D1D5DB', fontWeight: 600, fontSize: 13, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                    onClick={() => setConfirmComplete(true)}
                  >
                    Mark as Complete
                  </button>
                )}
              </div>
            )}

            {/* ── Change Orders ── */}
            <div style={{ marginTop: 24, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={S.sectionLabel}>Change Orders</div>
                <button onClick={() => { setShowCOPanel(true); setTimeout(() => coDescRef.current?.focus(), 50); }} style={{ padding: '5px 12px', background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 6, color: '#E8722A', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  + Add Change Order
                </button>
              </div>

              {/* Existing change orders */}
              {changeOrders.length === 0 && !showCOPanel && (
                <div style={{ fontSize: 12, color: '#D1D5DB', fontStyle: 'italic', marginBottom: 8 }}>No change orders on this job.</div>
              )}
              {changeOrders.map(co => {
                const stColor = co.status === 'Approved' ? '#22c55e' : co.status === 'Declined' ? '#ef4444' : '#f59e0b';
                return (
                  <div key={co.id} style={{ background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#FFFFFF' }}>{co.title}</div>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: stColor + '18', color: stColor }}>{co.status}</span>
                    </div>
                    {co.description && <div style={{ fontSize: 11, color: '#D1D5DB', marginBottom: 6 }}>{co.description}</div>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: '#22c55e' }}>+{fmt(co.amount)}</span>
                      <span style={{ color: '#D1D5DB' }}>{co.date}</span>
                      {co.phase && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: 'rgba(45,80,22,0.15)', color: '#2D5016' }}>
                          Phase {co.phase}
                        </span>
                      )}
                      {co.status === 'Pending' && (
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                          <button onClick={() => setChangeOrders(prev => prev.map(c => c.id === co.id ? { ...c, status: 'Approved' } : c))} style={{ padding: '3px 10px', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: '#22c55e', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Approve</button>
                          <button onClick={() => setChangeOrders(prev => prev.map(c => c.id === co.id ? { ...c, status: 'Declined' } : c))} style={{ padding: '3px 10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Decline</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Change order total */}
              {changeOrders.filter(c => c.status === 'Approved').length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB' }}>Change Order Total (Approved)</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#22c55e' }}>+{fmt(changeOrders.filter(c => c.status === 'Approved').reduce((s, c) => s + c.amount, 0))}</span>
                </div>
              )}

              {/* Change order slide-in panel is rendered at the modal level below */}
            </div>
          </>
        )}

        {modalTab === 'photos' && (
          <JobPhotosPanel
            lead={{ id: job.id, name: job.customer, stage: 'in_progress', trade: job.trade }}
            onCountChange={setPhotoCount}
            phases={getDefaultPhases(job.jobType)}
          />
        )}

        {modalTab === 'crew' && (
          <JobCrewSection
            job={job}
            crew={crew || []}
            assignments={assignments || {}}
            onAssign={onAssign}
            onUnassign={onUnassign}
            onAddCrew={onAddCrew}
          />
        )}

        {modalTab === 'chat' && (
          <JobChatPanel jobId={job.id} currentUser={currentUser} demoMessages={demoMessages} onCountChange={setMsgCount} />
        )}

        {modalTab === 'crewpay' && (
          <CostManagerPanel job={job} crew={crew || []} assignments={assignments || {}} rolePerms={rolePerms} isDemo={isDemo} />
        )}
        </div>{/* end scrollable content */}

        {/* ── Slide-in Change Order Panel ── */}
        {showCOPanel && (
          <div
            style={{
              position: 'absolute', top: 0, right: 0, bottom: 0,
              width: isMobile ? '100%' : 320,
              background: '#1E2329', borderLeft: isMobile ? 'none' : '1px solid rgba(255,255,255,0.1)',
              display: 'flex', flexDirection: 'column',
              zIndex: 20, boxShadow: '-4px 0 24px rgba(0,0,0,0.4)',
              animation: 'coSlideIn 0.15s ease-out',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setCODesc(''); setCOAmount(''); setCOReason(''); setCOPhase(''); setCOPhoto(null); setCONotes(''); setShowCOPanel(false); }
              if (e.key === 'Enter' && !e.shiftKey && coDesc.trim() && coAmount) {
                e.preventDefault();
                document.getElementById('co-save-btn')?.click();
              }
            }}
          >
            {/* Panel header */}
            <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#FFFFFF' }}>New Change Order</div>
                <button
                  onClick={() => { setCODesc(''); setCOAmount(''); setCOReason(''); setCOPhase(''); setCOPhoto(null); setCONotes(''); setShowCOPanel(false); }}
                  style={{ background: 'transparent', border: 'none', color: '#D1D5DB', fontSize: 22, cursor: 'pointer', padding: '8px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#D1D5DB', marginTop: 2 }}>{job.customer} · #{job.id}</div>
            </div>

            {/* Panel form */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Description */}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Description *</label>
                <input
                  ref={coDescRef}
                  value={coDesc}
                  onChange={e => setCODesc(e.target.value)}
                  placeholder="e.g. Additional decking repair"
                  style={{ ...FI, padding: '14px 12px', minHeight: 44, fontSize: 15 }}
                />
              </div>

              {/* Amount */}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Amount *</label>
                <input
                  value={coAmount}
                  onChange={e => setCOAmount(e.target.value)}
                  placeholder="0.00"
                  type="number"
                  min="0"
                  step="0.01"
                  style={{ ...FI, fontSize: 22, fontWeight: 700, padding: '14px 12px', minHeight: 52, letterSpacing: '0.5px' }}
                />
              </div>

              {/* Reason */}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Reason</label>
                <select
                  value={coReason}
                  onChange={e => setCOReason(e.target.value)}
                  style={{ ...FI, color: coReason ? '#C9D1D9' : '#D1D5DB', padding: '14px 12px', minHeight: 44, fontSize: 15 }}
                >
                  <option value="">Select reason…</option>
                  <option value="Customer Request">Customer Request</option>
                  <option value="Scope Increase">Scope Increase</option>
                  <option value="Unforeseen Condition">Unforeseen Condition</option>
                  <option value="Weather">Weather</option>
                  <option value="Materials Change">Materials Change</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Phase */}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Phase</label>
                <select
                  value={coPhase}
                  onChange={e => setCOPhase(e.target.value)}
                  style={{ ...FI, color: coPhase ? '#C9D1D9' : '#D1D5DB', padding: '14px 12px', minHeight: 44, fontSize: 15 }}
                >
                  <option value="">Current phase ({getDefaultPhases(job.jobType).find(p => p.id === localCurrentPhase)?.label || '—'})</option>
                  {getDefaultPhases(job.jobType).map(p => (
                    <option key={p.id} value={p.id}>Phase {p.id}: {p.label}</option>
                  ))}
                </select>
              </div>

              {/* Photo */}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Photo (optional)</label>
                <input ref={coPhotoRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) setCOPhoto(file.name);
                  e.target.value = '';
                }} />
                <button
                  onClick={() => coPhotoRef.current?.click()}
                  style={{
                    width: '100%', padding: '14px 12px', minHeight: 44, background: '#2A3140',
                    border: '1px dashed rgba(255,255,255,0.15)', borderRadius: 8,
                    color: coPhoto ? '#C9D1D9' : '#D1D5DB', fontSize: 14, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <span style={{ fontSize: 16 }}>📷</span>
                  {coPhoto || 'Attach photo'}
                </button>
              </div>

              {/* Notes */}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Notes (optional)</label>
                <textarea
                  value={coNotes}
                  onChange={e => setCONotes(e.target.value)}
                  placeholder="Additional context…"
                  rows={2}
                  style={{ ...FI, resize: 'vertical', minHeight: 48 }}
                />
              </div>
            </div>

            {/* Panel footer */}
            <div style={{ padding: '14px 18px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                id="co-save-btn"
                disabled={!coDesc.trim() || !coAmount}
                onClick={() => {
                  const newCO = {
                    id: `co-${Date.now()}`,
                    title: coDesc.trim(),
                    description: coNotes.trim() || undefined,
                    amount: parseFloat(coAmount) || 0,
                    status: 'Pending',
                    date: TODAY,
                    reason: coReason || undefined,
                    phase: coPhase ? parseInt(coPhase, 10) : localCurrentPhase,
                    loggedBy: currentUser,
                    timestamp: new Date().toISOString(),
                    ...(coPhoto ? { photo: coPhoto } : {}),
                  };
                  setChangeOrders(prev => [newCO, ...prev]);
                  setUndoCO(newCO.id);
                  if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
                  undoTimerRef.current = setTimeout(() => setUndoCO(null), 5000);
                  setCODesc(''); setCOAmount(''); setCOReason(''); setCOPhase(''); setCOPhoto(null); setCONotes('');
                  setShowCOPanel(false);
                }}
                style={{
                  flex: 1, padding: '14px', border: 'none', borderRadius: 10,
                  background: coDesc.trim() && coAmount ? 'linear-gradient(135deg, #2D5016, #3d6b1e)' : 'rgba(255,255,255,0.08)',
                  color: coDesc.trim() && coAmount ? '#fff' : '#D1D5DB',
                  fontSize: 15, fontWeight: 700, cursor: coDesc.trim() && coAmount ? 'pointer' : 'not-allowed',
                }}
              >
                Save Change Order
              </button>
              <button
                onClick={() => { setCODesc(''); setCOAmount(''); setCOReason(''); setCOPhase(''); setCOPhoto(null); setCONotes(''); setShowCOPanel(false); }}
                style={{ background: 'transparent', border: 'none', color: '#D1D5DB', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '10px 14px', minHeight: 44 }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Undo Toast ── */}
        {undoCO && (
          <div style={{
            position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
            background: '#052e16', border: '1px solid rgba(34,197,94,0.5)',
            borderRadius: 10, padding: '10px 18px', zIndex: 30,
            display: 'flex', alignItems: 'center', gap: 12,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            animation: 'coSlideIn 0.15s ease-out',
          }}>
            <span style={{ fontSize: 13, color: '#C9D1D9' }}>✓ Change order added.</span>
            <button
              onClick={() => {
                setChangeOrders(prev => prev.filter(c => c.id !== undoCO));
                setUndoCO(null);
                if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
              }}
              style={{
                background: 'transparent', border: '1px solid rgba(34,197,94,0.4)',
                borderRadius: 6, color: '#22c55e', fontSize: 12, fontWeight: 700,
                padding: '4px 10px', cursor: 'pointer',
              }}
            >
              Undo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Jobs Tab ─────────────────────────────────────────────────────────────────
// ─── Projects Tab (project-centric view; scopes live inside) ─────────────────
function deriveProjectStatusFromJobs(scopes) {
  if (!scopes || scopes.length === 0) return 'Not Started';
  // Precedence (highest → lowest):
  // 1. Complete — only if EVERY scope is Complete
  // 2. In Progress — if ANY scope is In Progress
  // 3. Scheduled — if ANY scope is Scheduled (and none In Progress)
  // 4. Not Started — fallback
  if (scopes.every(s => s.status === 'Complete')) return 'Complete';
  if (scopes.some(s => s.status === 'In Progress')) return 'In Progress';
  if (scopes.some(s => s.status === 'Scheduled')) return 'Scheduled';
  return 'Not Started';
}

function ProjectsTab({ jobs, customChecklist, crew, assignments, onAssign, onUnassign, onAddCrew, currentUser, demoMessages, onComplete, onUpdateSteps, onUpdateSchedule, rolePerms, quotes, onSaveQuote, onAddScope, onUpdateScope }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [quoteEditProject, setQuoteEditProject] = useState(null);
  const [filter, setFilter] = useState('all');

  const statuses = ['all', 'Scheduled', 'In Progress', 'Complete'];

  // Group jobs (scopes) into projects. Prefer the canonical projectId
  // attached to each scope by the DEMO_JOBS adapter so the project.id we
  // expose matches DEMO_PROJECTS — that lets Quotes / future entities key
  // off the same id. For non-demo data (no projectId), fall back to a
  // customer+address composite key.
  const projects = useMemo(() => {
    const byKey = new Map();
    jobs.forEach(j => {
      const key = j.projectId || `${j.customer}|${j.address}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          id: key,
          customerName: j.customer || 'Unknown',
          address: j.address || '',
          scopes: [],
        });
      }
      byKey.get(key).scopes.push(j);
    });
    return Array.from(byKey.values()).map(p => ({
      ...p,
      totalValue: p.scopes.reduce((s, j) => s + (j.value || 0), 0),
      status: deriveProjectStatusFromJobs(p.scopes),
      nextScheduledDate: p.scopes
        .map(s => s.scheduledDate)
        .filter(Boolean)
        .sort()[0] || null,
    }));
  }, [jobs]);

  const filtered = useMemo(
    () => filter === 'all' ? projects : projects.filter(p => p.status === filter),
    [projects, filter]
  );

  const statusColor = (s) => ({ Scheduled: '#6366f1', 'In Progress': '#E8722A', Complete: '#22c55e' }[s] || '#8B95A1');

  return (
    <div>
      <div style={S.filterRow}>
        {statuses.map(s => (
          <button key={s} style={S.filterBtn(filter === s)} onClick={() => setFilter(s)}>
            {s === 'all'
              ? `All (${projects.length})`
              : `${s} (${projects.filter(p => p.status === s).length})`}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: 13, color: '#D1D5DB' }}>
          Total: <span style={{ color: '#E8722A', fontWeight: 700, marginLeft: 4 }}>
            {rolePerms?.seeDollars !== false ? fmt(filtered.reduce((s, p) => s + p.totalValue, 0)) : '—'}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🔨</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#D1D5DB', marginBottom: 4 }}>No projects match this status</div>
            <div style={{ fontSize: 13, color: '#D1D5DB' }}>Try a different status filter or add a new project.</div>
          </div>
        )}
        {filtered.map(project => {
          const sc = statusColor(project.status);
          return (
            <div
              key={project.id}
              onClick={() => setSelectedProject(project)}
              style={{
                background: '#2A3140',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 12, padding: 14,
                cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s',
                overflow: 'hidden',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(232,114,42,0.6)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{project.customerName}</div>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
                      background: 'rgba(255,255,255,0.05)', color: '#9CA3AF',
                      letterSpacing: '0.3px', flexShrink: 0,
                    }}>
                      {project.scopes.length} {project.scopes.length === 1 ? 'scope' : 'scopes'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.address}</div>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                  background: sc + '22', color: sc,
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}>{project.status}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#D1D5DB', marginTop: 8 }}>
                <span>Total: <span style={{ color: '#22c55e', fontWeight: 700 }}>{rolePerms?.seeDollars !== false ? fmt(project.totalValue) : '—'}</span></span>
                {project.nextScheduledDate && <span>📅 {project.nextScheduledDate}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {selectedProject && (
        <ProjectDetail
          project={projects.find(p => p.id === selectedProject.id) || selectedProject}
          rolePerms={rolePerms}
          quotes={quotes}
          onClose={() => setSelectedProject(null)}
          onSelectScope={(scope) => { setSelectedJob(scope); }}
          onNewQuote={(project) => setQuoteEditProject({ project, quote: null, picker: true })}
          onOpenQuote={(quote, project) => setQuoteEditProject({ project, quote, picker: false })}
          onAddScope={onAddScope}
          onUpdateScope={onUpdateScope}
        />
      )}

      {selectedJob && (
        <JobModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          customChecklist={customChecklist}
          crew={crew}
          assignments={assignments}
          onAssign={onAssign}
          onUnassign={onUnassign}
          onAddCrew={onAddCrew}
          currentUser={currentUser}
          demoMessages={demoMessages ? demoMessages.filter(m => String(m.jobId) === String(selectedJob.id)) : null}
          onComplete={onComplete ? (id) => { onComplete(id); setSelectedJob(null); } : null}
          onUpdateSteps={onUpdateSteps}
          onUpdateSchedule={onUpdateSchedule}
          rolePerms={rolePerms}
          isDemo={!!demoMessages}
        />
      )}

      {quoteEditProject && quoteEditProject.picker && (
        <QuoteModePickerModal
          target={{
            kind: 'project', id: quoteEditProject.project.id,
            customerName: quoteEditProject.project.customerName,
            address: quoteEditProject.project.address,
            project: quoteEditProject.project,
          }}
          quotes={quotes}
          onPick={(quote) => setQuoteEditProject({ ...quoteEditProject, quote, picker: false })}
          onClose={() => setQuoteEditProject(null)}
        />
      )}

      {quoteEditProject && !quoteEditProject.picker && quoteEditProject.quote && (
        <QuoteDocumentEditor
          target={{
            kind: 'project', id: quoteEditProject.project.id,
            customerName: quoteEditProject.project.customerName,
            address: quoteEditProject.project.address,
            project: quoteEditProject.project,
          }}
          initialQuote={quoteEditProject.quote}
          onSave={(quote) => {
            if (onSaveQuote) onSaveQuote(quote);
            setQuoteEditProject(prev => prev ? { ...prev, quote } : prev);
          }}
          onClose={() => setQuoteEditProject(null)}
        />
      )}
    </div>
  );
}

// ─── Project Detail Modal (lists scopes inside a project) ────────────────────
function ProjectDetail({ project, rolePerms, quotes, onClose, onSelectScope, onNewQuote, onOpenQuote, onAddScope, onUpdateScope }) {
  useScrollLock();
  const isMobile = useMobile();
  const showToast = useToast();
  const statusColor = (s) => ({
    Scheduled: '#6366f1', 'In Progress': '#E8722A', Complete: '#22c55e',
    Pending: '#8B95A1', 'Pending Schedule': '#8B95A1', 'Not Started': '#8B95A1',
  }[s] || '#8B95A1');
  const sc = statusColor(project.status);
  const [expanded, setExpanded] = useState(() => new Set());
  const [showOverflow, setShowOverflow] = useState(false);
  const [showScopePicker, setShowScopePicker] = useState(false);

  const projectQuotes = useMemo(
    () => getQuotesForProject(project.id, quotes || DEMO_QUOTES),
    [project.id, quotes]
  );

  const quoteStatusColor = (s) => ({
    draft: '#9CA3AF', sent: '#3b82f6', viewed: '#06b6d4',
    signed: '#22c55e', rejected: '#ef4444', expired: '#f59e0b',
  }[s] || '#9CA3AF');
  const quoteStatusBg = (s) => ({
    draft: 'rgba(156,163,175,0.12)', sent: 'rgba(59,130,246,0.15)', viewed: 'rgba(6,182,212,0.15)',
    signed: 'rgba(34,197,94,0.15)', rejected: 'rgba(239,68,68,0.15)', expired: 'rgba(245,158,11,0.15)',
  }[s] || 'rgba(156,163,175,0.12)');

  const overlay = isMobile ? { ...S.overlay, padding: 0, alignItems: 'flex-end' } : S.overlay;
  const modal = isMobile
    ? { ...S.modal, maxWidth: '100vw', width: '100vw', maxHeight: '94dvh', borderRadius: '16px 16px 0 0', margin: 0 }
    : { ...S.modal, maxWidth: 600 };

  const toggleExpanded = (scopeId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(scopeId)) next.delete(scopeId);
      else next.add(scopeId);
      return next;
    });
  };

  const overflowItem = (label, action, danger) => (
    <button
      onClick={() => { setShowOverflow(false); action(); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', minHeight: 40, width: '100%',
        background: 'transparent', border: 'none',
        color: danger ? '#ef4444' : '#D1D5DB', fontSize: 13, fontWeight: 600,
        cursor: 'pointer', textAlign: 'left',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>

        <div style={{ paddingRight: 48 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#FFFFFF' }}>{project.customerName}</div>
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
              background: sc + '22', color: sc,
              textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>{project.status}</span>
            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              <button
                onClick={() => setShowOverflow(v => !v)}
                aria-label="Project actions"
                style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
                  color: '#D1D5DB', fontSize: 18, fontWeight: 700,
                  cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >⋯</button>
              {showOverflow && (
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    position: 'absolute', top: 36, right: 0, zIndex: 10,
                    minWidth: 200,
                    background: '#1E2329', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    display: 'flex', flexDirection: 'column', padding: 4,
                  }}
                >
                  {overflowItem('Edit Project', () => showToast('Edit Project — coming soon'))}
                  {overflowItem('Mark Complete', () => showToast('Mark Project Complete — coming soon'))}
                  {overflowItem('Convert to Invoice', () => showToast('Convert to Invoice — coming soon'))}
                  {overflowItem('Delete Project', () => showToast('Delete Project — coming soon'), true)}
                </div>
              )}
            </div>
          </div>
          <div style={{ fontSize: 13, color: '#D1D5DB', marginBottom: 8 }}>{project.address}</div>
          {(project.customerPhone || project.customerEmail) && (
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>
              {project.customerPhone && <span>📞 {project.customerPhone}</span>}
              {project.customerEmail && <span>✉️ {project.customerEmail}</span>}
            </div>
          )}

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 18, fontSize: 12, color: '#D1D5DB' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>Total Value</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#22c55e' }}>{rolePerms?.seeDollars !== false ? fmt(project.totalValue) : '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>Scopes</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#FFFFFF' }}>{project.scopes.length}</div>
            </div>
            {project.nextScheduledDate && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>Next Date</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#D1D5DB' }}>{project.nextScheduledDate}</div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Scopes</span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
              background: 'rgba(255,255,255,0.06)', color: '#D1D5DB',
            }}>{project.scopes.length}</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {project.scopes.map(scope => {
            const ssc = statusColor(scope.status);
            const isOpen = expanded.has(scope.id);
            const sections = Array.isArray(scope.sections) ? scope.sections : [];
            return (
              <div
                key={scope.id}
                style={{
                  background: '#1E2329', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, overflow: 'hidden',
                }}
              >
                <div
                  onClick={() => toggleExpanded(scope.id)}
                  style={{
                    padding: '12px 14px', cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#252b36'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                    <span style={{
                      fontSize: 11, color: '#9CA3AF', flexShrink: 0,
                      transition: 'transform 0.15s', display: 'inline-block',
                      transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}>▶</span>
                    <span style={{
                      fontSize: 13, fontWeight: 700, color: '#FFFFFF',
                      flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{scope.trade}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                      background: ssc + '22', color: ssc,
                      textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0,
                    }}>{scope.status}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#22c55e', flexShrink: 0 }}>
                      {rolePerms?.seeDollars !== false ? fmt(scope.value) : '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: '#9CA3AF', marginTop: 6, marginLeft: 18, flexWrap: 'wrap' }}>
                    {sections.length > 0 && (
                      <span>{sections.length} section{sections.length === 1 ? '' : 's'}</span>
                    )}
                    {scope.scheduledDate && <span>📅 {scope.scheduledDate}</span>}
                    {scope.jobType && scope.currentPhase != null && (
                      <span>Phase {scope.currentPhase}/{scope.jobType === 'Commercial' ? 7 : 4}</span>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div style={{
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    background: 'rgba(0,0,0,0.15)', padding: '10px 14px',
                  }}>
                    {sections.length === 0 ? (
                      <div style={{ fontSize: 12, color: '#9CA3AF', padding: '8px 0', textAlign: 'center' }}>
                        No sections yet. Open a quote to scaffold this scope.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {sections.map(sec => {
                          const lineItems = Array.isArray(sec.lineItems) ? sec.lineItems : [];
                          return (
                            <div key={sec.id} style={{
                              background: 'rgba(255,255,255,0.03)', borderRadius: 6,
                              padding: '8px 10px',
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: lineItems.length ? 6 : 0 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF' }}>
                                  {sec.name}{sec.optional ? ' (optional)' : ''}
                                </span>
                                {rolePerms?.seeDollars !== false && sec.subtotal != null && (
                                  <span style={{ fontSize: 12, fontWeight: 700, color: '#22c55e', flexShrink: 0 }}>
                                    {fmt(sec.subtotal)}
                                  </span>
                                )}
                              </div>
                              {lineItems.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                  {lineItems.map(li => (
                                    <div key={li.id} style={{
                                      display: 'flex', justifyContent: 'space-between', gap: 8,
                                      fontSize: 11, color: '#9CA3AF',
                                    }}>
                                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {li.description}
                                        {(li.quantity || li.unitPrice) && (
                                          <span style={{ color: '#6B7280' }}>
                                            {' '}· {Number(li.quantity) || 0} {li.unit || ''} × {rolePerms?.seeDollars !== false ? fmt(li.unitPrice || 0) : '—'}
                                          </span>
                                        )}
                                      </span>
                                      {rolePerms?.seeDollars !== false && (
                                        <span style={{ flexShrink: 0, color: '#D1D5DB' }}>{fmt(li.extension || 0)}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      {scope.status !== 'Complete' && onUpdateScope && (
                        <button
                          onClick={() => {
                            onUpdateScope({ ...scope, status: 'Complete', currentPhase: scope.jobType === 'Commercial' ? 7 : 4 });
                            showToast('Scope marked complete.');
                          }}
                          style={{
                            padding: '6px 12px', minHeight: 32,
                            background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.35)',
                            borderRadius: 6, color: '#22c55e', fontSize: 11, fontWeight: 700,
                            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          ✓ Mark Scope Complete
                        </button>
                      )}
                      {onSelectScope && (
                        <button
                          onClick={() => onSelectScope(scope)}
                          style={{
                            padding: '6px 12px', minHeight: 32,
                            background: 'transparent', border: '1px solid rgba(255,255,255,0.18)',
                            borderRadius: 6, color: '#D1D5DB', fontSize: 11, fontWeight: 700,
                            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          Open job details
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button
          onClick={() => setShowScopePicker(true)}
          style={{
            marginTop: 10, width: '100%', padding: '12px 14px', minHeight: 44,
            background: 'rgba(232,114,42,0.10)', border: '1px dashed rgba(232,114,42,0.4)',
            borderRadius: 8, color: '#E8722A', fontSize: 13, fontWeight: 700,
            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
          }}
        >
          + Add Scope
        </button>

        {/* ── Quotes section ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 22, marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Quotes</span>
            {projectQuotes.length > 0 && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
                background: 'rgba(255,255,255,0.06)', color: '#D1D5DB',
              }}>{projectQuotes.length}</span>
            )}
          </div>
          <button
            onClick={() => onNewQuote ? onNewQuote(project) : showToast('Quote Agent connecting in next commit')}
            style={{
              padding: '6px 12px', minHeight: 36,
              background: 'linear-gradient(135deg, #2D5016, #3d6b1e)',
              border: 'none', borderRadius: 6, color: '#fff', fontSize: 11, fontWeight: 700,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 2px 8px rgba(45,80,22,0.35)',
            }}
          >
            + New Quote
          </button>
        </div>

        {projectQuotes.length === 0 ? (
          <div style={{
            padding: '18px 16px', borderRadius: 8,
            background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.08)',
            textAlign: 'center', fontSize: 12, color: '#D1D5DB',
          }}>
            No quotes yet. Tap <strong style={{ color: '#FFFFFF' }}>+ New Quote</strong> to describe the job and let the agent draft one.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {projectQuotes.map(quote => {
              const qsc = quoteStatusColor(quote.status);
              const qsbg = quoteStatusBg(quote.status);
              return (
                <div
                  key={quote.id}
                  onClick={() => onOpenQuote && onOpenQuote(quote, project)}
                  style={{
                    background: '#1E2329', border: '1px solid rgba(255,255,255,0.08)',
                    borderLeft: `3px solid ${qsc}`, borderRadius: 8, padding: '12px 14px',
                    cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#252b36'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#1E2329'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap', minWidth: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF', whiteSpace: 'nowrap' }}>{quote.quoteNumber}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                      background: qsbg, color: qsc,
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>{quote.status}</span>
                    <span style={{
                      fontSize: 9, fontWeight: 600, padding: '1px 7px', borderRadius: 8,
                      background: 'rgba(255,255,255,0.05)', color: '#D1D5DB',
                      textTransform: 'uppercase', letterSpacing: '0.4px',
                    }}>{quote.mode}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: '#22c55e', whiteSpace: 'nowrap' }}>
                      {rolePerms?.seeDollars !== false ? fmt(quote.total) : '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: '#D1D5DB', flexWrap: 'wrap' }}>
                    <span>{(quote.sections || []).length} {(quote.sections || []).length === 1 ? 'section' : 'sections'}</span>
                    {quote.expirationDate && <span>Expires {quote.expirationDate}</span>}
                    {quote.sentDate && <span>Sent {quote.sentDate.slice(0, 10)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showScopePicker && (
        <ScopeTemplatePickerModal
          project={project}
          onPick={(scope) => {
            setShowScopePicker(false);
            if (onAddScope) {
              onAddScope(scope);
              setExpanded(prev => {
                const next = new Set(prev);
                next.add(scope.id);
                return next;
              });
            }
          }}
          onClose={() => setShowScopePicker(false)}
        />
      )}
    </div>
  );
}

// ─── Quote draft normalization helpers ───────────────────────────────────────
function normalizeQuoteDraft(draft) {
  if (!draft || typeof draft !== 'object') return null;
  const sections = Array.isArray(draft.sections) ? draft.sections.map((sec, i) => {
    const lineItems = Array.isArray(sec.lineItems) ? sec.lineItems.map((li, j) => {
      const qty = Number(li.quantity) || 0;
      const price = Number(li.unitPrice) || 0;
      const ext = li.extension != null ? Number(li.extension) : Math.round(qty * price * 100) / 100;
      return {
        id: li.id || `li-${i}-${j}-${Math.random().toString(36).slice(2, 6)}`,
        description: String(li.description || ''),
        category: li.category || 'Material',
        quantity: qty, unit: li.unit || 'EA', unitPrice: price,
        extension: ext, notes: li.notes || null,
      };
    }) : [];
    const secTotal = lineItems.reduce((s, li) => s + (li.extension || 0), 0);
    return {
      id: sec.id || `qs-${i}-${Math.random().toString(36).slice(2, 6)}`,
      scopeId: sec.scopeId || null,
      scopeType: sec.scopeType || 'Full Replacement',
      narrative: sec.narrative || '',
      lineItems,
      subtotal: Math.round(secTotal * 100) / 100,
    };
  }) : [];
  const subtotal = sections.reduce((s, sec) => s + sec.subtotal, 0);
  const tax = Number(draft.tax) || 0;
  return {
    mode: draft.mode === 'commercial' ? 'commercial' : 'residential',
    sections,
    subtotal: Math.round(subtotal * 100) / 100,
    tax,
    total: Math.round((subtotal + tax) * 100) / 100,
    terms: draft.terms || '',
    exclusions: Array.isArray(draft.exclusions) ? draft.exclusions : [],
    alternates: Array.isArray(draft.alternates) ? draft.alternates : [],
    paymentSchedule: Array.isArray(draft.paymentSchedule) ? draft.paymentSchedule : [],
  };
}

// ─── Quote Document Editor — primary "New Quote" surface ────────────────────
// Document-style editor: the document body scrolls vertically while the
// top/bottom bars stay sticky. Mobile-first.

const COMPANY_PROFILE = {
  name: 'EQUITY ROOFING',
  address: '218 Industrial Pkwy, Mifflinburg, PA 17844',
  license: 'PA HIC #PA-029847',
  phone: '(570) 966-7300',
  email: 'office@equityroofing.com',
};

function fmtCurrency(n) {
  const v = Number(n) || 0;
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ─── Template-first quote builder ───────────────────────────────────────────
// Two fully-scaffolded templates with realistic 2026 central PA pricing.
// The picker drawer reads from the per-template CATALOG; the editor opens
// pre-populated with the TEMPLATE sections. Templates and catalogs are
// strictly separated by mode — residential quotes never see commercial items
// in the picker and vice versa.
//
// Line item shape: { description, category, unit, unitPrice, quantity,
//   margin, notes?, isAllowance? }. `margin` is internal-only (35% default).

const DEFAULT_MARGIN = 35;

// ── RESIDENTIAL catalog (picker drawer source) ──
const RESIDENTIAL_CATALOG = [
  // Tear-off & Disposal
  { id: 'rc-to-labor', description: 'Tear-off labor', category: 'Tear-off & Disposal', unit: 'SQ', unitPrice: 65 },
  { id: 'rc-to-dump20', description: 'Dumpster (20yd)', category: 'Tear-off & Disposal', unit: 'EA', unitPrice: 450 },
  { id: 'rc-to-dumpfees', description: 'Dump fees', category: 'Tear-off & Disposal', unit: 'TON', unitPrice: 85 },
  { id: 'rc-to-tarps', description: 'Tarps & site protection', category: 'Tear-off & Disposal', unit: 'LS', unitPrice: 125 },
  { id: 'rc-to-extra-layer', description: 'Extra layer tear-off surcharge', category: 'Tear-off & Disposal', unit: 'SQ', unitPrice: 25 },

  // Decking & Repair
  { id: 'rc-dk-sheet', description: 'Sheathing replacement (allowance)', category: 'Decking & Repair', unit: 'EA', unitPrice: 85, isAllowance: true },
  { id: 'rc-dk-fasteners', description: 'Fasteners (re-nail deck)', category: 'Decking & Repair', unit: 'LS', unitPrice: 85 },
  { id: 'rc-dk-repair', description: 'Structural repair labor (allowance)', category: 'Decking & Repair', unit: 'HR', unitPrice: 95, isAllowance: true },

  // Underlayment
  { id: 'rc-un-synthetic', description: 'Synthetic underlayment', category: 'Underlayment', unit: 'SQ', unitPrice: 35 },
  { id: 'rc-un-iwshield', description: 'Ice & water shield (eaves + valleys)', category: 'Underlayment', unit: 'SQ', unitPrice: 95 },
  { id: 'rc-un-dripedge', description: 'Drip edge', category: 'Underlayment', unit: 'LF', unitPrice: 2.25 },

  // Roofing Material
  { id: 'rc-rm-shingles-arch', description: 'Architectural shingles installed', category: 'Roofing Material', unit: 'SQ', unitPrice: 385 },
  { id: 'rc-rm-shingles-designer', description: 'Designer shingles installed', category: 'Roofing Material', unit: 'SQ', unitPrice: 525 },
  { id: 'rc-rm-shingles-impact', description: 'Impact-resistant shingles installed', category: 'Roofing Material', unit: 'SQ', unitPrice: 460 },
  { id: 'rc-rm-starter', description: 'Starter strip', category: 'Roofing Material', unit: 'BDL', unitPrice: 65 },
  { id: 'rc-rm-ridgecap', description: 'Ridge cap / hip & ridge shingles', category: 'Roofing Material', unit: 'BDL', unitPrice: 85 },

  // Flashing & Penetrations
  { id: 'rc-fl-step', description: 'Step flashing kit', category: 'Flashing & Penetrations', unit: 'EA', unitPrice: 145 },
  { id: 'rc-fl-counter', description: 'Counter-flashing', category: 'Flashing & Penetrations', unit: 'LF', unitPrice: 8.50 },
  { id: 'rc-fl-pipeboot', description: 'Pipe boot', category: 'Flashing & Penetrations', unit: 'EA', unitPrice: 35 },
  { id: 'rc-fl-chimney', description: 'Chimney flashing kit', category: 'Flashing & Penetrations', unit: 'EA', unitPrice: 385 },
  { id: 'rc-fl-skylight', description: 'Skylight flashing kit', category: 'Flashing & Penetrations', unit: 'EA', unitPrice: 195 },

  // Ventilation
  { id: 'rc-vt-ridge', description: 'Ridge vent', category: 'Ventilation', unit: 'LF', unitPrice: 7.50 },
  { id: 'rc-vt-soffit', description: 'Soffit intake vent', category: 'Ventilation', unit: 'EA', unitPrice: 12 },
  { id: 'rc-vt-exhaust', description: 'Exhaust cap', category: 'Ventilation', unit: 'EA', unitPrice: 65 },
  { id: 'rc-vt-power', description: 'Power vent', category: 'Ventilation', unit: 'EA', unitPrice: 385 },

  // Labor — Install
  { id: 'rc-lb-install', description: 'Install labor', category: 'Labor — Install', unit: 'SQ', unitPrice: 185 },
  { id: 'rc-lb-equipday', description: 'Equipment day rate', category: 'Labor — Install', unit: 'DAY', unitPrice: 285 },
  { id: 'rc-lb-2story', description: '2nd story access surcharge', category: 'Labor — Install', unit: 'LS', unitPrice: 450 },
  { id: 'rc-lb-steep', description: 'Steep pitch surcharge (9/12+)', category: 'Labor — Install', unit: 'SQ', unitPrice: 35 },

  // Cleanup & Final
  { id: 'rc-cl-magnet', description: 'Magnet sweep & cleanup', category: 'Cleanup & Final', unit: 'LS', unitPrice: 250 },
  { id: 'rc-cl-debris', description: 'Debris haul-away', category: 'Cleanup & Final', unit: 'LS', unitPrice: 185 },
  { id: 'rc-cl-final-inspect', description: 'Final inspection walk-through', category: 'Cleanup & Final', unit: 'LS', unitPrice: 125 },

  // Extras / Adders
  { id: 'rc-ex-gutter-5', description: 'Gutter run (5" K-style aluminum)', category: 'Extras / Adders', unit: 'LF', unitPrice: 11.50 },
  { id: 'rc-ex-gutter-6', description: 'Gutter run (6" K-style aluminum)', category: 'Extras / Adders', unit: 'LF', unitPrice: 14 },
  { id: 'rc-ex-downspout', description: 'Downspout', category: 'Extras / Adders', unit: 'EA', unitPrice: 95 },
  { id: 'rc-ex-guards', description: 'Gutter guards (micromesh)', category: 'Extras / Adders', unit: 'LF', unitPrice: 9 },
  { id: 'rc-ex-soffit-wrap', description: 'Soffit/fascia aluminum wrap', category: 'Extras / Adders', unit: 'LF', unitPrice: 14 },
  { id: 'rc-ex-skylight-new', description: 'New skylight installation', category: 'Extras / Adders', unit: 'EA', unitPrice: 1450 },
  { id: 'rc-ex-skylight-replace', description: 'Skylight replacement', category: 'Extras / Adders', unit: 'EA', unitPrice: 685 },
  { id: 'rc-ex-solar-rr', description: 'Solar panel R&R (per panel)', category: 'Extras / Adders', unit: 'EA', unitPrice: 165 },
  { id: 'rc-ex-attic-insul', description: 'Attic insulation top-up', category: 'Extras / Adders', unit: 'SF', unitPrice: 2.25 },

  // Permits & Fees
  { id: 'rc-pf-permit', description: 'Municipal building permit', category: 'Permits & Fees', unit: 'LS', unitPrice: 185 },
  { id: 'rc-pf-dump-permit', description: 'Dump permit', category: 'Permits & Fees', unit: 'LS', unitPrice: 35 },
  { id: 'rc-pf-warranty', description: 'Manufacturer warranty registration', category: 'Permits & Fees', unit: 'LS', unitPrice: 0 },
];

// ── COMMERCIAL catalog ──
const COMMERCIAL_CATALOG = [
  // Tear-off & Disposal
  { id: 'cc-to-labor', description: 'Membrane tear-off labor', category: 'Tear-off & Disposal', unit: 'SF', unitPrice: 1.85 },
  { id: 'cc-to-insul-rem', description: 'Insulation removal', category: 'Tear-off & Disposal', unit: 'SF', unitPrice: 0.75 },
  { id: 'cc-to-dump30', description: 'Dumpster (30yd)', category: 'Tear-off & Disposal', unit: 'EA', unitPrice: 650 },
  { id: 'cc-to-lift-stage', description: 'Lift staging setup', category: 'Tear-off & Disposal', unit: 'LS', unitPrice: 485 },
  { id: 'cc-to-tarps', description: 'Tarps & site protection', category: 'Tear-off & Disposal', unit: 'LS', unitPrice: 285 },

  // Substrate Preparation
  { id: 'cc-sp-inspect', description: 'Deck inspection', category: 'Substrate Preparation', unit: 'LS', unitPrice: 350 },
  { id: 'cc-sp-repair', description: 'Deck repair allowance', category: 'Substrate Preparation', unit: 'SF', unitPrice: 4.50, isAllowance: true },
  { id: 'cc-sp-moisture', description: 'Moisture core cuts', category: 'Substrate Preparation', unit: 'EA', unitPrice: 95 },

  // Insulation System
  { id: 'cc-is-polyiso', description: 'Polyiso R-20 (2-layer staggered)', category: 'Insulation System', unit: 'SF', unitPrice: 2.85 },
  { id: 'cc-is-cover', description: '½" HD cover board', category: 'Insulation System', unit: 'SF', unitPrice: 1.45 },
  { id: 'cc-is-adhesive', description: 'Insulation adhesive', category: 'Insulation System', unit: 'SF', unitPrice: 0.55 },
  { id: 'cc-is-fasteners', description: 'Insulation fasteners + plates', category: 'Insulation System', unit: 'SF', unitPrice: 0.45 },
  { id: 'cc-is-tapered', description: 'Tapered insulation for slope (allowance)', category: 'Insulation System', unit: 'SF', unitPrice: 3.25, isAllowance: true },

  // Membrane System
  { id: 'cc-ms-tpo60', description: '60-mil TPO membrane', category: 'Membrane System', unit: 'SF', unitPrice: 1.95 },
  { id: 'cc-ms-tpo80', description: '80-mil TPO membrane', category: 'Membrane System', unit: 'SF', unitPrice: 2.45 },
  { id: 'cc-ms-epdm60', description: '60-mil EPDM membrane', category: 'Membrane System', unit: 'SF', unitPrice: 1.85 },
  { id: 'cc-ms-pvc60', description: '60-mil PVC membrane', category: 'Membrane System', unit: 'SF', unitPrice: 2.65 },
  { id: 'cc-ms-bonding', description: 'Bonding adhesive', category: 'Membrane System', unit: 'SF', unitPrice: 0.65 },
  { id: 'cc-ms-primer', description: 'Membrane primer', category: 'Membrane System', unit: 'SF', unitPrice: 0.25 },
  { id: 'cc-ms-seam-tape', description: 'Seam tape (where applicable)', category: 'Membrane System', unit: 'LF', unitPrice: 0.85 },

  // Edge Metal & Terminations
  { id: 'cc-em-edge', description: 'Edge metal', category: 'Edge Metal & Terminations', unit: 'LF', unitPrice: 8.50 },
  { id: 'cc-em-drip', description: 'Drip edge (commercial)', category: 'Edge Metal & Terminations', unit: 'LF', unitPrice: 5.25 },
  { id: 'cc-em-term-bar', description: 'Termination bar', category: 'Edge Metal & Terminations', unit: 'LF', unitPrice: 4.25 },
  { id: 'cc-em-coping', description: 'Coping cap', category: 'Edge Metal & Terminations', unit: 'LF', unitPrice: 12.50 },
  { id: 'cc-em-counter', description: 'Counter-flashing', category: 'Edge Metal & Terminations', unit: 'LF', unitPrice: 9.50 },

  // Penetrations & Flashing
  { id: 'cc-pf-pitch', description: 'Pitch pocket', category: 'Penetrations & Flashing', unit: 'EA', unitPrice: 185 },
  { id: 'cc-pf-pipeboot', description: 'Pipe boot (TPO)', category: 'Penetrations & Flashing', unit: 'EA', unitPrice: 85 },
  { id: 'cc-pf-curb', description: 'Curb flashing (HVAC / equipment)', category: 'Penetrations & Flashing', unit: 'LF', unitPrice: 28 },
  { id: 'cc-pf-parapet', description: 'Parapet wall flashing', category: 'Penetrations & Flashing', unit: 'LF', unitPrice: 24 },
  { id: 'cc-pf-flash-mem', description: 'Membrane flashing (walls/curbs)', category: 'Penetrations & Flashing', unit: 'LF', unitPrice: 18 },

  // Drainage
  { id: 'cc-dr-drain', description: 'Roof drain assembly', category: 'Drainage', unit: 'EA', unitPrice: 485 },
  { id: 'cc-dr-scupper', description: 'Scupper', category: 'Drainage', unit: 'EA', unitPrice: 325 },
  { id: 'cc-dr-overflow', description: 'Overflow drain', category: 'Drainage', unit: 'EA', unitPrice: 285 },
  { id: 'cc-dr-downspout', description: 'Downspout (commercial)', category: 'Drainage', unit: 'EA', unitPrice: 185 },

  // Walkway & Protection
  { id: 'cc-wp-pad', description: 'Walkway pad (3\'x10\')', category: 'Walkway & Protection', unit: 'EA', unitPrice: 145 },
  { id: 'cc-wp-snowguard', description: 'Snow guard / lightning protection', category: 'Walkway & Protection', unit: 'EA', unitPrice: 95 },

  // Labor — Install
  { id: 'cc-lb-install', description: 'Install crew labor', category: 'Labor — Install', unit: 'SF', unitPrice: 2.85 },
  { id: 'cc-lb-certified', description: 'Certified installer labor', category: 'Labor — Install', unit: 'HR', unitPrice: 145 },
  { id: 'cc-lb-welding', description: 'Seam welding labor', category: 'Labor — Install', unit: 'SF', unitPrice: 0.95 },

  // Equipment
  { id: 'cc-eq-boom', description: 'Boom lift / crane rental', category: 'Equipment', unit: 'DAY', unitPrice: 485 },
  { id: 'cc-eq-hoist', description: 'Material hoist', category: 'Equipment', unit: 'DAY', unitPrice: 285 },
  { id: 'cc-eq-crane-day', description: 'Crane day rate (rooftop sets)', category: 'Equipment', unit: 'DAY', unitPrice: 2100 },

  // Permits & Inspections
  { id: 'cc-pi-permit', description: 'Municipal building permit', category: 'Permits & Inspections', unit: 'LS', unitPrice: 485 },
  { id: 'cc-pi-pre', description: 'Manufacturer pre-install inspection', category: 'Permits & Inspections', unit: 'LS', unitPrice: 350 },
  { id: 'cc-pi-post', description: 'Manufacturer post-install inspection (NDL)', category: 'Permits & Inspections', unit: 'LS', unitPrice: 850 },

  // Cleanup & Final
  { id: 'cc-cl-debris', description: 'Debris haul-away', category: 'Cleanup & Final', unit: 'LS', unitPrice: 485 },
  { id: 'cc-cl-magnet', description: 'Magnetic sweep of parking lot', category: 'Cleanup & Final', unit: 'LS', unitPrice: 285 },
  { id: 'cc-cl-protect', description: 'Site protection / re-stripe', category: 'Cleanup & Final', unit: 'LS', unitPrice: 385 },
];

// Build a LineItem from a catalog entry (or descriptor for template scaffold).
// Stamps a fresh id, computes extension from qty * unitPrice, applies default
// margin if not provided.
function makeLineItem(src, qty) {
  const quantity = Number(qty != null ? qty : src.quantity) || 0;
  const unitPrice = Number(src.unitPrice) || 0;
  return {
    id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    description: src.description,
    category: src.category,
    quantity,
    unit: src.unit,
    unitPrice,
    extension: Math.round(quantity * unitPrice * 100) / 100,
    margin: src.margin != null ? src.margin : DEFAULT_MARGIN,
    notes: src.notes || null,
    isAllowance: !!src.isAllowance,
  };
}

// Lookup helper for template scaffolds — grabs catalog entry by description
// and instantiates a line item with the given default qty.
function liFromCat(catalog, description, qty) {
  const entry = catalog.find(c => c.description === description);
  if (!entry) return makeLineItem({ description, category: 'Custom', unit: 'EA', unitPrice: 0 }, qty);
  return makeLineItem(entry, qty);
}

// ── RESIDENTIAL template (full scaffold, in section order) ──
function buildResidentialSections() {
  const cat = RESIDENTIAL_CATALOG;
  return [
    {
      sectionName: 'Measurements & Scope',
      narrative: 'Squares: \nPitch: \nWaste factor: 10%\nComplexity notes: ',
      lineItems: [],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Tear-off & Disposal',
      narrative: 'Complete tear-off of existing roof system and disposal.',
      lineItems: [
        liFromCat(cat, 'Tear-off labor', 0),
        liFromCat(cat, 'Dumpster (20yd)', 1),
        liFromCat(cat, 'Dump fees', 2),
        liFromCat(cat, 'Tarps & site protection', 1),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Decking & Repair',
      narrative: 'Replace damaged sheathing as discovered during tear-off. Allowance billed only if used.',
      lineItems: [
        liFromCat(cat, 'Sheathing replacement (allowance)', 4),
        liFromCat(cat, 'Fasteners (re-nail deck)', 1),
        liFromCat(cat, 'Structural repair labor (allowance)', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Underlayment',
      narrative: 'Synthetic underlayment full coverage; ice & water shield at eaves and valleys; drip edge all sides.',
      lineItems: [
        liFromCat(cat, 'Synthetic underlayment', 0),
        liFromCat(cat, 'Ice & water shield (eaves + valleys)', 0),
        liFromCat(cat, 'Drip edge', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Roofing Material',
      narrative: 'Architectural shingles (primary system), starter strip, and matched hip & ridge.',
      lineItems: [
        liFromCat(cat, 'Architectural shingles installed', 0),
        liFromCat(cat, 'Starter strip', 0),
        liFromCat(cat, 'Ridge cap / hip & ridge shingles', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Flashing & Penetrations',
      narrative: 'Step + counter flashing at all walls; new pipe boots; chimney + skylight flashing kits as applicable.',
      lineItems: [
        liFromCat(cat, 'Step flashing kit', 0),
        liFromCat(cat, 'Counter-flashing', 0),
        liFromCat(cat, 'Pipe boot', 2),
        liFromCat(cat, 'Chimney flashing kit', 0),
        liFromCat(cat, 'Skylight flashing kit', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Ventilation',
      narrative: 'Continuous ridge vent paired with soffit intake for balanced attic ventilation.',
      lineItems: [
        liFromCat(cat, 'Ridge vent', 0),
        liFromCat(cat, 'Soffit intake vent', 0),
        liFromCat(cat, 'Exhaust cap', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Labor — Install',
      narrative: 'Shingle install labor by the square; equipment day rate covers ladders, lifts, and on-site tooling.',
      lineItems: [
        liFromCat(cat, 'Install labor', 0),
        liFromCat(cat, 'Equipment day rate', 1),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Cleanup & Final',
      narrative: 'Full magnetic sweep and haul-off of jobsite debris. Final walk-through with homeowner.',
      lineItems: [
        liFromCat(cat, 'Magnet sweep & cleanup', 1),
        liFromCat(cat, 'Debris haul-away', 1),
        liFromCat(cat, 'Final inspection walk-through', 1),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Extras / Adders',
      narrative: 'Optional scope add-ons — gutters, gutter guards, soffit/fascia, skylights, solar R&R.',
      lineItems: [],
      optional: true, collapsed: true,
    },
    {
      sectionName: 'Permits & Fees',
      narrative: 'Municipal building permit and dump permit. Filed under the company HIC license.',
      lineItems: [
        liFromCat(cat, 'Municipal building permit', 1),
        liFromCat(cat, 'Dump permit', 1),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Warranty & Terms',
      narrative: 'Manufacturer warranty: GAF System Plus 50-year (subject to product registration).\nWorkmanship warranty: 10 years parts + labor.\nPayment terms: 30% deposit at signing, 30% at material delivery, balance net 15 days from completion.',
      lineItems: [],
      optional: false, collapsed: false,
    },
  ];
}

// ── COMMERCIAL template (full scaffold, in section order) ──
function buildCommercialSections() {
  const cat = COMMERCIAL_CATALOG;
  return [
    {
      sectionName: 'Measurements & Scope',
      narrative: 'Sq ft: \nSlope: \nPenetrations count: \nCurrent system + age: \nAccess notes: ',
      lineItems: [],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Tear-off & Disposal',
      narrative: 'Strip existing membrane and insulation to deck. Stage and dispose responsibly.',
      lineItems: [
        liFromCat(cat, 'Membrane tear-off labor', 0),
        liFromCat(cat, 'Insulation removal', 0),
        liFromCat(cat, 'Dumpster (30yd)', 1),
        liFromCat(cat, 'Lift staging setup', 1),
        liFromCat(cat, 'Tarps & site protection', 1),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Substrate Preparation',
      narrative: 'Inspect deck condition, repair as needed (allowance), moisture core cuts at suspect areas.',
      lineItems: [
        liFromCat(cat, 'Deck inspection', 1),
        liFromCat(cat, 'Deck repair allowance', 0),
        liFromCat(cat, 'Moisture core cuts', 4),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Insulation System',
      narrative: 'Polyiso R-20 in 2-layer staggered configuration with ½" HD cover board over deck.',
      lineItems: [
        liFromCat(cat, 'Polyiso R-20 (2-layer staggered)', 0),
        liFromCat(cat, '½" HD cover board', 0),
        liFromCat(cat, 'Insulation adhesive', 0),
        liFromCat(cat, 'Insulation fasteners + plates', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Membrane System',
      narrative: '60-mil TPO fully adhered membrane (primary system). Seam-welded with bonding adhesive over substrate.',
      lineItems: [
        liFromCat(cat, '60-mil TPO membrane', 0),
        liFromCat(cat, 'Bonding adhesive', 0),
        liFromCat(cat, 'Membrane primer', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Edge Metal & Terminations',
      narrative: 'New edge metal, drip edge, termination bar at walls/curbs, coping cap on parapet walls.',
      lineItems: [
        liFromCat(cat, 'Edge metal', 0),
        liFromCat(cat, 'Drip edge (commercial)', 0),
        liFromCat(cat, 'Termination bar', 0),
        liFromCat(cat, 'Coping cap', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Penetrations & Flashing',
      narrative: 'Pitch pockets at penetrations; TPO pipe boots; curb + parapet flashing tied into membrane.',
      lineItems: [
        liFromCat(cat, 'Pitch pocket', 0),
        liFromCat(cat, 'Pipe boot (TPO)', 0),
        liFromCat(cat, 'Curb flashing (HVAC / equipment)', 0),
        liFromCat(cat, 'Parapet wall flashing', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Drainage',
      narrative: 'Re-set or replace drains as needed. Tapered insulation for proper drainage slope (allowance).',
      lineItems: [
        liFromCat(cat, 'Roof drain assembly', 0),
        liFromCat(cat, 'Scupper', 0),
        liFromCat(cat, 'Overflow drain', 0),
        liFromCat(cat, 'Downspout (commercial)', 0),
        liFromCat(cat, 'Tapered insulation for slope (allowance)', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Walkway & Protection',
      narrative: 'Walkway pads at HVAC service points and high-traffic areas to protect membrane.',
      lineItems: [
        liFromCat(cat, 'Walkway pad (3\'x10\')', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Labor — Install',
      narrative: 'Install crew labor by sq ft; certified installer hours; seam welding labor.',
      lineItems: [
        liFromCat(cat, 'Install crew labor', 0),
        liFromCat(cat, 'Certified installer labor', 0),
        liFromCat(cat, 'Seam welding labor', 0),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Equipment',
      narrative: 'Boom lift for access, material hoist for staging.',
      lineItems: [
        liFromCat(cat, 'Boom lift / crane rental', 1),
        liFromCat(cat, 'Material hoist', 1),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Permits & Inspections',
      narrative: 'Municipal commercial permit; manufacturer pre + post install inspections required for NDL warranty.',
      lineItems: [
        liFromCat(cat, 'Municipal building permit', 1),
        liFromCat(cat, 'Manufacturer pre-install inspection', 1),
        liFromCat(cat, 'Manufacturer post-install inspection (NDL)', 1),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Cleanup & Final',
      narrative: 'Debris haul-off, magnetic sweep of parking lot, restore site protection.',
      lineItems: [
        liFromCat(cat, 'Debris haul-away', 1),
        liFromCat(cat, 'Magnetic sweep of parking lot', 1),
        liFromCat(cat, 'Site protection / re-stripe', 1),
      ],
      optional: false, collapsed: false,
    },
    {
      sectionName: 'Warranty & Terms',
      narrative: 'Manufacturer NDL warranty: 20-year No Dollar Limit (TPO 60-mil, fully adhered).\nWorkmanship warranty: 2 years on labor.\nPayment terms: 25% deposit at signing, AIA G702/G703 monthly progress billing, 10% retainage released at substantial completion.',
      lineItems: [],
      optional: false, collapsed: false,
    },
  ];
}

// Named template constants, per the "Finish the Quote Builder" spec. These
// expose the section-scaffold output as plain arrays so callers (or future
// surfaces like a template editor) can introspect the section list without
// invoking the build function. Sections themselves are still id-stamped /
// subtotaled when materialised through buildQuoteFromMode.
export const RESIDENTIAL_TEMPLATE = buildResidentialSections();
export const COMMERCIAL_TEMPLATE = buildCommercialSections();

// Materialise a fresh quote from a mode template. Stamps section + line item
// ids, computes subtotals/total (excluding optional sections from the total),
// and anchors to a lead or project per the target.
function buildQuoteFromMode(mode, target, existing) {
  const sections = (mode === 'commercial' ? buildCommercialSections() : buildResidentialSections())
    .map(sec => {
      const lineItems = sec.lineItems.map(li => ({
        ...li,
        id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        extension: Math.round((Number(li.quantity) || 0) * (Number(li.unitPrice) || 0) * 100) / 100,
      }));
      const subtotal = lineItems.reduce((s, li) => s + (li.extension || 0), 0);
      return {
        ...sec,
        id: `qs-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        scopeId: null,
        scopeType: sec.sectionName.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
        lineItems,
        subtotal: Math.round(subtotal * 100) / 100,
        isSubsection: false,
      };
    });
  // Grand total excludes optional sections.
  const subtotal = sections
    .filter(s => !s.optional)
    .reduce((acc, s) => acc + (s.subtotal || 0), 0);
  const now = new Date();
  const exp = new Date(now); exp.setDate(exp.getDate() + 30);
  return {
    id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    quoteNumber: generateQuoteNumber(existing),
    leadId: target.kind === 'lead' ? target.id : null,
    projectId: target.kind === 'project' ? target.id : null,
    mode,
    status: 'draft',
    createdDate: now.toISOString(),
    sentDate: null, viewedDate: null, signedDate: null,
    expirationDate: exp.toISOString().slice(0, 10),
    subtotal: Math.round(subtotal * 100) / 100,
    tax: 0,
    total: Math.round(subtotal * 100) / 100,
    sections,
    attachments: [], signature: null,
    depositAmount: 0, depositPaid: false, paymentIntentId: null,
    shareToken: generateShareToken(),
    terms: mode === 'commercial' ? DEFAULT_TERMS_COMMERCIAL : DEFAULT_TERMS_RESIDENTIAL,
    exclusions: mode === 'commercial' ? [...DEFAULT_EXCLUSIONS_COMMERCIAL] : [...DEFAULT_EXCLUSIONS_RESIDENTIAL],
    included: mode === 'commercial' ? DEFAULT_INCLUDED_COMMERCIAL : DEFAULT_INCLUDED_RESIDENTIAL,
    alternates: [],
    paymentSchedule: [],
    customerInteractions: [], agentConversation: [],
    measurements: null,
  };
}

// Blank quote — preserves the old "start from scratch" path. No sections,
// no defaults; user builds entirely from the picker.
function buildBlankQuote(mode, target, existing) {
  const now = new Date();
  const exp = new Date(now); exp.setDate(exp.getDate() + 30);
  return {
    id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    quoteNumber: generateQuoteNumber(existing),
    leadId: target.kind === 'lead' ? target.id : null,
    projectId: target.kind === 'project' ? target.id : null,
    mode,
    status: 'draft',
    createdDate: now.toISOString(),
    sentDate: null, viewedDate: null, signedDate: null,
    expirationDate: exp.toISOString().slice(0, 10),
    subtotal: 0, tax: 0, total: 0,
    sections: [],
    attachments: [], signature: null,
    depositAmount: 0, depositPaid: false, paymentIntentId: null,
    shareToken: generateShareToken(),
    terms: mode === 'commercial' ? DEFAULT_TERMS_COMMERCIAL : DEFAULT_TERMS_RESIDENTIAL,
    exclusions: mode === 'commercial' ? [...DEFAULT_EXCLUSIONS_COMMERCIAL] : [...DEFAULT_EXCLUSIONS_RESIDENTIAL],
    included: mode === 'commercial' ? DEFAULT_INCLUDED_COMMERCIAL : DEFAULT_INCLUDED_RESIDENTIAL,
    alternates: [],
    paymentSchedule: [],
    customerInteractions: [], agentConversation: [],
    measurements: null,
  };
}

// Distribute a target value across a template's sections + line items.
// Template line items mostly default to qty=0 (the editor expects the user
// to fill quantities), so we can't just scale by the natural subtotal —
// it'd be near-zero. Instead, allocate the target value weighted by each
// section's line-item count, and split each section's share evenly across
// its line items. Sections that have no line items (narrative-only ones
// like "Measurements & Scope" and "Warranty & Terms") and optional sections
// stay at $0 — both more realistic and easier to read in the demo.
function scaleScopeSections(sections, targetValue) {
  const allocatable = sections.filter(s => !s.optional && (s.lineItems || []).length > 0);
  const weights = allocatable.map(s => s.lineItems.length);
  const wSum = weights.reduce((a, b) => a + b, 0);
  const subtotalByName = new Map();
  if (wSum > 0 && targetValue > 0) {
    let allocated = 0;
    allocatable.forEach((s, i) => {
      const subtotal = i === allocatable.length - 1
        ? Math.round((targetValue - allocated) * 100) / 100
        : Math.round((targetValue * weights[i] / wSum) * 100) / 100;
      allocated += subtotal;
      subtotalByName.set(s.sectionName, subtotal);
    });
  }
  return sections.map(sec => {
    const subtotal = subtotalByName.get(sec.sectionName) || 0;
    const items = sec.lineItems || [];
    if (items.length === 0 || subtotal === 0) {
      return {
        id: sec.id || `ss-${Math.random().toString(36).slice(2, 8)}`,
        name: sec.sectionName,
        narrative: sec.narrative || '',
        lineItems: items.map(li => ({ ...li, extension: 0 })),
        subtotal: 0,
        optional: !!sec.optional,
      };
    }
    const per = Math.round((subtotal / items.length) * 100) / 100;
    let alloc = 0;
    const lineItems = items.map((li, i) => {
      const ext = i === items.length - 1
        ? Math.round((subtotal - alloc) * 100) / 100
        : per;
      alloc += ext;
      const qty = Math.max(Number(li.quantity) || 1, 1);
      return {
        ...li,
        id: li.id || `li-${Math.random().toString(36).slice(2, 8)}`,
        quantity: qty,
        unitPrice: Math.round((ext / qty) * 100) / 100,
        extension: ext,
      };
    });
    return {
      id: sec.id || `ss-${Math.random().toString(36).slice(2, 8)}`,
      name: sec.sectionName,
      narrative: sec.narrative || '',
      lineItems,
      subtotal,
      optional: !!sec.optional,
    };
  });
}

// ── Scope builders (parallel to buildQuoteFromMode/buildBlankQuote) ──
// "+ Add Scope" inside a project uses these. A scope is one unit of work
// within a project (e.g. "Residential roof — Stoltzfus"); sections inside
// the scope mirror the quote-section scaffold so estimating and execution
// share the same vocabulary.
function lastWordOf(name) {
  const parts = String(name || '').trim().split(/\s+/);
  return parts[parts.length - 1] || 'Customer';
}

function defaultScopeName(mode, customerName) {
  const isCommercial = mode === 'commercial';
  const tag = isCommercial ? (customerName || 'Customer') : lastWordOf(customerName);
  return `${isCommercial ? 'Commercial' : 'Residential'} roof — ${tag}`;
}

function materializeScopeSections(mode) {
  const raw = mode === 'commercial' ? buildCommercialSections() : buildResidentialSections();
  return raw.map(sec => {
    const lineItems = sec.lineItems.map(li => ({
      ...li,
      id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      extension: Math.round((Number(li.quantity) || 0) * (Number(li.unitPrice) || 0) * 100) / 100,
    }));
    const subtotal = lineItems.reduce((s, li) => s + (li.extension || 0), 0);
    return {
      id: `ss-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: sec.sectionName,
      narrative: sec.narrative || '',
      lineItems,
      subtotal: Math.round(subtotal * 100) / 100,
      optional: !!sec.optional,
    };
  });
}

function buildScopeFromMode(mode, project) {
  const sections = materializeScopeSections(mode);
  const value = sections.filter(s => !s.optional).reduce((sum, s) => sum + (s.subtotal || 0), 0);
  const isCommercial = mode === 'commercial';
  return {
    id: `s-${project.id}-${Date.now().toString(36)}`,
    projectId: project.id,
    scopeType: defaultScopeName(mode, project.customerName),
    status: 'Pending Schedule',
    value: Math.round(value * 100) / 100,
    completedSteps: [],
    notes: '',
    phaseTemplate: isCommercial ? 'commercial-7-phase' : 'residential-4-phase',
    currentPhase: null,
    completedPhases: [],
    sections,
  };
}

function buildBlankScope(project) {
  return {
    id: `s-${project.id}-${Date.now().toString(36)}`,
    projectId: project.id,
    scopeType: `Custom scope — ${lastWordOf(project.customerName)}`,
    status: 'Pending Schedule',
    value: 0,
    completedSteps: [],
    notes: '',
    phaseTemplate: 'residential-4-phase',
    currentPhase: null,
    completedPhases: [],
    sections: [],
  };
}

// Template picker for "+ Add Scope" inside a project. Mirrors the quote
// picker UX but creates a scope (with section scaffold) instead of a quote.
function ScopeTemplatePickerModal({ project, onPick, onClose }) {
  useScrollLock();
  const isMobile = useMobile();

  const overlay = isMobile ? { ...S.overlay, padding: 0, alignItems: 'flex-end' } : S.overlay;
  const modal = isMobile
    ? { ...S.modal, maxWidth: '100vw', width: '100vw', maxHeight: '90dvh', borderRadius: '16px 16px 0 0', margin: 0 }
    : { ...S.modal, maxWidth: 520 };

  const bigBtn = (label, sub, accent, onClick) => (
    <button
      onClick={onClick}
      style={{
        width: '100%', padding: '20px 18px', minHeight: 84,
        background: accent === 'orange'
          ? 'linear-gradient(135deg, #E8722A, #e8640c)'
          : accent === 'blue'
            ? 'linear-gradient(135deg, #2D5016, #3d6b1e)'
            : 'transparent',
        border: accent === 'ghost' ? '1px dashed rgba(255,255,255,0.22)' : 'none',
        borderRadius: 12,
        color: accent === 'ghost' ? '#D1D5DB' : '#FFFFFF',
        textAlign: 'left', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
        boxShadow: accent !== 'ghost' ? '0 4px 14px rgba(0,0,0,0.25)' : 'none',
        display: 'flex', flexDirection: 'column', gap: 4,
      }}
    >
      <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.2px' }}>{label}</span>
      <span style={{ fontSize: 11, opacity: 0.9, fontWeight: 500 }}>{sub}</span>
    </button>
  );

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        <div style={{ ...S.modalTitle, paddingRight: 48 }}>Add scope to project</div>
        <div style={{ ...S.modalSub, marginBottom: 16 }}>
          Adding to <strong style={{ color: '#FFFFFF' }}>{project.customerName}</strong>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {bigBtn(
            '🏠  Residential',
            '12 sections scaffolded — tear-off, decking, underlayment, shingles, flashing, ventilation, labor, cleanup, permits, warranty',
            'orange',
            () => onPick(buildScopeFromMode('residential', project)),
          )}
          {bigBtn(
            '🏢  Commercial',
            '14 sections scaffolded — substrate prep, insulation, membrane, edge metal, drainage, walkway, equipment, inspections, warranty',
            'blue',
            () => onPick(buildScopeFromMode('commercial', project)),
          )}
          {bigBtn(
            '✏️  Start from scratch',
            'Blank scope — add sections and line items yourself.',
            'ghost',
            () => onPick(buildBlankScope(project)),
          )}
        </div>
      </div>
    </div>
  );
}

// Quote-mode picker: shown first when "+ New Quote" is tapped. Three big
// buttons; selection triggers the right builder and pops the editor.
function QuoteModePickerModal({ target, onPick, onClose, quotes }) {
  useScrollLock();
  const isMobile = useMobile();

  const overlay = isMobile ? { ...S.overlay, padding: 0, alignItems: 'flex-end' } : S.overlay;
  const modal = isMobile
    ? { ...S.modal, maxWidth: '100vw', width: '100vw', maxHeight: '90dvh', borderRadius: '16px 16px 0 0', margin: 0 }
    : { ...S.modal, maxWidth: 520 };

  const bigBtn = (label, sub, accent, onClick) => (
    <button
      onClick={onClick}
      style={{
        width: '100%', padding: '20px 18px', minHeight: 84,
        background: accent === 'orange'
          ? 'linear-gradient(135deg, #E8722A, #e8640c)'
          : accent === 'blue'
            ? 'linear-gradient(135deg, #2D5016, #3d6b1e)'
            : 'transparent',
        border: accent === 'ghost' ? '1px dashed rgba(255,255,255,0.22)' : 'none',
        borderRadius: 12,
        color: accent === 'ghost' ? '#D1D5DB' : '#FFFFFF',
        textAlign: 'left', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
        boxShadow: accent !== 'ghost' ? '0 4px 14px rgba(0,0,0,0.25)' : 'none',
        display: 'flex', flexDirection: 'column', gap: 4,
      }}
    >
      <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.2px' }}>{label}</span>
      <span style={{ fontSize: 11, opacity: 0.9, fontWeight: 500 }}>{sub}</span>
    </button>
  );

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        <div style={{ ...S.modalTitle, paddingRight: 48 }}>What type of quote?</div>
        <div style={{ ...S.modalSub, marginBottom: 16 }}>
          Quoting for <strong style={{ color: '#FFFFFF' }}>{target.customerName}</strong>{target.address ? ` · ${target.address}` : ''}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {bigBtn(
            '🏠  Residential',
            '12 sections scaffolded — tear-off, decking, underlayment, shingles, flashing, ventilation, labor, cleanup, permits, warranty',
            'orange',
            () => onPick(buildQuoteFromMode('residential', target, quotes)),
          )}
          {bigBtn(
            '🏢  Commercial',
            '14 sections scaffolded — substrate prep, insulation, membrane, edge metal, drainage, walkway, equipment, inspections, warranty',
            'blue',
            () => onPick(buildQuoteFromMode('commercial', target, quotes)),
          )}
          {bigBtn(
            '✏️  Start from scratch',
            'Blank quote — pick everything from the catalog yourself.',
            'ghost',
            () => onPick(buildBlankQuote('residential', target, quotes)),
          )}
        </div>
      </div>
    </div>
  );
}

// Smart default quantity for a line item given roof measurements. Maps
// catalog units → measurement fields by both unit and description keywords.
// When measurements are absent or the unit isn't measurement-driven, falls
// back to 1 for LS/EA and 0 for everything else (the user gets a Set
// Quantity prompt on the card).
function getSmartDefaultQty(item, m) {
  if (!item) return 0;
  const unit = item.unit;
  const desc = (item.description || '').toLowerCase();
  if (unit === 'LS') return 1;
  if (!m) {
    if (unit === 'EA') return 1;
    return 0;
  }
  if (unit === 'SQ') return Number(m.squares) || 0;
  // Commercial pricing is per-SF. 1 square = 100 sq ft, so map squares → SF
  // when the catalog item is SF-denominated.
  if (unit === 'SF') return (Number(m.squares) || 0) * 100;
  if (unit === 'EA') {
    if (/pipe boot/.test(desc)) return Number(m.pipes) || 1;
    if (/(box|power|off-ridge|ridge)\s*vent/.test(desc) && !/ridge cap/.test(desc)) return Number(m.vents) || 1;
    if (/skylight/.test(desc)) return Number(m.skylights) || 1;
    if (/chimney/.test(desc)) return Number(m.chimneys) || 1;
    return 1;
  }
  if (unit === 'LF') {
    if (/ridge/.test(desc)) return Number(m.ridgeLF) || 0;
    if (/hip/.test(desc)) return Number(m.hipLF) || 0;
    if (/valley/.test(desc)) return Number(m.valleyLF) || 0;
    if (/drip edge|gutter apron|starter/.test(desc)) return (Number(m.eaveLF) || 0) + (Number(m.rakeLF) || 0);
    if (/gutter/.test(desc)) return Number(m.eaveLF) || 0;
    return 0;
  }
  return 0;
}

// ── Measurements bar (collapsible, above first section) ──
function MeasurementsBar({ measurements, onChange, isMobile }) {
  const [open, setOpen] = useState(false);
  const m = measurements || {};
  const setField = (key, val) => onChange({ ...m, [key]: val });
  const completedCount = Object.values(m).filter(v => v !== null && v !== undefined && v !== '' && v !== 0).length;

  const input = {
    width: '100%', padding: '8px 10px', minHeight: 38,
    background: '#FFFFFF', border: '1px solid rgba(30,35,41,0.18)', borderRadius: 6,
    color: '#1E2329', fontSize: isMobile ? 16 : 13, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };
  const label = { fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 };

  return (
    <div style={{
      background: '#FFFFFF', border: '1px solid rgba(30,35,41,0.15)', borderRadius: 6,
      marginBottom: 12, overflow: 'hidden',
    }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%', padding: '12px 14px',
          background: open ? '#F8F4EC' : 'transparent', border: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          cursor: 'pointer', color: '#1E2329',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>📐 Roof Measurements</span>
          {completedCount > 0 && !open && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
              background: 'rgba(34,197,94,0.18)', color: '#16a34a',
            }}>{completedCount} set</span>
          )}
        </span>
        <span style={{ fontSize: 12, color: '#4A5568' }}>{open ? '▼' : '▶'}</span>
      </button>
      {open && (
        <div style={{ padding: 14, background: '#F8F4EC', borderTop: '1px solid rgba(30,35,41,0.08)' }}>
          <div style={{ fontSize: 11, color: '#4A5568', marginBottom: 12, lineHeight: 1.5 }}>
            Set measurements once and they'll auto-populate quantities when you add line items from the picker or bundles.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 10, marginBottom: 12 }}>
            <div>
              <div style={label}>Total Squares</div>
              <input type="number" inputMode="decimal" value={m.squares || ''} onChange={e => setField('squares', Number(e.target.value) || 0)} style={input} placeholder="0" />
            </div>
            <div>
              <div style={label}>Avg Pitch</div>
              <select value={m.pitch || ''} onChange={e => setField('pitch', e.target.value)} style={input}>
                <option value="">—</option>
                {['2/12', '3/12', '4/12', '5/12', '6/12', '7/12', '8/12', '9/12', '10/12', '11/12', '12/12+'].map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={label}>Stories</div>
              <select value={m.stories || ''} onChange={e => setField('stories', e.target.value)} style={input}>
                <option value="">—</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3+">3+</option>
              </select>
            </div>
            <div>
              <div style={label}>Layers</div>
              <select value={m.layersToTearOff != null ? String(m.layersToTearOff) : ''} onChange={e => setField('layersToTearOff', e.target.value === '' ? null : Number(e.target.value))} style={input}>
                <option value="">—</option>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </select>
            </div>
          </div>

          <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Linear feet</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: 10, marginBottom: 12 }}>
            {[['ridgeLF', 'Ridge'], ['hipLF', 'Hip'], ['valleyLF', 'Valley'], ['eaveLF', 'Eave'], ['rakeLF', 'Rake']].map(([k, lab]) => (
              <div key={k}>
                <div style={label}>{lab}</div>
                <input type="number" inputMode="decimal" value={m[k] || ''} onChange={e => setField(k, Number(e.target.value) || 0)} style={input} placeholder="0" />
              </div>
            ))}
          </div>

          <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Penetrations</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 10 }}>
            {[['vents', 'Vents'], ['pipes', 'Pipes'], ['skylights', 'Skylights'], ['chimneys', 'Chimneys']].map(([k, lab]) => (
              <div key={k}>
                <div style={label}>{lab}</div>
                <input type="number" inputMode="numeric" value={m[k] || ''} onChange={e => setField(k, Number(e.target.value) || 0)} style={input} placeholder="0" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Line Item Picker Drawer ────────────────────────────────────────────────
// Replaces the old catalog modal. Bottom sheet on mobile, right drawer on
// desktop. Items grouped by roofing-native categories (residential vs
// commercial); search across description + category; multi-select for batch
// adding; "+ Custom line item" form at the bottom.
function LineItemPickerDrawer({ mode, sectionName, onPick, onClose }) {
  useScrollLock();
  const isMobile = useMobile();
  const [search, setSearch] = useState('');
  const [multiSelect, setMultiSelect] = useState(false);
  const [selected, setSelected] = useState({}); // id → true
  const [openGroups, setOpenGroups] = useState(new Set());
  const [showCustom, setShowCustom] = useState(false);

  // Catalog is strictly mode-separated: residential quotes never see
  // commercial items and vice versa. Source of truth is the in-file
  // RESIDENTIAL_CATALOG / COMMERCIAL_CATALOG arrays.
  const allItems = useMemo(
    () => (mode === 'commercial' ? COMMERCIAL_CATALOG : RESIDENTIAL_CATALOG),
    [mode],
  );

  // Groups derived directly from the catalog's distinct categories — preserves
  // the catalog's authoring order so picker accordions can't drift out of sync
  // with what the catalog actually contains.
  const groupedItems = useMemo(() => {
    const byCat = new Map();
    allItems.forEach(li => {
      if (!byCat.has(li.category)) byCat.set(li.category, { id: li.category, label: li.category, items: [] });
      byCat.get(li.category).items.push(li);
    });
    return Array.from(byCat.values());
  }, [allItems]);

  // Search across all items, flat.
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return allItems.filter(li =>
      (li.description || '').toLowerCase().includes(q)
      || (li.category || '').toLowerCase().includes(q)
    );
  }, [search, allItems]);

  const toggleGroup = (id) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handlePick = (li) => {
    if (multiSelect) {
      setSelected(s => ({ ...s, [li.id]: !s[li.id] }));
    } else {
      onPick([li]);
    }
  };

  const applyMulti = () => {
    const items = allItems.filter(li => selected[li.id]);
    if (items.length === 0) return;
    onPick(items);
    setSelected({});
  };

  // Drawer chrome
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100,
    display: 'flex',
    alignItems: isMobile ? 'flex-end' : 'stretch',
    justifyContent: isMobile ? 'center' : 'flex-end',
  };
  const drawer = isMobile
    ? { background: '#1E2329', width: '100vw', maxWidth: '100vw', height: '88dvh',
        borderRadius: '16px 16px 0 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
    : { background: '#1E2329', width: 460, height: '100dvh',
        borderLeft: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden' };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={drawer} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          flexShrink: 0, padding: '12px 14px',
          background: '#161B22', borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF' }}>Add to {sectionName || 'Section'}</div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>{mode === 'commercial' ? 'Commercial' : 'Residential'} catalog</div>
          </div>
          <button
            onClick={() => setMultiSelect(v => !v)}
            style={{
              padding: '6px 10px', minHeight: 32,
              background: multiSelect ? 'rgba(232,114,42,0.18)' : 'transparent',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 6, color: multiSelect ? '#E8722A' : '#D1D5DB',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >{multiSelect ? '✓ Multi' : 'Pick multiple'}</button>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36, height: 36, padding: 0,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8, color: '#FFFFFF', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent',
            }}
          >×</button>
        </div>

        {/* Search */}
        <div style={{ flexShrink: 0, padding: 10, background: '#1E2329' }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search line items…"
            style={{
              width: '100%', padding: '10px 12px', minHeight: 40,
              background: '#161B22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
              color: '#FFFFFF', fontSize: isMobile ? 16 : 13, outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Body */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
          padding: '6px 10px',
        }}>
          {searchResults ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {searchResults.length === 0 && (
                <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>
                  No matches. Add a custom line item below.
                </div>
              )}
              {searchResults.map(li => (
                <PickerRow key={li.id} item={li} selected={!!selected[li.id]} multi={multiSelect} onPick={() => handlePick(li)} />
              ))}
            </div>
          ) : (
            groupedItems.map(g => (
              g.items.length === 0 ? null : (
                <div key={g.id} style={{ marginBottom: 4 }}>
                  <button
                    onClick={() => toggleGroup(g.id)}
                    style={{
                      width: '100%', padding: '10px 12px', minHeight: 40,
                      background: openGroups.has(g.id) ? '#2A3140' : 'transparent',
                      border: 'none', borderRadius: 6,
                      color: '#D1D5DB', fontSize: 12, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      cursor: 'pointer', textAlign: 'left',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <span>{g.label}</span>
                    <span style={{ fontSize: 11, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{g.items.length}</span>
                      <span>{openGroups.has(g.id) ? '▼' : '▶'}</span>
                    </span>
                  </button>
                  {openGroups.has(g.id) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 0 8px 0' }}>
                      {g.items.map(li => (
                        <PickerRow key={li.id} item={li} selected={!!selected[li.id]} multi={multiSelect} onPick={() => handlePick(li)} />
                      ))}
                    </div>
                  )}
                </div>
              )
            ))
          )}

          {/* Add custom line item */}
          <button
            onClick={() => setShowCustom(true)}
            style={{
              width: '100%', padding: '12px', minHeight: 44, marginTop: 8,
              background: 'transparent', border: '1px dashed rgba(255,255,255,0.18)',
              borderRadius: 8, color: '#D1D5DB', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
          >+ Add custom line item</button>
        </div>

        {/* Multi-select action bar */}
        {multiSelect && (
          <div style={{
            flexShrink: 0, padding: 12,
            background: '#161B22', borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', gap: 8, alignItems: 'center',
          }}>
            <div style={{ flex: 1, fontSize: 11, color: '#D1D5DB' }}>
              {Object.values(selected).filter(Boolean).length} selected
            </div>
            <button
              onClick={() => setSelected({})}
              style={{
                padding: '8px 12px', minHeight: 36,
                background: 'transparent', border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 6, color: '#D1D5DB', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              }}
            >Clear</button>
            <button
              onClick={applyMulti}
              disabled={Object.values(selected).filter(Boolean).length === 0}
              style={{
                padding: '8px 14px', minHeight: 36,
                background: 'linear-gradient(135deg, #E8722A, #e8640c)',
                border: 'none', borderRadius: 6,
                color: '#fff', fontSize: 11, fontWeight: 700,
                cursor: Object.values(selected).filter(Boolean).length === 0 ? 'not-allowed' : 'pointer',
                opacity: Object.values(selected).filter(Boolean).length === 0 ? 0.5 : 1,
                WebkitTapHighlightColor: 'transparent',
              }}
            >Add Selected</button>
          </div>
        )}

        {showCustom && (
          <CustomLineItemForm
            onCancel={() => setShowCustom(false)}
            onAdd={(li) => { onPick([li]); setShowCustom(false); }}
          />
        )}
      </div>
    </div>
  );
}

function PickerRow({ item, selected, multi, onPick }) {
  return (
    <button
      onClick={onPick}
      style={{
        width: '100%', textAlign: 'left', padding: '10px 12px',
        background: selected ? 'rgba(232,114,42,0.12)' : '#161B22',
        border: `1px solid ${selected ? 'rgba(232,114,42,0.35)' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: 6, color: '#FFFFFF', cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        display: 'flex', alignItems: 'center', gap: 10,
      }}
    >
      {multi && (
        <span style={{
          width: 18, height: 18, flexShrink: 0,
          borderRadius: 4, border: `1.5px solid ${selected ? '#E8722A' : 'rgba(255,255,255,0.25)'}`,
          background: selected ? '#E8722A' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 11, fontWeight: 800,
        }}>{selected ? '✓' : ''}</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description}</div>
        <div style={{ fontSize: 10, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          {item.category} · {item.unit}
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#22c55e', whiteSpace: 'nowrap', flexShrink: 0 }}>
        ${item.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
        <span style={{ fontSize: 9, color: '#9CA3AF', fontWeight: 500 }}> /{item.unit}</span>
      </div>
    </button>
  );
}

function CustomLineItemForm({ onCancel, onAdd }) {
  const isMobile = useMobile();
  const [draft, setDraft] = useState({
    description: '', quantity: 1, unit: 'EA', unitPrice: 0,
  });
  const inputStyle = {
    width: '100%', padding: '10px 12px', minHeight: 40,
    background: '#161B22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6,
    color: '#FFFFFF', fontSize: isMobile ? 16 : 13, outline: 'none', boxSizing: 'border-box',
  };
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 10,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#1E2329', borderRadius: 12, padding: 16,
          width: '100%', maxWidth: 360,
          border: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF', marginBottom: 4 }}>Custom Line Item</div>
        <input
          value={draft.description}
          onChange={e => setDraft(p => ({ ...p, description: e.target.value }))}
          placeholder="Description"
          style={inputStyle}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <input type="number" inputMode="decimal" value={draft.quantity} onChange={e => setDraft(p => ({ ...p, quantity: e.target.value }))} placeholder="Qty" style={inputStyle} />
          <select value={draft.unit} onChange={e => setDraft(p => ({ ...p, unit: e.target.value }))} style={inputStyle}>
            {['EA', 'SF', 'LF', 'SQ', 'CY', 'TON', 'HR', 'LS', 'DAY'].map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <input type="number" inputMode="decimal" value={draft.unitPrice} onChange={e => setDraft(p => ({ ...p, unitPrice: e.target.value }))} placeholder="Unit price" style={inputStyle} />
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: '10px', minHeight: 40,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 8, color: '#D1D5DB', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >Cancel</button>
          <button
            onClick={() => {
              if (!draft.description.trim()) return;
              const qty = Number(draft.quantity) || 0;
              const price = Number(draft.unitPrice) || 0;
              onAdd({
                id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                description: draft.description,
                category: 'Custom',
                quantity: qty, unit: draft.unit, unitPrice: price,
                extension: Math.round(qty * price * 100) / 100,
                notes: null,
              });
            }}
            style={{
              flex: 2, padding: '10px', minHeight: 40,
              background: 'linear-gradient(135deg, #E8722A, #e8640c)',
              border: 'none', borderRadius: 8,
              color: '#fff', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
          >Add Item</button>
        </div>
      </div>
    </div>
  );
}

// ── Section template picker (modal) ──
function SectionTemplatePicker({ mode, onPick, onPickBlank, onClose }) {
  useScrollLock();
  const isMobile = useMobile();
  const templates = useMemo(() => getAllSectionTemplates().filter(t => t.mode === mode), [mode]);

  const overlay = isMobile ? { ...S.overlay, padding: 0, alignItems: 'flex-end' } : S.overlay;
  const modal = isMobile
    ? { ...S.modal, maxWidth: '100vw', width: '100vw', maxHeight: '94dvh', borderRadius: '16px 16px 0 0', margin: 0 }
    : { ...S.modal, maxWidth: 560 };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        <div style={{ ...S.modalTitle, paddingRight: 48 }}>Add section</div>
        <div style={S.modalSub}>Pick a section template or start blank.</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {templates.map(t => (
            <button
              key={t.id}
              onClick={() => { onPick(t); onClose(); }}
              style={{
                textAlign: 'left', padding: 14,
                background: '#1E2329', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10, cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent', color: '#FFFFFF',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#252b36'; e.currentTarget.style.borderColor = '#E8722A'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#1E2329'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', marginBottom: 4 }}>{t.name}</div>
              <div style={{ fontSize: 11, color: '#D1D5DB' }}>
                {(t.defaultLineItems || []).length} line item{(t.defaultLineItems || []).length === 1 ? '' : 's'} · {t.scopeType}
              </div>
            </button>
          ))}

          <button
            onClick={() => { onPickBlank(); onClose(); }}
            style={{
              padding: 14, background: 'transparent',
              border: '1px dashed rgba(255,255,255,0.18)', borderRadius: 10,
              color: '#D1D5DB', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
          >
            + Blank Section (start from scratch)
          </button>
        </div>
      </div>
    </div>
  );
}

// ── The document editor itself ──
function QuoteDocumentEditor({ target, initialQuote, onSave, onClose, onMarkSigned, onConvertToProject }) {
  useScrollLock();
  const isMobile = useMobile();
  const showToast = useToast();

  const [quote, setQuote] = useState(initialQuote);
  const [savedIndicator, setSavedIndicator] = useState(false);
  const [showCatalogPicker, setShowCatalogPicker] = useState(null); // { sectionId } | null
  const [showSectionPicker, setShowSectionPicker] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [termsCollapsed, setTermsCollapsed] = useState(true);
  const [showAIDraft, setShowAIDraft] = useState(false);
  const [showTemplateMgr, setShowTemplateMgr] = useState(false);
  const [showSendPreview, setShowSendPreview] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const saveTimerRef = useRef(null);
  const initialMountRef = useRef(true);

  // Debounced auto-save: any change to `quote` triggers a save 1s later.
  useEffect(() => {
    if (!quote) return undefined;
    if (initialMountRef.current) { initialMountRef.current = false; return undefined; }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      onSave(quote);
      setSavedIndicator(true);
      const t = setTimeout(() => setSavedIndicator(false), 1600);
      return () => clearTimeout(t);
    }, 1000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [quote, onSave]);

  if (!quote) return null;

  // ── State mutators ──
  // Grand total EXCLUDES optional sections — that's the wedge feature. Optional
  // sections show their own subtotal in the editor (and on the PDF) but never
  // roll into the total the customer signs against.
  const recomputeTotals = (sections, tax = quote.tax || 0) => {
    const subtotal = sections
      .filter(s => !s.optional)
      .reduce((sum, sec) => sum + (sec.subtotal || 0), 0);
    return { subtotal: Math.round(subtotal * 100) / 100, tax, total: Math.round((subtotal + tax) * 100) / 100 };
  };
  const recomputeSection = (sec) => {
    const subtotal = (sec.lineItems || []).reduce((s, li) => s + (Number(li.extension) || 0), 0);
    return { ...sec, subtotal: Math.round(subtotal * 100) / 100 };
  };

  const updateSection = (sectionId, patch) => {
    setQuote(prev => {
      const sections = prev.sections.map(s => s.id === sectionId ? recomputeSection({ ...s, ...patch }) : s);
      const totals = recomputeTotals(sections);
      return { ...prev, sections, ...totals };
    });
  };
  const updateLineItem = (sectionId, lineItemId, patch) => {
    setQuote(prev => {
      const sections = prev.sections.map(sec => {
        if (sec.id !== sectionId) return sec;
        const lineItems = sec.lineItems.map(li => {
          if (li.id !== lineItemId) return li;
          const next = { ...li, ...patch };
          const qty = Number(next.quantity) || 0;
          const price = Number(next.unitPrice) || 0;
          next.extension = Math.round(qty * price * 100) / 100;
          return next;
        });
        return recomputeSection({ ...sec, lineItems });
      });
      const totals = recomputeTotals(sections);
      return { ...prev, sections, ...totals };
    });
  };
  const removeLineItem = (sectionId, lineItemId) => {
    setQuote(prev => {
      const sections = prev.sections.map(sec => {
        if (sec.id !== sectionId) return sec;
        return recomputeSection({ ...sec, lineItems: sec.lineItems.filter(li => li.id !== lineItemId) });
      });
      const totals = recomputeTotals(sections);
      return { ...prev, sections, ...totals };
    });
  };
  const addLineItemToSection = (sectionId, item) => {
    setQuote(prev => {
      const sections = prev.sections.map(sec => {
        if (sec.id !== sectionId) return sec;
        return recomputeSection({ ...sec, lineItems: [...sec.lineItems, item] });
      });
      const totals = recomputeTotals(sections);
      return { ...prev, sections, ...totals };
    });
  };
  const addSection = (section) => {
    setQuote(prev => {
      const sections = [...prev.sections, section];
      const totals = recomputeTotals(sections);
      return { ...prev, sections, ...totals };
    });
  };
  const removeSection = (sectionId) => {
    setQuote(prev => {
      const sections = prev.sections.filter(s => s.id !== sectionId);
      const totals = recomputeTotals(sections);
      return { ...prev, sections, ...totals };
    });
  };
  const moveSection = (sectionId, delta) => {
    setQuote(prev => {
      const idx = prev.sections.findIndex(s => s.id === sectionId);
      if (idx < 0) return prev;
      const target = idx + delta;
      if (target < 0 || target >= prev.sections.length) return prev;
      const next = [...prev.sections];
      const [moved] = next.splice(idx, 1);
      next.splice(target, 0, moved);
      return { ...prev, sections: next };
    });
  };
  const duplicateSection = (sectionId) => {
    setQuote(prev => {
      const idx = prev.sections.findIndex(s => s.id === sectionId);
      if (idx < 0) return prev;
      const src = prev.sections[idx];
      const dup = {
        ...src,
        id: `qs-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        sectionName: `${src.sectionName} (copy)`,
        lineItems: (src.lineItems || []).map(li => ({
          ...li, id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        })),
      };
      const next = [...prev.sections];
      next.splice(idx + 1, 0, dup);
      const totals = recomputeTotals(next);
      return { ...prev, sections: next, ...totals };
    });
  };
  const addSubsectionBelow = (sectionId) => {
    setQuote(prev => {
      const idx = prev.sections.findIndex(s => s.id === sectionId);
      if (idx < 0) return prev;
      const parent = prev.sections[idx];
      const sub = {
        id: `qs-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        scopeId: null,
        scopeType: parent.scopeType,
        sectionName: 'Related work',
        narrative: '',
        lineItems: [],
        subtotal: 0,
        optional: false,
        collapsed: false,
        isSubsection: true,
      };
      const next = [...prev.sections];
      next.splice(idx + 1, 0, sub);
      return { ...prev, sections: next };
    });
  };

  // ── Bill-to derived from target ──
  const billToName = target.kind === 'lead' ? (target.lead?.name || target.customerName) : target.customerName;
  const billToAddress = target.address || '';
  const billToPhone = target.kind === 'lead' ? (target.lead?.phone || '') : (target.project?.customerPhone || '');
  const billToEmail = target.kind === 'lead' ? (target.lead?.email || '') : (target.project?.customerEmail || '');

  // ── Mobile-first styles ──
  const overlay = { ...S.overlay, padding: 0, alignItems: 'stretch', justifyContent: 'stretch' };
  const modal = {
    background: '#1E2329', border: 'none', borderRadius: 0,
    width: '100%', height: '100dvh', maxWidth: '100vw', maxHeight: '100dvh',
    margin: 0, padding: 0, position: 'relative',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  };

  const statusColor = (s) => ({
    draft: '#9CA3AF', sent: '#3b82f6', viewed: '#06b6d4',
    signed: '#22c55e', rejected: '#ef4444', expired: '#f59e0b',
  }[s] || '#9CA3AF');

  // iOS bumps the page zoom on focus when input fontSize <16px. Mobile must use
  // 16+. Desktop keeps the denser 13px for table density.
  const cellInput = {
    width: '100%', minHeight: 36, padding: '6px 8px',
    background: 'transparent', border: '1px solid transparent', borderRadius: 4,
    color: '#1E2329', fontSize: isMobile ? 16 : 13, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };
  const cellInputFocus = {
    background: '#FFFFFF', border: '1px solid #E8722A',
  };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal} onClick={e => e.stopPropagation()}>

        {/* Top sticky bar */}
        <div style={{
          flexShrink: 0, padding: '10px 12px',
          background: '#161B22', borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
        }}>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36, height: 36, padding: 0, flexShrink: 0,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8, color: '#FFFFFF', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent',
            }}
          >×</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {quote.quoteNumber} · {billToName}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
                background: statusColor(quote.status) + '22', color: statusColor(quote.status),
                textTransform: 'uppercase', letterSpacing: '0.4px',
              }}>{quote.status}</span>
              {savedIndicator && (
                <span style={{ fontSize: 10, color: '#22c55e' }}>✓ Saved</span>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowAIDraft(true)}
            aria-label="AI Draft"
            title="AI Draft"
            style={{
              height: 36, padding: '0 10px', flexShrink: 0,
              background: 'linear-gradient(135deg, #E8722A, #e8640c)',
              border: 'none', borderRadius: 8,
              color: '#FFFFFF', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
              WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 2px 8px rgba(249,115,22,0.25)',
            }}
          >
            <span style={{ fontSize: 14 }}>✦</span>
            <span>AI Draft</span>
          </button>
          <button
            onClick={() => setShowMenu(v => !v)}
            aria-label="Menu"
            style={{
              width: 36, height: 36, padding: 0, flexShrink: 0,
              background: showMenu ? 'rgba(255,255,255,0.08)' : 'transparent',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
              color: '#FFFFFF', fontSize: 20, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent',
            }}
          >⋯</button>
          {showMenu && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', right: 10,
              background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 10, padding: 6, minWidth: 200, zIndex: 5,
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              display: 'flex', flexDirection: 'column', gap: 2,
            }}>
              <button
                onClick={() => { setShowMenu(false); setShowAIDraft(true); }}
                style={{ padding: '10px 12px', minHeight: 40, background: 'transparent', border: 'none', borderRadius: 6, color: '#E8722A', fontSize: 13, fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}
              >✦ Open AI Draft</button>
              <button
                onClick={() => { setShowMenu(false); setShowTemplateMgr(true); }}
                style={{ padding: '10px 12px', minHeight: 40, background: 'transparent', border: 'none', borderRadius: 6, color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
              >Manage Templates</button>
              <button
                onClick={() => { setShowMenu(false); onSave(quote); showToast('Draft saved.'); }}
                style={{ padding: '10px 12px', minHeight: 40, background: 'transparent', border: 'none', borderRadius: 6, color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
              >Save now</button>
              <button
                onClick={() => { setShowMenu(false); setShowPdfPreview(true); }}
                style={{ padding: '10px 12px', minHeight: 40, background: 'transparent', border: 'none', borderRadius: 6, color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
              >Preview PDF</button>
              <button
                onClick={() => { setShowMenu(false); showToast('Duplicate coming next session.'); }}
                style={{ padding: '10px 12px', minHeight: 40, background: 'transparent', border: 'none', borderRadius: 6, color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
              >Duplicate</button>
              {target.kind === 'lead' && quote.status === 'draft' && onMarkSigned && (
                <button
                  onClick={() => {
                    setShowMenu(false);
                    const signed = { ...quote, status: 'signed', signedDate: new Date().toISOString() };
                    onMarkSigned(signed);
                    // If anchored to a lead and the conversion callback is wired,
                    // convert in a single atomic action — that's the real
                    // wow-moment of the product.
                    if (onConvertToProject && target.lead) {
                      onConvertToProject(target.lead, signed);
                      showToast('Lead converted to project. Find it in the Projects tab.');
                    } else {
                      showToast('Quote marked as signed.');
                    }
                    onClose();
                  }}
                  style={{
                    padding: '10px 12px', minHeight: 44, background: 'transparent',
                    border: 'none', borderRadius: 6, color: '#22c55e',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer', textAlign: 'left',
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                  }}
                >
                  <span>✓ Mark as Signed (preview)</span>
                  <span style={{ fontSize: 10, fontWeight: 500, color: '#9CA3AF' }}>
                    Simulates customer e-signature for demo purposes
                  </span>
                </button>
              )}
              {target.kind === 'lead' && quote.status === 'signed' && onConvertToProject && (
                <button
                  onClick={() => {
                    setShowMenu(false);
                    // eslint-disable-next-line no-restricted-globals
                    const ok = window.confirm('This will create a new Project from this signed quote and move the lead out of the pipeline. Proceed?');
                    if (ok) {
                      onConvertToProject(target.lead, quote);
                      showToast('Lead converted to project. Find it in the Projects tab.');
                    }
                  }}
                  style={{ padding: '10px 12px', minHeight: 40, background: 'transparent', border: 'none', borderRadius: 6, color: '#E8722A', fontSize: 13, fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}
                >→ Convert Lead to Project</button>
              )}
            </div>
          )}
        </div>

        {/* Document body (scrollable) */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          background: '#2A2E36',
        }}>
          <div style={{
            background: '#F8F4EC', color: '#1E2329',
            margin: isMobile ? '12px 8px' : '20px auto',
            maxWidth: 820, padding: isMobile ? '18px 14px' : '32px 36px',
            borderRadius: 6,
            boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
          }}>

            {/* Company header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, letterSpacing: '-0.5px', color: '#1E2329', marginBottom: 4 }}>
                  {COMPANY_PROFILE.name}
                </div>
                <div style={{ fontSize: 11, color: '#4A5568', lineHeight: 1.5 }}>
                  {COMPANY_PROFILE.address}<br />
                  {COMPANY_PROFILE.phone} · {COMPANY_PROFILE.email}<br />
                  {COMPANY_PROFILE.license}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quote</div>
                <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: '#1E2329' }}>{quote.quoteNumber}</div>
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '2px solid #1E2329', margin: '14px 0' }} />

            {/* Bill To + metadata */}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Bill To</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1E2329' }}>{billToName}</div>
                <div style={{ fontSize: 11, color: '#4A5568', lineHeight: 1.5 }}>{billToAddress}</div>
                {billToPhone && <div style={{ fontSize: 11, color: '#4A5568' }}>{billToPhone}</div>}
                {billToEmail && <div style={{ fontSize: 11, color: '#4A5568' }}>{billToEmail}</div>}
              </div>
              <div style={{ flex: '0 0 auto', textAlign: 'right' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Date</div>
                <div style={{ fontSize: 12, color: '#1E2329', marginBottom: 6 }}>{quote.createdDate ? quote.createdDate.slice(0, 10) : ''}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Valid Through</div>
                <div style={{ fontSize: 12, color: '#1E2329' }}>{quote.expirationDate}</div>
              </div>
            </div>

            {/* Sections */}
            {quote.sections.length === 0 && (
              <div style={{
                padding: '24px 16px', textAlign: 'center',
                background: '#FFFFFF', border: '1px dashed rgba(30,35,41,0.2)',
                borderRadius: 6, color: '#4A5568', fontSize: 13, marginBottom: 16,
              }}>
                <div style={{ fontSize: 13, marginBottom: 14, color: '#4A5568' }}>
                  This quote is empty. Start by describing the job and let AI draft it for you, or build from scratch.
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setShowAIDraft(true)}
                    style={{
                      padding: '10px 16px', minHeight: 40,
                      background: 'linear-gradient(135deg, #E8722A, #e8640c)',
                      border: 'none', borderRadius: 8,
                      color: '#fff', fontSize: 13, fontWeight: 700,
                      cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                      boxShadow: '0 2px 10px rgba(249,115,22,0.3)',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}
                  >✦ AI Draft</button>
                  <button
                    onClick={() => setShowSectionPicker(true)}
                    style={{
                      padding: '10px 16px', minHeight: 40,
                      background: '#FFFFFF', border: '2px solid #1E2329',
                      borderRadius: 8, color: '#1E2329', fontSize: 13, fontWeight: 700,
                      cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                    }}
                  >+ Add Section</button>
                </div>
              </div>
            )}

            {quote.sections.length > 0 && (
              <MeasurementsBar
                measurements={quote.measurements}
                onChange={(m) => setQuote(prev => ({ ...prev, measurements: m }))}
                isMobile={isMobile}
              />
            )}

            {quote.sections.map((sec, idx) => (
              <QuoteSectionBlock
                key={sec.id}
                section={sec}
                index={idx}
                totalSections={quote.sections.length}
                isMobile={isMobile}
                cellInput={cellInput}
                cellInputFocus={cellInputFocus}
                measurements={quote.measurements}
                quoteMode={quote.mode}
                onRename={(name) => updateSection(sec.id, { sectionName: name })}
                onUpdateNarrative={(n) => updateSection(sec.id, { narrative: n })}
                onUpdateLineItem={(liId, patch) => updateLineItem(sec.id, liId, patch)}
                onRemoveLineItem={(liId) => removeLineItem(sec.id, liId)}
                onAddLineItem={() => setShowCatalogPicker({ sectionId: sec.id, mode: quote.mode })}
                onAddBundle={(bundleDef) => {
                  const items = instantiateBundle(bundleDef, (b) => getSmartDefaultQty(b, quote.measurements));
                  items.forEach(li => addLineItemToSection(sec.id, li));
                  showToast(`Added ${items.length} item${items.length === 1 ? '' : 's'} from "${bundleDef.name}".`);
                }}
                onOpenAIDraft={() => setShowAIDraft(true)}
                onRemoveSection={() => removeSection(sec.id)}
                onMoveUp={() => moveSection(sec.id, -1)}
                onMoveDown={() => moveSection(sec.id, +1)}
                onToggleOptional={() => updateSection(sec.id, { optional: !sec.optional })}
                onToggleCollapse={() => updateSection(sec.id, { collapsed: !sec.collapsed })}
                onDuplicate={() => duplicateSection(sec.id)}
                onAddSubsection={() => addSubsectionBelow(sec.id)}
              />
            ))}

            <button
              onClick={() => setShowSectionPicker(true)}
              style={{
                width: '100%', padding: '14px', marginTop: 8, marginBottom: 18,
                background: '#FFFFFF', border: '2px dashed #1E2329',
                borderRadius: 6, color: '#1E2329',
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >+ Add Section</button>

            {/* Totals */}
            <div style={{
              background: '#FFFFFF', border: '1px solid #1E2329', borderRadius: 6,
              padding: '14px 16px', marginBottom: 18,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: '#4A5568' }}>Subtotal</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1E2329' }}>{fmtCurrency(quote.subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: '#4A5568' }}>
                  Tax
                  <input
                    type="number"
                    inputMode="decimal"
                    value={Math.round(((quote.tax || 0) / Math.max(1, quote.subtotal || 1)) * 100 * 100) / 100}
                    onChange={e => {
                      const pct = Number(e.target.value) || 0;
                      const tax = Math.round((quote.subtotal || 0) * pct) / 100;
                      setQuote(prev => ({ ...prev, tax, total: Math.round(((prev.subtotal || 0) + tax) * 100) / 100 }));
                    }}
                    style={{
                      width: isMobile ? 70 : 56, marginLeft: 8, padding: isMobile ? '6px 8px' : '4px 6px',
                      background: '#F8F4EC', border: '1px solid #1E2329', borderRadius: 4,
                      color: '#1E2329', fontSize: isMobile ? 16 : 12, fontFamily: 'inherit',
                      textAlign: 'right',
                    }}
                  />
                  <span style={{ fontSize: 11, marginLeft: 4 }}>%</span>
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1E2329' }}>{fmtCurrency(quote.tax || 0)}</span>
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid #1E2329', margin: '6px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#1E2329' }}>Total</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#1E2329' }}>{fmtCurrency(quote.total)}</span>
              </div>
            </div>

            {/* What's Included */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>What's Included</div>
              <textarea
                value={quote.included || ''}
                onChange={e => setQuote(prev => ({ ...prev, included: e.target.value }))}
                rows={5}
                style={{
                  width: '100%', padding: '10px 12px',
                  background: '#FFFFFF', border: '1px solid rgba(30,35,41,0.2)', borderRadius: 6,
                  color: '#1E2329', fontSize: isMobile ? 16 : 12, lineHeight: 1.5,
                  fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Exclusions */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>What's Not Included</div>
                <button
                  onClick={() => setQuote(prev => ({ ...prev, exclusions: [...(prev.exclusions || []), 'New exclusion'] }))}
                  style={{ padding: '4px 10px', background: '#1E2329', border: 'none', borderRadius: 4, color: '#FFFFFF', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                >+ Add</button>
              </div>
              {(quote.exclusions || []).map((ex, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: '#4A5568' }}>•</span>
                  <input
                    value={ex}
                    onChange={e => setQuote(prev => {
                      const next = [...(prev.exclusions || [])]; next[i] = e.target.value; return { ...prev, exclusions: next };
                    })}
                    style={{
                      flex: 1, padding: '6px 8px',
                      background: '#FFFFFF', border: '1px solid rgba(30,35,41,0.15)', borderRadius: 4,
                      color: '#1E2329', fontSize: isMobile ? 16 : 12, fontFamily: 'inherit', outline: 'none',
                    }}
                  />
                  <button
                    onClick={() => setQuote(prev => ({ ...prev, exclusions: (prev.exclusions || []).filter((_, j) => j !== i) }))}
                    aria-label="Remove"
                    style={{
                      width: isMobile ? 36 : 28, height: isMobile ? 36 : 28,
                      padding: 0, background: 'transparent', border: 'none',
                      color: '#9CA3AF', fontSize: 18, cursor: 'pointer',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >×</button>
                </div>
              ))}
            </div>

            {/* Terms */}
            <div style={{ marginBottom: 18 }}>
              <button
                onClick={() => setTermsCollapsed(v => !v)}
                style={{
                  width: '100%', padding: '8px 10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: '#FFFFFF', border: '1px solid rgba(30,35,41,0.15)', borderRadius: 6,
                  color: '#1E2329', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span>Terms &amp; Conditions</span>
                <span style={{ fontSize: 12 }}>{termsCollapsed ? '▶' : '▼'}</span>
              </button>
              {!termsCollapsed && (
                <textarea
                  value={quote.terms || ''}
                  onChange={e => setQuote(prev => ({ ...prev, terms: e.target.value }))}
                  rows={10}
                  style={{
                    width: '100%', marginTop: 6, padding: '10px 12px',
                    background: '#FFFFFF', border: '1px solid rgba(30,35,41,0.2)', borderRadius: 6,
                    color: '#1E2329', fontSize: isMobile ? 16 : 12, lineHeight: 1.5,
                    fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                  }}
                />
              )}
            </div>

            {/* Signature block */}
            <div style={{
              borderTop: '1px dashed rgba(30,35,41,0.3)',
              paddingTop: 18, marginTop: 18,
              display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
            }}>
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 28 }}>Customer Signature</div>
                <div style={{ borderTop: '1px solid #1E2329', paddingTop: 4, fontSize: 10, color: '#4A5568' }}>Sign on file</div>
              </div>
              <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 28 }}>Date</div>
                <div style={{ borderTop: '1px solid #1E2329', paddingTop: 4, fontSize: 10, color: '#4A5568' }}>{quote.signedDate ? quote.signedDate.slice(0, 10) : ''}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom sticky bar */}
        <div style={{
          flexShrink: 0, padding: '10px 12px',
          background: '#161B22', borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <button
            onClick={() => { onSave(quote); showToast('Draft saved.'); }}
            style={{
              flex: 1, padding: '12px', minHeight: 44,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 8, color: '#D1D5DB', fontWeight: 700, fontSize: 13,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
          >Save Draft</button>
          <button
            onClick={() => setShowSendPreview(true)}
            style={{
              flex: 2, padding: '12px', minHeight: 44,
              background: 'linear-gradient(135deg, #E8722A, #e8640c)',
              border: 'none', borderRadius: 8,
              color: '#fff', fontWeight: 700, fontSize: 14,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 2px 10px rgba(249,115,22,0.3)',
            }}
          >Send to Customer →</button>
        </div>

        {/* Pickers */}
        {showCatalogPicker && (() => {
          const sec = quote.sections.find(s => s.id === showCatalogPicker.sectionId);
          return (
            <LineItemPickerDrawer
              mode={showCatalogPicker.mode}
              sectionName={sec?.sectionName || sec?.scopeType || 'section'}
              onPick={(items) => {
                items.forEach(entry => {
                  // entry is either a catalog row or a fully-formed line item
                  // from the custom form. Detect by presence of `extension`.
                  if (entry && Object.prototype.hasOwnProperty.call(entry, 'extension')) {
                    addLineItemToSection(showCatalogPicker.sectionId, entry);
                  } else {
                    const qty = getSmartDefaultQty(entry, quote.measurements);
                    addLineItemToSection(showCatalogPicker.sectionId, makeLineItem(entry, qty));
                  }
                });
              }}
              onClose={() => setShowCatalogPicker(null)}
            />
          );
        })()}
        {showSectionPicker && (
          <SectionTemplatePicker
            mode={quote.mode}
            onPick={(template) => {
              const sec = instantiateSectionFromTemplate(template);
              if (sec) addSection(sec);
            }}
            onPickBlank={() => {
              const id = `qs-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
              addSection({
                id, scopeId: null, scopeType: 'custom',
                sectionName: 'New Section', narrative: '',
                lineItems: [], subtotal: 0,
              });
            }}
            onClose={() => setShowSectionPicker(false)}
          />
        )}
        {showTemplateMgr && (
          <QuoteTemplateManager onClose={() => setShowTemplateMgr(false)} />
        )}
        {showAIDraft && (
          <AIDraftPanel
            target={target}
            currentQuote={quote}
            onApply={({ kind, preview }) => {
              if (!preview || !Array.isArray(preview.sections)) return;
              // Re-key incoming sections + line items to avoid id collisions
              const stamp = (sec) => {
                const id = `qs-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
                const lineItems = (sec.lineItems || []).map(li => ({
                  ...li,
                  id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                  extension: Math.round((Number(li.quantity) || 0) * (Number(li.unitPrice) || 0) * 100) / 100,
                }));
                const subtotal = lineItems.reduce((s, li) => s + (li.extension || 0), 0);
                return { ...sec, id, lineItems, subtotal: Math.round(subtotal * 100) / 100 };
              };
              const incoming = preview.sections.map(stamp);
              setQuote(prev => {
                const sections = kind === 'replace' ? incoming : [...prev.sections, ...incoming];
                const subtotal = sections.reduce((s, sec) => s + (sec.subtotal || 0), 0);
                const tax = prev.tax || 0;
                return {
                  ...prev,
                  sections,
                  subtotal: Math.round(subtotal * 100) / 100,
                  tax,
                  total: Math.round((subtotal + tax) * 100) / 100,
                  // Carry over AI-suggested mode-level fields only when replacing
                  ...(kind === 'replace' && preview.terms ? { terms: preview.terms } : {}),
                  ...(kind === 'replace' && Array.isArray(preview.exclusions) && preview.exclusions.length ? { exclusions: preview.exclusions } : {}),
                };
              });
              setShowAIDraft(false);
              showToast(kind === 'replace' ? 'Quote replaced with AI draft.' : 'AI sections added to quote.');
            }}
            onClose={() => setShowAIDraft(false)}
          />
        )}
        {showSendPreview && (
          <CustomerSendPreview
            quote={quote}
            target={target}
            onClose={() => setShowSendPreview(false)}
          />
        )}
        {showPdfPreview && (
          <PdfPreviewModal
            quote={quote}
            target={target}
            onClose={() => setShowPdfPreview(false)}
          />
        )}
      </div>
    </div>
  );
}

// ─── Customer Send Preview ──────────────────────────────────────────────────
// Stands in for the "Send to Customer" production flow that ships later. Walks
// the prospect through every step of what their customer would see: email →
// share link → signature & deposit → lead-to-project conversion. Does not
// actually send anything; ends with a "Close Preview" CTA.
// ─── PDF Preview Modal ──────────────────────────────────────────────────────
// Customer-facing PDF preview. Renders the quote document the way a customer
// would see it: company header, bill-to, sections grouped with subtotals,
// optional sections clearly marked + excluded from the grand total, warranty
// and payment terms at the bottom. Margins are NEVER rendered here — they
// only live in the editor view.
function PdfPreviewModal({ quote, target, onClose }) {
  useScrollLock();
  const isMobile = useMobile();

  const billToName = target.kind === 'lead'
    ? (target.lead?.name || target.customerName)
    : target.customerName;
  const billToAddress = target.address || '';

  const sections = quote.sections || [];
  const grandTotal = sections
    .filter(s => !s.optional)
    .reduce((sum, sec) => sum + (sec.subtotal || 0), 0);
  const optionalTotal = sections
    .filter(s => s.optional)
    .reduce((sum, sec) => sum + (sec.subtotal || 0), 0);

  // Find a "Warranty & Terms" section so we can render its narrative at the
  // bottom of the PDF instead of mid-document. Falls back to the quote-level
  // terms string if no such section exists.
  const warrantySection = sections.find(s =>
    (s.sectionName || '').toLowerCase().includes('warranty'));
  // Required sections render up top (and roll into Grand Total). Optional
  // sections render in a dedicated "Optional Add-Ons" group below the total
  // so the customer reads them as add-on riders, not part of the bid.
  const requiredSections = sections.filter(s => s !== warrantySection && !s.optional);
  const optionalSections = sections.filter(s => s !== warrantySection && s.optional);

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200,
    display: 'flex', alignItems: 'stretch', justifyContent: 'center', padding: 0,
  };
  const modal = {
    background: '#2A2E36', width: '100%', maxWidth: 820,
    height: '100dvh', maxHeight: '100dvh',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Top bar */}
        <div style={{
          flexShrink: 0, padding: '12px 14px',
          background: '#161B22', borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36, height: 36, padding: 0,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8, color: '#FFFFFF', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent',
            }}
          >×</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF' }}>Customer PDF Preview</div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>This is exactly what your customer will see</div>
          </div>
        </div>

        {/* Document body (scrollable) */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
          padding: isMobile ? '12px 10px' : '24px',
          background: '#2A2E36',
        }}>
          <div style={{
            background: '#FFFFFF', color: '#1E2329',
            margin: '0 auto', maxWidth: 720,
            padding: isMobile ? '20px 16px' : '36px 40px',
            borderRadius: 4,
            boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
          }}>
            {/* Company header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, letterSpacing: '-0.5px', color: '#1E2329', marginBottom: 4 }}>
                  {COMPANY_PROFILE.name}
                </div>
                <div style={{ fontSize: 11, color: '#4A5568', lineHeight: 1.5 }}>
                  {COMPANY_PROFILE.address}<br />
                  {COMPANY_PROFILE.phone} · {COMPANY_PROFILE.email}<br />
                  {COMPANY_PROFILE.license}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quote</div>
                <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: '#1E2329' }}>{quote.quoteNumber}</div>
                <div style={{ fontSize: 10, color: '#4A5568', marginTop: 4 }}>Valid through {quote.expirationDate}</div>
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '2px solid #1E2329', margin: '14px 0 18px' }} />

            {/* Bill To */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Prepared For</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1E2329' }}>{billToName}</div>
              {billToAddress && <div style={{ fontSize: 11, color: '#4A5568' }}>{billToAddress}</div>}
            </div>

            {/* Required sections — these roll into the Grand Total */}
            {requiredSections.map((sec, idx) => (
              <PdfSectionBlock key={sec.id || `req-${idx}`} sec={sec} />
            ))}

            {/* Totals */}
            <div style={{
              background: '#F8F4EC', border: '2px solid #1E2329',
              borderRadius: 4, padding: '14px 16px',
              marginTop: 18, marginBottom: 18,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#1E2329' }}>Total</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: '#1E2329' }}>{fmtCurrency(grandTotal)}</span>
              </div>
            </div>

            {/* Optional Add-Ons — grouped at the bottom, NOT in Grand Total */}
            {optionalSections.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{
                  background: 'rgba(232,114,42,0.08)',
                  border: '1px dashed rgba(232,114,42,0.55)',
                  borderRadius: 4, padding: '10px 14px', marginBottom: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Optional Add-Ons
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#c2410c', whiteSpace: 'nowrap' }}>
                      {fmtCurrency(optionalTotal)} combined
                    </div>
                  </div>
                </div>
                {optionalSections.map((sec, idx) => (
                  <PdfSectionBlock key={sec.id || `opt-${idx}`} sec={sec} optional />
                ))}
                <div style={{
                  marginTop: 8, padding: '10px 14px',
                  fontSize: 11, color: '#4A5568', fontStyle: 'italic', lineHeight: 1.55,
                  borderTop: '1px dashed rgba(30,35,41,0.2)',
                }}>
                  Optional add-ons are not included in the total above. Add any item by signing the optional add-on rider below the total.
                </div>
              </div>
            )}

            {/* Warranty & Terms at bottom */}
            <div style={{
              paddingTop: 14, marginTop: 14,
              borderTop: '1px dashed rgba(30,35,41,0.25)',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                Warranty &amp; Terms
              </div>
              <div style={{ fontSize: 11, color: '#1E2329', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {warrantySection?.narrative || quote.terms || '—'}
              </div>
            </div>

            {/* Exclusions */}
            {(quote.exclusions || []).length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                  Not Included
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: '#4A5568', lineHeight: 1.6 }}>
                  {quote.exclusions.map((ex, i) => <li key={i}>{ex}</li>)}
                </ul>
              </div>
            )}

            {/* Signature block */}
            <div style={{
              marginTop: 28, paddingTop: 18,
              borderTop: '1px dashed rgba(30,35,41,0.3)',
              display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
            }}>
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 28 }}>Customer Signature</div>
                <div style={{ borderTop: '1px solid #1E2329', paddingTop: 4, fontSize: 10, color: '#4A5568' }}>Sign on file</div>
              </div>
              <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 28 }}>Date</div>
                <div style={{ borderTop: '1px solid #1E2329', paddingTop: 4, fontSize: 10, color: '#4A5568' }}>{quote.signedDate ? quote.signedDate.slice(0, 10) : ''}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          flexShrink: 0, padding: 12,
          background: '#161B22', borderTop: '1px solid rgba(255,255,255,0.08)',
        }}>
          <button
            onClick={onClose}
            style={{
              width: '100%', padding: '14px', minHeight: 48,
              background: 'linear-gradient(135deg, #E8722A, #e8640c)',
              border: 'none', borderRadius: 8,
              color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 2px 10px rgba(249,115,22,0.3)',
            }}
          >Done</button>
        </div>
      </div>
    </div>
  );
}

// Shared section-block renderer for the customer PDF. Used twice in
// PdfPreviewModal: once for required sections (above the Grand Total) and
// once for optional sections (in the "Optional Add-Ons" group below).
function PdfSectionBlock({ sec, optional = false }) {
  return (
    <div style={{
      marginBottom: 16,
      padding: 12,
      background: optional ? '#FBF5EE' : 'transparent',
      border: optional ? '1px dashed rgba(232,114,42,0.5)' : '1px solid rgba(30,35,41,0.1)',
      borderRadius: 4,
      marginLeft: sec.isSubsection ? 16 : 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#1E2329' }}>{sec.sectionName}</span>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#1E2329', whiteSpace: 'nowrap' }}>
          {fmtCurrency(sec.subtotal || 0)}
        </div>
      </div>
      {sec.narrative && (
        <div style={{ fontSize: 11, color: '#4A5568', lineHeight: 1.55, marginBottom: 8, whiteSpace: 'pre-wrap' }}>
          {sec.narrative}
        </div>
      )}
      {(sec.lineItems || []).length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <tbody>
            {sec.lineItems.map(li => (
              <tr key={li.id} style={{ borderTop: '1px solid rgba(30,35,41,0.06)' }}>
                <td style={{ padding: '4px 6px', color: '#1E2329' }}>
                  {li.description}
                  {li.isAllowance && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                      background: 'rgba(245,158,11,0.18)', color: '#b45309',
                      marginLeft: 6, letterSpacing: '0.3px',
                    }}>Allowance</span>
                  )}
                </td>
                <td style={{ padding: '4px 6px', textAlign: 'right', color: '#4A5568', whiteSpace: 'nowrap' }}>
                  {Number(li.quantity) || 0} {li.unit}
                </td>
                <td style={{ padding: '4px 6px', textAlign: 'right', color: '#1E2329', whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {fmtCurrency(li.extension || 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CustomerSendPreview({ quote, target, onClose }) {
  useScrollLock();
  const isMobile = useMobile();

  const companyName = 'Equity Roofing';
  const companyDomain = 'equityroofing.com';
  const companyPhone = '(570) 966-7300';
  const salesperson = (target.lead && target.lead.assignedSalesperson)
    || (target.project && target.project.assignedSalesperson)
    || 'Mark Sensenig';
  const customer = target.lead || target.project || {};
  const customerName = target.customerName || customer.name || customer.customerName || 'Customer';
  const customerFirstName = customerName.split(/\s+/)[0];
  const customerEmail = customer.email || customer.customerEmail || 'customer@example.com';
  const address = target.address || customer.address || '';
  const total = (quote.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const validThrough = quote.expirationDate || '—';
  const shareToken = quote.shareToken || 'qst_preview';
  const depositAmount = quote.depositAmount || Math.round((quote.total || 0) * 0.30);
  const depositDisplay = depositAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200,
    display: 'flex', alignItems: 'stretch', justifyContent: 'center', padding: 0,
  };
  const modal = {
    background: '#1E2329', width: '100%', maxWidth: 640,
    height: '100dvh', maxHeight: '100dvh',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    boxShadow: '0 0 40px rgba(0,0,0,0.6)',
  };

  const StepHeader = ({ n, label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        background: 'linear-gradient(135deg, #E8722A, #e8640c)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: 12, fontWeight: 800, flexShrink: 0,
      }}>{n}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#D1D5DB', letterSpacing: '0.3px' }}>{label}</div>
    </div>
  );

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Top bar */}
        <div style={{
          flexShrink: 0, padding: '12px 14px',
          background: '#161B22', borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36, height: 36, padding: 0,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8, color: '#FFFFFF', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent',
            }}
          >×</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF' }}>Customer Send Preview</div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>Here's what would happen in your production environment</div>
          </div>
        </div>

        {/* Body */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
          padding: isMobile ? '14px 12px' : '20px 24px',
          background: '#1E2329',
        }}>

          {/* Step 1 — Email */}
          <div style={{ marginBottom: 18 }}>
            <StepHeader n="1" label={`We'd send this email to ${customerEmail}:`} />
            <div style={{
              background: '#FFFFFF', borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
              overflow: 'hidden',
            }}>
              <div style={{ padding: '12px 14px', background: '#F8F4EC', borderBottom: '1px solid rgba(30,35,41,0.12)' }}>
                <div style={{ fontSize: 11, color: '#4A5568', lineHeight: 1.6 }}>
                  <div><strong>From:</strong> {companyName} &lt;quotes@{companyDomain}&gt;</div>
                  <div><strong>To:</strong> {customerEmail}</div>
                  <div><strong>Subject:</strong> Your roofing quote — {quote.quoteNumber}</div>
                </div>
              </div>
              <div style={{ padding: '14px 16px', fontSize: 13, color: '#1E2329', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
{`Hi ${customerFirstName},

Attached is your quote for the roofing work we discussed at ${address || 'your property'}. Total: $${total}. Valid through ${validThrough}.

Review and sign your quote here: ridgeos.app/q/${shareToken.slice(0, 14)}

Questions? Reply to this email or call ${companyPhone}.

— ${salesperson}
${companyName}`}
              </div>
            </div>
          </div>

          {/* Step 2 — Share Page */}
          <div style={{ marginBottom: 18 }}>
            <StepHeader n="2" label="Customer clicks the link and sees this:" />
            <div style={{
              background: '#FFFFFF', borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
              overflow: 'hidden',
            }}>
              <div style={{ padding: '14px 16px', background: '#1E2329', textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#E8722A', letterSpacing: '-0.3px' }}>{companyName.toUpperCase()}</div>
                <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>Mifflinburg, PA · {companyPhone}</div>
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Quote for</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1E2329' }}>{customerName}</div>
                {address && <div style={{ fontSize: 12, color: '#4A5568', marginTop: 2 }}>{address}</div>}
                <hr style={{ border: 'none', borderTop: '1px solid rgba(30,35,41,0.12)', margin: '12px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: '#4A5568' }}>Quote #</span>
                  <span style={{ fontSize: 12, color: '#1E2329', fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>{quote.quoteNumber}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: '#4A5568' }}>Sections</span>
                  <span style={{ fontSize: 12, color: '#1E2329', fontWeight: 700 }}>{(quote.sections || []).length}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: '#4A5568' }}>Line items</span>
                  <span style={{ fontSize: 12, color: '#1E2329', fontWeight: 700 }}>
                    {(quote.sections || []).reduce((s, sec) => s + (sec.lineItems || []).length, 0)}
                  </span>
                </div>
                <hr style={{ border: 'none', borderTop: '1px solid rgba(30,35,41,0.12)', margin: '10px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: '#4A5568' }}>Total</span>
                  <span style={{ fontSize: 24, fontWeight: 800, color: '#1E2329' }}>${total}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button
                    disabled
                    style={{
                      flex: 1, padding: '12px', minHeight: 44,
                      background: 'transparent', border: '1px solid rgba(30,35,41,0.2)',
                      borderRadius: 8, color: '#1E2329', fontSize: 12, fontWeight: 700,
                      cursor: 'not-allowed',
                    }}
                  >Review Full Quote</button>
                  <button
                    disabled
                    style={{
                      flex: 1, padding: '12px', minHeight: 44,
                      background: 'linear-gradient(135deg, #E8722A, #e8640c)',
                      border: 'none', borderRadius: 8,
                      color: '#fff', fontSize: 12, fontWeight: 800,
                      cursor: 'not-allowed', opacity: 0.95,
                    }}
                  >Sign &amp; Accept</button>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 — Sign + Pay */}
          <div style={{ marginBottom: 18 }}>
            <StepHeader n="3" label="Customer signs and pays deposit:" />
            <div style={{
              background: '#FFFFFF', borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
              padding: 16,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Sign here</div>
              <div style={{
                height: 72, background: '#F8F4EC',
                border: '2px dashed rgba(30,35,41,0.2)', borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: '"Brush Script MT", cursive', fontSize: 28,
                color: '#1E2329', fontStyle: 'italic',
              }}>
                {customerName}
              </div>
              <div style={{ fontSize: 10, color: '#4A5568', marginTop: 4, textAlign: 'center' }}>
                Customer signature
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid rgba(30,35,41,0.12)', margin: '14px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: '#4A5568' }}>Deposit (30%)</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#1E2329' }}>${depositDisplay}</span>
              </div>
              <button
                disabled
                style={{
                  width: '100%', padding: '12px', minHeight: 48,
                  background: '#635BFF', border: 'none', borderRadius: 8,
                  color: '#fff', fontSize: 14, fontWeight: 700,
                  cursor: 'not-allowed', opacity: 0.95,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <span>Pay ${depositDisplay}</span>
                <span style={{ fontSize: 10, opacity: 0.7 }}>powered by Stripe</span>
              </button>
            </div>
          </div>

          {/* Step 4 — Lead → Project */}
          <div style={{ marginBottom: 18 }}>
            <StepHeader n="4" label="Quote becomes a project:" />
            <div style={{
              background: '#2A3140', borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.08)',
              padding: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 14 }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '6px 12px', borderRadius: 999,
                  background: 'rgba(232,114,42,0.18)', color: '#E8722A',
                  textTransform: 'uppercase', letterSpacing: '0.4px',
                }}>Lead</span>
                <span style={{ fontSize: 18, color: '#9CA3AF' }}>→</span>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '6px 12px', borderRadius: 999,
                  background: 'rgba(59,130,246,0.18)', color: '#60a5fa',
                  textTransform: 'uppercase', letterSpacing: '0.4px',
                }}>Project</span>
              </div>
              <div style={{ fontSize: 12, color: '#D1D5DB', lineHeight: 1.55 }}>
                The signed quote automatically converts this lead to a project.
                The new project appears in your Projects tab with scopes ready
                to schedule, crew assignments ready to make, and the signed
                contract on file.
              </div>
            </div>
          </div>

          <div style={{
            fontSize: 11, fontStyle: 'italic', color: '#9CA3AF',
            textAlign: 'center', padding: '12px 8px', lineHeight: 1.5,
          }}>
            Customer Send launches with the next product update. Reach out if you want early access for your team.
          </div>
        </div>

        {/* Footer */}
        <div style={{
          flexShrink: 0, padding: 12,
          background: '#161B22', borderTop: '1px solid rgba(255,255,255,0.08)',
        }}>
          <button
            onClick={onClose}
            style={{
              width: '100%', padding: '14px', minHeight: 48,
              background: 'linear-gradient(135deg, #E8722A, #e8640c)',
              border: 'none', borderRadius: 8,
              color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 2px 10px rgba(249,115,22,0.3)',
            }}
          >Close Preview</button>
        </div>
      </div>
    </div>
  );
}

// ── Section block (header + bundle pills + line-items + notes) ──
function QuoteSectionBlock({ section, index, totalSections, isMobile, cellInput, cellInputFocus, measurements, quoteMode, onRename, onUpdateNarrative, onUpdateLineItem, onRemoveLineItem, onAddLineItem, onAddBundle, onOpenAIDraft, onRemoveSection, onMoveUp, onMoveDown, onToggleOptional, onToggleCollapse, onDuplicate, onAddSubsection }) {
  const [secMenuOpen, setSecMenuOpen] = useState(false);
  const [focusedCell, setFocusedCell] = useState(null);
  const [editingLi, setEditingLi] = useState(null); // line-item being edited in card drawer
  const narrow = useNarrowMobile();

  // Bundles that target this section's scopeType. Merged seeded + user-saved.
  const sectionBundles = useMemo(() => {
    const all = getAllBundles();
    return all.filter(b => b.parentScopeType === section.scopeType);
  }, [section.scopeType]);

  const cellStyleFor = (cellKey) => focusedCell === cellKey
    ? { ...cellInput, ...cellInputFocus }
    : cellInput;

  const isOptional = !!section.optional;
  const isCollapsed = !!section.collapsed;
  const isSubsection = !!section.isSubsection;
  const menuItem = {
    padding: '8px 10px', background: 'transparent', border: 'none',
    borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    textAlign: 'left',
  };

  return (
    <div style={{
      background: '#FFFFFF',
      border: isOptional ? '1px dashed rgba(232,114,42,0.55)' : '1px solid #1E2329',
      borderRadius: 6,
      padding: isMobile ? 10 : 14,
      marginBottom: 12,
      marginLeft: isSubsection ? (isMobile ? 12 : 22) : 0,
      position: 'relative',
      opacity: isOptional ? 0.92 : 1,
    }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isCollapsed ? 0 : 10, flexWrap: 'wrap' }}>
        <button
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
          style={{
            width: 28, height: 28, padding: 0, flexShrink: 0,
            background: 'transparent', border: 'none',
            color: '#1E2329', fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            WebkitTapHighlightColor: 'transparent',
          }}
        >{isCollapsed ? '▶' : '▼'}</button>
        {isSubsection && (
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8,
            background: 'rgba(99,102,241,0.15)', color: '#6366f1',
            textTransform: 'uppercase', letterSpacing: '0.4px', flexShrink: 0,
          }}>Sub</span>
        )}
        {isOptional && (
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8,
            background: 'rgba(232,114,42,0.15)', color: '#E8722A',
            textTransform: 'uppercase', letterSpacing: '0.4px', flexShrink: 0,
          }}>Optional add-on</span>
        )}
        <input
          value={section.sectionName || section.scopeType || 'Section'}
          onChange={e => onRename(e.target.value)}
          style={{
            flex: 1, minWidth: 140, padding: '6px 8px',
            background: 'transparent', border: '1px solid transparent', borderRadius: 4,
            color: '#1E2329', fontSize: 15, fontWeight: 800, fontFamily: 'inherit',
            outline: 'none',
          }}
          onFocus={e => { e.target.style.background = '#F8F4EC'; e.target.style.border = '1px solid #E8722A'; }}
          onBlur={e => { e.target.style.background = 'transparent'; e.target.style.border = '1px solid transparent'; }}
        />
        <div style={{ fontSize: 13, fontWeight: 800, color: '#1E2329', whiteSpace: 'nowrap' }}>{fmtCurrency(section.subtotal || 0)}</div>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setSecMenuOpen(v => !v)}
            aria-label="Section menu"
            style={{
              width: 32, height: 32, padding: 0,
              background: secMenuOpen ? 'rgba(30,35,41,0.08)' : 'transparent',
              border: '1px solid rgba(30,35,41,0.15)', borderRadius: 6,
              color: '#1E2329', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >⋯</button>
          {secMenuOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', right: 0,
              background: '#FFFFFF', border: '1px solid #1E2329', borderRadius: 8,
              padding: 4, minWidth: 200, zIndex: 6,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              display: 'flex', flexDirection: 'column', gap: 2,
            }}>
              <button
                onClick={() => { setSecMenuOpen(false); onToggleOptional && onToggleOptional(); }}
                style={{ ...menuItem, color: isOptional ? '#E8722A' : '#1E2329' }}
              >{isOptional ? '✓ Marked Optional' : 'Mark Optional'}</button>
              <button
                onClick={() => { setSecMenuOpen(false); onDuplicate && onDuplicate(); }}
                style={{ ...menuItem, color: '#1E2329' }}
              >⎘ Duplicate</button>
              <button
                onClick={() => { setSecMenuOpen(false); onAddSubsection && onAddSubsection(); }}
                style={{ ...menuItem, color: '#1E2329' }}
              >+ Add sub-section</button>
              <div style={{ height: 1, background: 'rgba(30,35,41,0.1)', margin: '4px 0' }} />
              <button
                disabled={index === 0}
                onClick={() => { setSecMenuOpen(false); onMoveUp(); }}
                style={{ ...menuItem, color: index === 0 ? '#9CA3AF' : '#1E2329', cursor: index === 0 ? 'not-allowed' : 'pointer' }}
              >↑ Move up</button>
              <button
                disabled={index === totalSections - 1}
                onClick={() => { setSecMenuOpen(false); onMoveDown(); }}
                style={{ ...menuItem, color: index === totalSections - 1 ? '#9CA3AF' : '#1E2329', cursor: index === totalSections - 1 ? 'not-allowed' : 'pointer' }}
              >↓ Move down</button>
              <div style={{ height: 1, background: 'rgba(30,35,41,0.1)', margin: '4px 0' }} />
              <button
                onClick={() => {
                  setSecMenuOpen(false);
                  // eslint-disable-next-line no-restricted-globals
                  if (window.confirm('Delete this section?')) onRemoveSection();
                }}
                style={{ ...menuItem, color: '#ef4444', fontWeight: 700 }}
              >× Delete section</button>
            </div>
          )}
        </div>
      </div>

      {/* Everything below the header is hidden when collapsed. Subtotal + pill
          stay visible above so the row reads like a single-line collapsed
          summary. */}
      {!isCollapsed && (<>

      {/* Quick Bundle pills — single-tap drops a packaged set of items in */}
      {sectionBundles.length > 0 && (
        <div style={{
          display: 'flex', gap: 6, marginBottom: 10,
          overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          paddingBottom: 4,
        }}>
          {sectionBundles.map(b => (
            <button
              key={b.id}
              onClick={() => onAddBundle && onAddBundle(b)}
              style={{
                flexShrink: 0,
                padding: '8px 12px', minHeight: 36,
                background: 'rgba(232,114,42,0.08)',
                border: '1px solid rgba(232,114,42,0.35)',
                borderRadius: 999,
                color: '#1E2329', fontSize: 12, fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                WebkitTapHighlightColor: 'transparent',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
              title={`Adds ${b.lineItems.length} line items`}
            >
              <span style={{ color: '#E8722A', fontWeight: 800 }}>+</span>
              <span>{b.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Empty section state when no line items — friendly 3-way CTA */}
      {section.lineItems.length === 0 && (
        <div style={{
          padding: '20px 12px', marginBottom: 10,
          background: '#F8F4EC', border: '1px dashed rgba(30,35,41,0.2)', borderRadius: 6,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#4A5568', marginBottom: 12, textAlign: 'center' }}>
            This section is empty. Get started:
          </div>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
            <button
              onClick={onAddLineItem}
              style={{
                flex: 1, padding: '12px', minHeight: 44,
                background: '#FFFFFF', border: '1px solid #1E2329', borderRadius: 8,
                color: '#1E2329', fontSize: 12, fontWeight: 700,
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              }}
            >📚 Pick from Library</button>
            {sectionBundles.length > 0 && (
              <button
                onClick={() => onAddBundle && onAddBundle(sectionBundles[0])}
                style={{
                  flex: 1, padding: '12px', minHeight: 44,
                  background: '#FFFFFF', border: '1px solid #1E2329', borderRadius: 8,
                  color: '#1E2329', fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                }}
                title={`Adds ${sectionBundles[0].name}`}
              >⚡ Use a Quick Bundle</button>
            )}
            {onOpenAIDraft && (
              <button
                onClick={onOpenAIDraft}
                style={{
                  flex: 1, padding: '12px', minHeight: 44,
                  background: 'linear-gradient(135deg, #E8722A, #e8640c)',
                  border: 'none', borderRadius: 8,
                  color: '#fff', fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                }}
              >✦ Describe the Job</button>
            )}
          </div>
        </div>
      )}

      {/* Line items — stacked cards under 480px, table at 480px+ */}
      {narrow ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {section.lineItems.map(li => (
            <LineItemCard
              key={li.id}
              item={li}
              onTap={() => setEditingLi(li.id)}
              onRemove={() => onRemoveLineItem(li.id)}
            />
          ))}
        </div>
      ) : section.lineItems.length === 0 ? null : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: 8 }}>
          <table style={{
            width: '100%', minWidth: 520,
            borderCollapse: 'collapse', fontSize: 12,
          }}>
            <thead>
              <tr style={{ background: '#F8F4EC', borderBottom: '1.5px solid #1E2329' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#1E2329', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Description</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#1E2329', textTransform: 'uppercase', letterSpacing: '0.4px', width: 60 }}>Qty</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#1E2329', textTransform: 'uppercase', letterSpacing: '0.4px', width: 50 }}>Unit</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#1E2329', textTransform: 'uppercase', letterSpacing: '0.4px', width: 90 }}>Unit Price</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#1E2329', textTransform: 'uppercase', letterSpacing: '0.4px', width: 100 }}>Extension</th>
                <th style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {section.lineItems.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '20px 12px', textAlign: 'center', fontSize: 12, color: '#4A5568' }}>
                    Add line items to this section.
                  </td>
                </tr>
              )}
              {section.lineItems.map(li => (
                <tr key={li.id} style={{ borderBottom: '1px solid rgba(30,35,41,0.08)' }}>
                  <td style={{ padding: '4px 4px' }}>
                    <input
                      value={li.description}
                      onChange={e => onUpdateLineItem(li.id, { description: e.target.value })}
                      style={cellStyleFor(`${li.id}-desc`)}
                      onFocus={() => setFocusedCell(`${li.id}-desc`)}
                      onBlur={() => setFocusedCell(null)}
                    />
                  </td>
                  <td style={{ padding: '4px 4px' }}>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={li.quantity}
                      onChange={e => onUpdateLineItem(li.id, { quantity: e.target.value })}
                      style={{ ...cellStyleFor(`${li.id}-qty`), textAlign: 'right' }}
                      onFocus={() => setFocusedCell(`${li.id}-qty`)}
                      onBlur={() => setFocusedCell(null)}
                    />
                  </td>
                  <td style={{ padding: '4px 4px' }}>
                    <input
                      value={li.unit}
                      onChange={e => onUpdateLineItem(li.id, { unit: e.target.value })}
                      style={cellStyleFor(`${li.id}-unit`)}
                      onFocus={() => setFocusedCell(`${li.id}-unit`)}
                      onBlur={() => setFocusedCell(null)}
                    />
                  </td>
                  <td style={{ padding: '4px 4px' }}>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={li.unitPrice}
                      onChange={e => onUpdateLineItem(li.id, { unitPrice: e.target.value })}
                      style={{ ...cellStyleFor(`${li.id}-price`), textAlign: 'right' }}
                      onFocus={() => setFocusedCell(`${li.id}-price`)}
                      onBlur={() => setFocusedCell(null)}
                    />
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#1E2329', whiteSpace: 'nowrap' }}>
                    <div>{fmtCurrency(li.extension || 0)}</div>
                    {li.margin != null && (
                      <div style={{
                        fontSize: 9, fontWeight: 700, color: '#6b7280',
                        marginTop: 2, letterSpacing: '0.3px',
                      }} title="Internal margin — not shown on the customer PDF">
                        m {Number(li.margin)}%
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '4px 4px', textAlign: 'center' }}>
                    <button
                      onClick={() => onRemoveLineItem(li.id)}
                      aria-label="Remove line item"
                      style={{
                        width: isMobile ? 36 : 28, height: isMobile ? 36 : 28,
                        padding: 0, background: 'transparent', border: 'none',
                        color: '#9CA3AF', fontSize: 18, cursor: 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button
        onClick={onAddLineItem}
        style={{
          padding: isMobile ? '10px 16px' : '6px 12px',
          minHeight: isMobile ? 40 : 32, marginTop: 4, marginBottom: 8,
          background: 'transparent', border: '1px dashed rgba(30,35,41,0.3)',
          borderRadius: 4, color: '#1E2329', fontSize: isMobile ? 13 : 12, fontWeight: 700,
          cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
        }}
      >+ Add Line</button>

      <div style={{ marginTop: 4 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Section Notes</div>
        <textarea
          value={section.narrative || ''}
          onChange={e => onUpdateNarrative(e.target.value)}
          rows={2}
          placeholder="What's included in this scope…"
          style={{
            width: '100%', padding: '8px 10px',
            background: '#FFFFFF', border: '1px solid rgba(30,35,41,0.15)', borderRadius: 4,
            color: '#1E2329', fontSize: isMobile ? 16 : 12, lineHeight: 1.4,
            fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box',
          }}
        />
      </div>

      </>)}

      {editingLi && (
        <LineItemEditDrawer
          item={section.lineItems.find(li => li.id === editingLi)}
          onSave={(patch) => { onUpdateLineItem(editingLi, patch); setEditingLi(null); }}
          onClose={() => setEditingLi(null)}
          onRemove={() => { onRemoveLineItem(editingLi); setEditingLi(null); }}
        />
      )}
    </div>
  );
}

// Card row for narrow-mobile line items. Tap → opens edit drawer; swipe left →
// reveals delete button.
function LineItemCard({ item, onTap, onRemove }) {
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startXRef = useRef(0);
  const movedRef = useRef(false);

  const REVEAL = 76;
  const onPointerDown = (e) => {
    setDragging(true);
    movedRef.current = false;
    startXRef.current = e.clientX;
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - startXRef.current;
    if (Math.abs(dx) > 4) movedRef.current = true;
    // Only allow left-swipe (negative dx) to reveal delete; clamp.
    setDragX(Math.min(0, Math.max(-REVEAL, dx)));
  };
  const onPointerUp = () => {
    setDragging(false);
    setDragX(prev => (prev < -REVEAL / 2 ? -REVEAL : 0));
  };
  const handleClick = () => {
    if (movedRef.current) { movedRef.current = false; return; }
    if (dragX < 0) { setDragX(0); return; }
    onTap();
  };

  return (
    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 6 }}>
      {/* Delete button revealed on swipe left */}
      <button
        onClick={onRemove}
        aria-label="Delete line item"
        style={{
          position: 'absolute', right: 0, top: 0, bottom: 0, width: REVEAL,
          background: '#ef4444', border: 'none', color: '#fff',
          fontSize: 13, fontWeight: 700, cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >Delete</button>
      {/* Card */}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={handleClick}
        style={{
          padding: '10px 12px', background: '#FFFFFF',
          border: '1px solid rgba(30,35,41,0.12)', borderRadius: 6,
          cursor: 'pointer', userSelect: 'none', touchAction: 'pan-y',
          transform: `translateX(${dragX}px)`,
          transition: dragging ? 'none' : 'transform 0.18s ease',
          position: 'relative', zIndex: 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: '#1E2329', fontWeight: 600, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
              {item.description || '(no description)'}
              {item.isAllowance && (
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                  background: 'rgba(245,158,11,0.18)', color: '#b45309',
                  textTransform: 'uppercase', letterSpacing: '0.4px', marginLeft: 6,
                }}>Allowance</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#4A5568', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span>{Number(item.quantity) || 0} × ${(Number(item.unitPrice) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {item.unit}</span>
              {item.margin != null && (
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                  background: 'rgba(30,35,41,0.07)', color: '#6b7280',
                  letterSpacing: '0.3px',
                }} title="Internal margin — not shown on the customer PDF">
                  m {Number(item.margin)}%
                </span>
              )}
            </div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#1E2329', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {fmtCurrency(item.extension || 0)}
          </div>
        </div>
      </div>
    </div>
  );
}

// Bottom-sheet edit drawer for a single line item on narrow-mobile.
function LineItemEditDrawer({ item, onSave, onClose, onRemove }) {
  useScrollLock();
  const [draft, setDraft] = useState({
    description: item?.description || '',
    quantity: item?.quantity ?? 0,
    unit: item?.unit || 'EA',
    unitPrice: item?.unitPrice ?? 0,
    notes: item?.notes || '',
    margin: item?.margin != null ? item.margin : DEFAULT_MARGIN,
  });

  const inputStyle = {
    width: '100%', padding: '12px 14px', minHeight: 44,
    background: '#FFFFFF', border: '1px solid rgba(30,35,41,0.18)', borderRadius: 6,
    color: '#1E2329', fontSize: 16, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };
  const label = { fontSize: 10, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#F8F4EC', width: '100%', maxWidth: 520,
          borderRadius: '16px 16px 0 0', maxHeight: '86dvh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 -8px 24px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{
          flexShrink: 0, padding: '14px 16px',
          background: '#FFFFFF', borderBottom: '1px solid rgba(30,35,41,0.12)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: '#1E2329' }}>Edit Line Item</div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36, height: 36, padding: 0,
              background: 'transparent', border: '1px solid rgba(30,35,41,0.18)',
              borderRadius: 8, color: '#1E2329', fontSize: 18, cursor: 'pointer',
            }}
          >×</button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={label}>Description</div>
            <textarea
              value={draft.description}
              onChange={e => setDraft(p => ({ ...p, description: e.target.value }))}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={label}>Qty</div>
              <input
                type="number"
                inputMode="decimal"
                value={draft.quantity}
                onChange={e => setDraft(p => ({ ...p, quantity: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <div style={label}>Unit</div>
              <select
                value={draft.unit}
                onChange={e => setDraft(p => ({ ...p, unit: e.target.value }))}
                style={inputStyle}
              >
                {['EA', 'SF', 'LF', 'SQ', 'CY', 'TON', 'HR', 'LS', 'DAY'].map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <div style={label}>Unit Price ($)</div>
            <input
              type="number"
              inputMode="decimal"
              value={draft.unitPrice}
              onChange={e => setDraft(p => ({ ...p, unitPrice: e.target.value }))}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={label}>Internal margin %  <span style={{ color: '#9CA3AF', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(never shown to customer)</span></div>
            <input
              type="number"
              inputMode="numeric"
              value={draft.margin}
              onChange={e => setDraft(p => ({ ...p, margin: Number(e.target.value) || 0 }))}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={label}>Notes (optional)</div>
            <textarea
              value={draft.notes}
              onChange={e => setDraft(p => ({ ...p, notes: e.target.value }))}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
          <div style={{
            padding: 10, background: '#FFFFFF',
            border: '1px solid rgba(30,35,41,0.12)', borderRadius: 6,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: 11, color: '#4A5568', fontWeight: 700 }}>Extension</span>
            <span style={{ fontSize: 16, color: '#1E2329', fontWeight: 800 }}>
              {fmtCurrency((Number(draft.quantity) || 0) * (Number(draft.unitPrice) || 0))}
            </span>
          </div>
        </div>

        <div style={{
          flexShrink: 0, padding: 12,
          borderTop: '1px solid rgba(30,35,41,0.12)', background: '#FFFFFF',
          display: 'flex', gap: 8,
        }}>
          <button
            onClick={onRemove}
            style={{
              padding: '12px 14px', minHeight: 44,
              background: 'transparent', border: '1px solid rgba(239,68,68,0.4)',
              borderRadius: 8, color: '#ef4444', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
          >Delete</button>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '12px', minHeight: 44,
              background: 'transparent', border: '1px solid rgba(30,35,41,0.18)',
              borderRadius: 8, color: '#1E2329', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
          >Cancel</button>
          <button
            onClick={() => onSave(draft)}
            style={{
              flex: 2, padding: '12px', minHeight: 44,
              background: 'linear-gradient(135deg, #E8722A, #e8640c)',
              border: 'none', borderRadius: 8,
              color: '#fff', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
          >Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── AI Draft Panel — repositioned agent as document-editor tool ─────────────
// Opens as a right-side drawer on desktop, bottom sheet on mobile. Calls the
// existing /api/quote-agent endpoint with current quote context, parses the
// returned <quote> JSON, and shows a PREVIEW that the user explicitly applies
// (or cancels) — never auto-overwrites the document.
function AIDraftPanel({ target, currentQuote, onApply, onClose }) {
  useScrollLock();
  const isMobile = useMobile();

  const [mode, setMode] = useState('describe'); // 'describe' | 'append'
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [aiPreview, setAiPreview] = useState(null);
  const [error, setError] = useState('');

  // ── Voice input (Web Speech API), same pattern as Session 1 ──
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const baseTextRef = useRef('');
  const speechSupported = typeof window !== 'undefined'
    && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_e) { /* noop */ }
      recognitionRef.current = null;
    }
  }, []);

  const startListening = () => {
    if (!speechSupported || isListening || sending) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    let rec;
    try { rec = new SR(); } catch (_e) { return; }
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    baseTextRef.current = input.trim() ? input.trim() + ' ' : '';
    rec.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      setInput(baseTextRef.current + final + interim);
    };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => { setIsListening(false); recognitionRef.current = null; };
    recognitionRef.current = rec;
    setIsListening(true);
    try { rec.start(); } catch (_e) { setIsListening(false); recognitionRef.current = null; }
  };
  const stopListening = () => {
    if (!recognitionRef.current) { setIsListening(false); return; }
    try { recognitionRef.current.stop(); } catch (_e) { /* noop */ }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const userTurn = { role: 'user', timestamp: new Date().toISOString(), content: text };
    setConversation(prev => [...prev, userTurn]);
    setInput('');
    setSending(true);
    setError('');
    try {
      const res = await fetch('/api/quote-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectContext: {
            customerName: target.customerName,
            address: target.address,
            parentKind: target.kind,
            intent: mode === 'append' ? 'append_sections' : 'fresh_draft',
          },
          mode: currentQuote?.mode || 'residential',
          userMessage: text,
          conversationHistory: conversation,
          currentQuoteDraft: currentQuote,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'AI service is temporarily unavailable.');
        setConversation(prev => prev.slice(0, -1));
        setInput(text);
        return;
      }
      const assistantTurn = {
        role: 'assistant', timestamp: new Date().toISOString(),
        content: data.agentMessage || '',
      };
      setConversation(prev => [...prev, assistantTurn]);
      if (data.updatedQuoteDraft) {
        setAiPreview(normalizeQuoteDraft(data.updatedQuoteDraft));
      }
    } catch (_e) {
      setError("Couldn't reach the AI service. Try again.");
      setConversation(prev => prev.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const applyReplace = () => {
    if (!aiPreview) return;
    onApply({ kind: 'replace', preview: aiPreview });
  };
  const applyAppend = () => {
    if (!aiPreview) return;
    onApply({ kind: 'append', preview: aiPreview });
  };
  const cancelPreview = () => setAiPreview(null);

  // ── Layout: right drawer on desktop, bottom sheet on mobile ──
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: isMobile ? 'flex-end' : 'stretch', justifyContent: isMobile ? 'center' : 'flex-end',
    zIndex: 1100, padding: 0, overscrollBehavior: 'contain',
  };
  const panel = isMobile
    ? { background: '#1E2329', width: '100vw', maxWidth: '100vw', height: '88dvh', borderRadius: '16px 16px 0 0',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 -8px 24px rgba(0,0,0,0.5)', position: 'relative' }
    : { background: '#1E2329', width: 460, height: '100dvh', borderLeft: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '-8px 0 24px rgba(0,0,0,0.4)', position: 'relative' };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          flexShrink: 0, padding: '12px 14px',
          background: '#161B22', borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'linear-gradient(135deg, #E8722A, #e8640c)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, color: '#fff', flexShrink: 0,
          }}>✦</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#FFFFFF' }}>AI Draft Assistant</div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>{target.customerName}</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36, height: 36, padding: 0,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8, color: '#FFFFFF', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent',
            }}
          >×</button>
        </div>

        {/* Mode toggle */}
        <div style={{ flexShrink: 0, padding: '10px 12px', background: '#1E2329' }}>
          <div style={{ display: 'flex', gap: 6, background: '#161B22', borderRadius: 8, padding: 4 }}>
            <button
              onClick={() => setMode('describe')}
              style={{
                flex: 1, padding: '8px', minHeight: 36,
                background: mode === 'describe' ? 'linear-gradient(135deg, #2D5016, #3d6b1e)' : 'transparent',
                border: 'none', borderRadius: 6,
                color: mode === 'describe' ? '#fff' : '#D1D5DB',
                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >Describe a job</button>
            <button
              onClick={() => setMode('append')}
              style={{
                flex: 1, padding: '8px', minHeight: 36,
                background: mode === 'append' ? 'linear-gradient(135deg, #2D5016, #3d6b1e)' : 'transparent',
                border: 'none', borderRadius: 6,
                color: mode === 'append' ? '#fff' : '#D1D5DB',
                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >Add to current quote</button>
          </div>
        </div>

        {/* Conversation history + preview */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          padding: '8px 12px',
          WebkitOverflowScrolling: 'touch',
        }}>
          {conversation.length === 0 && !aiPreview && (
            <div style={{ padding: '20px 8px', textAlign: 'center', color: '#D1D5DB', fontSize: 12, lineHeight: 1.55 }}>
              {mode === 'describe'
                ? 'Tell me about the job. Layers, squares, pitch, materials, anything else worth pricing — I\'ll draft the full quote.'
                : 'Tell me what to add to this quote. New section, extra line items, additional scope — I\'ll write it up.'}
            </div>
          )}
          {conversation.map((m, i) => (
            <div key={i} style={{
              display: 'flex', flexDirection: 'column',
              alignItems: m.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 10,
            }}>
              <div style={{
                maxWidth: '92%', padding: '8px 12px', borderRadius: 10,
                background: m.role === 'user' ? 'linear-gradient(135deg, #E8722A, #e8640c)' : '#2A3140',
                border: m.role === 'assistant' ? '1px solid rgba(255,255,255,0.08)' : 'none',
                color: '#FFFFFF', fontSize: 12, lineHeight: 1.5,
                overflowWrap: 'anywhere', whiteSpace: 'pre-wrap',
              }}>{m.content}</div>
            </div>
          ))}
          {sending && (
            <div style={{
              padding: '8px 12px', borderRadius: 10,
              background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
              color: '#D1D5DB', fontSize: 12,
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
            }}>
              <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', borderTopColor: '#E8722A', animation: 'ri-spin 0.8s linear infinite' }} />
              Drafting…
            </div>
          )}
          {error && (
            <div style={{
              padding: '8px 12px', borderRadius: 8,
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
              color: '#f87171', fontSize: 11, marginBottom: 8,
            }}>{error}</div>
          )}

          {/* AI Preview */}
          {aiPreview && (
            <div style={{
              marginTop: 8, padding: 10,
              background: '#2A3140', border: '1px solid rgba(232,114,42,0.4)', borderRadius: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#E8722A', textTransform: 'uppercase', letterSpacing: '0.5px' }}>AI Preview</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#22c55e' }}>{fmtCurrency(aiPreview.total || aiPreview.subtotal || 0)}</div>
              </div>
              {(aiPreview.sections || []).map(sec => (
                <div key={sec.id} style={{
                  padding: 8, marginBottom: 6,
                  background: '#1E2329', borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.05)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF' }}>{sec.scopeType}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#22c55e' }}>{fmtCurrency(sec.subtotal || 0)}</span>
                  </div>
                  {(sec.lineItems || []).slice(0, 4).map(li => (
                    <div key={li.id} style={{ fontSize: 11, color: '#D1D5DB', display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {li.description}
                      </span>
                      <span style={{ flexShrink: 0, color: '#9CA3AF' }}>{li.quantity} {li.unit} · {fmtCurrency(li.extension)}</span>
                    </div>
                  ))}
                  {(sec.lineItems || []).length > 4 && (
                    <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 4, fontStyle: 'italic' }}>
                      +{sec.lineItems.length - 4} more line item{sec.lineItems.length - 4 === 1 ? '' : 's'}
                    </div>
                  )}
                </div>
              ))}
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button
                  onClick={cancelPreview}
                  style={{
                    flex: 1, padding: '8px', minHeight: 36,
                    background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 6, color: '#D1D5DB', fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                  }}
                >Cancel</button>
                {mode === 'describe' ? (
                  <>
                    <button
                      onClick={applyAppend}
                      style={{
                        flex: 1, padding: '8px', minHeight: 36,
                        background: 'rgba(232,114,42,0.12)', border: '1px solid rgba(232,114,42,0.4)',
                        borderRadius: 6, color: '#E8722A', fontSize: 11, fontWeight: 700,
                        cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                      }}
                    >Add to quote</button>
                    <button
                      onClick={applyReplace}
                      style={{
                        flex: 1, padding: '8px', minHeight: 36,
                        background: 'linear-gradient(135deg, #E8722A, #e8640c)',
                        border: 'none', borderRadius: 6,
                        color: '#fff', fontSize: 11, fontWeight: 700,
                        cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                      }}
                    >Replace quote</button>
                  </>
                ) : (
                  <button
                    onClick={applyAppend}
                    style={{
                      flex: 2, padding: '8px', minHeight: 36,
                      background: 'linear-gradient(135deg, #E8722A, #e8640c)',
                      border: 'none', borderRadius: 6,
                      color: '#fff', fontSize: 12, fontWeight: 700,
                      cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                    }}
                  >Add to quote</button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div style={{
          flexShrink: 0, padding: '10px 12px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          background: '#161B22',
          display: 'flex', alignItems: 'flex-end', gap: 6,
        }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={mode === 'describe'
              ? 'Tear off existing roof, 2 layers, 32 squares, 7/12 pitch, two story, Timberline HDZ weathered wood, full ice & water, gutter run 60ft, dumpster + permit…'
              : 'Add gutter installation, 5 inch, 100 ft of run, white K-style…'}
            rows={1}
            disabled={sending}
            style={{
              flex: 1, minHeight: 44, maxHeight: 100,
              padding: isMobile ? '11px 12px' : '8px 12px',
              background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10,
              color: '#FFFFFF', fontSize: isMobile ? 16 : 13,
              fontFamily: "'Inter', -apple-system, sans-serif",
              lineHeight: 1.4, outline: 'none', resize: 'none', boxSizing: 'border-box',
            }}
          />
          {speechSupported && (
            <button
              type="button"
              onPointerDown={e => { e.preventDefault(); startListening(); }}
              onPointerUp={() => stopListening()}
              onPointerLeave={() => { if (isListening) stopListening(); }}
              onPointerCancel={() => stopListening()}
              aria-label="Voice input — press and hold"
              disabled={sending}
              style={{
                width: 44, height: 44, padding: 0, flexShrink: 0,
                background: isListening ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${isListening ? '#ef4444' : 'rgba(255,255,255,0.12)'}`,
                borderRadius: '50%',
                color: isListening ? '#fff' : '#D1D5DB',
                fontSize: 18, lineHeight: 1, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
                boxShadow: isListening ? '0 0 0 3px rgba(239,68,68,0.25)' : 'none',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'none', userSelect: 'none',
              }}
            >🎤</button>
          )}
          <button
            onClick={send}
            disabled={!input.trim() || sending}
            style={{
              minWidth: 60, minHeight: 44, padding: '0 12px', flexShrink: 0,
              background: input.trim() && !sending
                ? 'linear-gradient(135deg, #E8722A, #e8640c)' : 'rgba(255,255,255,0.06)',
              border: 'none', borderRadius: 10,
              color: input.trim() && !sending ? '#fff' : '#D1D5DB',
              fontSize: 12, fontWeight: 700,
              cursor: input.trim() && !sending ? 'pointer' : 'not-allowed',
              WebkitTapHighlightColor: 'transparent',
            }}
          >Generate</button>
        </div>
      </div>
    </div>
  );
}

// ─── Quote Template Manager (Phase 4) ───────────────────────────────────────
// Three-tab modal for managing user-saved templates: Quote Templates, Section
// Templates, Line Item Catalog. Seeded items are read-only with a "Duplicate"
// action that clones into the user namespace for editing.
function QuoteTemplateManager({ onClose }) {
  useScrollLock();
  const isMobile = useMobile();
  const showToast = useToast();

  const [tab, setTab] = useState('quote'); // 'quote' | 'section' | 'lineItem'
  const [refreshTick, setRefreshTick] = useState(0);
  const bump = () => setRefreshTick(t => t + 1);

  // Re-pull data on every refresh tick so edits land immediately.
  const seededQuoteIds = useMemo(() => new Set(QUOTE_TEMPLATES.map(t => t.id)), []);
  const seededSectionIds = useMemo(() => new Set(SECTION_TEMPLATES.map(t => t.id)), []);
  const seededLineIds = useMemo(() => new Set(LINE_ITEM_CATALOG.map(t => t.id)), []);
  const seededBundleIds = useMemo(() => new Set(BUNDLES.map(b => b.id)), []);

  const allQuoteTemplates = useMemo(() => getAllQuoteTemplates(), [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const allSectionTemplates = useMemo(() => getAllSectionTemplates(), [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const allLineItems = useMemo(() => getAllLineItems(), [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const allBundlesList = useMemo(() => getAllBundles(), [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Editor state: null when list is shown, an object when editing
  const [editing, setEditing] = useState(null); // { kind, item, isNew }

  const overlay = isMobile ? { ...S.overlay, padding: 0, alignItems: 'stretch' } : S.overlay;
  const modal = isMobile
    ? { ...S.modal, maxWidth: '100vw', width: '100vw', maxHeight: '100dvh', height: '100dvh', borderRadius: 0, margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }
    : { ...S.modal, maxWidth: 880, width: '100%', maxHeight: '90vh', padding: 0, display: 'flex', flexDirection: 'column' };

  // ── CRUD helpers (operate on user-saved arrays only) ──
  const deleteUserItem = (kind, id) => {
    if (kind === 'quote') {
      const next = loadUserQuoteTemplates().filter(t => t.id !== id);
      saveUserQuoteTemplates(next);
    } else if (kind === 'section') {
      const next = loadUserSectionTemplates().filter(t => t.id !== id);
      saveUserSectionTemplates(next);
    } else if (kind === 'lineItem') {
      const next = loadUserLineItems().filter(t => t.id !== id);
      saveUserLineItems(next);
    } else if (kind === 'bundle') {
      const next = loadUserBundles().filter(t => t.id !== id);
      saveUserBundles(next);
    }
    bump();
    showToast('Deleted.');
  };

  const upsertUserItem = (kind, item) => {
    if (kind === 'quote') {
      const cur = loadUserQuoteTemplates().filter(t => t.id !== item.id);
      saveUserQuoteTemplates([...cur, item]);
    } else if (kind === 'section') {
      const cur = loadUserSectionTemplates().filter(t => t.id !== item.id);
      saveUserSectionTemplates([...cur, item]);
    } else if (kind === 'lineItem') {
      const cur = loadUserLineItems().filter(t => t.id !== item.id);
      saveUserLineItems([...cur, item]);
    } else if (kind === 'bundle') {
      const cur = loadUserBundles().filter(t => t.id !== item.id);
      saveUserBundles([...cur, item]);
    }
    bump();
    showToast('Saved.');
  };

  const duplicateSeeded = (kind, original) => {
    const stamp = `${kind.slice(0, 2)}-usr-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`;
    const copy = JSON.parse(JSON.stringify(original));
    copy.id = stamp;
    copy.name = `${copy.name || copy.description || 'Custom'} (copy)`;
    if (kind === 'lineItem') copy.description = `${copy.description || 'Custom item'} (copy)`;
    upsertUserItem(kind, copy);
    setEditing({ kind, item: copy, isNew: false });
  };

  const newBlank = (kind) => {
    const stamp = `${kind.slice(0, 2)}-usr-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`;
    if (kind === 'quote') {
      setEditing({
        kind, isNew: true,
        item: {
          id: stamp, name: 'New Quote Template', mode: 'residential',
          defaultSections: [],
          defaultTerms: DEFAULT_TERMS_RESIDENTIAL,
          defaultExclusions: [...DEFAULT_EXCLUSIONS_RESIDENTIAL],
          defaultIncluded: DEFAULT_INCLUDED_RESIDENTIAL,
        },
      });
    } else if (kind === 'section') {
      setEditing({
        kind, isNew: true,
        item: {
          id: stamp, name: 'New Section Template', mode: 'residential',
          scopeType: 'custom', defaultLineItems: [], defaultNotes: '',
        },
      });
    } else if (kind === 'lineItem') {
      setEditing({
        kind, isNew: true,
        item: {
          id: stamp, description: 'New line item',
          category: 'Material', unit: 'EA', unitPrice: 0, defaultQuantity: 1, notes: '',
        },
      });
    } else if (kind === 'bundle') {
      setEditing({
        kind, isNew: true,
        item: {
          id: stamp, name: 'New Bundle',
          parentScopeType: 'full_replacement',
          lineItems: [],
        },
      });
    }
  };

  // ── Header + tabs ──
  const tabs = [
    { key: 'quote', label: 'Quote Templates', count: allQuoteTemplates.length },
    { key: 'section', label: 'Sections', count: allSectionTemplates.length },
    { key: 'lineItem', label: 'Line Items', count: allLineItems.length },
    { key: 'bundle', label: 'Bundles', count: allBundlesList.length },
  ];

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          flexShrink: 0, padding: '14px 16px',
          background: '#161B22', borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 10,
          borderRadius: isMobile ? 0 : '12px 12px 0 0',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#FFFFFF' }}>Template Library</div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>Customize quote templates, sections, and the line-item catalog</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36, height: 36, padding: 0,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8, color: '#FFFFFF', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent',
            }}
          >×</button>
        </div>

        {!editing && (
          <>
            {/* Tabs */}
            <div style={{
              flexShrink: 0, padding: '10px 12px', background: '#1E2329',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}>
              <div style={{ display: 'flex', gap: 4, background: '#161B22', borderRadius: 8, padding: 4 }}>
                {tabs.map(t => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    style={{
                      flex: 1, padding: '8px', minHeight: 36,
                      background: tab === t.key ? 'linear-gradient(135deg, #2D5016, #3d6b1e)' : 'transparent',
                      border: 'none', borderRadius: 6,
                      color: tab === t.key ? '#fff' : '#D1D5DB',
                      fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      WebkitTapHighlightColor: 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    <span>{t.label}</span>
                    <span style={{
                      fontSize: 9, padding: '1px 6px', borderRadius: 8,
                      background: tab === t.key ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.06)',
                      color: tab === t.key ? '#fff' : '#9CA3AF',
                    }}>{t.count}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* List body (scrollable) */}
            <div style={{
              flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
              padding: '12px', background: '#1E2329',
            }}>
              {tab === 'quote' && (
                <TemplateList
                  kind="quote"
                  items={allQuoteTemplates}
                  isSeeded={(it) => seededQuoteIds.has(it.id)}
                  primaryLabel={(it) => it.name}
                  secondaryLabel={(it) => `${it.mode} · ${(it.defaultSections || []).length} section${(it.defaultSections || []).length === 1 ? '' : 's'}`}
                  onNew={() => newBlank('quote')}
                  onEdit={(it) => setEditing({ kind: 'quote', item: it, isNew: false })}
                  onDuplicate={(it) => duplicateSeeded('quote', it)}
                  onDelete={(it) => deleteUserItem('quote', it.id)}
                />
              )}
              {tab === 'section' && (
                <TemplateList
                  kind="section"
                  items={allSectionTemplates}
                  isSeeded={(it) => seededSectionIds.has(it.id)}
                  primaryLabel={(it) => it.name}
                  secondaryLabel={(it) => `${it.mode} · ${(it.defaultLineItems || []).length} line item${(it.defaultLineItems || []).length === 1 ? '' : 's'}`}
                  onNew={() => newBlank('section')}
                  onEdit={(it) => setEditing({ kind: 'section', item: it, isNew: false })}
                  onDuplicate={(it) => duplicateSeeded('section', it)}
                  onDelete={(it) => deleteUserItem('section', it.id)}
                />
              )}
              {tab === 'lineItem' && (
                <TemplateList
                  kind="lineItem"
                  items={allLineItems}
                  isSeeded={(it) => seededLineIds.has(it.id)}
                  primaryLabel={(it) => it.description}
                  secondaryLabel={(it) => `${it.category} · ${it.unit} · ${fmtCurrency(it.unitPrice || 0)}`}
                  onNew={() => newBlank('lineItem')}
                  onEdit={(it) => setEditing({ kind: 'lineItem', item: it, isNew: false })}
                  onDuplicate={(it) => duplicateSeeded('lineItem', it)}
                  onDelete={(it) => deleteUserItem('lineItem', it.id)}
                />
              )}
              {tab === 'bundle' && (
                <TemplateList
                  kind="bundle"
                  items={allBundlesList}
                  isSeeded={(it) => seededBundleIds.has(it.id)}
                  primaryLabel={(it) => it.name}
                  secondaryLabel={(it) => `${it.parentScopeType} · ${(it.lineItems || []).length} line item${(it.lineItems || []).length === 1 ? '' : 's'}`}
                  onNew={() => newBlank('bundle')}
                  onEdit={(it) => setEditing({ kind: 'bundle', item: it, isNew: false })}
                  onDuplicate={(it) => duplicateSeeded('bundle', it)}
                  onDelete={(it) => deleteUserItem('bundle', it.id)}
                />
              )}
            </div>
          </>
        )}

        {editing && (
          <div style={{
            flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            padding: '14px 16px', background: '#1E2329',
          }}>
            {editing.kind === 'lineItem' && (
              <LineItemEditor
                initial={editing.item}
                onCancel={() => setEditing(null)}
                onSave={(item) => { upsertUserItem('lineItem', item); setEditing(null); }}
              />
            )}
            {editing.kind === 'section' && (
              <SectionTemplateEditor
                initial={editing.item}
                allLineItems={allLineItems}
                onCancel={() => setEditing(null)}
                onSave={(item) => { upsertUserItem('section', item); setEditing(null); }}
              />
            )}
            {editing.kind === 'quote' && (
              <QuoteTemplateEditor
                initial={editing.item}
                allSectionTemplates={allSectionTemplates}
                onCancel={() => setEditing(null)}
                onSave={(item) => { upsertUserItem('quote', item); setEditing(null); }}
              />
            )}
            {editing.kind === 'bundle' && (
              <BundleEditor
                initial={editing.item}
                allLineItems={allLineItems}
                allSectionTemplates={allSectionTemplates}
                onCancel={() => setEditing(null)}
                onSave={(item) => { upsertUserItem('bundle', item); setEditing(null); }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BundleEditor({ initial, allLineItems, allSectionTemplates, onCancel, onSave }) {
  const isMobile = useMobile();
  const [item, setItem] = useState(initial);
  const [showPicker, setShowPicker] = useState(false);

  // Unique scopeTypes that any section template exposes — bundle attaches to
  // one of these so the right bundles surface in the right context.
  const scopeTypes = useMemo(() => {
    const set = new Set(allSectionTemplates.map(s => s.scopeType).filter(Boolean));
    return Array.from(set);
  }, [allSectionTemplates]);

  const inputStyle = {
    width: '100%', padding: '10px 12px', minHeight: 40,
    background: '#161B22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
    color: '#FFFFFF', fontSize: isMobile ? 16 : 13, outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box',
  };

  const removeLi = (idx) => setItem(p => ({ ...p, lineItems: p.lineItems.filter((_, i) => i !== idx) }));
  const updateLi = (idx, patch) => setItem(p => {
    const next = [...p.lineItems];
    next[idx] = { ...next[idx], ...patch };
    return { ...p, lineItems: next };
  });
  const addFromCatalog = (entry) => {
    setItem(p => ({
      ...p,
      lineItems: [...(p.lineItems || []), {
        description: entry.description,
        category: entry.category,
        unit: entry.unit,
        unitPrice: entry.unitPrice,
        quantity: entry.defaultQuantity || 0,
        notes: entry.notes || null,
      }],
    }));
  };

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF', marginBottom: 12 }}>
        {initial.name ? 'Edit Bundle' : 'New Bundle'}
      </div>

      <FieldRow label="Bundle Name">
        <input
          value={item.name || ''}
          onChange={e => setItem(p => ({ ...p, name: e.target.value }))}
          style={inputStyle}
          placeholder="GAF Timberline HDZ"
        />
      </FieldRow>

      <FieldRow label="Parent Section Type">
        <select
          value={item.parentScopeType || ''}
          onChange={e => setItem(p => ({ ...p, parentScopeType: e.target.value }))}
          style={inputStyle}
        >
          <option value="">—</option>
          {scopeTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </FieldRow>

      <FieldRow label={`Line Items (${(item.lineItems || []).length})`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {(item.lineItems || []).map((li, idx) => (
            <div key={idx} style={{
              padding: 8, background: '#161B22',
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6,
              display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis' }}>{li.description}</div>
                <div style={{ fontSize: 10, color: '#9CA3AF' }}>{li.category} · {li.unit} · {fmtCurrency(li.unitPrice || 0)}</div>
              </div>
              <input
                type="number"
                value={li.quantity ?? 0}
                onChange={e => updateLi(idx, { quantity: Number(e.target.value) || 0 })}
                style={{
                  width: 70, padding: '6px 8px', minHeight: 32,
                  background: '#1E2329', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6,
                  color: '#FFFFFF', fontSize: isMobile ? 16 : 12, outline: 'none', textAlign: 'right',
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={() => removeLi(idx)}
                aria-label="Remove"
                style={{
                  width: 32, height: 32, padding: 0, flexShrink: 0,
                  background: 'transparent', border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 6, color: '#f87171', fontSize: 14, cursor: 'pointer',
                }}
              >×</button>
            </div>
          ))}
        </div>
        <button
          onClick={() => setShowPicker(true)}
          style={{
            width: '100%', padding: '10px', minHeight: 40,
            background: 'transparent', border: '1px dashed rgba(255,255,255,0.2)',
            borderRadius: 8, color: '#D1D5DB', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
          }}
        >+ Add line item from catalog</button>
      </FieldRow>

      <EditorActions
        onCancel={onCancel}
        onSave={() => onSave(item)}
        disabled={!item.name?.trim() || !item.parentScopeType?.trim()}
      />

      {showPicker && (
        <CatalogPickerInline
          allLineItems={allLineItems}
          onPick={(entry) => { addFromCatalog(entry); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

function TemplateList({ kind, items, isSeeded, primaryLabel, secondaryLabel, onNew, onEdit, onDuplicate, onDelete }) {
  const [filter, setFilter] = useState('');
  const isMobile = useMobile();
  const filtered = items.filter(it => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (primaryLabel(it) || '').toLowerCase().includes(q)
      || (secondaryLabel(it) || '').toLowerCase().includes(q);
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={`Search ${kind === 'lineItem' ? 'line items' : kind === 'section' ? 'section templates' : 'quote templates'}…`}
          style={{
            flex: 1, minWidth: 180, padding: '10px 12px', minHeight: 40,
            background: '#161B22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
            color: '#FFFFFF', fontSize: isMobile ? 16 : 13, outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <button
          onClick={onNew}
          style={{
            padding: '10px 14px', minHeight: 40,
            background: 'linear-gradient(135deg, #E8722A, #e8640c)',
            border: 'none', borderRadius: 8,
            color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent', whiteSpace: 'nowrap',
          }}
        >+ New</button>
      </div>

      {filtered.length === 0 && (
        <div style={{ padding: 28, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>
          {filter.trim() ? 'No matches.' : 'No templates yet.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filtered.map(it => {
          const seeded = isSeeded(it);
          return (
            <div
              key={it.id}
              style={{
                padding: 12, background: '#161B22',
                border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>{primaryLabel(it)}</span>
                  {seeded ? (
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                      background: 'rgba(156,163,175,0.18)', color: '#9CA3AF',
                      textTransform: 'uppercase', letterSpacing: '0.4px',
                    }}>System</span>
                  ) : (
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                      background: 'rgba(232,114,42,0.18)', color: '#E8722A',
                      textTransform: 'uppercase', letterSpacing: '0.4px',
                    }}>Custom</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{secondaryLabel(it)}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {seeded ? (
                  <button
                    onClick={() => onDuplicate(it)}
                    style={{
                      padding: '8px 12px', minHeight: 36,
                      background: 'transparent', border: '1px solid rgba(255,255,255,0.18)',
                      borderRadius: 6, color: '#D1D5DB', fontSize: 11, fontWeight: 700,
                      cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                    }}
                  >Duplicate</button>
                ) : (
                  <>
                    <button
                      onClick={() => onEdit(it)}
                      style={{
                        padding: '8px 12px', minHeight: 36,
                        background: 'rgba(232,114,42,0.15)', border: '1px solid rgba(232,114,42,0.35)',
                        borderRadius: 6, color: '#E8722A', fontSize: 11, fontWeight: 700,
                        cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                      }}
                    >Edit</button>
                    <button
                      onClick={() => {
                        // eslint-disable-next-line no-restricted-globals
                        if (window.confirm('Delete this template?')) onDelete(it);
                      }}
                      style={{
                        padding: '8px 10px', minHeight: 36,
                        background: 'transparent', border: '1px solid rgba(239,68,68,0.3)',
                        borderRadius: 6, color: '#f87171', fontSize: 11, fontWeight: 700,
                        cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                      }}
                    >×</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: '#9CA3AF',
        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4,
      }}>{label}</div>
      {children}
    </div>
  );
}

function LineItemEditor({ initial, onCancel, onSave }) {
  const isMobile = useMobile();
  const [item, setItem] = useState(initial);

  const inputStyle = {
    width: '100%', padding: '10px 12px', minHeight: 40,
    background: '#161B22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
    color: '#FFFFFF', fontSize: isMobile ? 16 : 13, outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box',
  };

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF', marginBottom: 12 }}>
        {initial.description ? 'Edit Line Item' : 'New Line Item'}
      </div>

      <FieldRow label="Description">
        <input
          value={item.description || ''}
          onChange={e => setItem(p => ({ ...p, description: e.target.value }))}
          style={inputStyle}
        />
      </FieldRow>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
        <FieldRow label="Category">
          <select
            value={item.category || 'Material'}
            onChange={e => setItem(p => ({ ...p, category: e.target.value }))}
            style={inputStyle}
          >
            {LINE_ITEM_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </FieldRow>
        <FieldRow label="Unit">
          <select
            value={item.unit || 'EA'}
            onChange={e => setItem(p => ({ ...p, unit: e.target.value }))}
            style={inputStyle}
          >
            {['EA', 'SF', 'LF', 'SQ', 'CY', 'TON', 'HR', 'LS', 'DAY'].map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </FieldRow>
        <FieldRow label="Unit Price ($)">
          <input
            type="number"
            value={item.unitPrice ?? 0}
            onChange={e => setItem(p => ({ ...p, unitPrice: Number(e.target.value) || 0 }))}
            style={inputStyle}
          />
        </FieldRow>
        <FieldRow label="Default Quantity">
          <input
            type="number"
            value={item.defaultQuantity ?? 1}
            onChange={e => setItem(p => ({ ...p, defaultQuantity: Number(e.target.value) || 0 }))}
            style={inputStyle}
          />
        </FieldRow>
      </div>

      <FieldRow label="Notes (optional)">
        <textarea
          value={item.notes || ''}
          onChange={e => setItem(p => ({ ...p, notes: e.target.value }))}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </FieldRow>

      <EditorActions onCancel={onCancel} onSave={() => onSave(item)} disabled={!item.description?.trim()} />
    </div>
  );
}

function SectionTemplateEditor({ initial, allLineItems, onCancel, onSave }) {
  const isMobile = useMobile();
  const [item, setItem] = useState(initial);
  const [showPicker, setShowPicker] = useState(false);

  const inputStyle = {
    width: '100%', padding: '10px 12px', minHeight: 40,
    background: '#161B22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
    color: '#FFFFFF', fontSize: isMobile ? 16 : 13, outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box',
  };

  const removeLi = (idx) => setItem(p => ({ ...p, defaultLineItems: p.defaultLineItems.filter((_, i) => i !== idx) }));
  const updateLi = (idx, patch) => setItem(p => {
    const next = [...p.defaultLineItems];
    next[idx] = { ...next[idx], ...patch };
    return { ...p, defaultLineItems: next };
  });
  const addLiFromCatalog = (catalogEntry) => {
    const li = {
      id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
      description: catalogEntry.description,
      category: catalogEntry.category,
      unit: catalogEntry.unit,
      unitPrice: catalogEntry.unitPrice,
      quantity: catalogEntry.defaultQuantity || 0,
      extension: 0,
      notes: catalogEntry.notes || null,
    };
    setItem(p => ({ ...p, defaultLineItems: [...(p.defaultLineItems || []), li] }));
  };

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF', marginBottom: 12 }}>
        {initial.name ? 'Edit Section Template' : 'New Section Template'}
      </div>

      <FieldRow label="Name">
        <input
          value={item.name || ''}
          onChange={e => setItem(p => ({ ...p, name: e.target.value }))}
          style={inputStyle}
        />
      </FieldRow>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
        <FieldRow label="Mode">
          <select
            value={item.mode || 'residential'}
            onChange={e => setItem(p => ({ ...p, mode: e.target.value }))}
            style={inputStyle}
          >
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>
        </FieldRow>
        <FieldRow label="Scope Type (slug)">
          <input
            value={item.scopeType || ''}
            onChange={e => setItem(p => ({ ...p, scopeType: e.target.value }))}
            placeholder="full_replacement"
            style={inputStyle}
          />
        </FieldRow>
      </div>

      <FieldRow label="Default Notes (shown as narrative)">
        <textarea
          value={item.defaultNotes || ''}
          onChange={e => setItem(p => ({ ...p, defaultNotes: e.target.value }))}
          rows={2}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </FieldRow>

      <FieldRow label={`Line Items (${(item.defaultLineItems || []).length})`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {(item.defaultLineItems || []).map((li, idx) => (
            <div key={idx} style={{
              padding: 8, background: '#161B22',
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6,
              display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis' }}>{li.description}</div>
                <div style={{ fontSize: 10, color: '#9CA3AF' }}>{li.category} · {li.unit} · {fmtCurrency(li.unitPrice || 0)}</div>
              </div>
              <input
                type="number"
                value={li.quantity ?? 0}
                onChange={e => updateLi(idx, { quantity: Number(e.target.value) || 0 })}
                style={{
                  width: 70, padding: '6px 8px', minHeight: 32,
                  background: '#1E2329', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6,
                  color: '#FFFFFF', fontSize: isMobile ? 16 : 12, outline: 'none', textAlign: 'right',
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={() => removeLi(idx)}
                aria-label="Remove"
                style={{
                  width: 32, height: 32, padding: 0, flexShrink: 0,
                  background: 'transparent', border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 6, color: '#f87171', fontSize: 14, cursor: 'pointer',
                }}
              >×</button>
            </div>
          ))}
        </div>
        <button
          onClick={() => setShowPicker(true)}
          style={{
            width: '100%', padding: '10px', minHeight: 40,
            background: 'transparent', border: '1px dashed rgba(255,255,255,0.2)',
            borderRadius: 8, color: '#D1D5DB', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
          }}
        >+ Add line item from catalog</button>
      </FieldRow>

      <EditorActions onCancel={onCancel} onSave={() => onSave(item)} disabled={!item.name?.trim() || !item.scopeType?.trim()} />

      {showPicker && (
        <CatalogPickerInline
          allLineItems={allLineItems}
          onPick={(entry) => { addLiFromCatalog(entry); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

function QuoteTemplateEditor({ initial, allSectionTemplates, onCancel, onSave }) {
  const isMobile = useMobile();
  const [item, setItem] = useState(initial);

  const inputStyle = {
    width: '100%', padding: '10px 12px', minHeight: 40,
    background: '#161B22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
    color: '#FFFFFF', fontSize: isMobile ? 16 : 13, outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box',
  };

  const sectionsForMode = allSectionTemplates.filter(s => s.mode === item.mode);

  const addSectionRef = (sectionTemplate) => {
    setItem(p => ({
      ...p,
      defaultSections: [...(p.defaultSections || []), {
        scopeType: sectionTemplate.scopeType,
        sectionTemplateId: sectionTemplate.id,
      }],
    }));
  };
  const removeSectionRef = (idx) => {
    setItem(p => ({ ...p, defaultSections: p.defaultSections.filter((_, i) => i !== idx) }));
  };
  const moveSectionRef = (idx, delta) => {
    setItem(p => {
      const next = [...(p.defaultSections || [])];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return p;
      const [m] = next.splice(idx, 1);
      next.splice(target, 0, m);
      return { ...p, defaultSections: next };
    });
  };

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF', marginBottom: 12 }}>
        {initial.name ? 'Edit Quote Template' : 'New Quote Template'}
      </div>

      <FieldRow label="Name">
        <input
          value={item.name || ''}
          onChange={e => setItem(p => ({ ...p, name: e.target.value }))}
          style={inputStyle}
        />
      </FieldRow>

      <FieldRow label="Mode">
        <select
          value={item.mode || 'residential'}
          onChange={e => setItem(p => ({ ...p, mode: e.target.value, defaultSections: [] }))}
          style={inputStyle}
        >
          <option value="residential">Residential</option>
          <option value="commercial">Commercial</option>
        </select>
      </FieldRow>

      <FieldRow label={`Default Sections (${(item.defaultSections || []).length})`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {(item.defaultSections || []).map((ref, idx) => {
            const st = allSectionTemplates.find(s => s.id === ref.sectionTemplateId);
            return (
              <div key={idx} style={{
                padding: 8, background: '#161B22',
                border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF' }}>{st ? st.name : ref.scopeType}</div>
                  <div style={{ fontSize: 10, color: '#9CA3AF' }}>{ref.scopeType}</div>
                </div>
                <button
                  onClick={() => moveSectionRef(idx, -1)}
                  disabled={idx === 0}
                  aria-label="Move up"
                  style={{
                    width: 32, height: 32, padding: 0,
                    background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 6, color: idx === 0 ? '#4A5568' : '#D1D5DB',
                    fontSize: 12, cursor: idx === 0 ? 'not-allowed' : 'pointer',
                  }}
                >↑</button>
                <button
                  onClick={() => moveSectionRef(idx, +1)}
                  disabled={idx === (item.defaultSections || []).length - 1}
                  aria-label="Move down"
                  style={{
                    width: 32, height: 32, padding: 0,
                    background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 6, color: idx === (item.defaultSections || []).length - 1 ? '#4A5568' : '#D1D5DB',
                    fontSize: 12, cursor: idx === (item.defaultSections || []).length - 1 ? 'not-allowed' : 'pointer',
                  }}
                >↓</button>
                <button
                  onClick={() => removeSectionRef(idx)}
                  aria-label="Remove"
                  style={{
                    width: 32, height: 32, padding: 0,
                    background: 'transparent', border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: 6, color: '#f87171', fontSize: 14, cursor: 'pointer',
                  }}
                >×</button>
              </div>
            );
          })}
        </div>
        <select
          value=""
          onChange={e => {
            const id = e.target.value;
            if (!id) return;
            const st = sectionsForMode.find(s => s.id === id);
            if (st) addSectionRef(st);
            e.target.value = '';
          }}
          style={{
            ...inputStyle,
            background: 'transparent', border: '1px dashed rgba(255,255,255,0.2)',
            color: '#D1D5DB', fontWeight: 700, cursor: 'pointer',
          }}
        >
          <option value="">+ Add section template…</option>
          {sectionsForMode.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </FieldRow>

      <FieldRow label="What's Included (default)">
        <textarea
          value={item.defaultIncluded || ''}
          onChange={e => setItem(p => ({ ...p, defaultIncluded: e.target.value }))}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </FieldRow>

      <FieldRow label="Exclusions (one per line)">
        <textarea
          value={(item.defaultExclusions || []).join('\n')}
          onChange={e => setItem(p => ({ ...p, defaultExclusions: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) }))}
          rows={4}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </FieldRow>

      <FieldRow label="Terms (default)">
        <textarea
          value={item.defaultTerms || ''}
          onChange={e => setItem(p => ({ ...p, defaultTerms: e.target.value }))}
          rows={6}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </FieldRow>

      <EditorActions onCancel={onCancel} onSave={() => onSave(item)} disabled={!item.name?.trim()} />
    </div>
  );
}

function EditorActions({ onCancel, onSave, disabled }) {
  return (
    <div style={{
      display: 'flex', gap: 8, marginTop: 16, paddingTop: 14,
      borderTop: '1px solid rgba(255,255,255,0.08)',
    }}>
      <button
        onClick={onCancel}
        style={{
          flex: 1, padding: '12px', minHeight: 44,
          background: 'transparent', border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 8, color: '#D1D5DB', fontSize: 13, fontWeight: 700,
          cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
        }}
      >Cancel</button>
      <button
        onClick={onSave}
        disabled={disabled}
        style={{
          flex: 2, padding: '12px', minHeight: 44,
          background: disabled ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #E8722A, #e8640c)',
          border: 'none', borderRadius: 8,
          color: disabled ? '#9CA3AF' : '#fff', fontSize: 13, fontWeight: 700,
          cursor: disabled ? 'not-allowed' : 'pointer', WebkitTapHighlightColor: 'transparent',
        }}
      >Save Template</button>
    </div>
  );
}

function CatalogPickerInline({ allLineItems, onPick, onClose }) {
  const isMobile = useMobile();
  const [filter, setFilter] = useState('');
  const filtered = allLineItems.filter(li => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (li.description || '').toLowerCase().includes(q)
      || (li.category || '').toLowerCase().includes(q);
  });

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 0 : 16,
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: '#1E2329', borderRadius: isMobile ? '16px 16px 0 0' : 12,
          width: '100%', maxWidth: 520, maxHeight: '86dvh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          position: isMobile ? 'fixed' : 'relative', bottom: isMobile ? 0 : 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          padding: '12px 14px', background: '#161B22',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: '#FFFFFF' }}>Pick a line item</div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, padding: 0,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 6, color: '#FFFFFF', fontSize: 16, cursor: 'pointer',
            }}
          >×</button>
        </div>
        <div style={{ padding: 10, background: '#1E2329', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Search catalog…"
            style={{
              width: '100%', padding: '10px 12px', minHeight: 40,
              background: '#161B22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
              color: '#FFFFFF', fontSize: isMobile ? 16 : 13, outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 8 }}>
          {filtered.map(li => (
            <button
              key={li.id}
              onClick={() => onPick(li)}
              style={{
                width: '100%', textAlign: 'left', padding: 10, marginBottom: 4,
                background: '#161B22', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 6, color: '#FFFFFF', cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF' }}>{li.description}</div>
              <div style={{ fontSize: 10, color: '#9CA3AF' }}>{li.category} · {li.unit} · {fmtCurrency(li.unitPrice || 0)}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Quotes Tab (Phase 1 of morning batch) ──────────────────────────────────
// Dedicated quote-management surface accessible from the main bottom nav.
// Lists every quote across all customers, grouped by status with a "Needs
// action" group at the top, with mode/status filters and search. Tap a row
// to open the editor; long-press for the action sheet.
function QuotesTab({ quotes, leads, jobs, onSaveQuote, onConvertLead, onAddLead, isDemo, rolePerms, currentUser }) {
  const isMobile = useMobile();
  const showToast = useToast();

  const [modeFilter, setModeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  // newQuoteFlow: null | { step: 'customer' | 'newCustomer' | 'template' | 'editor', target?, quote? }
  const [newQuoteFlow, setNewQuoteFlow] = useState(null);
  const [openQuote, setOpenQuote] = useState(null); // { quote, target }
  const [actionSheet, setActionSheet] = useState(null); // { quote }

  const todayMs = Date.now();
  const daysSince = (iso) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.max(0, Math.round((todayMs - t) / 86400000));
  };
  const daysUntil = (iso) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.round((t - todayMs) / 86400000);
  };
  const ago = (n, noun) => {
    if (n == null) return '';
    if (n === 0) return `${noun} today`;
    if (n === 1) return `${noun} yesterday`;
    return `${noun} ${n} day${n === 1 ? '' : 's'} ago`;
  };

  // Build a project lookup from `jobs` the same way ProjectsTab does — jobs
  // are scopes (id = scope id, projectId = the actual project id), so we group
  // by projectId to get a project-shaped record that quote.projectId can match.
  const projectsById = useMemo(() => {
    const m = new Map();
    jobs.forEach(j => {
      const pid = String(j.projectId || `${j.customer}|${j.address}`);
      if (!m.has(pid)) {
        m.set(pid, {
          id: pid,
          customer: j.customer || j.customerName,
          customerName: j.customer || j.customerName,
          address: j.address || '',
          scopes: [],
        });
      }
      m.get(pid).scopes.push(j);
    });
    return m;
  }, [jobs]);

  const customerOf = (q) => {
    if (q.leadId != null) {
      const l = leads.find(x => String(x.id) === String(q.leadId));
      return l ? { kind: 'lead', name: l.name, address: l.address || '', source: l } : null;
    }
    if (q.projectId != null) {
      const p = projectsById.get(String(q.projectId));
      if (p) return { kind: 'project', name: p.customer || p.customerName || '(project)', address: p.address || '', source: p };
    }
    return null;
  };

  const isNeedsAction = (q) => {
    if (!q) return false;
    if (q.status === 'draft' && (daysSince(q.createdDate) || 0) > 3) return true;
    if (q.status === 'sent' && (daysSince(q.sentDate || q.createdDate) || 0) > 5) return true;
    if (q.expirationDate) {
      const d = daysUntil(q.expirationDate);
      if (d != null && d >= 0 && d <= 7 && q.status !== 'signed' && q.status !== 'rejected') return true;
    }
    return false;
  };

  const statusColor = (s) => ({
    draft: '#9CA3AF', sent: '#3b82f6', viewed: '#06b6d4',
    signed: '#22c55e', rejected: '#ef4444', expired: '#f59e0b',
  }[s] || '#9CA3AF');

  const lastActivity = (q) => {
    if (q.status === 'signed' && q.signedDate) return ago(daysSince(q.signedDate), 'Signed');
    if (q.status === 'rejected' && q.rejectedDate) return ago(daysSince(q.rejectedDate), 'Rejected');
    if (q.status === 'viewed' && q.viewedDate) return ago(daysSince(q.viewedDate), 'Viewed by customer');
    if (q.status === 'sent' && q.sentDate) return ago(daysSince(q.sentDate), 'Sent');
    if (q.status === 'expired') return ago(daysSince(q.expirationDate), 'Expired');
    return ago(daysSince(q.lastEditedDate || q.createdDate), 'Updated');
  };

  // Effective status: a draft/sent quote whose validThrough has passed is
  // treated as expired regardless of its stored status. This way nobody sees
  // "Sent · 30 days ago" on a quote that's actually unreachable.
  const effectiveStatus = (q) => {
    if (!q) return 'draft';
    if (q.status === 'signed' || q.status === 'rejected' || q.status === 'expired') return q.status;
    if (q.expirationDate) {
      const d = daysUntil(q.expirationDate);
      if (d != null && d < 0 && (q.status === 'draft' || q.status === 'sent' || q.status === 'viewed')) {
        return 'expired';
      }
    }
    return q.status || 'draft';
  };

  const filtered = quotes.filter(q => {
    if (modeFilter !== 'all' && q.mode !== modeFilter) return false;
    if (statusFilter !== 'all' && effectiveStatus(q) !== statusFilter) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      const cust = customerOf(q);
      const haystack = [
        q.quoteNumber || '',
        cust?.name || '',
        cust?.address || '',
      ].join(' ').toLowerCase();
      if (!haystack.includes(s)) return false;
    }
    return true;
  });

  // Group ordering
  const groupOrder = ['needsAction', 'draft', 'sent', 'viewed', 'signed', 'rejected', 'expired'];
  const groupLabels = {
    needsAction: '⚠ Needs Action', draft: 'Draft', sent: 'Sent', viewed: 'Viewed',
    signed: 'Signed', rejected: 'Rejected', expired: 'Expired',
  };

  const grouped = useMemo(() => {
    const map = { needsAction: [], draft: [], sent: [], viewed: [], signed: [], rejected: [], expired: [] };
    filtered.forEach(q => {
      if (isNeedsAction(q)) map.needsAction.push(q);
      const eff = effectiveStatus(q);
      const key = map[eff] ? eff : 'draft';
      // Always also include in status group so user can locate it where expected
      if (!isNeedsAction(q) || statusFilter !== 'all') map[key].push(q);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => {
      const ad = new Date(a.lastEditedDate || a.createdDate || 0).getTime();
      const bd = new Date(b.lastEditedDate || b.createdDate || 0).getTime();
      return bd - ad;
    }));
    return map;
  }, [filtered, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditor = (q) => {
    const cust = customerOf(q);
    if (!cust) { showToast('Customer record not found for this quote.', 'error'); return; }
    setOpenQuote({
      quote: q,
      target: {
        kind: cust.kind,
        id: cust.source.id,
        customerName: cust.name,
        address: cust.address,
        [cust.kind]: cust.source,
      },
    });
  };

  const duplicateQuote = (q) => {
    const stamp = Date.now().toString(36);
    const dup = {
      ...JSON.parse(JSON.stringify(q)),
      id: `q-${stamp}`,
      quoteNumber: `${q.quoteNumber || 'QUOTE'}-COPY`,
      status: 'draft',
      sentDate: null, viewedDate: null, signedDate: null, rejectedDate: null,
      createdDate: new Date().toISOString(),
      shareToken: `qst_${Math.random().toString(36).slice(2, 18)}`,
    };
    onSaveQuote(dup);
    showToast('Quote duplicated as draft.');
  };

  const deleteQuote = (q) => {
    // Soft delete via status flip; the renderer hides status='archived'.
    // (No backend yet — keep state honest by writing the new status through.)
    onSaveQuote({ ...q, status: 'archived' });
    showToast('Quote deleted.');
  };

  // ── Layout ──
  const containerPad = isMobile ? 12 : 16;
  const chipRow = { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 };
  const chipBase = (active, color = '#E8722A') => ({
    padding: '6px 12px', minHeight: 32,
    background: active ? color : 'transparent',
    border: `1px solid ${active ? color : 'rgba(255,255,255,0.15)'}`,
    borderRadius: 999, color: active ? '#fff' : '#D1D5DB',
    fontSize: 11, fontWeight: 700, cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  });

  return (
    <div style={{ padding: containerPad, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Sticky header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 5,
        background: '#1E2329', marginLeft: -containerPad, marginRight: -containerPad,
        padding: `8px ${containerPad}px 6px`,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.3px' }}>Quotes</div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>{quotes.filter(q => q.status !== 'archived').length} total · {grouped.needsAction.length} needs action</div>
          </div>
          <button
            onClick={() => setNewQuoteFlow({ step: 'customer' })}
            style={{
              padding: '10px 14px', minHeight: 40, flexShrink: 0,
              background: 'linear-gradient(135deg, #E8722A, #e8640c)',
              border: 'none', borderRadius: 10,
              color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 2px 10px rgba(249,115,22,0.3)',
            }}
          >+ New Quote</button>
        </div>

        <div style={chipRow}>
          {['all', 'residential', 'commercial'].map(m => (
            <button key={m} onClick={() => setModeFilter(m)} style={chipBase(modeFilter === m, '#2D5016')}>
              {m === 'all' ? 'All' : m === 'residential' ? 'Residential' : 'Commercial'}
            </button>
          ))}
        </div>
        <div style={chipRow}>
          {['all', 'draft', 'sent', 'viewed', 'signed', 'rejected', 'expired'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} style={chipBase(statusFilter === s, s === 'all' ? '#E8722A' : statusColor(s))}>
              {s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search customer, address, quote #"
          style={{
            width: '100%', padding: '10px 12px', minHeight: 40,
            background: '#161B22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
            color: '#FFFFFF', fontSize: isMobile ? 16 : 13, outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Body */}
      {quotes.filter(q => q.status !== 'archived').length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 16px' }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>📄</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF', marginBottom: 6 }}>No quotes yet</div>
          <div style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 18 }}>Start your first quote and it'll show up here.</div>
          <button
            onClick={() => setNewQuoteFlow({ step: 'customer' })}
            style={{
              padding: '12px 22px', minHeight: 44,
              background: 'linear-gradient(135deg, #E8722A, #e8640c)',
              border: 'none', borderRadius: 10,
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 2px 10px rgba(249,115,22,0.3)',
            }}
          >Start your first quote</button>
        </div>
      ) : (
        groupOrder.map(group => {
          const items = grouped[group].filter(q => q.status !== 'archived');
          if (items.length === 0) return null;
          if (group === 'needsAction' && statusFilter !== 'all') return null;
          return (
            <div key={group} style={{ marginBottom: 4 }}>
              <div style={{
                fontSize: 11, fontWeight: 700,
                color: group === 'needsAction' ? '#f59e0b' : '#D1D5DB',
                textTransform: 'uppercase', letterSpacing: '0.5px',
                padding: '8px 4px 6px',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span>{groupLabels[group]}</span>
                <span style={{ fontSize: 10, color: '#9CA3AF' }}>({items.length})</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.map(q => {
                  const eff = effectiveStatus(q);
                  return (
                    <QuoteRow
                      key={q.id}
                      quote={{ ...q, status: eff }}
                      customer={customerOf(q)}
                      statusColor={statusColor(eff)}
                      lastActivity={lastActivity(q)}
                      needsAction={isNeedsAction(q)}
                      onTap={() => openEditor(q)}
                      onLongPress={() => setActionSheet({ quote: q })}
                    />
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {/* Action sheet for long-press */}
      {actionSheet && (
        <ActionSheet
          title={actionSheet.quote.quoteNumber || 'Quote'}
          onClose={() => setActionSheet(null)}
          actions={[
            { label: 'Open', onClick: () => { openEditor(actionSheet.quote); setActionSheet(null); } },
            { label: 'Duplicate as draft', onClick: () => { duplicateQuote(actionSheet.quote); setActionSheet(null); } },
            { label: 'Send reminder', onClick: () => { showToast('Send reminder coming next session.'); setActionSheet(null); }, disabled: actionSheet.quote.status !== 'sent' && actionSheet.quote.status !== 'viewed' },
            { label: 'Mark Lost', onClick: () => { showToast('Mark Lost coming next session.'); setActionSheet(null); }, danger: true },
            { label: 'Delete', onClick: () => { /* eslint-disable-next-line no-restricted-globals */ if (window.confirm('Delete this quote?')) { deleteQuote(actionSheet.quote); setActionSheet(null); } }, danger: true },
          ]}
        />
      )}

      {/* New quote flow */}
      {newQuoteFlow?.step === 'customer' && (
        <NewQuoteCustomerPicker
          leads={leads.filter(l => l.stage !== 'lost' && l.stage !== 'converted_to_project')}
          projects={Array.from(projectsById.values())}
          onPickLead={(lead) => {
            const target = {
              kind: 'lead', id: lead.id, customerName: lead.name,
              address: lead.address || '', lead,
            };
            setNewQuoteFlow({ step: 'template', target });
          }}
          onPickProject={(p) => {
            const target = {
              kind: 'project', id: p.id, customerName: p.customer || p.customerName,
              address: p.address || '', project: p,
            };
            setNewQuoteFlow({ step: 'template', target });
          }}
          onPickNewCustomer={() => setNewQuoteFlow({ step: 'newCustomer' })}
          onClose={() => setNewQuoteFlow(null)}
        />
      )}
      {newQuoteFlow?.step === 'newCustomer' && (
        <NewCustomerQuickForm
          onCancel={() => setNewQuoteFlow({ step: 'customer' })}
          onCreate={(formData) => {
            const newLead = onAddLead && onAddLead({
              name: formData.name,
              address: formData.address || '',
              phone: formData.phone || '',
              email: formData.email || '',
              stage: 'lead',
              trade: 'roofing',
              value: 0,
              createdDate: new Date().toISOString(),
            });
            if (!newLead) {
              showToast('Could not create lead.', 'error');
              setNewQuoteFlow(null);
              return;
            }
            const target = {
              kind: 'lead', id: newLead.id, customerName: newLead.name,
              address: newLead.address || '', lead: newLead,
            };
            setNewQuoteFlow({ step: 'template', target });
          }}
        />
      )}
      {newQuoteFlow?.step === 'template' && newQuoteFlow.target && (
        <QuoteModePickerModal
          target={newQuoteFlow.target}
          quotes={quotes}
          onPick={(quote) => setNewQuoteFlow({ ...newQuoteFlow, step: 'editor', quote })}
          onClose={() => setNewQuoteFlow(null)}
        />
      )}
      {newQuoteFlow?.step === 'editor' && newQuoteFlow.target && newQuoteFlow.quote && (
        <QuoteDocumentEditor
          target={newQuoteFlow.target}
          initialQuote={newQuoteFlow.quote}
          onSave={(q) => { onSaveQuote(q); setNewQuoteFlow(prev => prev ? { ...prev, quote: q } : prev); }}
          onMarkSigned={(q) => onSaveQuote(q)}
          onConvertToProject={(lead, signedQuote) => {
            if (onConvertLead) { onConvertLead(lead, signedQuote); setNewQuoteFlow(null); }
          }}
          onClose={() => setNewQuoteFlow(null)}
        />
      )}

      {/* Existing-quote editor */}
      {openQuote && (
        <QuoteDocumentEditor
          target={openQuote.target}
          initialQuote={openQuote.quote}
          onSave={(q) => { onSaveQuote(q); setOpenQuote(prev => prev ? { ...prev, quote: q } : prev); }}
          onMarkSigned={(q) => onSaveQuote(q)}
          onConvertToProject={(lead, signedQuote) => {
            if (onConvertLead) { onConvertLead(lead, signedQuote); setOpenQuote(null); }
          }}
          onClose={() => setOpenQuote(null)}
        />
      )}
    </div>
  );
}

function QuoteRow({ quote, customer, statusColor, lastActivity, needsAction, onTap, onLongPress }) {
  const pressTimerRef = useRef(null);
  const longFiredRef = useRef(false);

  const startPress = () => {
    longFiredRef.current = false;
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      longFiredRef.current = true;
      onLongPress();
    }, 500);
  };
  const cancelPress = () => {
    if (pressTimerRef.current) { clearTimeout(pressTimerRef.current); pressTimerRef.current = null; }
  };
  const onClick = () => {
    if (longFiredRef.current) { longFiredRef.current = false; return; }
    onTap();
  };

  return (
    <div
      onClick={onClick}
      onPointerDown={startPress}
      onPointerUp={cancelPress}
      onPointerLeave={cancelPress}
      onPointerCancel={cancelPress}
      onContextMenu={e => { e.preventDefault(); onLongPress(); }}
      style={{
        padding: 12, background: '#161B22',
        border: `1px solid ${needsAction ? 'rgba(245,158,11,0.4)' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: 10, cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent', userSelect: 'none',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', fontFamily: 'ui-monospace, monospace' }}>
          {quote.quoteNumber || quote.id}
        </span>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
          background: statusColor + '22', color: statusColor,
          textTransform: 'uppercase', letterSpacing: '0.4px',
        }}>{quote.status}</span>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
          background: 'rgba(255,255,255,0.06)', color: '#D1D5DB',
          textTransform: 'uppercase', letterSpacing: '0.4px',
        }}>{quote.mode}</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF' }}>
          ${(quote.total || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#FFFFFF' }}>
        {customer ? customer.name : '(no customer)'}
      </div>
      {customer?.address && (
        <div style={{ fontSize: 11, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {customer.address}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
        <span style={{
          fontSize: 10, padding: '2px 6px', borderRadius: 4,
          background: customer?.kind === 'project' ? 'rgba(59,130,246,0.15)' : 'rgba(232,114,42,0.15)',
          color: customer?.kind === 'project' ? '#60a5fa' : '#E8722A',
          fontWeight: 700,
        }}>
          {customer ? (customer.kind === 'lead' ? '→ Lead' : '→ Project') : '— Unanchored'}
        </span>
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>{lastActivity}</span>
      </div>
    </div>
  );
}

function ActionSheet({ title, actions, onClose }) {
  const isMobile = useMobile();
  useScrollLock();
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100,
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        padding: isMobile ? 0 : 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#1E2329', borderRadius: isMobile ? '16px 16px 0 0' : 12,
          width: '100%', maxWidth: 380,
          padding: 10, display: 'flex', flexDirection: 'column', gap: 4,
        }}
      >
        <div style={{ padding: '8px 12px', color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>{title}</div>
        {actions.map((a, i) => (
          <button
            key={i}
            onClick={a.onClick}
            disabled={a.disabled}
            style={{
              padding: '12px 14px', minHeight: 44,
              background: a.disabled ? 'transparent' : '#2A3140',
              border: 'none', borderRadius: 8,
              color: a.disabled ? '#4A5568' : a.danger ? '#f87171' : '#FFFFFF',
              fontSize: 13, fontWeight: 700, textAlign: 'left',
              cursor: a.disabled ? 'not-allowed' : 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >{a.label}</button>
        ))}
        <button
          onClick={onClose}
          style={{
            padding: '12px 14px', minHeight: 44, marginTop: 4,
            background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8, color: '#D1D5DB', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >Cancel</button>
      </div>
    </div>
  );
}

function NewQuoteCustomerPicker({ leads, projects, onPickLead, onPickProject, onPickNewCustomer, onClose }) {
  const isMobile = useMobile();
  useScrollLock();
  const [tab, setTab] = useState('lead'); // 'lead' | 'project'
  const [filter, setFilter] = useState('');

  const filtered = (tab === 'lead' ? leads : projects).filter(item => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    const name = tab === 'lead' ? item.name : (item.customer || item.customerName || '');
    const addr = item.address || '';
    return name.toLowerCase().includes(q) || addr.toLowerCase().includes(q);
  });

  const overlay = isMobile ? { ...S.overlay, padding: 0, alignItems: 'flex-end' } : S.overlay;
  const modal = isMobile
    ? { ...S.modal, maxWidth: '100vw', width: '100vw', maxHeight: '92dvh', borderRadius: '16px 16px 0 0', margin: 0, display: 'flex', flexDirection: 'column' }
    : { ...S.modal, maxWidth: 520, display: 'flex', flexDirection: 'column', maxHeight: '85vh' };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        <div style={{ ...S.modalTitle, paddingRight: 48 }}>Quote for whom?</div>
        <div style={{ ...S.modalSub, marginBottom: 12 }}>Pick an existing customer or create a new one.</div>

        <button
          onClick={onPickNewCustomer}
          style={{
            width: '100%', padding: '14px', marginBottom: 12, minHeight: 48,
            background: 'linear-gradient(135deg, #2D5016, #3d6b1e)',
            border: 'none', borderRadius: 10,
            color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >+ New Customer</button>

        <div style={{ display: 'flex', gap: 4, background: '#161B22', borderRadius: 8, padding: 4, marginBottom: 10 }}>
          <button
            onClick={() => setTab('lead')}
            style={{
              flex: 1, padding: '8px', minHeight: 36,
              background: tab === 'lead' ? 'linear-gradient(135deg, #E8722A, #e8640c)' : 'transparent',
              border: 'none', borderRadius: 6,
              color: tab === 'lead' ? '#fff' : '#D1D5DB',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >Existing Lead ({leads.length})</button>
          <button
            onClick={() => setTab('project')}
            style={{
              flex: 1, padding: '8px', minHeight: 36,
              background: tab === 'project' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'transparent',
              border: 'none', borderRadius: 6,
              color: tab === 'project' ? '#fff' : '#D1D5DB',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >Existing Project ({projects.length})</button>
        </div>

        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={`Search ${tab === 'lead' ? 'leads' : 'projects'}…`}
          style={{
            width: '100%', padding: '10px 12px', marginBottom: 10, minHeight: 40,
            background: '#161B22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
            color: '#FFFFFF', fontSize: isMobile ? 16 : 13, outline: 'none', boxSizing: 'border-box',
          }}
        />

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {filtered.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>
              {filter.trim() ? 'No matches.' : `No ${tab === 'lead' ? 'leads' : 'projects'} yet.`}
            </div>
          )}
          {filtered.map(item => {
            const name = tab === 'lead' ? item.name : (item.customer || item.customerName || '(unnamed)');
            const addr = item.address || '';
            return (
              <button
                key={item.id}
                onClick={() => tab === 'lead' ? onPickLead(item) : onPickProject(item)}
                style={{
                  textAlign: 'left', padding: 12,
                  background: '#161B22', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, color: '#FFFFFF', cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>{name}</div>
                {addr && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{addr}</div>}
                {tab === 'lead' && item.stage && (
                  <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    Stage: {String(item.stage).replace(/_/g, ' ')}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function NewCustomerQuickForm({ onCancel, onCreate }) {
  const isMobile = useMobile();
  useScrollLock();
  const [form, setForm] = useState({ name: '', address: '', phone: '', email: '' });

  const inputStyle = {
    width: '100%', padding: '10px 12px', minHeight: 40,
    background: '#161B22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
    color: '#FFFFFF', fontSize: isMobile ? 16 : 13, outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box',
  };

  const overlay = isMobile ? { ...S.overlay, padding: 0, alignItems: 'flex-end' } : S.overlay;
  const modal = isMobile
    ? { ...S.modal, maxWidth: '100vw', width: '100vw', maxHeight: '92dvh', borderRadius: '16px 16px 0 0', margin: 0 }
    : { ...S.modal, maxWidth: 460 };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onCancel}>×</button>
        <div style={{ ...S.modalTitle, paddingRight: 48 }}>New Customer</div>
        <div style={{ ...S.modalSub, marginBottom: 14 }}>Creates a lead in the pipeline at "Lead" stage so you can quote them now.</div>

        <FieldRow label="Name *">
          <input
            value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            placeholder="Allen Yoder"
            style={inputStyle}
          />
        </FieldRow>
        <FieldRow label="Address">
          <input
            value={form.address}
            onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
            placeholder="1234 Main St, Mifflinburg PA 17844"
            style={inputStyle}
          />
        </FieldRow>
        <FieldRow label="Phone">
          <input
            value={form.phone}
            onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
            placeholder="(570) 555-0142"
            style={inputStyle}
            inputMode="tel"
          />
        </FieldRow>
        <FieldRow label="Email">
          <input
            value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            placeholder="allen@example.com"
            style={inputStyle}
            inputMode="email"
          />
        </FieldRow>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: '12px', minHeight: 44,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 8, color: '#D1D5DB', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
          >Back</button>
          <button
            onClick={() => onCreate(form)}
            disabled={!form.name.trim()}
            style={{
              flex: 2, padding: '12px', minHeight: 44,
              background: form.name.trim() ? 'linear-gradient(135deg, #E8722A, #e8640c)' : 'rgba(255,255,255,0.06)',
              border: 'none', borderRadius: 8,
              color: form.name.trim() ? '#fff' : '#9CA3AF', fontSize: 13, fontWeight: 700,
              cursor: form.name.trim() ? 'pointer' : 'not-allowed',
              WebkitTapHighlightColor: 'transparent',
            }}
          >Create &amp; Start Quote</button>
        </div>
      </div>
    </div>
  );
}


function JobsTab({ jobs, customChecklist, crew, assignments, onAssign, onUnassign, onAddCrew, currentUser, demoMessages, onComplete, onUpdateSteps, onUpdateSchedule, rolePerms }) {
  const [selectedJob, setSelectedJob] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [filter, setFilter] = useState('all');
  const [tradeFilter, setTradeFilter] = useState('all');

  const statuses = ['all', 'Scheduled', 'In Progress', 'Complete'];

  const filtered = useMemo(() => {
    let result = filter === 'all' ? jobs : jobs.filter(j => j.status === filter);
    if (tradeFilter === '__OTHER__') {
      result = result.filter(j => j.trade && !TRADE_LIST.includes(j.trade));
    } else if (tradeFilter !== 'all') {
      result = result.filter(j => j.trade === tradeFilter);
    }
    return result;
  }, [jobs, filter, tradeFilter]);

  const statusColor = (s) => ({ Scheduled: '#6366f1', 'In Progress': '#E8722A', Complete: '#22c55e' }[s] || '#8B95A1');

  return (
    <div>
      <div style={S.filterRow}>
        {statuses.map(s => (
          <button key={s} style={S.filterBtn(filter === s)} onClick={() => setFilter(s)}>
            {s === 'all'
              ? `All (${jobs.length})`
              : `${s} (${jobs.filter(j => j.status === s).length})`}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: 13, color: '#D1D5DB' }}>
          Total: <span style={{ color: '#E8722A', fontWeight: 700, marginLeft: 4 }}>
            {rolePerms?.seeDollars !== false ? fmt(filtered.reduce((s, j) => s + j.value, 0)) : '—'}
          </span>
        </div>
      </div>

      <div style={S.tradeFilterRow}>
        <button
          style={S.tradeFilterBtn(tradeFilter === 'all', '#E8722A')}
          onClick={() => setTradeFilter('all')}
        >
          All Job Types
        </button>
        {TRADE_LIST.map(t => (
          <button
            key={t}
            style={S.tradeFilterBtn(tradeFilter === t, TRADE_COLORS[t])}
            onClick={() => setTradeFilter(tradeFilter === t ? 'all' : t)}
          >
            {t}
          </button>
        ))}
        <button
          style={S.tradeFilterBtn(tradeFilter === '__OTHER__', CUSTOM_TRADE_COLOR)}
          onClick={() => setTradeFilter(tradeFilter === '__OTHER__' ? 'all' : '__OTHER__')}
        >
          Other
        </button>
      </div>

      <div style={S.grid}>
        {filtered.length === 0 && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '48px 24px' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🔨</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#D1D5DB', marginBottom: 4 }}>No jobs match your filters</div>
            <div style={{ fontSize: 13, color: '#D1D5DB' }}>Try adjusting the status or job type filter above.</div>
          </div>
        )}
        {filtered.map(job => {
          const steps = TRADE_CHECKLISTS[job.trade]
            || (customChecklist ? customChecklist.map((label, i) => ({ id: i + 1, label })) : null)
            || TRADE_CHECKLISTS['Full Replacement'];
          const doneCount = job.completedSteps.length;
          const total = steps.length;
          const pct = Math.round(doneCount / total * 100);
          const color = statusColor(job.status);
          const tradeColor = getTradeColor(job.trade);

          return (
            <div
              key={job.id}
              style={S.card(hovered === job.id)}
              onMouseEnter={() => setHovered(job.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => setSelectedJob(job)}
            >
              <div style={S.cardHeader}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.cardName}>{job.customer}</div>
                  <div style={S.cardContact}>{job.address}</div>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                  background: color + '22', color, textTransform: 'uppercase',
                  letterSpacing: '0.5px', whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {job.status}
                </span>
              </div>

              <span style={{
                display: 'inline-block', fontSize: 10, fontWeight: 600,
                padding: '2px 8px', borderRadius: 10, marginBottom: 8,
                background: tradeColor + '22', color: tradeColor,
                letterSpacing: '0.3px',
                maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle',
              }}>
                {job.trade}
              </span>
              {job.jobType && (
                <span style={{
                  display: 'inline-block', fontSize: 10, fontWeight: 600,
                  padding: '2px 8px', borderRadius: 10, marginBottom: 8, marginLeft: 6,
                  background: 'rgba(45,80,22,0.15)', color: '#2D5016',
                  letterSpacing: '0.3px',
                }}>
                  Phase {job.currentPhase || 1}/{getDefaultPhases(job.jobType).length}
                </span>
              )}

              <div style={S.cardMeta}>
                <div style={S.metaItem}>Value: <span style={{ ...S.metaValue, color: '#22c55e' }}>{rolePerms?.seeDollars !== false ? fmt(job.value) : '—'}</span></div>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 11, color: '#D1D5DB', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                    {steps[doneCount < total ? doneCount : total - 1]?.label || 'Complete'}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color, flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {doneCount}/{total}
                  </span>
                </div>
                <div style={S.progressTrack}>
                  <div style={S.progressFill(pct, color)} />
                </div>
              </div>

              <div style={{ marginTop: 10, fontSize: 11, color: '#D1D5DB' }}>
                📅 Scheduled: {job.scheduledDate}
              </div>
            </div>
          );
        })}
      </div>

      {selectedJob && (
        <JobModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          customChecklist={customChecklist}
          crew={crew}
          assignments={assignments}
          onAssign={onAssign}
          onUnassign={onUnassign}
          onAddCrew={onAddCrew}
          currentUser={currentUser}
          demoMessages={demoMessages ? demoMessages.filter(m => String(m.jobId) === String(selectedJob.id)) : null}
          onComplete={onComplete ? (id) => { onComplete(id); setSelectedJob(null); } : null}
          onUpdateSteps={onUpdateSteps}
          onUpdateSchedule={onUpdateSchedule}
          rolePerms={rolePerms}
          isDemo={!!demoMessages}
        />
      )}
    </div>
  );
}

// ─── Team Tab ─────────────────────────────────────────────────────────────────
const DEMO_TEAM = [
  {
    id: 't1', name: 'Marcus Johnson', initials: 'MJ',
    color: '#6366f1', role: 'Senior Sales Rep', status: 'active',
    leads: 24, won: 8, lost: 3, winRate: 73,
    pipelineValue: 187400, revenue: 142000,
    callbacksDue: 6, avgDealAge: 28,
  },
  {
    id: 't2', name: 'Sara Chen', initials: 'SC',
    color: '#22c55e', role: 'Sales Rep', status: 'active',
    leads: 19, won: 9, lost: 2, winRate: 82,
    pipelineValue: 221600, revenue: 198500,
    callbacksDue: 3, avgDealAge: 21,
  },
  {
    id: 't3', name: 'Dale Russo', initials: 'DR',
    color: '#E8722A', role: 'Sales Rep', status: 'away',
    leads: 11, won: 3, lost: 4, winRate: 43,
    pipelineValue: 94200, revenue: 67800,
    callbacksDue: 8, avgDealAge: 41,
  },
];

function TeamTab() {
  const [hovered, setHovered] = useState(null);
  const totals = {
    leads: DEMO_TEAM.reduce((s, r) => s + r.leads, 0),
    revenue: DEMO_TEAM.reduce((s, r) => s + r.revenue, 0),
    pipeline: DEMO_TEAM.reduce((s, r) => s + r.pipelineValue, 0),
    callbacks: DEMO_TEAM.reduce((s, r) => s + r.callbacksDue, 0),
  };

  return (
    <div style={{ padding: '0 2px' }}>
      {/* Summary bar */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24,
      }}>
        {[
          { label: 'Total Leads', value: totals.leads, color: '#6366f1' },
          { label: 'Pipeline Value', value: fmt(totals.pipeline), color: '#E8722A' },
          { label: 'Revenue Closed', value: fmt(totals.revenue), color: '#22c55e' },
          { label: 'Callbacks Due', value: totals.callbacks, color: '#ef4444' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
            padding: '14px 16px',
          }}>
            <div style={{ fontSize: 11, color: '#D1D5DB', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Rep cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {DEMO_TEAM.map(rep => (
          <div
            key={rep.id}
            style={{
              background: hovered === rep.id ? '#1a2035' : '#2A3140',
              border: `1px solid ${hovered === rep.id ? rep.color + '44' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 12, padding: '16px 20px',
              display: 'grid', gridTemplateColumns: 'auto 1fr repeat(5, auto)',
              gap: 16, alignItems: 'center',
              transition: 'all 0.15s', cursor: 'default',
            }}
            onMouseEnter={() => setHovered(rep.id)}
            onMouseLeave={() => setHovered(null)}
          >
            {/* Avatar */}
            <div style={{
              width: 42, height: 42, borderRadius: '50%',
              background: rep.color + '22', border: `2px solid ${rep.color}44`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, color: rep.color, flexShrink: 0,
            }}>
              {rep.initials}
            </div>

            {/* Name + role */}
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#D1D5DB', display: 'flex', alignItems: 'center', gap: 8 }}>
                {rep.name}
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10,
                  background: rep.status === 'active' ? '#22c55e22' : '#6b728022',
                  color: rep.status === 'active' ? '#22c55e' : '#6b7280',
                  border: `1px solid ${rep.status === 'active' ? '#22c55e33' : '#6b728033'}`,
                }}>
                  {rep.status === 'active' ? 'Active' : 'Away'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 2 }}>{rep.role}</div>
            </div>

            {/* Stats */}
            {[
              { label: 'Leads', value: rep.leads, color: '#D1D5DB' },
              { label: 'Win Rate', value: `${rep.winRate}%`, color: rep.winRate >= 70 ? '#22c55e' : rep.winRate >= 50 ? '#E8722A' : '#ef4444' },
              { label: 'Pipeline', value: fmt(rep.pipelineValue), color: '#E8722A' },
              { label: 'Closed Revenue', value: fmt(rep.revenue), color: '#22c55e' },
              { label: 'Callbacks Due', value: rep.callbacksDue, color: rep.callbacksDue > 5 ? '#ef4444' : '#D1D5DB' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ textAlign: 'center', minWidth: 80 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
                <div style={{ fontSize: 10, color: '#D1D5DB', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Auth Styles ─────────────────────────────────────────────────────────────
const A = {
  page: {
    minHeight: '100vh',
    background: '#2A3140',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    background: '#2A3140',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    padding: '40px 40px',
    width: '100%',
    maxWidth: 440,
  },
  logo: {
    fontSize: 28,
    fontWeight: 800,
    color: '#E8722A',
    letterSpacing: '-1px',
    textAlign: 'center',
    marginBottom: 8,
  },
  tagline: {
    fontSize: 13,
    color: '#D1D5DB',
    textAlign: 'center',
    marginBottom: 36,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#D1D5DB',
    marginBottom: 6,
    marginTop: 18,
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    background: '#1E2329',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8,
    color: '#FFFFFF',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  inputFocus: {
    border: '1px solid #E8722A',
  },
  btn: {
    width: '100%',
    padding: '11px 16px',
    marginTop: 24,
    background: 'linear-gradient(135deg, #E8722A, #ea580c)',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.2px',
  },
  btnSecondary: {
    width: '100%',
    padding: '11px 16px',
    marginTop: 10,
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8,
    color: '#D1D5DB',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  link: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: 13,
    color: '#D1D5DB',
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#E8722A',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    padding: 0,
  },
  error: {
    marginTop: 14,
    padding: '10px 12px',
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.2)',
    borderRadius: 8,
    color: '#ef4444',
    fontSize: 12,
  },
  stepIndicator: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 28,
  },
  stepDot: (active, done) => ({
    width: active ? 24 : 8,
    height: 8,
    borderRadius: 4,
    background: done ? '#E8722A' : active ? '#E8722A' : 'rgba(255,255,255,0.08)',
    transition: 'all 0.2s',
  }),
  stepTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#FFFFFF',
    marginBottom: 4,
  },
  stepSub: {
    fontSize: 13,
    color: '#D1D5DB',
    marginBottom: 24,
  },
  tradeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    marginBottom: 24,
    maxHeight: 340,
    overflowY: 'auto',
    paddingRight: 4,
  },
  tradeCard: (selected, color) => ({
    padding: '10px 8px',
    borderRadius: 8,
    border: `1px solid ${selected ? color : 'rgba(255,255,255,0.08)'}`,
    background: selected ? color + '18' : '#2A3140',
    color: selected ? color : '#D1D5DB',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: selected ? 600 : 400,
    textAlign: 'center',
    transition: 'all 0.12s',
  }),
  planGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
    marginBottom: 24,
  },
  planCard: (selected) => ({
    padding: '20px 16px',
    borderRadius: 10,
    border: `2px solid ${selected ? '#E8722A' : 'rgba(255,255,255,0.08)'}`,
    background: selected ? 'rgba(249,115,22,0.06)' : '#2A3140',
    cursor: 'pointer',
    transition: 'all 0.12s',
    position: 'relative',
  }),
  planName: { fontSize: 14, fontWeight: 700, color: '#FFFFFF', marginBottom: 4 },
  planPrice: { fontSize: 22, fontWeight: 800, color: '#E8722A', marginBottom: 12 },
  planPriceSub: { fontSize: 11, color: '#D1D5DB', fontWeight: 400 },
  planFeature: {
    fontSize: 11,
    color: '#D1D5DB',
    marginBottom: 5,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
  },
  planCheck: { color: '#22c55e', fontWeight: 700, flexShrink: 0 },
  popularBadge: {
    position: 'absolute',
    top: -10,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#E8722A',
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    padding: '2px 10px',
    borderRadius: 10,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
};

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$29',
    features: ['Up to 25 active leads', 'Job checklists', 'Job progress tracking', 'Basic analytics', 'Email support'],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$79',
    popular: true,
    features: ['Unlimited leads', 'All 9 job types', 'AI coaching (Claude)', 'Advanced analytics', 'Callbacks & reminders', 'Priority support'],
  },
  {
    id: 'business',
    name: 'Business',
    price: '$149',
    features: ['Everything in Pro', 'Multi-user access', 'Team analytics', 'Custom checklist steps', 'Dedicated account manager', 'API access'],
  },
];

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin, onStartSignup }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email === 'demo@ridgeos.com' && password === 'demo123') {
      onLogin({ isDemo: true, companyName: 'Keystone Roofing', userName: 'Demo Owner', trade: 'Full Replacement', plan: 'pro' });
    } else if (email && password) {
      setError('Incorrect email or password. Try demo@ridgeos.com / demo123');
    } else {
      setError('Please enter your email and password.');
    }
  };

  const inputStyle = (field) => ({
    ...A.input,
    ...(focusedField === field ? A.inputFocus : {}),
  });

  return (
    <div style={A.page}>
      <div style={A.card}>
        <div style={A.logo}>RidgeOS</div>
        <div style={A.tagline}>By roofers, for roofers.</div>

        <div style={{ marginBottom: 8 }}>
          <button
            onClick={() => onLogin({ isDemo: true, companyName: 'Keystone Roofing', userName: 'Demo Owner', trade: 'Full Replacement', plan: 'pro' })}
            style={{ width: '100%', height: 48, background: '#e8722a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', letterSpacing: '-0.3px' }}
          >
            Try Demo — No signup needed
          </button>
          <div style={{ textAlign: 'center', fontSize: 12, color: '#D1D5DB', marginTop: 8 }}>
            Preloaded with real roofing data
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
          <span style={{ fontSize: 12, color: '#D1D5DB', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>or sign in</span>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
        </div>

        <form onSubmit={handleSubmit}>
          <label style={A.label}>Email</label>
          <input
            style={inputStyle('email')}
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onFocus={() => setFocusedField('email')}
            onBlur={() => setFocusedField(null)}
            autoComplete="email"
          />

          <label style={A.label}>Password</label>
          <input
            style={inputStyle('password')}
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onFocus={() => setFocusedField('password')}
            onBlur={() => setFocusedField(null)}
            autoComplete="current-password"
          />

          {error && <div style={A.error}>{error}</div>}

          <button style={A.btn} type="submit">Sign In</button>
        </form>

        <div style={A.link}>
          New here?{' '}
          <button style={A.linkBtn} onClick={onStartSignup}>Create an account</button>
        </div>

      </div>
    </div>
  );
}

// ─── Onboarding Flow ──────────────────────────────────────────────────────────
function OnboardingFlow({ onComplete, onBackToLogin }) {
  const [step, setStep] = useState(1);
  const [focusedField, setFocusedField] = useState(null);
  const [form, setForm] = useState({
    companyName: '', userName: '', email: '', password: '',
    trade: '', plan: 'pro',
  });
  const [errors, setErrors] = useState({});
  const [customTradeInput, setCustomTradeInput] = useState('');
  const [customTradeConfig, setCustomTradeConfig] = useState(null);
  const [customTradeLoading, setCustomTradeLoading] = useState(false);
  const [customTradeError, setCustomTradeError] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const inputStyle = (field) => ({
    ...A.input,
    ...(focusedField === field ? A.inputFocus : {}),
    ...(errors[field] ? { border: '1px solid #ef4444' } : {}),
  });

  const validateStep1 = () => {
    const e = {};
    if (!form.companyName.trim()) e.companyName = true;
    if (!form.userName.trim()) e.userName = true;
    if (!form.email.trim()) e.email = true;
    if (form.password.length < 6) e.password = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !form.trade) {
      setErrors({ trade: true });
      return;
    }
    setErrors({});
    setStep(s => s + 1);
  };

  const handleGenerateCustomTrade = async () => {
    const tradeName = customTradeInput.trim();
    if (!tradeName) return;
    setCustomTradeLoading(true);
    setCustomTradeError('');
    setCustomTradeConfig(null);
    try {
      const res = await fetch('/api/custom-trade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tradeName }),
      });
      const data = await res.json();
      if (!res.ok) {
        setCustomTradeError(data.error || "Couldn't generate that job type — try being more specific.");
      } else {
        setCustomTradeConfig(data);
        set('trade', tradeName);
        setErrors({});
      }
    } catch {
      setCustomTradeError("Couldn't generate that job type — try being more specific.");
    } finally {
      setCustomTradeLoading(false);
    }
  };

  const handleComplete = () => {
    onComplete({
      isDemo: false,
      companyName: form.companyName,
      userName: form.userName,
      email: form.email,
      trade: form.trade,
      plan: form.plan,
      customTradeConfig: customTradeConfig || null,
    });
  };

  return (
    <div style={A.page}>
      <div style={{ ...A.card, maxWidth: step === 2 ? 560 : step === 3 ? 720 : 440 }}>
        <div style={A.logo}>RidgeOS</div>

        <div style={A.stepIndicator}>
          {[1, 2, 3].map(n => (
            <div key={n} style={A.stepDot(step === n, step > n)} />
          ))}
        </div>

        {/* ── Step 1: Company Info ── */}
        {step === 1 && (
          <>
            <div style={A.stepTitle}>Set up your account</div>
            <div style={A.stepSub}>Just a few details to get started.</div>

            <label style={A.label}>Company name</label>
            <input
              style={inputStyle('companyName')}
              placeholder="Apex Roofing LLC"
              value={form.companyName}
              onChange={e => set('companyName', e.target.value)}
              onFocus={() => setFocusedField('companyName')}
              onBlur={() => setFocusedField(null)}
            />

            <label style={A.label}>Your name</label>
            <input
              style={inputStyle('userName')}
              placeholder="Jane Smith"
              value={form.userName}
              onChange={e => set('userName', e.target.value)}
              onFocus={() => setFocusedField('userName')}
              onBlur={() => setFocusedField(null)}
            />

            <label style={A.label}>Email</label>
            <input
              style={inputStyle('email')}
              type="email"
              placeholder="jane@apexroofing.com"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
            />

            <label style={A.label}>Password</label>
            <input
              style={inputStyle('password')}
              type="password"
              placeholder="Min. 6 characters"
              value={form.password}
              onChange={e => set('password', e.target.value)}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
            />

            {errors.password && (
              <div style={{ fontSize: 11, color: '#ef4444', marginTop: 6 }}>
                Password must be at least 6 characters.
              </div>
            )}
            {(errors.companyName || errors.userName || errors.email) && (
              <div style={{ fontSize: 11, color: '#ef4444', marginTop: 6 }}>
                Please fill in all fields.
              </div>
            )}

            <button style={A.btn} onClick={handleNext}>Next →</button>
            <div style={A.link}>
              Already have an account?{' '}
              <button style={A.linkBtn} onClick={onBackToLogin}>Sign in</button>
            </div>
          </>
        )}

        {/* ── Step 2: Pick Trade ── */}
        {step === 2 && (
          <>
            <div style={A.stepTitle}>What type of roofing work do you do?</div>
            <div style={A.stepSub}>
              {errors.trade
                ? <span style={{ color: '#ef4444' }}>Please select a job type to continue.</span>
                : 'Select the job type you do most often.'}
            </div>

            <div style={A.tradeGrid}>
              {TRADE_LIST.map(trade => (
                <div
                  key={trade}
                  style={A.tradeCard(form.trade === trade, TRADE_COLORS[trade])}
                  onClick={() => { set('trade', trade); setErrors({}); setCustomTradeConfig(null); setShowCustomInput(false); }}
                >
                  {trade}
                </div>
              ))}
            </div>

            {/* Custom trade section */}
            {!showCustomInput ? (
              <button
                onClick={() => { setShowCustomInput(true); setErrors({}); }}
                style={{
                  width: '100%', marginBottom: 16, padding: '9px 12px',
                  background: 'transparent',
                  border: '1px dashed rgba(255,255,255,0.08)',
                  borderRadius: 8, color: '#D1D5DB',
                  fontSize: 13, cursor: 'pointer', textAlign: 'center',
                }}
              >
                ✨ My job type isn't listed — generate it with AI
              </button>
            ) : (
              <div style={{
                background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 8, padding: '14px', marginBottom: 16,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#D1D5DB', marginBottom: 10 }}>
                  ✨ Custom Job Type Generator
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    value={customTradeInput}
                    onChange={e => { setCustomTradeInput(e.target.value); setCustomTradeError(''); }}
                    placeholder="e.g. Metal Roofing, Slate Restoration, TPO Systems"
                    style={{
                      ...A.input, flex: 1, fontSize: 13, padding: '9px 12px',
                    }}
                    onKeyDown={e => e.key === 'Enter' && handleGenerateCustomTrade()}
                  />
                  <button
                    onClick={handleGenerateCustomTrade}
                    disabled={customTradeLoading || !customTradeInput.trim()}
                    style={{
                      padding: '9px 16px',
                      background: customTradeLoading ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #6366f1, #4f46e5)',
                      border: 'none', borderRadius: 7,
                      color: customTradeLoading ? '#8B95A1' : '#fff',
                      fontWeight: 700, fontSize: 13,
                      cursor: customTradeLoading || !customTradeInput.trim() ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {customTradeLoading ? '⟳ Generating…' : 'Generate'}
                  </button>
                </div>
                {customTradeError && (
                  <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 6 }}>{customTradeError}</div>
                )}
                {customTradeConfig && (
                  <div style={{ fontSize: 11, color: '#22c55e', marginBottom: 6 }}>
                    ✓ Job type "{form.trade}" generated — {customTradeConfig.checklist.length} checklist steps, {customTradeConfig.pipeline.length} pipeline stages
                  </div>
                )}
              </div>
            )}

            <button style={A.btn} onClick={handleNext}>Next →</button>
            <button style={A.btnSecondary} onClick={() => setStep(1)}>← Back</button>
          </>
        )}

        {/* ── Step 3: Pick Plan ── */}
        {step === 3 && (
          <>
            <div style={A.stepTitle}>Choose your plan</div>
            <div style={A.stepSub}>All plans include a 14-day free trial. No card required.</div>

            <div style={A.planGrid}>
              {PLANS.map(plan => (
                <div
                  key={plan.id}
                  style={A.planCard(form.plan === plan.id)}
                  onClick={() => set('plan', plan.id)}
                >
                  {plan.popular && <div style={A.popularBadge}>Most Popular</div>}
                  <div style={A.planName}>{plan.name}</div>
                  <div style={A.planPrice}>
                    {plan.price}
                    <span style={A.planPriceSub}>/mo</span>
                  </div>
                  {plan.features.map(f => (
                    <div key={f} style={A.planFeature}>
                      <span style={A.planCheck}>✓</span>
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <button style={A.btn} onClick={handleComplete}>Start Free Trial →</button>
            <button style={A.btnSecondary} onClick={() => setStep(2)}>← Back</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ icon, title, sub, btnLabel, onAction }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '80px 24px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.4 }}>{icon}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: '#D1D5DB', marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#D1D5DB', marginBottom: 28, maxWidth: 320 }}>{sub}</div>
      <button
        style={{
          padding: '10px 24px',
          background: 'linear-gradient(135deg, #E8722A, #ea580c)',
          border: 'none', borderRadius: 8,
          color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
        }}
        onClick={onAction}
      >
        {btnLabel}
      </button>
    </div>
  );
}

// ─── Demo Page ────────────────────────────────────────────────────────────────
const TRADE_ICONS = {
  'Full Replacement': '🏠', 'Repair': '🔧', 'Inspection': '🔍',
  'Storm Damage': '⛈️', 'Gutter Install': '🌧', 'Skylight': '☀️',
  'Flashing Repair': '🛡', 'Ventilation': '💨', 'Emergency Tarp': '🚨',
};

// compact lead builder
const dl = (id, name, contact, role, status, value, stage, callbackDate, stallReason, notes, industry, dealAge, trade) => ({
  id: `dl_${id}`, name, contact, role, trade, status, value, stage,
  callbackDate, lastContact: '2026-03-20', stallReason, notes,
  industry, dealAge, phone: '', source: 'Referral',
});
// compact job builder
const dj = (id, customer, address, trade, value, status, scheduledDate, completedSteps, notes) => ({
  id: `dj_${id}`, customer, address, trade, value, status, scheduledDate, completedSteps, notes,
});

const TRADE_DEMO_DATA = {
  'Full Replacement': {
    leads: [
      dl(1,'Shumaker Residence','Dave Shumaker','Homeowner','active',14800,'estimate_sent','2026-03-25',null,'28 sq, 7/12 pitch. GAF Timberline HDZ Charcoal. 1 layer tear-off over plywood. Wants 50yr warranty.','Residential',12,'Full Replacement'),
      dl(2,'Bethany Village Retirement','Carol Snyder','Facilities Dir','stalled',34700,'contract_signed','2026-03-23','budget_freeze','Flat roof, 110 sq TPO on main building. Full board approval needed. 2 HVAC curbs to reflash.','Institutional',41,'Full Replacement'),
      dl(3,'Riverwoods HOA Phase II','Susan Yoder','HOA President','active',52000,'inspection_scheduled','2026-03-27',null,'28 townhome units, 240 sq total. Phased replacement. Architectural shingles. Ice & water at eaves.','HOA',8,'Full Replacement'),
      dl(4,'Kowalski Residence','Brian Kowalski','Homeowner','stalled',11200,'estimate_sent','2026-03-19','no_response','22 sq, 6/12 pitch. Visible wind damage on north slope. 3 calls, 2 emails. Try door knock.','Residential',29,'Full Replacement'),
      dl(5,'Monroe Twp Municipal Bldg','Diane Okafor','Facilities Mgr','active',41500,'contract_signed','2026-03-26',null,'TPO membrane, 140 sq. Contract under review with township solicitor.','Commercial',19,'Full Replacement'),
      dl(6,'Deimler Residence','Scott Deimler','Homeowner','stalled',42000,'estimate_sent','2026-03-22','price_objection','48 sq standing seam metal, 10/12 pitch. Got bid $4k lower from Lancaster crew.','Residential',33,'Full Replacement'),
      dl(7,'Milton Area School Dist','Tom Hensley','Facilities Dir','cold',78000,'lead','2026-04-15','timing','Large job — 320 sq across gym + cafeteria. School board vote in April.','Institutional',53,'Full Replacement'),
      dl(8,'Metzger Residence','Raj Metzger','Homeowner','won',14600,'paid',null,null,'Closed! CertainTeed Landmark Pro, 26 sq. 8/12 pitch. 1 layer over OSB.','Residential',24,'Full Replacement'),
      dl(9,'Klinger Trucking','Jim Klinger','Owner','stalled',27300,'contract_signed','2026-03-24','competitor','Standing seam metal on 3-bay shop, 34 sq. Getting 3 bids from valley roofers.','Commercial',37,'Full Replacement'),
      dl(10,'First Baptist Sunbury','Pastor Rick Adams','Admin Dir','lost',23400,'lost',null,'competitor','Lost to roofer from Williamsport area.','Institutional',61,'Full Replacement'),
    ],
    jobs: [
      dj(1,'Dave & Lisa Shumaker','142 Chestnut St, Mifflinburg PA 17844','Full Replacement',14800,'In Progress','2026-03-20',[1,2,3,4,5,6,7,8],'GAF Timberline HDZ Charcoal. Tear-off done, shingles going on.'),
      dj(2,'Raj & Nina Metzger','1108 Buffalo Rd, Lewisburg PA 17837','Full Replacement',14600,'Scheduled','2026-03-26',[1,2],'CertainTeed Landmark Pro. Materials at Boise Cascade Sunbury.'),
      dj(3,'Riverwoods HOA Block A','200 Riverwoods Dr, Lewisburg PA 17837','Full Replacement',24500,'Complete','2026-03-12',[1,2,3,4,5,6,7,8,9,10,11,12,13,14],'12 units Phase I. Signed off and paid.'),
      dj(4,'Monroe Twp Municipal','88 W Main St, Selinsgrove PA 17870','Full Replacement',41500,'In Progress','2026-03-18',[1,2,3],'TPO membrane, 140 sq. Underlayment next.'),
      dj(5,'Klinger Trucking Shop','1100 Industrial Park Rd, Milton PA 17847','Full Replacement',27300,'Scheduled','2026-04-02',[1],'Standing seam metal, 3-bay shop. Crew set for 4/2.'),
    ],
  },
  'Repair': {
    leads: [
      dl(1,'Northumberland Apts Bldg C','Rick Hess','Building Mgr','stalled',4200,'estimate_sent','2026-03-25',null,'Valley flashing failed. Active leak. 3-tab match, 6/12 pitch. Decking stained.','Commercial',41,'Repair'),
      dl(2,'Packwood House Museum','Patricia Landis','Facilities Mgr','active',6400,'estimate_sent','2026-03-27',null,'Slate roof — 8 cracked tiles + ridge mortar. Must match PA slate. 12/12 pitch.','Commercial',20,'Repair'),
      dl(3,'Sheetz #412','Brian Lehr','Facilities Coord','active',3800,'inspection_scheduled','2026-03-26',null,'Wind-damaged 3-tab on flat canopy + 2 pipe boots need reseal. Selinsgrove.','Commercial',12,'Repair'),
      dl(4,'Fischer Residence','Jack Fischer','Homeowner','stalled',2400,'estimate_sent','2026-03-20','price_objection','5 missing shingles from wind on north slope. Homeowner thinks $1,800 is fair. Danville.','Residential',22,'Repair'),
      dl(5,'Valley Fitness','Marcus Stover','Owner','active',5200,'contract_signed','2026-03-26',null,'Ponding water on flat section. TPO seam repair + drain install near HVAC curb.','Commercial',16,'Repair'),
      dl(6,'Chen Residence','Linda Chen','Homeowner','stalled',1800,'estimate_sent','2026-03-22','budget_freeze','Ridge cap blown off in March wind. Waiting on tax return. Watsontown.','Residential',28,'Repair'),
      dl(7,'Buffalo Valley Clubhouse','Dan Marsh','HOA Mgr','cold',3400,'lead','2026-04-10','timing','Soffit damage + fascia rot at 2 corners. Spring maintenance budget.','HOA',34,'Repair'),
      dl(8,'Porter Residence','Amy Porter','Homeowner','won',2800,'paid',null,null,'Closed! Patched 3 sq wind damage + resealed all boots. Milton.','Residential',14,'Repair'),
      dl(9,'Country Diner','Roy Burns','Owner','lost',4100,'lost',null,'competitor','Lost — handyman patched it for half price. Middleburg.','Commercial',35,'Repair'),
    ],
    jobs: [
      dj(1,'Packwood House Museum','15 N Water St, Lewisburg PA 17837','Repair',6400,'In Progress','2026-03-21',[1,2,3,4],'Slate tile replacement. 5 of 8 tiles done. Matching PA slate.'),
      dj(2,'Amy Porter','610 Front St, Milton PA 17847','Repair',2800,'Complete','2026-03-19',[1,2,3,4,5,6,7,8,9,10],'3 sq wind patch + boot reseal. Done and paid.'),
      dj(3,'Valley Fitness','1420 N Susquehanna Trail, Selinsgrove PA 17870','Repair',5200,'Scheduled','2026-04-02',[1,2],'TPO seam repair + new drain. Materials ordered.'),
      dj(4,'Northumberland Apts Bldg C','350 Front St, Northumberland PA 17857','Repair',4200,'In Progress','2026-03-18',[1,2,3,4,5],'Valley flashing replaced. Shingle match in progress.'),
    ],
  },
  'Inspection': {
    leads: [
      dl(1,'Danville Heritage Villas','Dave Moyer','Board President','cold',1800,'lead','2026-04-10',null,'15-building annual roof inspection. Roofs are 18+ years. Could lead to phased replacement.','HOA',71,'Inspection'),
      dl(2,'Milton Area School Dist','Carl Beck','Facilities Dir','active',2200,'inspection_scheduled','2026-03-27',null,'Annual condition report for 4 campus buildings. Insurance requirement.','Institutional',14,'Inspection'),
      dl(3,'Sunbury Apartments','Donna Kwan','Property Mgr','active',3400,'estimate_sent','2026-03-25',null,'32-unit complex pre-purchase inspection for new buyer.','Commercial',9,'Inspection'),
      dl(4,'Rossmoyne Business Park','Carl Stein','Developer','stalled',4800,'estimate_sent','2026-03-20','no_response','5-building portfolio inspection in Shamokin Dam. Due diligence for refinance.','Commercial',28,'Inspection'),
      dl(5,'Dreisbach Church','Deacon Paul Smith','Admin','stalled',1400,'estimate_sent','2026-03-22','budget_freeze','Annual roof check on sanctuary + fellowship hall. Budget frozen.','Institutional',33,'Inspection'),
      dl(6,'Evangelical Community Hospital','Greg Stauffer','Facilities Mgr','active',6200,'contract_signed','2026-03-26',null,'Outpatient wing warranty inspection — 1-year mark. Lewisburg.','Institutional',12,'Inspection'),
      dl(7,'Susquehanna Industrial Park','Nick Etter','GM','won',1600,'paid',null,null,'Closed! 3-building annual inspection. Report delivered. Northumberland.','Commercial',18,'Inspection'),
      dl(8,'Bloomsburg School Dist','Stan Wells','Facilities Dir','cold',5400,'lead','2026-04-15','timing','4 campus buildings. Budget not released until May.','Institutional',44,'Inspection'),
      dl(9,'Comfort Inn Selinsgrove','Lisa Cole','GM','lost',2800,'lost',null,'competitor','Lost to existing maintenance company from Williamsport.','Commercial',52,'Inspection'),
    ],
    jobs: [
      dj(1,'Susquehanna Industrial Park','825 Point Township Dr, Northumberland PA 17857','Inspection',1600,'Complete','2026-03-15',[1,2,3,4,5,6,7,8,9],'3-building inspection complete. Report delivered.'),
      dj(2,'Milton Area School Dist','700 Mahoning St, Milton PA 17847','Inspection',2200,'Scheduled','2026-03-27',[1],'4 campus buildings. Drone + ladder access confirmed.'),
      dj(3,'Evangelical Hospital — Warranty','1 Hospital Dr, Lewisburg PA 17837','Inspection',6200,'In Progress','2026-03-22',[1,2,3,4,5],'Outpatient wing. 60% inspected.'),
    ],
  },
  'Storm Damage': {
    leads: [
      dl(1,'Bowman Family','Tina Bowman','Homeowner','active',18700,'inspection_scheduled','2026-03-23',null,'Wind damage from 3/15 storm. Erie Insurance claim filed. 24 sq, 6/12 pitch. Lewisburg.','Residential',9,'Storm Damage'),
      dl(2,'Shamokin Creek Condos','Bob Reider','Board Treasurer','stalled',47500,'estimate_sent','2026-03-24','wrong_contact','6 buildings, wind + ice dam damage. Nationwide adjusting. Need full board.','HOA',62,'Storm Damage'),
      dl(3,'Christ Lutheran Mifflinburg','Pastor Ed Zimmerman','Facilities Coord','won',9400,'paid',null,null,'Closed! Wind damage — ridge cap + 3 sq on sanctuary. Nationwide covered.','Institutional',55,'Storm Damage'),
      dl(4,'Nelson Residence','Greg Nelson','Homeowner','active',11400,'estimate_sent','2026-03-27',null,'Wind lifted 6 sq architectural on south slope. Decking exposed. State Farm filed. Bloomsburg.','Residential',13,'Storm Damage'),
      dl(5,'Sunbury Housing Authority','Chad Miller','Property Mgr','stalled',54000,'contract_signed','2026-03-23','budget_freeze','20 units, wind damage. Insurance approved but deductible dispute.','Institutional',41,'Storm Damage'),
      dl(6,'Osei Residence','Kwame Osei','Homeowner','active',14200,'inspection_scheduled','2026-03-26',null,'Tree limb punched through roof during ice storm. Tarped. Adjuster scheduled. Williamsport.','Residential',8,'Storm Damage'),
      dl(7,'Danville Fire Station','Chief Pat Moore','Borough Admin','cold',31000,'lead','2026-05-01','timing','Wind damage to 2 apparatus bays. Borough insurance process is slow.','Institutional',71,'Storm Damage'),
      dl(8,'Pham Residence','Linda Pham','Homeowner','won',14900,'paid',null,null,'Closed! Full replacement after March wind. Erie Insurance paid in full. Milton.','Residential',18,'Storm Damage'),
      dl(9,'Planet Fitness Selinsgrove','Gina Cruz','Owner','lost',22000,'lost',null,'competitor','Lost — went with storm chaser outfit from out of state.','Commercial',62,'Storm Damage'),
    ],
    jobs: [
      dj(1,'Tina & Mark Bowman','227 Market St, Lewisburg PA 17837','Storm Damage',18700,'Complete','2026-03-14',[1,2,3,4,5,6,7,8,9,10,11],'Erie claim. 24 sq architectural replaced. Union County permit closed.'),
      dj(2,'Christ Lutheran','130 S 5th St, Mifflinburg PA 17844','Storm Damage',9400,'Complete','2026-03-10',[1,2,3,4,5,6,7,8,9,10,11],'Wind damage repair. Ridge cap + 3 sq. Nationwide paid.'),
      dj(3,'Linda Pham','918 Front St, Milton PA 17847','Storm Damage',14900,'Complete','2026-03-08',[1,2,3,4,5,6,7,8,9,10,11],'Full replacement after wind. CertainTeed Landmark.'),
      dj(4,'Sunbury Housing Phase 1','400 Chestnut St, Sunbury PA 17801','Storm Damage',54000,'Scheduled','2026-04-05',[1,2,3,4,5,6],'20 units wind damage. Adjuster approved. Materials ordered.'),
    ],
  },
  'Gutter Install': {
    leads: [
      dl(1,'Market Street Commons','Tom Brubaker','Property Mgr','stalled',5800,'estimate_sent','2026-03-15',null,'220 LF seamless aluminum 5" K-style + leaf guards. 1890s building facade. Sunbury.','Commercial',49,'Gutter Install'),
      dl(2,'RiverWoods Senior Living','Andrew Musser','Maintenance Dir','active',13200,'estimate_sent','2026-03-26',null,'480 LF 6" commercial + 12 downspouts on main building. Splash guards at walkways.','Institutional',22,'Gutter Install'),
      dl(3,'Brennan Residence','Pat Brennan','Homeowner','stalled',2600,'estimate_sent','2026-03-20','price_objection','140 LF seamless + guards on rancher. Competitor quoted $400 less. Watsontown.','Residential',22,'Gutter Install'),
      dl(4,'Buffalo Valley Townhomes','Karen Bender','HOA Director','active',8400,'contract_signed','2026-03-26',null,'Half-round copper on historic-style facade. 14 townhome units. Lewisburg.','HOA',16,'Gutter Install'),
      dl(5,'Kim Residence','Susan Kim','Homeowner','active',1900,'inspection_scheduled','2026-03-27',null,'March storm tore off 60 LF of gutter. Fascia damage visible. Danville.','Residential',8,'Gutter Install'),
      dl(6,'Northumberland Apartments','Rick Hess','Building Mgr','cold',12000,'lead','2026-04-10','timing','Full gutter replacement, 6 buildings. Budget not approved until April.','Commercial',34,'Gutter Install'),
      dl(7,'Okafor Residence','Emeka Okafor','Homeowner','won',3800,'paid',null,null,'Closed! Seamless aluminum + leaf guards. 180 LF. Selinsgrove.','Residential',14,'Gutter Install'),
      dl(8,'Dreisbach Church','Deacon Willis','Facilities','stalled',4400,'estimate_sent','2026-03-22','budget_freeze','Spring maintenance budget not released yet. Mifflinburg.','Institutional',28,'Gutter Install'),
      dl(9,'Parks Residence','Tom Parks','Homeowner','lost',2200,'lost',null,'competitor','DIY. Bought sectional gutters at Lowe\'s in Selinsgrove.','Residential',31,'Gutter Install'),
    ],
    jobs: [
      dj(1,'Market Street Commons','118 Market St, Sunbury PA 17801','Gutter Install',5800,'In Progress','2026-03-16',[1,2,3,4,5],'220 LF K-style aluminum. Old gutters off, new going up.'),
      dj(2,'Emeka Okafor','224 Pine St, Selinsgrove PA 17870','Gutter Install',3800,'Complete','2026-03-12',[1,2,3,4,5,6,7,8,9,10,11],'Seamless aluminum + guards. Fully installed and tested.'),
      dj(3,'Buffalo Valley Townhomes','450 Buffalo Rd, Lewisburg PA 17837','Gutter Install',8400,'Scheduled','2026-04-01',[1,2],'Half-round copper. Materials on order.'),
      dj(4,'RiverWoods Senior Living','150 RiverWoods Dr, Lewisburg PA 17837','Gutter Install',13200,'Scheduled','2026-04-07',[1,2],'480 LF 6" commercial. Materials ordered from Boise Cascade.'),
    ],
  },
  'Skylight': {
    leads: [
      dl(1,'Hartman Residence','Joe Hartman','Homeowner','stalled',4800,'contract_signed','2026-03-23','wrong_contact','2 Velux curb-mount for bathroom + kitchen. Need wife to confirm sizes. Watsontown.','Residential',46,'Skylight'),
      dl(2,'Brenner Residence','Sandra Brenner','Homeowner','stalled',6100,'scheduled_for_install','2026-03-23',null,'3 Velux deck-mount in cathedral ceiling. Flashing kit backordered. Hughesville.','Residential',40,'Skylight'),
      dl(3,'Rusty Rail Brewing','Chris Roth','Owner','active',3200,'estimate_sent','2026-03-25',null,'1 large commercial skylight over taproom. Want natural light. Mifflinburg.','Commercial',14,'Skylight'),
      dl(4,'Comfort Inn Selinsgrove','Mark Steele','GM','stalled',18000,'estimate_sent','2026-03-22','budget_freeze','4 large skylights in lobby atrium. Capital project pending corporate.','Commercial',44,'Skylight'),
      dl(5,'Buffalo Valley Clubhouse','Dan Marsh','HOA Mgr','active',5400,'inspection_scheduled','2026-03-27',null,'2 tubular skylights for interior hallway. Minimal roof penetration.','HOA',9,'Skylight'),
      dl(6,'Garcia Residence','Maria Garcia','Homeowner','cold',2800,'lead','2026-04-08','timing','1 Velux deck-mount in master bath. Wants to wait for spring. Bloomsburg.','Residential',24,'Skylight'),
      dl(7,'Campus Theatre','Tom Lewin','Owner','won',7600,'paid',null,null,'Closed! 2 commercial skylights + flashing. Installed and sealed. Lewisburg.','Commercial',27,'Skylight'),
      dl(8,'SV Mall — JCPenney Wing','Janet Groff','Facilities Mgr','stalled',24000,'estimate_sent','2026-03-21','no_response','6 skylight replacements in food court. Submitted 2 weeks ago.','Commercial',58,'Skylight'),
      dl(9,'Thomas Residence','Ben Thomas','Homeowner','lost',3500,'lost',null,'competitor','Lost — went with handyman for cheaper install. Shamokin.','Residential',40,'Skylight'),
    ],
    jobs: [
      dj(1,'Joe & Barb Hartman','78 Pine Creek Rd, Watsontown PA 17777','Skylight',4800,'Complete','2026-03-11',[1,2,3,4,5,6,7,8,9,10,11],'2 Velux curb-mount installed. Sealed and trimmed.'),
      dj(2,'Campus Theatre','413 Market St, Lewisburg PA 17837','Skylight',7600,'Complete','2026-03-14',[1,2,3,4,5,6,7,8,9,10,11],'2 commercial skylights. Installed, flashed, sealed.'),
      dj(3,'Sandra Brenner','19 Covered Bridge Rd, Hughesville PA 17737','Skylight',6100,'Scheduled','2026-04-05',[1,2],'3 Velux deck-mount. Waiting on flashing kit delivery.'),
    ],
  },
  'Flashing Repair': {
    leads: [
      dl(1,'Blasius Chevrolet','Steve Blasius','Shop Owner','active',2800,'inspection_complete','2026-03-28',null,'Chimney flashing failed on service bay. Active leak into office. Selinsgrove.','Commercial',8,'Flashing Repair'),
      dl(2,'SV Mall — JCPenney Wing','Janet Groff','Facilities Mgr','stalled',8500,'estimate_sent','2026-03-21','no_response','Parapet wall flashing failing on 3 sections. No reply from corporate.','Commercial',58,'Flashing Repair'),
      dl(3,'Yamamoto Residence','Ken Yamamoto','Homeowner','active',1800,'estimate_sent','2026-03-25',null,'Skylight flashing leaking in master bedroom. Counter-flashing pulling away. Bloomsburg.','Residential',15,'Flashing Repair'),
      dl(4,'Dreisbach Church','Pastor Dale Ruiz','Admin','stalled',4200,'estimate_sent','2026-03-20','budget_freeze','Chimney + 2 pipe boot failures. Waiting on maintenance budget.','Institutional',33,'Flashing Repair'),
      dl(5,'Sunbury Apartments','Sara Owens','Property Mgr','active',6800,'contract_signed','2026-03-26',null,'Valley flashing on 4 units needs replacement. Insurance approved.','Commercial',18,'Flashing Repair'),
      dl(6,'Susquehanna Industrial Park','Nick Etter','GM','stalled',3200,'estimate_sent','2026-03-22','price_objection','Roof-to-wall flashing at loading dock. Thinks $3k is too much.','Commercial',31,'Flashing Repair'),
      dl(7,'Flynn Residence','Kate Flynn','Homeowner','won',2200,'paid',null,null,'Closed! Chimney reflashing + 2 pipe boots. No more leaks. New Berlin.','Residential',17,'Flashing Repair'),
      dl(8,'Bloomsburg School Dist','Stan Wells','Facilities Dir','cold',9200,'lead','2026-04-15','timing','Gym building flashing at 6 penetrations. Budget pending.','Institutional',44,'Flashing Repair'),
      dl(9,'Country Diner','Roy Burns','Owner','lost',1600,'lost',null,'competitor','Lost — roofer sealed it with tar for $200. Middleburg.','Commercial',35,'Flashing Repair'),
    ],
    jobs: [
      dj(1,'Kate Flynn','45 Penns Creek Rd, New Berlin PA 17855','Flashing Repair',2200,'Complete','2026-03-15',[1,2,3,4,5,6,7,8,9],'Chimney reflashing + 2 pipe boots. Sealed and tested.'),
      dj(2,'Sunbury Apartments','200 Market St, Sunbury PA 17801','Flashing Repair',6800,'In Progress','2026-03-19',[1,2,3,4],'Valley flashing. 2 of 4 units complete.'),
      dj(3,'Blasius Chevrolet','400 S Market St, Selinsgrove PA 17870','Flashing Repair',2800,'Scheduled','2026-03-28',[1,2,3],'Chimney flashing on service bay. Materials staged.'),
    ],
  },
  'Ventilation': {
    leads: [
      dl(1,'Rohrer Residence','Mike Rohrer','Homeowner','active',3200,'inspection_complete','2026-03-27',null,'Attic 145°F in summer. No ridge vent, 2 box vents on 1960s ranch. OSB deck — check moisture. Bloomsburg.','Residential',17,'Ventilation'),
      dl(2,'Northumberland Apartments','Rick Hess','Building Mgr','stalled',9400,'estimate_sent','2026-03-22','no_response','16 units — inadequate attic ventilation causing ice dams in winter.','Commercial',33,'Ventilation'),
      dl(3,'Johnson Residence','Tim Johnson','Homeowner','active',2400,'estimate_sent','2026-03-25',null,'Convert 4 box vents to continuous ridge vent. Add 6 soffit vents. Danville.','Residential',10,'Ventilation'),
      dl(4,'Milton Area School Annex','Carl Beck','Facilities Dir','stalled',5600,'contract_signed','2026-03-23','budget_freeze','Gym building needs 8 turbine vents replaced with powered units.','Institutional',28,'Ventilation'),
      dl(5,'Klinger Trucking Warehouse','Jim Klinger','Owner','active',4800,'contract_signed','2026-03-26',null,'Commercial exhaust fans + ridge vent on 8,000 sq ft warehouse. Milton.','Commercial',16,'Ventilation'),
      dl(6,'Central Machine Inc','Ray Toro','Plant Manager','cold',7200,'lead','2026-04-10','timing','Factory ventilation upgrade. Capital budget Q3. Northumberland.','Industrial',44,'Ventilation'),
      dl(7,'Crane Residence','Bob Crane','Homeowner','won',1800,'paid',null,null,'Closed! Ridge vent + 6 soffit vents. Attic dropped 30°F. Selinsgrove.','Residential',12,'Ventilation'),
      dl(8,'Valley Fitness','Marcus Stover','Owner','stalled',4200,'estimate_sent','2026-03-20','price_objection','4 powered attic fans. Owner wants cheaper passive option.','Commercial',22,'Ventilation'),
      dl(9,'Hendricks Residence','Tom Hendricks','Homeowner','lost',2600,'lost',null,'competitor','Lost — went with solar-powered vent from Home Depot. Shamokin.','Residential',38,'Ventilation'),
    ],
    jobs: [
      dj(1,'Mike & Jen Rohrer','340 Fairground Rd, Bloomsburg PA 17815','Ventilation',3200,'Scheduled','2026-04-03',[1,2,3],'Ridge vent (42 LF) + 8 soffit vents. Materials from Boise Cascade.'),
      dj(2,'Bob Crane','2108 N Market St, Selinsgrove PA 17870','Ventilation',1800,'Complete','2026-03-17',[1,2,3,4,5,6,7,8,9,10,11],'Ridge vent + soffits installed. Airflow verified.'),
      dj(3,'Klinger Trucking Warehouse','1100 Industrial Park Rd, Milton PA 17847','Ventilation',4800,'Scheduled','2026-04-07',[1,2],'Exhaust fans + ridge vent. Northumberland County permit pulled.'),
    ],
  },
  'Emergency Tarp': {
    leads: [
      dl(1,'Flickinger Residence','Carol Flickinger','Homeowner','active',1200,'inspection_scheduled','2026-03-25',null,'Oak tree fell on roof — active water into upstairs bedroom. Tarp ASAP. Middleburg.','Residential',12,'Emergency Tarp'),
      dl(2,'Sunbury Urgent Care','Dr. Amir Fahad','Owner','active',800,'lead','2026-03-23',null,'Wind ripped off ridge cap. Rain coming tonight. Emergency call.','Commercial',1,'Emergency Tarp'),
      dl(3,'Comfort Inn Selinsgrove','Mark Steele','GM','active',2400,'contract_signed','2026-03-24',null,'Large section of flat roof membrane peeled back. 3 rooms flooding.','Commercial',3,'Emergency Tarp'),
      dl(4,'Osei Residence','Kwame Osei','Homeowner','active',900,'inspection_scheduled','2026-03-26',null,'Tree limb punched through roof during ice storm. 4x6 ft hole. Williamsport.','Residential',2,'Emergency Tarp'),
      dl(5,'Dreisbach Church','Rev. Tim Carroll','Admin','stalled',1400,'estimate_sent','2026-03-22','budget_freeze','Wind peeled shingles off north slope. Tarped but needs permanent fix.','Institutional',15,'Emergency Tarp'),
      dl(6,'Susquehanna Industrial Bldg 3','Nick Etter','Operations Mgr','active',1800,'contract_signed','2026-03-26',null,'Roof puncture from fallen HVAC unit in storm. Tarped same day.','Commercial',5,'Emergency Tarp'),
      dl(7,'Davis Residence','Ron Davis','Homeowner','won',650,'paid',null,null,'Closed! Emergency tarp after March storm. Full replacement sold. Watsontown.','Residential',8,'Emergency Tarp'),
      dl(8,'Danville Heritage Villas','Dave Moyer','Board President','cold',3200,'lead','2026-04-05','timing','3 units need emergency tarps. HOA management approval slow.','HOA',18,'Emergency Tarp'),
      dl(9,'Bloomsburg Elementary','Phil Long','Facilities Dir','lost',1100,'lost',null,'competitor','Lost — school used in-house maintenance crew for tarp.','Institutional',22,'Emergency Tarp'),
    ],
    jobs: [
      dj(1,'Carol Flickinger','56 Maple Ave, Middleburg PA 17842','Emergency Tarp',1200,'Scheduled','2026-03-25',[1,2],'Tree damage. Tarp dispatch. Full replacement to follow.'),
      dj(2,'Ron Davis','610 Main St, Watsontown PA 17777','Emergency Tarp',650,'Complete','2026-03-18',[1,2,3,4,5,6,7,8],'Tarped same day. Sold full replacement.'),
      dj(3,'Comfort Inn Selinsgrove','1 Comfort Ln, Selinsgrove PA 17870','Emergency Tarp',2400,'In Progress','2026-03-24',[1,2,3,4],'Flat roof membrane tarped. Permanent fix scheduled.'),
    ],
  },
};

// ─── Trade Select Screen ───────────────────────────────────────────────────────
const CUSTOM_TRADE_DEMO = {
  checklist: [
    'Site inspection & measurements',
    'Panel layout and trim plan',
    'Material order (panels, trim, fasteners)',
    'Tear off existing roof',
    'Install underlayment & ice shield',
    'Install metal panels',
    'Install ridge cap & trim',
    'Seal & flash all penetrations',
    'Final inspection & nail sweep',
  ],
  pipeline: ['Lead', 'Inspection', 'Estimate', 'Contract', 'Install', 'Complete'],
};

function TradeSelectScreen({ onSelect }) {
  const [hovered, setHovered] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [customInput, setCustomInput] = useState('');
  const [customResult, setCustomResult] = useState(null); // {name, checklist, pipeline}
  const [customError, setCustomError] = useState('');
  const goToSignup = () => { window.location.href = '/'; };

  const handleTryIt = () => {
    setCustomResult({ name: 'Metal Roofing', ...CUSTOM_TRADE_DEMO });
    setCustomError('');
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#2A3140',
      color: '#D1D5DB', fontFamily: "'Inter', -apple-system, sans-serif",
      padding: '40px 24px 80px',
    }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#E8722A', letterSpacing: '-1px', marginBottom: 16 }}>
            RidgeOS
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#FFFFFF', marginBottom: 10 }}>
            See RidgeOS in action
          </div>
          <div style={{ fontSize: 15, color: '#D1D5DB', maxWidth: 480, margin: '0 auto' }}>
            Pick a job type to see a live demo tailored to your roofing business
          </div>
        </div>

        {/* Trade grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))',
          gap: 10,
          marginBottom: 16,
        }}>
          {TRADE_LIST.map(trade => {
            const color = TRADE_COLORS[trade];
            const isHovered = hovered === trade;
            return (
              <div
                key={trade}
                style={{
                  background: isHovered ? color + '18' : '#2A3140',
                  border: `1px solid ${isHovered ? color : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: 10, padding: '16px 10px',
                  cursor: 'pointer', textAlign: 'center',
                  transition: 'all 0.15s',
                  transform: isHovered ? 'translateY(-2px)' : 'none',
                  boxShadow: isHovered ? `0 6px 20px ${color}22` : 'none',
                }}
                onMouseEnter={() => setHovered(trade)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onSelect(trade)}
              >
                <div style={{ fontSize: 26, marginBottom: 8 }}>
                  {TRADE_ICONS[trade]}
                </div>
                <div style={{
                  fontSize: 12, fontWeight: 600,
                  color: isHovered ? color : '#D1D5DB',
                  lineHeight: 1.3,
                }}>
                  {trade}
                </div>
              </div>
            );
          })}

          {/* Custom trade tile */}
          <div
            style={{
              background: showCustom ? 'rgba(99,102,241,0.1)' : '#2A3140',
              border: `1px dashed ${showCustom ? '#6366f1' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 10, padding: '16px 10px',
              cursor: 'pointer', textAlign: 'center',
              transition: 'all 0.15s',
            }}
            onClick={() => { setShowCustom(true); setCustomResult(null); setCustomError(''); }}
          >
            <div style={{ fontSize: 26, marginBottom: 8 }}>✨</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: showCustom ? '#818cf8' : '#D1D5DB', lineHeight: 1.3 }}>
              + Add your<br/>job type
            </div>
          </div>
        </div>

        {/* Custom trade panel */}
        {showCustom && (
          <div style={{
            background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12, padding: '20px 20px',
            marginBottom: 32,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#FFFFFF', marginBottom: 4 }}>
              ✨ Custom Job Type Generator
            </div>
            <div style={{ fontSize: 12, color: '#D1D5DB', marginBottom: 14 }}>
              AI builds your job checklist and pipeline stages automatically.
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <input
                value={customInput}
                onChange={e => { setCustomInput(e.target.value); setCustomError(''); setCustomResult(null); }}
                placeholder="e.g. Metal Roofing, Slate Restoration, TPO Systems"
                style={{
                  flex: 1, minWidth: 200, padding: '9px 12px',
                  background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 7, color: '#D1D5DB', fontSize: 13,
                  outline: 'none',
                }}
              />
              <button
                onClick={handleTryIt}
                style={{
                  padding: '9px 16px', background: 'rgba(99,102,241,0.15)',
                  border: '1px solid rgba(99,102,241,0.35)',
                  borderRadius: 7, color: '#818cf8',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                Try it (Metal Roofing)
              </button>
              <DisabledTooltip active label="Sign up to generate your own job type">
                <button
                  style={{
                    padding: '9px 16px',
                    background: 'linear-gradient(135deg, #E8722A, #ea580c)',
                    border: 'none', borderRadius: 7,
                    color: '#fff', fontWeight: 700, fontSize: 13,
                    cursor: 'not-allowed', opacity: 0.5, whiteSpace: 'nowrap',
                  }}
                  disabled
                >
                  Generate
                </button>
              </DisabledTooltip>
            </div>

            {customError && (
              <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 10 }}>
                {customError}
              </div>
            )}

            {customResult && (
              <div style={{
                background: '#2A3140', border: '1px solid rgba(99,102,241,0.25)',
                borderRadius: 8, padding: '14px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#FFFFFF' }}>
                      ✨ {customResult.name}
                    </span>
                    <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'rgba(99,102,241,0.2)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }}>
                      AI Generated
                    </span>
                  </div>
                  <button
                    onClick={() => onSelect(customResult.name, customResult)}
                    style={{
                      padding: '7px 18px',
                      background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                      border: 'none', borderRadius: 7,
                      color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                    }}
                  >
                    View {customResult.name} Demo →
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                      Job Checklist ({customResult.checklist.length} steps)
                    </div>
                    {customResult.checklist.map((step, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#D1D5DB', padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 10, color: '#D1D5DB', minWidth: 16 }}>{i + 1}.</span>
                        {step}
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                      Pipeline Stages
                    </div>
                    {customResult.pipeline.map((stage, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#D1D5DB', padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 10, background: '#6366f120', color: '#818cf8', borderRadius: 4, padding: '1px 5px', minWidth: 18, textAlign: 'center' }}>{i + 1}</span>
                        {stage}
                      </div>
                    ))}
                    <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.18)', borderRadius: 6, fontSize: 11, color: '#D1D5DB' }}>
                      🔒 Sign up to generate your own job type with real data
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bottom CTA */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#D1D5DB', marginBottom: 12 }}>
            Already have an account?
          </div>
          <button
            onClick={goToSignup}
            style={{
              padding: '10px 28px',
              background: 'linear-gradient(135deg, #E8722A, #ea580c)',
              border: 'none', borderRadius: 8,
              color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}
          >
            Sign In / Start Free Trial
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Photo Log Tab ────────────────────────────────────────────────────────────
const DEMO_PHOTOS = [
  { id: '1', phase: 'before',  caption: 'NE corner damage',              takenBy: 'Dave M.', time: '8:14 AM',  clientFacing: true  },
  { id: '2', phase: 'before',  caption: 'Flashing separation',           takenBy: 'Dave M.', time: '8:21 AM',  clientFacing: false },
  { id: '3', phase: 'during',  caption: 'Old shingles removed',          takenBy: 'Dave M.', time: '10:02 AM', clientFacing: false },
  { id: '4', phase: 'during',  caption: 'Ice & water barrier installed', takenBy: 'Dave M.', time: '11:30 AM', clientFacing: true  },
  { id: '5', phase: 'damage',  caption: 'Deck rot — 4×8 section',        takenBy: 'Dave M.', time: '9:02 AM',  clientFacing: true  },
  { id: '6', phase: 'after',   caption: 'Completed — south face',        takenBy: 'Dave M.', time: '3:45 PM',  clientFacing: true  },
];

const PHASE_META = {
  before: { label: 'Before', color: '#3b82f6' },
  during: { label: 'During', color: '#f59e0b' },
  after:  { label: 'After',  color: '#22c55e' },
  damage: { label: 'Damage', color: '#ef4444' },
};

function PhotoLogTab({ tier }) {
  const isMobile = useMobile();
  const [phaseFilter, setPhaseFilter] = useState('all');

  const filtered = phaseFilter === 'all'
    ? DEMO_PHOTOS
    : DEMO_PHOTOS.filter(p => p.phase === phaseFilter);

  const clientCount = DEMO_PHOTOS.filter(p => p.clientFacing).length;

  // Locked for Starter — section is visible but blurred with an overlay
  const isLocked = tier === 'starter';

  const gridCols = isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))';

  return (
    <div>
      {/* Section header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, flexWrap: 'wrap', gap: 8,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#FFFFFF' }}>Photo Log</div>
          <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 2 }}>
            Job site documentation &amp; client report photos
          </div>
        </div>
        <DisabledTooltip active label="Available on Starter and above">
          <button
            style={{
              padding: '7px 14px', borderRadius: 7,
              background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
              color: '#D1D5DB', fontSize: 12, fontWeight: 600,
              cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: 6,
            }}
            disabled
          >
            <span style={{ fontSize: 14 }}>📷</span>
            + Add Photo
          </button>
        </DisabledTooltip>
      </div>

      {/* Tier callout */}
      <div style={{
        background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.18)',
        borderRadius: 8, padding: '10px 14px', marginBottom: 20,
        display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>📸</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#E8722A', marginBottom: 4 }}>
            Photo Log — tier features
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 6 : 16 }}>
            {[
              { tier: 'Starter', color: '#D1D5DB', desc: 'Photo upload + captions' },
              { tier: 'Pro',     color: '#E8722A', desc: 'Client-facing toggle + PDF export' },
              { tier: 'Business',color: '#6366f1', desc: 'Crew attribution + bulk download' },
            ].map(({ tier: t, color, desc }) => (
              <span key={t} style={{ fontSize: 12, color: '#D1D5DB', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10, background: color + '22', color, border: `1px solid ${color}33` }}>{t}</span>
                {desc}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Locked overlay wrapper */}
      <div style={{ position: 'relative' }}>
        {isLocked && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(15,17,23,0.72)',
            backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
            borderRadius: 10,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 10,
          }}>
            <span style={{ fontSize: 32 }}>🔒</span>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#D1D5DB' }}>Photo Log — Pro feature</div>
            <div style={{ fontSize: 12, color: '#D1D5DB', textAlign: 'center', maxWidth: 260 }}>
              Upgrade to Pro or Business to access the photo log.
            </div>
            <button
              onClick={() => { window.location.href = '/'; }}
              style={{
                marginTop: 4, padding: '7px 20px',
                background: 'linear-gradient(135deg, #E8722A, #ea580c)',
                border: 'none', borderRadius: 7,
                color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer',
              }}
            >
              Upgrade to Pro
            </button>
          </div>
        )}

        {/* Phase filter pills */}
        <div style={{
          display: 'flex', gap: 6, marginBottom: 16,
          overflowX: 'auto', flexWrap: 'nowrap',
          WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
        }}>
          <button
            style={S.filterBtn(phaseFilter === 'all')}
            onClick={() => setPhaseFilter('all')}
          >
            All ({DEMO_PHOTOS.length})
          </button>
          {Object.entries(PHASE_META).map(([key, meta]) => {
            const count = DEMO_PHOTOS.filter(p => p.phase === key).length;
            return (
              <button
                key={key}
                style={{
                  ...S.filterBtn(phaseFilter === key),
                  ...(phaseFilter === key ? { background: meta.color, borderColor: meta.color } : {}),
                }}
                onClick={() => setPhaseFilter(key)}
              >
                {meta.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Photo grid */}
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 14 }}>
          {filtered.map(photo => {
            const phase = PHASE_META[photo.phase];
            return (
              <div
                key={photo.id}
                style={{
                  background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 10, overflow: 'hidden',
                }}
              >
                {/* Thumbnail placeholder */}
                <div style={{
                  height: 130, background: '#2A3140',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  gap: 8, position: 'relative',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}>
                  {/* Phase badge on thumbnail */}
                  <span style={{
                    position: 'absolute', top: 8, left: 8,
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                    background: phase.color + '33', color: phase.color,
                    border: `1px solid ${phase.color}44`, letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                  }}>
                    {phase.label}
                  </span>
                  {/* PDF badge */}
                  {photo.clientFacing && (
                    <span style={{
                      position: 'absolute', top: 8, right: 8,
                      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                      background: 'rgba(99,102,241,0.2)', color: '#818cf8',
                      border: '1px solid rgba(99,102,241,0.3)', letterSpacing: '0.4px',
                    }}>
                      PDF
                    </span>
                  )}
                  {/* Camera icon SVG */}
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                  <span style={{ fontSize: 11, color: '#D1D5DB' }}>{photo.time}</span>
                </div>

                {/* Card body */}
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#D1D5DB', marginBottom: 4 }}>
                    {photo.caption}
                  </div>
                  <div style={{ fontSize: 11, color: '#D1D5DB' }}>
                    📷 {photo.takenBy}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 16, padding: '9px 14px',
          background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.18)',
          borderRadius: 7, fontSize: 12, color: '#D1D5DB',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ color: '#818cf8', fontWeight: 600 }}>{clientCount}</span>
          {' '}photo{clientCount !== 1 ? 's' : ''} marked for client PDF report
        </div>
      </div>
    </div>
  );
}

// ─── Global Photo Log (real users) ───────────────────────────────────────────
// Demo placeholder photo entries — colored tiles, no actual images
const PHOTO_CATS = ['Pre-Install', 'Damage', 'In-Progress', 'Completion', 'Insurance', 'Punch List'];
const PHOTO_CAT_COLORS = { 'Pre-Install': '#3b82f6', 'Damage': '#ef4444', 'In-Progress': '#E8722A', 'Completion': '#22c55e', 'Insurance': '#8b5cf6', 'Punch List': '#f59e0b' };

const DEMO_JOB_PHOTOS = [
  { id: 'dp1', jobId: 201, category: 'Pre-Install', timestamp: 1744600000000, caption: 'North slope overview' },
  { id: 'dp2', jobId: 201, category: 'Pre-Install', timestamp: 1744600100000, caption: 'Chimney flashing detail' },
  { id: 'dp3', jobId: 201, category: 'In-Progress', timestamp: 1744650000000, caption: 'Tear-off day 1' },
  { id: 'dp4', jobId: 201, category: 'In-Progress', timestamp: 1744660000000, caption: 'Underlayment installed' },
  { id: 'dp5', jobId: 201, category: 'Damage', timestamp: 1744590000000, caption: 'Wind damage ridge cap' },
  { id: 'dp6', jobId: 201, category: 'Completion', timestamp: 1744700000000, caption: 'Finished — south side' },
  { id: 'dp7', jobId: 202, category: 'Damage', timestamp: 1744700000000, caption: 'Hail impact marks' },
  { id: 'dp8', jobId: 202, category: 'Damage', timestamp: 1744700100000, caption: 'Adjuster photo — valley' },
  { id: 'dp9', jobId: 202, category: 'Pre-Install', timestamp: 1744710000000, caption: 'Full roof from driveway' },
  { id: 'dp10', jobId: 202, category: 'Pre-Install', timestamp: 1744710100000, caption: 'Attic decking condition' },
  { id: 'dp11', jobId: 205, category: 'Pre-Install', timestamp: 1744720000000, caption: 'Slate condition overview' },
  { id: 'dp12', jobId: 205, category: 'Pre-Install', timestamp: 1744720100000, caption: 'Chimney 1 detail' },
  { id: 'dp13', jobId: 205, category: 'Pre-Install', timestamp: 1744720200000, caption: 'Copper gutter tie-in' },
  { id: 'dp14', jobId: 204, category: 'In-Progress', timestamp: 1744730000000, caption: 'Old shingles removed' },
  { id: 'dp15', jobId: 204, category: 'In-Progress', timestamp: 1744730100000, caption: 'New underlayment going on' },
  { id: 'dp16', jobId: 204, category: 'Completion', timestamp: 1744740000000, caption: 'Finished — front elevation' },
  { id: 'dp17', jobId: 204, category: 'Punch List', timestamp: 1744750000000, caption: 'Ridge cap alignment check' },
  { id: 'dp18', jobId: 207, category: 'Pre-Install', timestamp: 1744760000000, caption: 'Existing TPO condition' },
  { id: 'dp19', jobId: 207, category: 'Pre-Install', timestamp: 1744760100000, caption: 'Drain location mapping' },
  { id: 'dp20', jobId: 207, category: 'In-Progress', timestamp: 1744770000000, caption: 'Insulation board laid' },
  { id: 'dp21', jobId: 207, category: 'In-Progress', timestamp: 1744770100000, caption: 'TPO membrane welding' },
  { id: 'dp22', jobId: 207, category: 'Completion', timestamp: 1744780000000, caption: 'Finished flat roof' },
];

// ─── Team Management ─────────────────────────────────────────────────────────
const DEMO_TEAM_ROSTER = [
  { id: 'tm1', name: 'Jake Stoltzfus', email: 'jake@keystoneroofing.com', role: 'Foreman', status: 'Active', phone: '(570) 555-2341' },
  { id: 'tm2', name: 'Tom Bricker', email: 'tom@keystoneroofing.com', role: 'Installer', status: 'Active', phone: '(570) 555-3456' },
  { id: 'tm3', name: 'Luis Ortiz', email: 'luis@keystoneroofing.com', role: 'Installer', status: 'Active', phone: '(570) 555-4567' },
  { id: 'tm4', name: 'Ryan Hess', email: 'ryan@keystoneroofing.com', role: 'Installer', status: 'Active', phone: '(570) 555-5678' },
  { id: 'tm5', name: 'Mark Sensenig', email: 'mark@keystoneroofing.com', role: 'Estimator', status: 'Active', phone: '(570) 555-6789' },
  { id: 'tm6', name: 'Tara Zimmerman', email: 'tara@keystoneroofing.com', role: 'Sales', status: 'Active', phone: '(570) 555-7890' },
];

function TeamManagementTab() {
  const [roster, setRoster] = useState(DEMO_TEAM_ROSTER);
  const [showImport, setShowImport] = useState(false);
  const [importMode, setImportMode] = useState('csv'); // 'csv' | 'paste'
  const [pasteText, setPasteText] = useState('');
  const [parsedPreview, setParsedPreview] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const initialAddForm = { firstName: '', lastName: '', email: '', phone: '', role: 'Installer' };
  const [addForm, setAddForm] = useState(initialAddForm);
  const [addErrors, setAddErrors] = useState({});
  const isMobile = useMobile();
  const fileRef = useRef(null);
  const ROLE_OPTIONS = ['Foreman', 'Installer', 'Estimator', 'Sales'];
  useScrollLock(showImport || showAddMember);

  const closeAddMember = () => { setShowAddMember(false); setAddForm(initialAddForm); setAddErrors({}); };

  const handleAddMember = () => {
    const errs = {};
    if (!addForm.firstName.trim()) errs.firstName = true;
    if (!addForm.lastName.trim()) errs.lastName = true;
    if (!addForm.email.trim()) errs.email = true;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addForm.email.trim())) errs.email = 'invalid';
    if (!addForm.role) errs.role = true;
    setAddErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const member = {
      id: `tm-add-${Date.now()}`,
      name: `${addForm.firstName.trim()} ${addForm.lastName.trim()}`,
      email: addForm.email.trim(),
      phone: addForm.phone.trim(),
      role: addForm.role,
      status: 'Active',
    };
    setRoster(prev => [...prev, member]);
    closeAddMember();
  };

  const roleColor = (r) => ({ Owner: '#7C3AED', Foreman: '#E8722A', Installer: '#2563EB', Sales: '#DB2777', Estimator: '#059669' }[r] || '#8B95A1');
  const statusBg = (s) => ({ Active: 'rgba(16,185,129,0.15)', 'Invite Pending': 'rgba(245,158,11,0.15)', Inactive: '#2A3140' }[s] || '#2A3140');
  const statusFg = (s) => ({ Active: '#10B981', 'Invite Pending': '#FCD34D', Inactive: '#D1D5DB' }[s] || '#8B95A1');

  const parseCSV = (text) => {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];
    return lines.slice(1).map(line => {
      const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
      return { name: `${cols[0] || ''} ${cols[1] || ''}`.trim(), email: cols[2] || '', phone: cols[3] || '', role: cols[4] || 'Installer' };
    }).filter(p => p.name);
  };

  const parsePaste = (text) => {
    return text.trim().split('\n').filter(l => l.trim()).map(line => {
      const parts = line.split(/[,\t]+/).map(s => s.trim());
      const name = parts[0] || '';
      const email = parts.find(p => p.includes('@')) || '';
      return { name, email, phone: '', role: 'Installer' };
    }).filter(p => p.name);
  };

  const handleFileRead = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      setParsedPreview(parsed);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.csv')) handleFileRead(file);
  };

  const handleImportConfirm = () => {
    const newMembers = parsedPreview.map((p, i) => ({
      id: `tm-imp-${Date.now()}-${i}`, name: p.name, email: p.email, phone: p.phone, role: p.role, status: 'Invite Pending',
    }));
    setRoster(prev => [...prev, ...newMembers]);
    setParsedPreview([]); setPasteText(''); setShowImport(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#FFFFFF' }}>Team Management</div>
          <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 2 }}>{roster.length} team members</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={() => setShowAddMember(true)}
            style={{ padding: '10px 16px', minHeight: 44, background: 'linear-gradient(135deg, #E8722A, #e8640c)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 2px 10px rgba(249,115,22,0.3)', WebkitTapHighlightColor: 'transparent', whiteSpace: 'nowrap' }}
          >
            + Add Member
          </button>
          <button
            onClick={() => { setShowImport(true); setParsedPreview([]); setPasteText(''); }}
            style={{ padding: '10px 14px', minHeight: 44, background: 'transparent', border: '1px solid rgba(34,197,94,0.4)', borderRadius: 8, color: '#22c55e', fontWeight: 600, fontSize: 13, cursor: 'pointer', WebkitTapHighlightColor: 'transparent', whiteSpace: 'nowrap' }}
          >
            + Import Team
          </button>
        </div>
      </div>

      {/* Team roster */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {roster.map(member => (
          <div key={member.id} style={{ background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: roleColor(member.role) + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: roleColor(member.role), flexShrink: 0 }}>
              {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.name}</div>
              <div style={{ fontSize: 11, color: '#D1D5DB', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.email}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
              <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 6, background: roleColor(member.role), color: '#2A3140' }}>{member.role}</span>
              <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 6, background: statusBg(member.status), color: statusFg(member.status) }}>{member.status}</span>
            </div>
            {member.status === 'Invite Pending' && (
              <button onClick={() => setRoster(prev => prev.map(m => m.id === member.id ? { ...m, status: 'Active' } : m))} style={{ padding: '4px 10px', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: '#22c55e', fontSize: 10, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                Send Invite
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add Member Modal */}
      {showAddMember && (
        <div style={S.overlay} onClick={closeAddMember}>
          <div style={{ ...S.modal, maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <button className="ri-close-btn" style={S.closeBtn} onClick={closeAddMember}>×</button>
            <div style={{ ...S.modalTitle, paddingRight: 48 }}>Add Team Member</div>
            <div style={S.modalSub}>Add one person to your team.</div>

            <div style={isMobile ? {} : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={FLbl}>First Name *</label>
                <input
                  value={addForm.firstName}
                  onChange={e => setAddForm(f => ({ ...f, firstName: e.target.value }))}
                  placeholder="Jake"
                  style={{ ...FI, fontSize: isMobile ? 16 : 13, padding: isMobile ? '13px 14px' : '10px 12px', border: `1px solid ${addErrors.firstName ? '#ef4444' : 'rgba(255,255,255,0.12)'}`, minHeight: 44 }}
                />
              </div>
              <div>
                <label style={FLbl}>Last Name *</label>
                <input
                  value={addForm.lastName}
                  onChange={e => setAddForm(f => ({ ...f, lastName: e.target.value }))}
                  placeholder="Stoltzfus"
                  style={{ ...FI, fontSize: isMobile ? 16 : 13, padding: isMobile ? '13px 14px' : '10px 12px', border: `1px solid ${addErrors.lastName ? '#ef4444' : 'rgba(255,255,255,0.12)'}`, minHeight: 44 }}
                />
              </div>
            </div>

            <label style={FLbl}>Email *</label>
            <input
              type="email"
              value={addForm.email}
              onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
              placeholder="jake@keystoneroofing.com"
              style={{ ...FI, fontSize: isMobile ? 16 : 13, padding: isMobile ? '13px 14px' : '10px 12px', border: `1px solid ${addErrors.email ? '#ef4444' : 'rgba(255,255,255,0.12)'}`, minHeight: 44 }}
            />
            {addErrors.email === 'invalid' && (
              <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>Enter a valid email address.</div>
            )}

            <div style={isMobile ? {} : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={FLbl}>Phone</label>
                <input
                  type="tel"
                  value={addForm.phone}
                  onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="(570) 555-1234"
                  style={{ ...FI, fontSize: isMobile ? 16 : 13, padding: isMobile ? '13px 14px' : '10px 12px', minHeight: 44 }}
                />
              </div>
              <div>
                <label style={FLbl}>Role *</label>
                <select
                  value={addForm.role}
                  onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}
                  style={{ ...FI, fontSize: isMobile ? 16 : 13, padding: isMobile ? '13px 14px' : '10px 12px', border: `1px solid ${addErrors.role ? '#ef4444' : 'rgba(255,255,255,0.12)'}`, minHeight: 44 }}
                >
                  {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button
                onClick={closeAddMember}
                className="ri-btn ri-btn-secondary"
                style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#D1D5DB', fontWeight: 600, fontSize: 14, cursor: 'pointer', minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                style={{ flex: 2, padding: '12px', background: 'linear-gradient(135deg, #E8722A, #e8640c)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', minHeight: 44, boxShadow: '0 2px 10px rgba(249,115,22,0.3)', WebkitTapHighlightColor: 'transparent' }}
              >
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImport && (
        <div style={S.overlay} onClick={() => setShowImport(false)}>
          <div style={{ ...S.modal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <button className="ri-close-btn" style={S.closeBtn} onClick={() => setShowImport(false)}>×</button>
            <div style={S.modalTitle}>Import Team Members</div>
            <div style={S.modalSub}>Add your crew in bulk</div>

            {/* Mode toggle */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button onClick={() => { setImportMode('csv'); setParsedPreview([]); }} style={{ flex: 1, padding: '9px', borderRadius: 6, border: importMode === 'csv' ? '1px solid #22c55e' : '1px solid rgba(255,255,255,0.08)', background: importMode === 'csv' ? 'rgba(34,197,94,0.1)' : 'transparent', color: importMode === 'csv' ? '#22c55e' : '#D1D5DB', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>CSV Upload</button>
              <button onClick={() => { setImportMode('paste'); setParsedPreview([]); }} style={{ flex: 1, padding: '9px', borderRadius: 6, border: importMode === 'paste' ? '1px solid #22c55e' : '1px solid rgba(255,255,255,0.08)', background: importMode === 'paste' ? 'rgba(34,197,94,0.1)' : 'transparent', color: importMode === 'paste' ? '#22c55e' : '#D1D5DB', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Paste List</button>
            </div>

            {importMode === 'csv' && (
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                style={{ border: `2px dashed ${dragOver ? '#22c55e' : 'rgba(255,255,255,0.08)'}`, borderRadius: 10, padding: '32px 20px', textAlign: 'center', cursor: 'pointer', background: dragOver ? 'rgba(34,197,94,0.05)' : 'transparent', marginBottom: 16 }}
              >
                <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleFileRead(e.target.files[0]); }} />
                <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#D1D5DB' }}>Drop a .csv file here or click to browse</div>
                <div style={{ fontSize: 11, color: '#D1D5DB', marginTop: 4 }}>Expected columns: First Name, Last Name, Email, Phone, Role</div>
              </div>
            )}

            {importMode === 'paste' && (
              <div style={{ marginBottom: 16 }}>
                <textarea
                  value={pasteText}
                  onChange={e => { setPasteText(e.target.value); setParsedPreview(parsePaste(e.target.value)); }}
                  placeholder={"Jake Stoltzfus, jake@keystoneroofing.com\nTom Bricker, tom@keystoneroofing.com"}
                  rows={5}
                  style={{ ...FI, resize: 'none', marginBottom: 8 }}
                />
              </div>
            )}

            {/* Preview table */}
            {parsedPreview.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#D1D5DB', textTransform: 'uppercase', marginBottom: 8 }}>Preview ({parsedPreview.length} employees)</div>
                <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6 }}>
                  {parsedPreview.map((p, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 12 }}>
                      <span style={{ flex: 1, color: '#FFFFFF', fontWeight: 600 }}>{p.name}</span>
                      <span style={{ color: '#D1D5DB' }}>{p.email}</span>
                      <span style={{ color: '#D1D5DB', fontSize: 10 }}>{p.role}</span>
                    </div>
                  ))}
                </div>
                <button onClick={handleImportConfirm} style={{ width: '100%', marginTop: 10, padding: '10px', background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 7, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  Import {parsedPreview.length} employee{parsedPreview.length !== 1 ? 's' : ''}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GlobalPhotoLog({ jobs }) {
  const [selectedJob, setSelectedJob] = useState(null);
  const [catFilter, setCatFilter] = useState('All');
  const [realPhotos, setRealPhotos] = useState([]);
  const isMobile = useMobile();
  const photoFileRef = useRef(null);

  // Load real photos from IndexedDB
  const loadPhotos = useCallback(() => {
    photoDB.getAll().then(all => setRealPhotos(all.sort((a, b) => b.timestamp - a.timestamp))).catch(() => {});
  }, []);
  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  const handlePhotoUpload = async (files) => {
    if (!files || !files.length || !selectedJob) return;
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      const imageData = await compressImage(file);
      await photoDB.add({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        jobId: String(selectedJob.id),
        jobName: selectedJob.customer,
        imageData,
        timestamp: Date.now(),
        caption: '',
        category: catFilter !== 'All' ? catFilter : 'Pre-Install',
        stage: 'in_progress',
        trade: selectedJob.trade || '',
      });
    }
    loadPhotos();
  };

  const allJobs = jobs || DEMO_JOBS;
  const allPhotos = [...DEMO_JOB_PHOTOS, ...realPhotos];

  const photoCountForJob = (jobId) => allPhotos.filter(p => String(p.jobId) === String(jobId)).length;

  const formatTs = (ts) => new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  // ── Job Folder Grid ──
  if (!selectedJob) {
    const totalPhotos = allPhotos.length;
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#FFFFFF' }}>Job Photos</div>
          <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 2 }}>{totalPhotos} photos across {allJobs.length} jobs</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {allJobs.map(job => {
            const count = photoCountForJob(job.id);
            const tc = getTradeColor(job.trade);
            const stColor = job.status === 'Complete' ? '#22c55e' : job.status === 'In Progress' ? '#E8722A' : '#6366f1';
            return (
              <div
                key={job.id}
                onClick={() => { setSelectedJob(job); setCatFilter('All'); }}
                style={{
                  background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
                  padding: 14, cursor: 'pointer', transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#06b6d4'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
              >
                {/* Thumbnail area */}
                <div style={{
                  background: count > 0 ? '#2A3140' : '#2A3140', borderRadius: 8,
                  height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 10, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden',
                }}>
                  {count > 0 ? (
                    <div style={{ display: 'flex', gap: 3, padding: 4, width: '100%', height: '100%' }}>
                      {allPhotos.filter(p => String(p.jobId) === String(job.id)).slice(0, 3).map((p, i) => (
                        <div key={p.id} style={{
                          flex: 1, background: PHOTO_CAT_COLORS[p.category] || '#8B95A1',
                          borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: 0.7 + i * 0.1,
                        }}>
                          <span style={{ fontSize: 16 }}>📷</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: 28, opacity: 0.3 }}>📷</span>
                  )}
                </div>
                {/* Job info */}
                <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.customer}</div>
                <div style={{ fontSize: 10, color: '#D1D5DB', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.address}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 6, background: tc + '22', color: tc }}>{job.trade}</span>
                  <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 6, background: stColor + '22', color: stColor }}>{job.status}</span>
                  <span style={{ fontSize: 10, color: count > 0 ? '#06b6d4' : '#D1D5DB', fontWeight: 600, marginLeft: 'auto' }}>
                    {count > 0 ? `${count} photos` : 'No photos'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Job Photo View ──
  const jobPhotos = allPhotos.filter(p => String(p.jobId) === String(selectedJob.id));
  const filtered = catFilter === 'All' ? jobPhotos : jobPhotos.filter(p => p.category === catFilter);
  const usedCats = ['All', ...PHOTO_CATS.filter(c => jobPhotos.some(p => p.category === c))];
  const tc = getTradeColor(selectedJob.trade);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <button onClick={() => setSelectedJob(null)} style={{ background: 'none', border: 'none', color: '#D1D5DB', fontSize: 14, cursor: 'pointer', padding: '4px 8px' }}>← Back</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedJob.customer}</div>
          <div style={{ fontSize: 11, color: '#D1D5DB' }}>{selectedJob.address}</div>
        </div>
        <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: tc + '22', color: tc }}>{selectedJob.trade}</span>
      </div>

      {/* Category filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}>
        {usedCats.map(c => {
          const isActive = catFilter === c;
          const color = c === 'All' ? '#06b6d4' : (PHOTO_CAT_COLORS[c] || '#8B95A1');
          return (
            <button key={c} onClick={() => setCatFilter(c)} style={{
              padding: '5px 12px', borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              border: isActive ? `1px solid ${color}` : '1px solid rgba(255,255,255,0.08)',
              background: isActive ? color + '18' : 'transparent',
              color: isActive ? color : '#D1D5DB', whiteSpace: 'nowrap',
            }}>
              {c} {c === 'All' ? `(${jobPhotos.length})` : `(${jobPhotos.filter(p => p.category === c).length})`}
            </button>
          );
        })}
      </div>

      {/* Photo grid */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 10 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📷</div>
          <div style={{ fontSize: 13, color: '#D1D5DB' }}>No photos in this category yet</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
          {filtered.map(photo => {
            const catColor = PHOTO_CAT_COLORS[photo.category] || '#8B95A1';
            const isPlaceholder = !photo.imageData;
            return (
              <div key={photo.id} style={{
                position: 'relative', borderRadius: 8, overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)', aspectRatio: '4/3',
                background: isPlaceholder ? catColor + '18' : '#2A3140',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                cursor: isPlaceholder ? 'default' : 'pointer',
              }}>
                {isPlaceholder ? (
                  <>
                    <span style={{ fontSize: 24, marginBottom: 4 }}>📷</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: catColor }}>{photo.category}</span>
                  </>
                ) : (
                  <img src={photo.imageData} alt={photo.caption || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                )}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'rgba(0,0,0,0.12)', padding: '4px 7px',
                }}>
                  <span style={{ background: catColor + '44', color: catColor, padding: '1px 5px', borderRadius: 4, fontSize: 9, fontWeight: 600, marginRight: 4 }}>{photo.category}</span>
                  <span style={{ fontSize: 9, color: '#D1D5DB' }}>{photo.caption || formatTs(photo.timestamp)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* + Add Photo button */}
      <input ref={photoFileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => { handlePhotoUpload(e.target.files); e.target.value = ''; }} />
      <button
        onClick={() => photoFileRef.current?.click()}
        style={{ width: '100%', marginTop: 14, padding: '11px', background: 'linear-gradient(135deg,#06b6d4,#0891b2)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
      >
        + Add Photo
      </button>
    </div>
  );
}

// ─── Add Job Modal ────────────────────────────────────────────────────────────
function AddJobModal({ onSave, onClose }) {
  const isMobile = useMobile();
  useScrollLock();
  const [form, setForm] = useState({
    customer: '', address: '', trade: 'Full Replacement', tradeCustom: '', value: '', scheduledDate: '', notes: '', jobType: 'Residential',
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.customer.trim()) e.customer = true;
    if (!form.value || isNaN(Number(form.value)) || Number(form.value) <= 0) e.value = true;
    if (form.trade === '__OTHER__' && !form.tradeCustom.trim()) e.trade = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const resolvedTrade = form.trade === '__OTHER__' ? form.tradeCustom.trim() : form.trade;

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      id: Date.now(),
      customer: form.customer.trim(),
      address: form.address.trim(),
      trade: resolvedTrade,
      value: Math.round(Number(form.value)),
      status: 'Scheduled',
      scheduledDate: form.scheduledDate || new Date().toISOString().slice(0, 10),
      completedSteps: [],
      notes: form.notes.trim(),
      jobType: form.jobType,
      currentPhase: 1,
      completedPhases: [],
    });
  };

  const mobOverlay = isMobile ? { ...S.overlay, padding: 0, alignItems: 'flex-end' } : S.overlay;
  const mobModal = isMobile
    ? { ...S.modal, maxWidth: '100vw', width: '100vw', maxHeight: '90dvh', borderRadius: '16px 16px 0 0', margin: 0 }
    : { ...S.modal, maxWidth: 520 };
  const fi = (field) => ({
    ...FI, fontSize: isMobile ? 16 : 13, padding: isMobile ? '13px 14px' : '9px 12px',
    border: `1px solid ${errors[field] ? '#ef4444' : 'rgba(255,255,255,0.08)'}`,
  });

  return (
    <div style={mobOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={mobModal}>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        <div style={{ ...S.modalTitle, paddingRight: 48 }}>Add New Project</div>
        <div style={S.modalSub}>Required fields marked with *</div>

        <label style={FLbl}>Customer Name *</label>
        <input style={fi('customer')} value={form.customer} onChange={e => set('customer', e.target.value)} placeholder="Customer or company name" />

        <label style={FLbl}>Job Address</label>
        <input style={fi('address')} value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St, Mechanicsburg PA 17055" />

        <label style={FLbl}>Commercial / Residential</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {['Residential', 'Commercial'].map(t => (
            <button
              key={t}
              onClick={() => set('jobType', t)}
              style={{
                flex: 1, padding: '9px', border: `1px solid ${form.jobType === t ? '#2D5016' : 'rgba(255,255,255,0.08)'}`,
                background: form.jobType === t ? 'rgba(45,80,22,0.15)' : 'transparent',
                borderRadius: 8, color: form.jobType === t ? '#2D5016' : '#D1D5DB',
                fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}
            >
              {t} ({t === 'Commercial' ? '7 phases' : '4 phases'})
            </button>
          ))}
        </div>

        <div style={isMobile ? {} : FRow}>
          <div>
            <label style={FLbl}>Trade Type</label>
            <select style={fi('trade')} value={form.trade} onChange={e => set('trade', e.target.value)}>
              {TRADE_LIST.map(t => <option key={t} value={t}>{t}</option>)}
              <option value="__OTHER__">Other…</option>
            </select>
            {form.trade === '__OTHER__' && (
              <input
                style={{ ...fi('trade'), marginTop: 8 }}
                value={form.tradeCustom}
                onChange={e => set('tradeCustom', e.target.value)}
                placeholder="Describe job type (e.g. Solar reinstall, Snow guards)"
                autoFocus
              />
            )}
          </div>
          <div>
            <label style={FLbl}>Job Value *</label>
            <input style={fi('value')} value={form.value} onChange={e => set('value', e.target.value)} placeholder="15000" type="number" min="0" />
          </div>
        </div>

        <label style={FLbl}>Scheduled Date</label>
        <input style={fi('scheduledDate')} value={form.scheduledDate} onChange={e => set('scheduledDate', e.target.value)} type="date" />

        <label style={FLbl}>Notes</label>
        <textarea style={{ ...fi('notes'), minHeight: 72, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Job details, materials, crew notes..." />

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="ri-btn ri-btn-secondary" style={{ flex: 1, padding: '10px 16px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#D1D5DB', cursor: 'pointer', fontSize: 13, fontWeight: 500 }} onClick={onClose}>Cancel</button>
          <button className="ri-btn ri-btn-primary" style={{ flex: 2, padding: '10px 16px', background: 'linear-gradient(135deg, #E8722A, #e8640c)', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, boxShadow: '0 2px 10px rgba(249,115,22,0.3)' }} onClick={handleSave}>Add Project</button>
        </div>
      </div>
    </div>
  );
}

// ─── Job Crew Section ──────────────────────────────────────────────────────────
function JobCrewSection({ job, crew, assignments, onAssign, onUnassign, onAddCrew }) {
  const [entries, setEntries] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [showAssign, setShowAssign] = useState(false);
  const [newName, setNewName] = useState('');
  // Local state for demo/read-only mode so UI still feels functional
  const [localAssignedIds, setLocalAssignedIds] = useState([]);
  const [localCrew, setLocalCrew] = useState([]);
  const newNameRef = useRef(null);
  const isMobile = useMobile();

  // Merge persisted + local assignments/crew so both modes work
  const persistedIds = assignments[String(job.id)] || [];
  const assignedIds = [...new Set([...persistedIds, ...localAssignedIds])];
  const allCrew = [...crew, ...localCrew];
  const assignedCrew = allCrew.filter(m => assignedIds.includes(m.id));
  const unassignedCrew = allCrew.filter(m => !assignedIds.includes(m.id));

  const refresh = useCallback(() => {
    timeDB.getAll().then(all => {
      setEntries(all.filter(e => String(e.jobId) === String(job.id)));
    }).catch(() => {});
  }, [job.id]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const hasOpen = entries.some(e => !e.clockOut);
    if (!hasOpen) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [entries]);

  useEffect(() => {
    if (showAssign && newNameRef.current) newNameRef.current.focus();
  }, [showAssign]);

  const clockedInEntry = (memberId) => entries.find(e => e.crewMemberId === memberId && !e.clockOut);
  const memberTotalMs = (memberId) => computeTotalMs(entries.filter(e => e.crewMemberId === memberId));

  const handleClockIn = async (member) => {
    await timeDB.add({ id: `te-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, jobId: String(job.id), crewMemberId: member.id, clockIn: Date.now(), clockOut: null });
    refresh();
  };
  const handleClockOut = async (member) => {
    const open = clockedInEntry(member.id);
    if (!open) return;
    await timeDB.put({ ...open, clockOut: Date.now() });
    refresh();
  };

  const doAssign = (memberId) => {
    if (onAssign) onAssign(job.id, memberId);
    else setLocalAssignedIds(prev => [...prev, memberId]);
  };
  const doUnassign = (memberId) => {
    if (onUnassign) onUnassign(job.id, memberId);
    else setLocalAssignedIds(prev => prev.filter(id => id !== memberId));
  };

  const handleCreateAndAssign = () => {
    const name = newName.trim();
    if (!name) return;
    const id = `crew-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const member = { id, name, role: '', phone: '', specialties: [] };
    if (onAddCrew) onAddCrew(member);
    else setLocalCrew(prev => [...prev, member]);
    doAssign(id);
    setNewName('');
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={S.sectionLabel}>Crew</div>
        <button
          style={{ fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 6, background: showAssign ? 'rgba(255,255,255,0.08)' : 'rgba(249,115,22,0.12)', border: `1px solid ${showAssign ? 'rgba(255,255,255,0.08)' : 'rgba(249,115,22,0.3)'}`, color: showAssign ? '#8B95A1' : '#E8722A', cursor: 'pointer' }}
          onClick={() => { setShowAssign(s => !s); setNewName(''); }}
        >
          {showAssign ? 'Done' : '+ Assign Crew'}
        </button>
      </div>

      {showAssign && (
        <div style={{ background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '12px', marginBottom: 12 }}>
          {/* Text input — always visible, fastest path */}
          <div style={{ display: 'flex', gap: 8, marginBottom: unassignedCrew.length > 0 ? 10 : 4 }}>
            <input
              ref={newNameRef}
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreateAndAssign(); } }}
              placeholder="Type crew member name…"
              style={{ flex: 1, background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, color: '#FFFFFF', fontSize: 13, padding: '8px 10px', fontFamily: 'inherit', outline: 'none' }}
            />
            <button
              onClick={handleCreateAndAssign}
              disabled={!newName.trim()}
              style={{ padding: '8px 14px', background: newName.trim() ? 'linear-gradient(135deg,#E8722A,#ea580c)' : 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 7, color: newName.trim() ? '#fff' : '#D1D5DB', fontWeight: 700, fontSize: 12, cursor: newName.trim() ? 'pointer' : 'not-allowed' }}
            >
              Add
            </button>
          </div>
          {/* Existing unassigned crew */}
          {unassignedCrew.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: '#D1D5DB', marginBottom: 6 }}>Or pick from your crew:</div>
              {unassignedCrew.map(member => (
                <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 7, marginBottom: 4, background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#D1D5DB' }}>
                    {member.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#D1D5DB' }}>{member.name}</div>
                    {member.role && <div style={{ fontSize: 11, color: '#D1D5DB' }}>{member.role}</div>}
                  </div>
                  <button
                    onClick={() => doAssign(member.id)}
                    style={{ padding: '5px 12px', background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 6, color: '#E8722A', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}
                  >
                    + Add
                  </button>
                </div>
              ))}
            </div>
          )}
          {unassignedCrew.length === 0 && !newName && (
            <div style={{ fontSize: 11, color: '#D1D5DB', fontStyle: 'italic' }}>All crew members are assigned.</div>
          )}
        </div>
      )}

      {/* Assigned crew */}
      {assignedCrew.length === 0 && !showAssign && (
        <div style={{ fontSize: 12, color: '#D1D5DB', padding: '4px 0 8px' }}>
          No crew assigned yet. Click <span style={{ color: '#E8722A' }}>+ Assign Crew</span> above.
        </div>
      )}
      {assignedCrew.map(member => {
        const open = clockedInEntry(member.id);
        const totalMs = memberTotalMs(member.id);
        const elapsed = open ? (now - open.clockIn) : 0;
        return (
          <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '14px 12px' : '10px 12px', borderRadius: 8, marginBottom: 6, background: open ? 'rgba(34,197,94,0.06)' : '#2A3140', border: `1px solid ${open ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.08)'}` }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: open ? '#22c55e' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: open ? '#fff' : '#D1D5DB' }}>
              {member.name.slice(0, 2).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#D1D5DB', marginBottom: 2 }}>{member.name}</div>
              <div style={{ fontSize: 11, color: '#D1D5DB' }}>
                {open
                  ? <span style={{ color: '#22c55e' }}>● Clocked in · {fmtDuration(elapsed)}</span>
                  : `Total: ${fmtDuration(totalMs)}`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button
                style={{ padding: isMobile ? '8px 10px' : '5px 9px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#D1D5DB', fontSize: 11, fontWeight: 600, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                onClick={() => doUnassign(member.id)}
              >
                Remove
              </button>
              <button
                style={{ padding: isMobile ? '10px 14px' : '7px 12px', minWidth: isMobile ? 90 : 80, borderRadius: 7, border: 'none', background: open ? '#ef4444' : '#22c55e', color: '#fff', fontWeight: 700, fontSize: isMobile ? 13 : 12, cursor: 'pointer', flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}
                onClick={() => open ? handleClockOut(member) : handleClockIn(member)}
              >
                {open ? 'Clock Out' : 'Clock In'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Crew Member Modal ──────────────────────────────────────────────────────────
function CrewMemberModal({ member, onSave, onClose }) {
  const isMobile = useMobile();
  useScrollLock();
  const [name, setName] = useState(member?.name || '');
  const [role, setRole] = useState(member?.role || '');
  const [phone, setPhone] = useState(member?.phone || '');
  const [specialties, setSpecialties] = useState(member?.specialties || []);
  const [nameErr, setNameErr] = useState(false);

  const toggleSpecialty = (trade) => {
    setSpecialties(prev => prev.includes(trade) ? prev.filter(t => t !== trade) : [...prev, trade]);
  };

  const handleSave = () => {
    if (!name.trim()) { setNameErr(true); return; }
    onSave({
      id: member?.id || `crew-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: name.trim(), role: role.trim(), phone: phone.trim(), specialties,
    });
  };

  const mobOverlay = isMobile ? { ...S.overlay, padding: 0, alignItems: 'flex-end' } : S.overlay;
  const mobModal = isMobile
    ? { ...S.modal, maxWidth: '100vw', width: '100vw', maxHeight: '90dvh', borderRadius: '16px 16px 0 0', margin: 0 }
    : { ...S.modal, maxWidth: 520 };
  const inp = (err) => ({ ...FI, fontSize: isMobile ? 16 : 13, padding: isMobile ? '13px 14px' : '9px 12px', border: `1px solid ${err ? '#ef4444' : 'rgba(255,255,255,0.08)'}` });

  return (
    <div style={mobOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={mobModal}>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        <div style={{ ...S.modalTitle, paddingRight: 48 }}>{member ? 'Edit Crew Member' : 'Add Crew Member'}</div>
        <div style={S.modalSub}>Profile and job type specialties</div>

        <label style={FLbl}>Name *</label>
        <input style={inp(nameErr)} value={name} onChange={e => { setName(e.target.value); setNameErr(false); }} placeholder="Full name" />
        {nameErr && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 6 }}>Name is required</div>}

        <label style={FLbl}>Role / Title</label>
        <input style={inp(false)} value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. Foreman, Tear-off Crew, Install Crew, Estimator" />

        <label style={FLbl}>Phone</label>
        <input style={inp(false)} value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 000-0000" type="tel" />

        <label style={FLbl}>Job Type Specialties</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
          {TRADE_LIST.map(trade => {
            const sel = specialties.includes(trade);
            const color = TRADE_COLORS[trade] || '#8B95A1';
            return (
              <button key={trade} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, border: `1px solid ${sel ? color : 'rgba(255,255,255,0.08)'}`, background: sel ? color + '22' : 'transparent', color: sel ? color : '#D1D5DB', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }} onClick={() => toggleSpecialty(trade)}>
                {trade}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button className="ri-btn ri-btn-secondary" style={{ flex: 1, padding: '10px 16px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#D1D5DB', cursor: 'pointer', fontSize: 13, fontWeight: 500 }} onClick={onClose}>Cancel</button>
          <button className="ri-btn ri-btn-primary" style={{ flex: 2, padding: '10px 16px', background: 'linear-gradient(135deg, #E8722A, #e8640c)', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, boxShadow: '0 2px 10px rgba(249,115,22,0.3)' }} onClick={handleSave}>{member ? 'Save Changes' : 'Add Member'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Crew Tab ──────────────────────────────────────────────────────────────────
function CrewTab({ crew, jobs, assignments, onAddMember, onEditMember, onDeleteMember }) {
  const [allEntries, setAllEntries] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [crewModal, setCrewModal] = useState(null);
  const isMobile = useMobile();

  useEffect(() => {
    const load = () => timeDB.getAll().then(setAllEntries).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const hasOpen = allEntries.some(e => !e.clockOut);
    if (!hasOpen) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [allEntries]);

  const weekMs = weekStartMs();

  const memberStatus = (member) => {
    const open = allEntries.find(e => e.crewMemberId === member.id && !e.clockOut);
    if (!open) return { clockedIn: false, jobName: null, openEntry: null };
    const job = jobs.find(j => String(j.id) === String(open.jobId));
    return { clockedIn: true, jobName: job ? job.customer : 'Unknown Job', openEntry: open };
  };

  const memberWeekMs = (member) => {
    const we = allEntries.filter(e => e.crewMemberId === member.id && e.clockIn >= weekMs);
    const live = computeTotalMs(we);
    const seed = (member.seedHoursThisWeek || 0) * 3600000;
    return live + seed;
  };

  const memberTotalMs = (member) => {
    const live = computeTotalMs(allEntries.filter(e => e.crewMemberId === member.id));
    const seed = (member.seedHoursAllTime || 0) * 3600000;
    return live + seed;
  };

  const handleSaveMember = (data) => {
    if (crewModal === 'add') onAddMember(data);
    else onEditMember(data);
    setCrewModal(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#FFFFFF' }}>Crew</div>
          <div style={{ fontSize: 13, color: '#D1D5DB' }}>
            {crew.length} member{crew.length !== 1 ? 's' : ''} · Weekly hours overview
          </div>
        </div>
        <button
          style={{ padding: '10px 18px', background: 'linear-gradient(135deg, #E8722A, #ea580c)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
          onClick={() => setCrewModal('add')}
        >
          + Add Member
        </button>
      </div>

      {crew.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>👷</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#D1D5DB', marginBottom: 6 }}>No crew members yet</div>
          <div style={{ fontSize: 13, color: '#D1D5DB', marginBottom: 20 }}>Add your first team member to start tracking time on jobs.</div>
          <button style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #E8722A, #ea580c)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }} onClick={() => setCrewModal('add')}>
            Add First Member
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {crew.map(member => {
            const status = memberStatus(member);
            const wMs = memberWeekMs(member);
            const tMs = memberTotalMs(member);
            const elapsed = status.clockedIn ? (now - status.openEntry.clockIn) : 0;
            const sessionMs = status.clockedIn ? elapsed : 0;

            return (
              <div key={member.id} style={{ background: '#2A3140', border: `1px solid ${status.clockedIn ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 10, padding: 16, boxShadow: status.clockedIn ? '0 0 12px rgba(34,197,94,0.08)' : 'none', overflow: 'hidden', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12, minWidth: 0 }}>
                  <div style={{ width: 42, height: 42, borderRadius: '50%', flexShrink: 0, background: status.clockedIn ? '#22c55e' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: status.clockedIn ? '#fff' : '#D1D5DB', transition: 'all 0.2s' }}>
                    {member.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.name}</div>
                    {member.role && <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.role}</div>}
                    {member.phone && <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.phone}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="ri-btn ri-btn-secondary" style={{ padding: '8px 14px', minHeight: 44, background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#D1D5DB', cursor: 'pointer', fontSize: 13, WebkitTapHighlightColor: 'transparent' }} onClick={() => setCrewModal(member)}>Edit</button>
                    <button style={{ padding: '8px 12px', minHeight: 44, minWidth: 44, background: 'transparent', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', cursor: 'pointer', fontSize: 16, lineHeight: 1, WebkitTapHighlightColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => onDeleteMember(member.id)}>×</button>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, marginBottom: 12, background: status.clockedIn ? 'rgba(34,197,94,0.1)' : 'rgba(100,116,139,0.1)', border: `1px solid ${status.clockedIn ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.08)'}`, maxWidth: '100%', minWidth: 0 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: status.clockedIn ? '#22c55e' : '#D1D5DB', display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: status.clockedIn ? '#22c55e' : '#D1D5DB', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {status.clockedIn ? `On job · ${status.jobName}` : 'Off duty'}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <div style={{ background: '#2A3140', borderRadius: 7, padding: '10px 12px' }}>
                    <div style={{ fontSize: 11, color: '#D1D5DB', marginBottom: 4 }}>This Week</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#E8722A' }}>{fmtDuration(wMs + sessionMs)}</div>
                    {member.offThisWeek && (
                      <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>Off this week</div>
                    )}
                  </div>
                  <div style={{ background: '#2A3140', borderRadius: 7, padding: '10px 12px' }}>
                    <div style={{ fontSize: 11, color: '#D1D5DB', marginBottom: 4 }}>All Time</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#D1D5DB' }}>{fmtDuration(tMs + sessionMs)}</div>
                  </div>
                </div>

                {member.specialties && member.specialties.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {member.specialties.slice(0, 3).map(t => (
                      <span key={t} style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, background: (TRADE_COLORS[t] || '#8B95A1') + '22', color: TRADE_COLORS[t] || '#8B95A1' }}>{t}</span>
                    ))}
                    {member.specialties.length > 3 && (
                      <span style={{ fontSize: 10, color: '#D1D5DB', padding: '2px 7px' }}>+{member.specialties.length - 3} more</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {crewModal && (
        <CrewMemberModal
          member={crewModal === 'add' ? null : crewModal}
          onSave={handleSaveMember}
          onClose={() => setCrewModal(null)}
        />
      )}
    </div>
  );
}

// ─── Job Chat Panel ──────────────────────────────────────────────────────────
function JobChatPanel({ jobId, currentUser, demoMessages, onCountChange }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const endRef = useRef(null);
  const isMobile = useMobile();

  const load = useCallback(() => {
    if (demoMessages) {
      const sorted = [...demoMessages].sort((a, b) => a.timestamp - b.timestamp);
      setMessages(sorted);
      if (onCountChange) onCountChange(sorted.filter(m => m.type === 'user').length);
      return;
    }
    chatDB.getByJob(String(jobId)).then(msgs => {
      const sorted = msgs.sort((a, b) => a.timestamp - b.timestamp);
      setMessages(sorted);
      if (onCountChange) onCountChange(sorted.length);
    }).catch(() => {});
  }, [jobId, demoMessages, onCountChange]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    if (demoMessages) {
      const demoMsg = { id: `demo-${Date.now()}`, jobId: String(jobId), senderId: 'owner', senderName: currentUser || 'Demo Owner', text: t, timestamp: Date.now(), type: 'user' };
      setMessages(prev => [...prev, demoMsg]);
      setText('');
      return;
    }
    const msg = {
      id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      jobId: String(jobId),
      senderId: 'user',
      senderName: currentUser || 'You',
      text: t,
      timestamp: Date.now(),
      type: 'user',
    };
    await chatDB.add(msg).catch(() => {});
    setText('');
    load();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const formatTs = (ts) => new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{
        overflowY: 'auto', maxHeight: '50dvh', minHeight: 200,
        paddingBottom: 8, WebkitOverflowScrolling: 'touch',
      }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#D1D5DB' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>💬</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#D1D5DB' }}>No messages yet</div>
            <div style={{ fontSize: 12, marginTop: 4, color: '#D1D5DB' }}>Start the conversation below</div>
          </div>
        ) : messages.map(msg => (
          msg.type === 'system' ? (
            <div key={msg.id} style={{ textAlign: 'center', padding: '5px 0', margin: '4px 0' }}>
              <span style={{ fontSize: 11, color: '#D1D5DB', background: '#2A3140', padding: '3px 12px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.08)' }}>
                {msg.text}
              </span>
            </div>
          ) : (
            <div key={msg.id} style={{ marginBottom: 14, padding: '0 2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#D1D5DB', flexShrink: 0 }}>
                  {msg.senderName.slice(0, 2).toUpperCase()}
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#D1D5DB' }}>{msg.senderName}</span>
                <span style={{ fontSize: 11, color: '#D1D5DB' }}>{formatTs(msg.timestamp)}</span>
              </div>
              <div style={{ marginLeft: 34, fontSize: 13, color: '#D1D5DB', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.text}</div>
            </div>
          )
        ))}
        <div ref={endRef} />
      </div>

      <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 4 }}>
        <textarea
          style={{
            flex: 1, padding: isMobile ? '11px 12px' : '8px 12px',
            background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7,
            color: '#D1D5DB', fontSize: isMobile ? 16 : 13, fontFamily: "'Inter', -apple-system, sans-serif",
            resize: 'none', minHeight: isMobile ? 44 : 38, maxHeight: 100,
            lineHeight: 1.4, outline: 'none', boxSizing: 'border-box',
          }}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={1}
        />
        <button
          style={{
            padding: '8px 14px', border: 'none', borderRadius: 7,
            background: text.trim() ? 'linear-gradient(135deg, #E8722A, #e8640c)' : '#2A3140',
            color: text.trim() ? '#fff' : '#D1D5DB',
            cursor: text.trim() ? 'pointer' : 'default',
            fontWeight: 600, fontSize: 13, flexShrink: 0,
            minWidth: 60, minHeight: 44, WebkitTapHighlightColor: 'transparent',
          }}
          onClick={send}
        >
          Send
        </button>
      </div>
    </div>
  );
}

// ─── Chat Tab (global) ────────────────────────────────────────────────────────
function ChatTab({ jobs, demoMessages }) {
  const [dbMessages, setDbMessages] = useState([]);
  const [jobFilter, setJobFilter] = useState('all');
  const [composerText, setComposerText] = useState('');
  const [localMessages, setLocalMessages] = useState([]);
  const isMobile = useMobile();

  useEffect(() => {
    if (!demoMessages) {
      chatDB.getAll().then(msgs => {
        setDbMessages(msgs.sort((a, b) => b.timestamp - a.timestamp));
      }).catch(() => {});
    }
  }, [demoMessages]);

  const allMessages = useMemo(() => {
    const base = demoMessages ? [...demoMessages] : dbMessages;
    return [...base, ...localMessages].sort((a, b) => b.timestamp - a.timestamp);
  }, [demoMessages, dbMessages, localMessages]);

  const jobsWithMsgs = useMemo(() => {
    const ids = [...new Set(allMessages.map(m => m.jobId))];
    return ids.map(id => jobs.find(j => String(j.id) === id)).filter(Boolean);
  }, [allMessages, jobs]);

  const filtered = useMemo(() => {
    if (jobFilter === 'all') return allMessages;
    return allMessages.filter(m => m.jobId === jobFilter);
  }, [allMessages, jobFilter]);

  // Target job for the composer: the filtered job if a filter is active,
  // otherwise default to the first job with messages, then any job.
  const targetJob = useMemo(() => {
    if (jobFilter !== 'all') return jobs.find(j => String(j.id) === jobFilter) || null;
    return jobsWithMsgs[0] || jobs[0] || null;
  }, [jobFilter, jobs, jobsWithMsgs]);

  const sendMessage = () => {
    const t = composerText.trim();
    if (!t || !targetJob) return;
    const msg = {
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      jobId: String(targetJob.id),
      senderId: 'owner',
      senderName: 'Demo Owner',
      text: t,
      timestamp: Date.now(),
      type: 'user',
    };
    setLocalMessages(prev => [...prev, msg]);
    if (!demoMessages) chatDB.add(msg).catch(() => {});
    setComposerText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const jobName = (jobId) => {
    const j = jobs.find(jb => String(jb.id) === jobId);
    if (!j) return 'Unknown Job';
    return j.trade ? `${j.customer} · ${j.trade}` : j.customer;
  };

  const formatTs = (ts) => new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#FFFFFF' }}>Job Chat</div>
        <div style={{ fontSize: 13, color: '#D1D5DB' }}>Recent messages across all jobs</div>
      </div>

      <div style={S.filterRow}>
        <button style={S.filterBtn(jobFilter === 'all')} onClick={() => setJobFilter('all')}>
          All Jobs ({allMessages.filter(m => m.type === 'user').length})
        </button>
        {jobsWithMsgs.map(j => (
          <button key={j.id} style={S.filterBtn(jobFilter === String(j.id))} onClick={() => setJobFilter(String(j.id))}>
            {j.customer}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#D1D5DB' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#D1D5DB', marginBottom: 6 }}>No messages yet</div>
          <div style={{ fontSize: 13 }}>Open a job and start chatting with your crew.</div>
        </div>
      ) : (
        <div>
          {filtered.map(msg => (
            msg.type === 'system' ? (
              <div key={msg.id} style={{ textAlign: 'center', padding: '4px 0', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: '#D1D5DB' }}>
                  {jobName(msg.jobId)} · {msg.text}
                </span>
              </div>
            ) : (
              <div key={msg.id} style={{
                background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8,
                padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 12,
                overflow: 'hidden',
              }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#D1D5DB', flexShrink: 0 }}>
                  {msg.senderName.slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap', minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#D1D5DB' }}>{msg.senderName}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#E8722A', background: 'rgba(249,115,22,0.1)', padding: '1px 8px', borderRadius: 10, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{jobName(msg.jobId)}</span>
                    <span style={{ fontSize: 11, color: '#D1D5DB', marginLeft: 'auto' }}>{formatTs(msg.timestamp)}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#D1D5DB', overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.45 }}>{msg.text}</div>
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Composer */}
      <div style={{
        marginTop: 16, paddingTop: 12,
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 11, color: '#D1D5DB' }}>
          <span>Sending to:</span>
          <select
            value={targetJob ? String(targetJob.id) : ''}
            onChange={e => setJobFilter(e.target.value)}
            style={{
              flex: 1, minWidth: 0, background: '#2A3140',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6,
              color: '#E8722A', fontSize: 12, fontWeight: 600,
              padding: '6px 8px', minHeight: 32, outline: 'none',
              maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {jobs.length === 0 && <option value="">No jobs available</option>}
            {jobs.map(j => (
              <option key={j.id} value={String(j.id)}>{j.customer}{j.trade ? ` — ${j.trade}` : ''}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <textarea
            value={composerText}
            onChange={e => setComposerText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            disabled={!targetJob}
            style={{
              flex: 1, padding: isMobile ? '11px 12px' : '8px 12px',
              background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7,
              color: '#D1D5DB', fontSize: isMobile ? 16 : 13, fontFamily: "'Inter', -apple-system, sans-serif",
              resize: 'none', minHeight: isMobile ? 44 : 38, maxHeight: 100,
              lineHeight: 1.4, outline: 'none', boxSizing: 'border-box',
            }}
          />
          <button
            onClick={sendMessage}
            disabled={!composerText.trim() || !targetJob}
            style={{
              padding: '8px 14px', border: 'none', borderRadius: 7,
              background: composerText.trim() && targetJob ? 'linear-gradient(135deg, #E8722A, #e8640c)' : '#2A3140',
              color: composerText.trim() && targetJob ? '#fff' : '#D1D5DB',
              cursor: composerText.trim() && targetJob ? 'pointer' : 'not-allowed',
              fontWeight: 600, fontSize: 13, flexShrink: 0,
              minWidth: 60, minHeight: 44, WebkitTapHighlightColor: 'transparent',
            }}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Schedule Job Modal (reschedule existing) ──────────────────────────────────
function ScheduleJobModal({ defaultDate, jobs, onSave, onClose, targetJob }) {
  useScrollLock();
  const activeJobs = jobs.filter(j => j.status !== 'Complete');
  const isReschedule = targetJob && targetJob.scheduledDate;
  const [jobId, setJobId] = useState(targetJob ? String(targetJob.id) : (activeJobs[0] ? String(activeJobs[0].id) : ''));
  const [date, setDate] = useState(targetJob ? (targetJob.scheduledDate || defaultDate || TODAY) : (defaultDate || TODAY));
  const [duration, setDuration] = useState(targetJob ? jobDuration(targetJob) : 1);

  const handleSave = () => {
    if (!jobId || !date) return;
    onSave(jobId, date, duration);
    onClose();
  };

  const btnBase = { padding: '9px 20px', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
  const title = targetJob ? (isReschedule ? `Reschedule — ${targetJob.customer}` : `Schedule — ${targetJob.customer}`) : 'Schedule Existing Job';

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#FFFFFF', marginBottom: 18, paddingRight: 40 }}>{title}</div>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        {!targetJob && (
          <>
            <label style={FLbl}>Job</label>
            <select style={{ ...FI, marginBottom: 4 }} value={jobId} onChange={e => setJobId(e.target.value)}>
              <option value="">Select a job…</option>
              {activeJobs.map(j => <option key={j.id} value={String(j.id)}>{j.customer} — {j.trade} ({j.status})</option>)}
            </select>
          </>
        )}
        <label style={FLbl}>Start Date</label>
        <input type="date" style={{ ...FI, marginBottom: 4 }} value={date} onChange={e => setDate(e.target.value)} />
        <label style={FLbl}>Duration (days)</label>
        <input type="number" min={1} max={365} style={{ ...FI, marginBottom: 20 }} value={duration} onChange={e => setDuration(Math.max(1, parseInt(e.target.value) || 1))} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={{ ...btnBase, background: 'rgba(255,255,255,0.08)', color: '#D1D5DB' }} onClick={onClose}>Cancel</button>
          <button style={{ ...btnBase, background: jobId && date ? 'linear-gradient(135deg,#E8722A,#ea580c)' : 'rgba(255,255,255,0.08)', color: jobId && date ? '#fff' : '#D1D5DB' }} onClick={handleSave} disabled={!jobId || !date}>
            {isReschedule ? 'Reschedule' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Roofing workflow phases for checklist smart insertion ─────────────────────
const ROOFING_WORKFLOW_PHASES = [
  { score: 1,  keys: ['site inspect', 'initial inspect', 'site visit', 'measur', 'survey', 'assess', 'estimate'] },
  { score: 2,  keys: ['permit', 'hoa', 'approv'] },
  { score: 3,  keys: ['material', 'order', 'deliv', 'suppli', 'purchas'] },
  { score: 4,  keys: ['safety', 'scaffold', 'staging', 'equipment staging', 'set up equip'] },
  { score: 5,  keys: ['tear off', 'tear-off', 'strip roof', 'remove old roof', 'demo old'] },
  { score: 6,  keys: ['deck', 'board', 'rotted', 'rot', 'plywood', 'sheathing', 'damaged wood', 'wood repair'] },
  { score: 7,  keys: ['drip edge', 'drip'] },
  { score: 8,  keys: ['ice and water', 'ice & water', 'water shield', 'ice shield'] },
  { score: 9,  keys: ['underlayment', 'roofing felt', 'synthetic felt', 'peel and stick'] },
  { score: 10, keys: ['install shingle', 'lay shingle', 'new roof', 'roof system', 'membrane', 'flat roof', 'asphalt'] },
  { score: 11, keys: ['ridge vent', 'ridge cap', 'ventilation', 'soffit vent', 'attic vent'] },
  { score: 12, keys: ['flash', 'seal', 'caulk', 'chimney', 'skylight', 'valley'] },
  { score: 13, keys: ['gutter', 'downspout', 'fascia'] },
  { score: 14, keys: ['cleanup', 'clean up', 'debris', 'haul away', 'sweep', 'disposal'] },
  { score: 15, keys: ['final inspect', 'final check', 'walkthrough', 'punch list', 'completion'] },
  { score: 16, keys: ['invoice', 'billing', 'send bill', 'statement'] },
  { score: 17, keys: ['payment', 'paid', 'receiv', 'collect payment'] },
];
function getWorkflowScore(text) {
  const lower = text.toLowerCase();
  for (const phase of ROOFING_WORKFLOW_PHASES) {
    if (phase.keys.some(k => lower.includes(k))) return phase.score;
  }
  return 14.5; // default: before final inspection
}

// ─── Quick Schedule Bar (inline panel) ─────────────────────────────────────────
function QuickScheduleBar({ date, job, userTrade, onSave, onClose }) {
  useScrollLock();
  const [description, setDescription] = useState(job?.description || '');
  const [customer, setCustomer] = useState(job?.customer || '');
  const [duration, setDuration] = useState(job?.duration || 1);
  const [showChecklist, setShowChecklist] = useState(!!(job?.taskList?.length));
  const [taskList, setTaskList] = useState(job?.taskList || []);
  const [taskHistory, setTaskHistory] = useState([]); // undo stack, max 5
  const [taskInput, setTaskInput] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => { if (textareaRef.current) textareaRef.current.focus(); }, []);

  const isEdit = !!job;
  const displayDate = date ? new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
  const canSave = description.trim() || customer.trim();

  const pushHistory = (list) => setTaskHistory(h => [...h.slice(-4), list]);

  const addTask = () => {
    const raw = taskInput.trim();
    if (!raw) return;
    // Detect "3. Step text" or "3) Step text" → insert at that position
    const numbered = raw.match(/^(\d+)[.)]\s*(.+)/);
    let label, insertIdx;
    if (numbered) {
      label = numbered[2].trim();
      insertIdx = Math.min(parseInt(numbered[1]) - 1, taskList.length); // clamp to end
    } else {
      label = raw;
      const newScore = getWorkflowScore(label);
      const firstHigher = taskList.map(s => getWorkflowScore(s)).findIndex(s => s > newScore);
      insertIdx = firstHigher === -1 ? taskList.length : firstHigher;
    }
    const next = [...taskList];
    next.splice(insertIdx, 0, label);
    pushHistory(taskList);
    setTaskList(next);
    setTaskInput('');
  };

  const removeTask = (i) => {
    pushHistory(taskList);
    setTaskList(prev => prev.filter((_, idx) => idx !== i));
  };

  const undo = () => {
    if (taskHistory.length === 0) return;
    setTaskList(taskHistory[taskHistory.length - 1]);
    setTaskHistory(h => h.slice(0, -1));
  };

  const useTemplate = () => {
    const tmpl = TRADE_CHECKLISTS[userTrade] || TRADE_CHECKLISTS['Full Replacement'];
    pushHistory(taskList);
    setTaskList(tmpl.map(s => s.label));
    setShowChecklist(true);
  };

  const handleSave = () => {
    if (!canSave) return;
    const desc = description.trim();
    const cust = customer.trim() || (desc ? desc.slice(0, 40) : 'Untitled');
    onSave({
      ...(isEdit ? { id: job.id } : {}),
      description: desc,
      customer: cust,
      trade: userTrade,
      scheduledDate: date,
      duration,
      taskList,
      value: job?.value || 0,
    });
    onClose();
  };

  return (
    <div style={{ background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '16px 18px', marginBottom: 16, position: 'relative' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#E8722A' }}>{isEdit ? 'Edit Entry' : '+ Schedule'}</span>
          {displayDate && <span style={{ fontSize: 12, color: '#D1D5DB', marginLeft: 8 }}>{displayDate}</span>}
          <span style={{ fontSize: 11, color: '#D1D5DB', marginLeft: 8 }}>{userTrade}</span>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#D1D5DB', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
      </div>

      {/* Main textarea */}
      <textarea
        ref={textareaRef}
        value={description}
        onChange={e => setDescription(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleSave(); }}
        placeholder={`What's happening this day? (e.g. Roof replacement, final inspection, material delivery…)`}
        rows={3}
        style={{ width: '100%', boxSizing: 'border-box', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#FFFFFF', fontSize: 14, padding: '10px 12px', resize: 'vertical', fontFamily: 'inherit', outline: 'none', marginBottom: 10 }}
      />

      {/* Secondary fields */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <input
          value={customer}
          onChange={e => setCustomer(e.target.value)}
          placeholder="Client name (optional)"
          style={{ flex: '2 1 160px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#FFFFFF', fontSize: 13, padding: '8px 10px', fontFamily: 'inherit', outline: 'none' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '1 1 120px' }}>
          <span style={{ fontSize: 12, color: '#D1D5DB', whiteSpace: 'nowrap' }}>Duration:</span>
          <input
            type="number" min={1} max={365} value={duration}
            onChange={e => setDuration(Math.max(1, parseInt(e.target.value) || 1))}
            style={{ width: 54, background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#FFFFFF', fontSize: 13, padding: '7px 8px', textAlign: 'center', fontFamily: 'inherit', outline: 'none' }}
          />
          <span style={{ fontSize: 12, color: '#D1D5DB' }}>day{duration !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Checklist toggle */}
      <div style={{ marginBottom: showChecklist ? 10 : 0 }}>
        <button
          onClick={() => setShowChecklist(v => !v)}
          style={{ background: 'none', border: 'none', color: showChecklist ? '#E8722A' : '#D1D5DB', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: showChecklist ? 8 : 0 }}
        >
          {showChecklist ? '▾ Checklist' : '▸ Add Checklist'}
        </button>
        {!showChecklist && taskList.length > 0 && <span style={{ fontSize: 11, color: '#D1D5DB', marginLeft: 8 }}>{taskList.length} items</span>}

        {showChecklist && (
          <div style={{ background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '10px 12px' }}>
            {/* Input row */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input
                value={taskInput}
                onChange={e => setTaskInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTask(); } }}
                placeholder='Step name, or "3. Step" to insert at position 3…'
                style={{ flex: 1, background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#FFFFFF', fontSize: 12, padding: '6px 10px', fontFamily: 'inherit', outline: 'none' }}
              />
              <button onClick={useTemplate} style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#D1D5DB', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Use {userTrade} template
              </button>
            </div>
            {/* Undo row */}
            {taskHistory.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <button onClick={undo} style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                  ↩ Undo{taskHistory.length > 1 ? ` (${taskHistory.length} steps)` : ''}
                </button>
              </div>
            )}
            {/* Steps list */}
            {taskList.length === 0 && <div style={{ fontSize: 11, color: '#D1D5DB', fontStyle: 'italic' }}>No steps yet — type above or use the template</div>}
            {taskList.map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: i < taskList.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                <span style={{ fontSize: 11, color: '#D1D5DB', minWidth: 18, flexShrink: 0 }}>{i + 1}.</span>
                <span style={{ flex: 1, fontSize: 12, color: '#D1D5DB' }}>{t}</span>
                <button onClick={() => removeTask(i)} style={{ background: 'none', border: 'none', color: '#D1D5DB', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <span style={{ fontSize: 11, color: '#9CA3AF', marginRight: 'auto' }}>Ctrl+Enter to save</span>
        <button onClick={onClose} style={{ padding: '7px 16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#D1D5DB', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button
          onClick={handleSave}
          disabled={!canSave}
          style={{ padding: '7px 20px', background: canSave ? 'linear-gradient(135deg,#E8722A,#ea580c)' : 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, color: canSave ? '#fff' : '#D1D5DB', fontSize: 13, fontWeight: 700, cursor: canSave ? 'pointer' : 'not-allowed' }}
        >
          {isEdit ? 'Update' : 'Schedule'}
        </button>
      </div>
    </div>
  );
}

// ─── Stage Change Modal ─────────────────────────────────────────────────────────
function StageChangeModal({ job, onSave, onClose }) {
  useScrollLock();
  const stages = [
    { key: 'materials_ordered', label: 'Materials Ordered', color: '#8b5cf6' },
    { key: 'scheduled_for_install', label: 'Scheduled for Install', color: '#06b6d4' },
    { key: 'in_progress', label: 'In Progress', color: '#E8722A' },
    { key: 'punch_list', label: 'Punch List', color: '#ec4899' },
    { key: 'completed', label: 'Completed', color: '#22c55e' },
    { key: 'invoiced', label: 'Invoiced', color: '#a855f7' },
    { key: 'paid', label: 'Paid', color: '#14b8a6' },
  ];
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 300 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#FFFFFF', marginBottom: 4, paddingRight: 40 }}>Change Stage</div>
        <div style={{ fontSize: 12, color: '#D1D5DB', marginBottom: 18 }}>{job.customer} · {job.trade}</div>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {stages.map(s => (
            <button key={s.key} onClick={() => { onSave(s.key); onClose(); }} style={{ padding: '10px 16px', borderRadius: 8, border: `1px solid ${s.color}44`, background: `${s.color}18`, color: s.color, fontWeight: 700, fontSize: 14, cursor: 'pointer', textAlign: 'left' }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Quick Assign Crew Panel ─────────────────────────────────────────────────────
function QuickAssignCrewPanel({ job, crew, assignments, onAssign, onUnassign, onClose }) {
  useScrollLock();
  const assignedIds = assignments[String(job.id)] || [];
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 340 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#FFFFFF', marginBottom: 4, paddingRight: 40 }}>Assign Crew</div>
        <div style={{ fontSize: 12, color: '#D1D5DB', marginBottom: 16 }}>{job.customer} · {job.trade}</div>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        {crew.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#D1D5DB', fontSize: 13, padding: '16px 0' }}>No crew members added yet. Go to the Crew tab to add your team.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {crew.map(m => {
              const assigned = assignedIds.includes(m.id);
              return (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#2A3140', border: `1px solid ${assigned ? 'rgba(249,115,22,0.3)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: assigned ? '#E8722A' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: assigned ? '#fff' : '#D1D5DB', flexShrink: 0 }}>
                    {m.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#D1D5DB' }}>{m.name}</div>
                    <div style={{ fontSize: 11, color: '#D1D5DB' }}>{m.role}</div>
                  </div>
                  <button onClick={() => assigned ? onUnassign(job.id, m.id) : onAssign(job.id, m.id)} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', background: assigned ? 'rgba(239,68,68,0.15)' : 'rgba(249,115,22,0.15)', color: assigned ? '#ef4444' : '#E8722A' }}>
                    {assigned ? 'Remove' : '+ Add'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <button style={{ marginTop: 16, width: '100%', padding: '10px', background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, color: '#D1D5DB', fontWeight: 600, fontSize: 13, cursor: 'pointer' }} onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

// ─── Today's Summary Panel ───────────────────────────────────────────────────────
function TodaySummaryPanel({ jobs, crew, assignments, conflicts, onJobClick }) {
  const todayJobs = useMemo(() => jobs.filter(j => jobOccupiesDate(j, TODAY)), [jobs]);
  const unassignedToday = useMemo(() => todayJobs.filter(j => !(assignments[String(j.id)] || []).length), [todayJobs, assignments]);
  return (
    <div style={{ marginTop: 24, background: '#2A3140', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#E8722A' }}>Today's Dispatch</div>
          <div style={{ fontSize: 12, color: '#D1D5DB' }}>{new Date(TODAY + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
        </div>
        <div style={{ fontSize: 12, color: '#D1D5DB', textAlign: 'right' }}>
          <span style={{ color: '#FFFFFF', fontWeight: 700 }}>{todayJobs.length}</span> jobs ·{' '}
          <span style={{ color: unassignedToday.length ? '#E8722A' : '#22c55e', fontWeight: 700 }}>{unassignedToday.length}</span> unassigned
        </div>
      </div>
      {todayJobs.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#D1D5DB', fontSize: 13, padding: '12px 0' }}>No jobs scheduled today</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: crew.length > 0 ? 16 : 0 }}>
          {todayJobs.map(job => {
            const tc = getTradeColor(job.trade);
            const assignedCrew = (assignments[String(job.id)] || []).map(id => crew.find(m => m.id === id)).filter(Boolean);
            const hasConflict = assignedCrew.some(m => conflicts.has(`${m.id}-${String(job.id)}`));
            return (
              <div key={job.id} onClick={() => onJobClick(job)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#2A3140', border: `1px solid ${hasConflict ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.08)'}`, borderLeft: `3px solid ${tc}`, borderRadius: 8, cursor: 'pointer' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.customer}</div>
                  <div style={{ fontSize: 11, color: '#D1D5DB' }}>{job.trade} · {jobDuration(job)}d</div>
                </div>
                {assignedCrew.length > 0 ? (
                  <div style={{ display: 'flex', gap: 3 }}>
                    {assignedCrew.slice(0, 3).map(m => (
                      <div key={m.id} title={m.name} style={{ width: 22, height: 22, borderRadius: '50%', background: '#E8722A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff' }}>
                        {m.name.slice(0, 2).toUpperCase()}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span style={{ fontSize: 11, color: '#E8722A', fontWeight: 600 }}>Unassigned</span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {crew.length > 0 && (
        <>
          <div style={S.sectionLabel}>Crew Status</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6 }}>
            {crew.map(member => {
              const memberJobs = todayJobs.filter(j => (assignments[String(j.id)] || []).includes(member.id));
              const busy = memberJobs.length > 0;
              const hasConflict = memberJobs.some(j => conflicts.has(`${member.id}-${String(j.id)}`));
              return (
                <div key={member.id} style={{ padding: '8px 10px', borderRadius: 8, background: '#2A3140', border: `1px solid ${hasConflict ? 'rgba(239,68,68,0.3)' : busy ? 'rgba(249,115,22,0.2)' : 'rgba(255,255,255,0.08)'}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: busy ? '#E8722A' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: busy ? '#fff' : '#D1D5DB' }}>
                    {member.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.name}</div>
                    <div style={{ fontSize: 10, color: hasConflict ? '#ef4444' : busy ? '#E8722A' : '#22c55e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {hasConflict ? '⚠ Conflict' : busy ? memberJobs[0].customer : 'Available'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Calendar: Context Menu ─────────────────────────────────────────────────────
function CalendarContextMenu({ job, x, y, onView, onReschedule, onAssignCrew, onChangeStage, onRemove, onComplete, onDelete }) {
  const tc = getTradeColor(job.trade);
  const items = [
    { label: 'View Details', action: onView, icon: '📋' },
    { label: 'Reschedule', action: onReschedule, icon: '📅' },
    { label: 'Assign Crew', action: onAssignCrew, icon: '👷' },
    { label: 'Change Stage', action: onChangeStage, icon: '🔄' },
    job.status !== 'Complete' ? { label: 'Mark Complete', action: onComplete, icon: '✓', color: '#22c55e' } : null,
    { label: 'Remove from Calendar', action: onRemove, icon: '✕', color: '#ef4444' },
    { label: 'Delete Job', action: onDelete, icon: '🗑', color: '#ef4444' },
  ].filter(Boolean);
  return (
    <div style={{ position: 'fixed', left: x, top: y, zIndex: 2000, background: '#1e293b', border: `1px solid ${tc}44`, borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', minWidth: 200, overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px 6px', fontSize: 11, fontWeight: 700, color: '#D1D5DB', borderBottom: '1px solid rgba(255,255,255,0.12)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{job.customer}</div>
      {items.map(item => (
        <button key={item.label} onClick={item.action} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: item.color || '#8B95A1', fontSize: 13, cursor: 'pointer', textAlign: 'left', WebkitTapHighlightColor: 'transparent' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
          <span style={{ width: 16, textAlign: 'center' }}>{item.icon}</span>
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ─── Calendar: Day Dispatch ─────────────────────────────────────────────────────
function DayDispatch({ dateStr, jobs, crew, assignments, conflicts, onClose, onJobClick, onCreateForDate }) {
  const d = new Date(dateStr + 'T12:00:00');
  const dayJobs = jobs.filter(j => jobOccupiesDate(j, dateStr));

  return (
    <div style={{ background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 16, marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14, gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#FFFFFF' }}>
            {d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </div>
          <div style={{ fontSize: 12, color: '#D1D5DB' }}>{dayJobs.length} job{dayJobs.length !== 1 ? 's' : ''} scheduled</div>
        </div>
        {onCreateForDate && (
          <button onClick={onCreateForDate} style={{ padding: '6px 14px', background: 'linear-gradient(135deg,#E8722A,#ea580c)', border: 'none', borderRadius: 7, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
            + New Job
          </button>
        )}
        {onClose && <button style={{ ...S.closeBtn, position: 'relative', top: 'auto', right: 'auto', flexShrink: 0 }} onClick={onClose}>×</button>}
      </div>

      {dayJobs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#D1D5DB', fontSize: 13 }}>No jobs scheduled this day</div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          {dayJobs.map(job => {
            const tc = getTradeColor(job.trade);
            const assignedCrew = (assignments[String(job.id)] || []).map(id => crew.find(m => m.id === id)).filter(Boolean);
            const hasConflict = assignedCrew.some(m => conflicts.has(`${m.id}-${String(job.id)}`));
            return (
              <div
                key={job.id}
                onClick={() => onJobClick && onJobClick(job)}
                style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 6, background: '#2A3140', border: `1px solid ${hasConflict ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.08)'}`, borderLeft: `3px solid ${tc}`, cursor: onJobClick ? 'pointer' : 'default' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#FFFFFF' }}>{job.customer}</div>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: tc + '22', color: tc }}>{job.status}</span>
                </div>
                <div style={{ fontSize: 11, color: '#D1D5DB', marginBottom: assignedCrew.length ? 6 : 0 }}>
                  {job.trade} · {job.scheduledDate} → {jobEndDate(job)} · {jobDuration(job)}d
                </div>
                {assignedCrew.length > 0 && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {assignedCrew.map(m => (
                      <span key={m.id} style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'rgba(255,255,255,0.08)', color: conflicts.has(`${m.id}-${String(job.id)}`) ? '#ef4444' : '#D1D5DB' }}>
                        {m.name}{conflicts.has(`${m.id}-${String(job.id)}`) ? ' ⚠' : ''}
                      </span>
                    ))}
                  </div>
                )}
                {hasConflict && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>⚠ Crew conflict on this date</div>}
              </div>
            );
          })}
        </div>
      )}

      {crew.length > 0 && (
        <div>
          <div style={S.sectionLabel}>Crew Availability</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {crew.map(member => {
              const memberJobs = dayJobs.filter(j => (assignments[String(j.id)] || []).includes(member.id));
              const busy = memberJobs.length > 0;
              return (
                <div key={member.id} style={{ padding: '8px 10px', borderRadius: 7, background: '#2A3140', border: `1px solid ${busy ? 'rgba(249,115,22,0.3)' : 'rgba(255,255,255,0.08)'}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: busy ? '#E8722A' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: busy ? '#fff' : '#D1D5DB' }}>
                    {member.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.name}</div>
                    <div style={{ fontSize: 10, color: busy ? '#E8722A' : '#22c55e' }}>{busy ? memberJobs[0].customer : 'Available'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Calendar: Month View ─────────────────────────────────────────────────────
function MonthView({ days, currentMonth, today, dayJobsFn, onDayClick, selectedDate, onJobClick, onJobContextMenu, dragJobId, dragOverDate, onDragStart, onDragOver, onDrop, onDragEnd, dayNotes }) {
  const isMobile = useMobile();
  const DAY_NAMES = isMobile ? ['S','M','T','W','T','F','S'] : ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
        {DAY_NAMES.map((d, i) => <div key={i} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#D1D5DB', padding: '4px 0' }}>{d}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {days.map(dateStr => {
          const dayJobs = dayJobsFn(dateStr);
          const isToday = dateStr === today;
          const isSelected = dateStr === selectedDate;
          const isDragOver = dragOverDate === dateStr && dragJobId;
          const inMonth = dateStr.slice(0, 7) === currentMonth;
          const maxVis = isMobile ? 1 : 3;
          const overflow = dayJobs.length - maxVis;
          return (
            <div
              key={dateStr}
              onClick={() => onDayClick(dateStr)}
              onDragOver={e => { e.preventDefault(); if (onDragOver) onDragOver(dateStr); }}
              onDrop={e => { e.preventDefault(); const jid = e.dataTransfer.getData('jobId'); if (jid && onDrop) onDrop(jid, dateStr); }}
              onDragLeave={() => { if (onDragOver) onDragOver(null); }}
              style={{ minHeight: isMobile ? 52 : 80, background: isDragOver ? 'rgba(232,114,42,0.1)' : isSelected ? 'rgba(232,114,42,0.1)' : (parseInt(dateStr.slice(8)) % 7 === 0 || parseInt(dateStr.slice(8)) % 7 === 6) ? '#1E2329' : '#2A3140', border: `1px solid ${isDragOver ? '#E8722A' : isSelected ? '#E8722A' : isToday ? '#E8722A' : 'rgba(255,255,255,0.08)'}`, borderRadius: 6, padding: isMobile ? '3px 4px' : '5px 6px', cursor: 'pointer', overflow: 'hidden', opacity: inMonth ? 1 : 0.35 }}
            >
              <div style={{ marginBottom: 2 }}>
                {isToday
                  ? <span style={{ background: '#E8722A', color: '#fff', borderRadius: '50%', width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{parseInt(dateStr.slice(8))}</span>
                  : <span style={{ fontSize: isMobile ? 10 : 12, color: '#D1D5DB', fontWeight: 500 }}>{parseInt(dateStr.slice(8))}</span>
                }
              </div>
              {dayJobs.slice(0, maxVis).map(job => {
                const isDragging = dragJobId === String(job.id);
                return (
                  <div
                    key={job.id}
                    draggable
                    onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData('jobId', String(job.id)); e.dataTransfer.effectAllowed = 'move'; if (onDragStart) onDragStart(String(job.id)); }}
                    onDragEnd={() => { if (onDragEnd) onDragEnd(); }}
                    onClick={e => { e.stopPropagation(); onJobClick && onJobClick(job); }}
                    onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onJobContextMenu && onJobContextMenu(job, e); }}
                    style={{ fontSize: isMobile ? 9 : 11, fontWeight: 500, background: '#E8722A', color: '#2A3140', padding: isMobile ? '1px 4px' : '2px 6px', borderRadius: 4, marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'grab', opacity: isDragging ? 0.4 : 1 }}
                  >
                    {isMobile ? (job.description || job.customer).slice(0, 6) : (job.description || job.customer).split(' ').slice(0, 3).join(' ')}
                  </div>
                );
              })}
              {overflow > 0 && <div style={{ fontSize: 9, color: '#D1D5DB', padding: '1px 2px' }}>+{overflow}</div>}
              {dayNotes && dayNotes[dateStr] && <div style={{ fontSize: 8, color: '#818cf8', lineHeight: 1, paddingTop: 1 }}>&#128221;</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Calendar: Week View ──────────────────────────────────────────────────────
function WeekView({ days, today, dayJobsFn, onDayClick, selectedDate, onJobClick, onJobContextMenu, dragJobId, dragOverDate, onDragStart, onDragOver, onDrop, onDragEnd, dayNotes }) {
  const isMobile = useMobile();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: isMobile ? 2 : 6 }}>
      {days.map(dateStr => {
        const dayJobs = dayJobsFn(dateStr);
        const isToday = dateStr === today;
        const isSelected = dateStr === selectedDate;
        const isDragOver = dragOverDate === dateStr && dragJobId;
        const d = new Date(dateStr + 'T12:00:00');
        return (
          <div
            key={dateStr}
            onClick={() => onDayClick(dateStr)}
            onDragOver={e => { e.preventDefault(); if (onDragOver) onDragOver(dateStr); }}
            onDrop={e => { e.preventDefault(); const jid = e.dataTransfer.getData('jobId'); if (jid && onDrop) onDrop(jid, dateStr); }}
            onDragLeave={() => { if (onDragOver) onDragOver(null); }}
            style={{ background: isDragOver ? 'rgba(249,115,22,0.12)' : isSelected ? 'rgba(249,115,22,0.08)' : '#2A3140', border: `1px solid ${isDragOver ? '#E8722A' : isSelected ? '#E8722A' : isToday ? 'rgba(249,115,22,0.5)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 8, padding: isMobile ? '6px 4px' : '10px 8px', cursor: 'pointer', minHeight: 100 }}
          >
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: isMobile ? 9 : 10, fontWeight: 600, color: '#D1D5DB', textTransform: 'uppercase' }}>{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
              <div style={{ fontSize: isMobile ? 13 : 18, fontWeight: 700, color: isToday ? '#fff' : '#1E2329', background: isToday ? '#E8722A' : 'transparent', borderRadius: '50%', width: isMobile ? 22 : 28, height: isMobile ? 22 : 28, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '2px auto 0' }}>
                {d.getDate()}
              </div>
            </div>
            {dayJobs.map(job => {
              const tc = getTradeColor(job.trade);
              const isDragging = dragJobId === String(job.id);
              return (
                <div
                  key={job.id}
                  draggable
                  onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData('jobId', String(job.id)); e.dataTransfer.effectAllowed = 'move'; if (onDragStart) onDragStart(String(job.id)); }}
                  onDragEnd={() => { if (onDragEnd) onDragEnd(); }}
                  onClick={e => { e.stopPropagation(); onJobClick && onJobClick(job); }}
                  onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onJobContextMenu && onJobContextMenu(job, e); }}
                  style={{ fontSize: isMobile ? 9 : 10, fontWeight: 600, padding: isMobile ? '2px 4px' : '3px 6px', borderRadius: 4, background: tc + '22', color: tc, borderLeft: `2px solid ${tc}`, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'grab', opacity: isDragging ? 0.4 : 1 }}
                >
                  {isMobile ? (job.description || job.customer).slice(0, 6) : (job.description || job.customer)}
                </div>
              );
            })}
            {dayNotes && dayNotes[dateStr] && (
              <div style={{ fontSize: 9, color: '#818cf8', marginTop: 4, padding: '2px 4px', background: 'rgba(99,102,241,0.1)', borderRadius: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                &#128221; {dayNotes[dateStr].slice(0, 18)}{dayNotes[dateStr].length > 18 ? '…' : ''}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Day Action Modal ──────────────────────────────────────────────────────────
function DayActionModal({ date, allJobs, assignments, crew, existingNote, onScheduleExisting, onCreateNew, onAddNote, onClose }) {
  useScrollLock();
  const [mode, setMode] = useState('menu'); // 'menu' | 'schedule' | 'create' | 'note' | 'dayDetail'
  const [note, setNote] = useState(existingNote || '');
  const [description, setDescription] = useState('');
  const [customer, setCustomer] = useState('');
  const [duration, setDuration] = useState(1);
  const [search, setSearch] = useState('');
  const [justScheduled, setJustScheduled] = useState(null);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [schedJobId, setSchedJobId] = useState('');
  const [schedCrew, setSchedCrew] = useState([]);
  const [schedTime, setSchedTime] = useState('7:00 AM');
  const [schedHours, setSchedHours] = useState(8);
  const [schedNotes, setSchedNotes] = useState('');
  const noteRef = useRef(null);
  const descRef = useRef(null);
  const searchRef = useRef(null);
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });

  useEffect(() => { if (mode === 'note' && noteRef.current) noteRef.current.focus(); }, [mode]);
  useEffect(() => { if (mode === 'create' && descRef.current) descRef.current.focus(); }, [mode]);
  useEffect(() => { if (mode === 'schedule' && searchRef.current) searchRef.current.focus(); }, [mode]);
  useEffect(() => {
    if (!justScheduled) return;
    const t = setTimeout(() => closeRef.current(), 1300);
    return () => clearTimeout(t);
  }, [justScheduled]);

  const displayDate = new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const subTitle = mode === 'menu' ? 'What would you like to do?'
    : mode === 'schedule' ? 'Tap a job to schedule or reschedule it'
    : mode === 'create' ? 'Quick-create a new job'
    : mode === 'dayDetail' ? 'Jobs, crew, and hours at a glance'
    : 'Add a note for this day';

  const backBtn = (
    <button
      onClick={() => setMode('menu')}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#D1D5DB', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '2px 0', marginBottom: 14 }}
    >
      &#8592; Back
    </button>
  );

  const actionBtn = (icon, label, sub, onClick) => (
    <button
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '13px 16px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 9, cursor: 'pointer', textAlign: 'left', marginBottom: 8, WebkitTapHighlightColor: 'transparent' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#E8722A'; e.currentTarget.style.background = 'rgba(249,115,22,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = '#2A3140'; }}
    >
      <span style={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#D1D5DB' }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: '#D1D5DB', marginTop: 2 }}>{sub}</div>}
      </div>
    </button>
  );

  const jobs = allJobs || [];
  const q = search.toLowerCase();
  const filtered = q ? jobs.filter(j => (j.description || '').toLowerCase().includes(q) || (j.customer || '').toLowerCase().includes(q)) : jobs;
  const unscheduledSection = filtered.filter(j => !j.scheduledDate || j.explicitlyScheduled === false);
  const scheduledSection = filtered.filter(j => j.scheduledDate && j.explicitlyScheduled !== false);

  const crewLabel = (jobId) => {
    const ids = (assignments || {})[String(jobId)] || [];
    const names = ids.map(id => (crew || []).find(m => m.id === id)).filter(Boolean).map(m => m.name);
    return names.length ? names.join(', ') : null;
  };

  const JobRow = ({ job }) => {
    const alreadyHere = job.scheduledDate === date;
    const crewNames = crewLabel(job.id);
    const fmtDate = job.scheduledDate ? new Date(job.scheduledDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Unscheduled';
    return (
      <button
        onClick={alreadyHere ? undefined : () => { onScheduleExisting(date, job.id); setJustScheduled(job); }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: alreadyHere ? '#2A3140' : '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, cursor: alreadyHere ? 'default' : 'pointer', textAlign: 'left', width: '100%', boxSizing: 'border-box', opacity: alreadyHere ? 0.5 : 1 }}
        onMouseEnter={alreadyHere ? undefined : (e => { e.currentTarget.style.borderColor = '#E8722A'; e.currentTarget.style.background = 'rgba(249,115,22,0.06)'; })}
        onMouseLeave={alreadyHere ? undefined : (e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = '#2A3140'; })}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: alreadyHere ? '#8B95A1' : '#1E2329', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.customer}{job.trade ? ` — ${job.trade}` : ''}</div>
          <div style={{ fontSize: 11, color: '#D1D5DB', marginTop: 1 }}>
            {job.customer && job.description ? `${job.customer} · ` : ''}{job.status || ''}{fmtDate ? ` · ${fmtDate}` : ''}
          </div>
          {crewNames && <div style={{ fontSize: 10, color: '#D1D5DB', marginTop: 1 }}>&#128100; {crewNames}</div>}
        </div>
        {alreadyHere
          ? <span style={{ fontSize: 10, color: '#D1D5DB', flexShrink: 0, whiteSpace: 'nowrap' }}>Already here</span>
          : <span style={{ fontSize: 11, color: '#E8722A', fontWeight: 700, flexShrink: 0 }}>&#8594;</span>
        }
      </button>
    );
  };

  const SuccessView = ({ icon, title, sub }) => (
    <div style={{ textAlign: 'center', padding: '28px 0' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#4ade80', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12, color: '#D1D5DB' }}>{sub}</div>
    </div>
  );

  const dayIsMobile = useMobile();
  const dayOverlay = dayIsMobile ? { ...S.overlay, padding: 0, alignItems: 'flex-end' } : S.modalOverlay;
  const dayModalStyle = dayIsMobile
    ? { ...S.modal, maxWidth: '100vw', width: '100vw', maxHeight: '90dvh', borderRadius: '16px 16px 0 0', margin: 0, padding: '20px 16px 24px' }
    : { ...S.modal, maxWidth: mode === 'dayDetail' ? 560 : 400 };

  return (
    <div style={dayOverlay} onClick={onClose}>
      <div style={dayModalStyle} onClick={e => e.stopPropagation()}>
        <button className="ri-close-btn" style={S.closeBtn} onClick={onClose}>×</button>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#E8722A', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>Dispatch Board</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#FFFFFF', marginBottom: 2 }}>{displayDate}</div>
        <div style={{ fontSize: 12, color: '#D1D5DB', marginBottom: 20 }}>{subTitle}</div>

        {mode === 'menu' && (
          <div>
            {(() => {
              const dayJobs = (allJobs || []).filter(j => jobOccupiesDate(j, date));
              const dayCrewCount = new Set(dayJobs.flatMap(j => (assignments || {})[String(j.id)] || [])).size;
              return actionBtn('📊', 'Day Detail',
                dayJobs.length > 0 ? `${dayJobs.length} job${dayJobs.length !== 1 ? 's' : ''} · ${dayCrewCount} crew` : 'No jobs scheduled',
                () => setMode('dayDetail')
              );
            })()}
            {actionBtn('📋', 'Schedule / Reschedule Job',
              jobs.length > 0 ? `${jobs.length} job${jobs.length !== 1 ? 's' : ''} in pipeline` : 'Pick from pipeline jobs',
              () => setMode('schedule')
            )}
            {actionBtn('✏️', 'Create New Job', 'Quick-create and schedule for this date',
              () => setMode('create')
            )}
            {actionBtn('📝', existingNote ? 'Edit Note' : 'Add Note',
              existingNote ? `"${existingNote.slice(0, 40)}${existingNote.length > 40 ? '…' : ''}"` : 'Office closed, rain day, site visit…',
              () => setMode('note')
            )}
          </div>
        )}

        {mode === 'schedule' && !justScheduled && (
          <div>
            {backBtn}
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by job or client name&#8230;"
              style={{ width: '100%', boxSizing: 'border-box', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#FFFFFF', fontSize: 13, padding: '8px 12px', fontFamily: 'inherit', outline: 'none', marginBottom: 12 }}
            />
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#D1D5DB', fontSize: 13, padding: '20px 0' }}>No jobs match your search.</div>
            ) : (
              <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}>
                {unscheduledSection.length > 0 && (
                  <>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Unscheduled</div>
                    {unscheduledSection.map(job => <JobRow key={job.id} job={job} />)}
                  </>
                )}
                {scheduledSection.length > 0 && (
                  <>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: unscheduledSection.length > 0 ? 10 : 0, marginBottom: 3 }}>Reschedule</div>
                    {scheduledSection.map(job => <JobRow key={job.id} job={job} />)}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {mode === 'schedule' && justScheduled && (
          <SuccessView icon="✅" title="Scheduled!" sub={`${justScheduled.description || justScheduled.customer} → ${displayDate}`} />
        )}

        {mode === 'create' && !justScheduled && (
          <div>
            {backBtn}
            <input
              ref={descRef}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Job description (required)"
              style={{ width: '100%', boxSizing: 'border-box', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#FFFFFF', fontSize: 14, padding: '10px 12px', fontFamily: 'inherit', outline: 'none', marginBottom: 8 }}
            />
            <input
              value={customer}
              onChange={e => setCustomer(e.target.value)}
              placeholder="Client name (optional)"
              style={{ width: '100%', boxSizing: 'border-box', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#FFFFFF', fontSize: 14, padding: '10px 12px', fontFamily: 'inherit', outline: 'none', marginBottom: 8 }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: '#D1D5DB', flexShrink: 0 }}>Duration (days):</label>
              <input
                type="number"
                min={1}
                max={30}
                value={duration}
                onChange={e => setDuration(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
                style={{ width: 60, background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#FFFFFF', fontSize: 14, padding: '8px 10px', fontFamily: 'inherit', outline: 'none', textAlign: 'center' }}
              />
            </div>
            <button
              onClick={() => {
                if (description.trim()) {
                  onCreateNew(date, { description: description.trim(), customer: customer.trim(), duration });
                  setJustScheduled({ description: description.trim() });
                }
              }}
              disabled={!description.trim()}
              style={{ width: '100%', padding: '10px', background: description.trim() ? 'linear-gradient(135deg,#E8722A,#ea580c)' : 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 7, color: description.trim() ? '#fff' : '#D1D5DB', fontWeight: 700, fontSize: 13, cursor: description.trim() ? 'pointer' : 'not-allowed' }}
            >
              Create &amp; Schedule
            </button>
          </div>
        )}

        {mode === 'create' && justScheduled && (
          <SuccessView icon="✅" title="Job Created!" sub={`${justScheduled.description} scheduled for ${displayDate}`} />
        )}

        {mode === 'note' && !justScheduled && (
          <div>
            {backBtn}
            <textarea
              ref={noteRef}
              value={note}
              onChange={e => setNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) { if (note.trim()) { onAddNote(date, note.trim()); setJustScheduled({ description: note.trim() }); } } }}
              placeholder="e.g. Office closed, rain day, equipment delivery&#8230;"
              rows={3}
              style={{ width: '100%', boxSizing: 'border-box', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#FFFFFF', fontSize: 14, padding: '10px 12px', resize: 'none', fontFamily: 'inherit', outline: 'none', marginBottom: 10 }}
            />
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 10 }}>Ctrl+Enter to save</div>
            <button
              onClick={() => { if (note.trim()) { onAddNote(date, note.trim()); setJustScheduled({ description: note.trim() }); } }}
              disabled={!note.trim()}
              style={{ width: '100%', padding: '9px', background: note.trim() ? 'linear-gradient(135deg,#E8722A,#ea580c)' : 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 7, color: note.trim() ? '#fff' : '#D1D5DB', fontWeight: 700, fontSize: 13, cursor: note.trim() ? 'pointer' : 'not-allowed' }}
            >
              Save Note
            </button>
          </div>
        )}

        {mode === 'note' && justScheduled && (
          <SuccessView icon="&#128221;" title="Note Saved!" sub={`Visible on ${displayDate}`} />
        )}

        {mode === 'dayDetail' && (() => {
          const dayJobs = jobs.filter(j => jobOccupiesDate(j, date));
          const demoDay = DEMO_DAY_DETAIL[date] || {};
          const allCrewIds = new Set();
          const jobDetails = dayJobs.map(job => {
            const jid = typeof job.id === 'number' ? job.id : String(job.id);
            const assignedIds = (assignments || {})[String(job.id)] || [];
            const demoEntries = demoDay[jid] || [];
            const steps = TRADE_CHECKLISTS[job.trade] || TRADE_CHECKLISTS['Full Replacement'] || [];
            const completedCount = (job.completedSteps || []).length;
            const nextStep = steps[completedCount] || steps[steps.length - 1] || { label: 'Complete' };
            const crewDetails = assignedIds.map(cid => {
              allCrewIds.add(cid);
              const member = (crew || []).find(m => m.id === cid);
              const demoEntry = demoEntries.find(e => e.crewId === cid) || {};
              return {
                id: cid,
                name: member?.name || cid,
                role: member?.role || '',
                clockIn: demoEntry.clockIn || '—',
                status: demoEntry.status || 'Scheduled',
                hoursLogged: demoEntry.hoursLogged || 0,
                payRate: demoEntry.payRate || 0,
              };
            });
            // Also include demo entries for crew not in assignments (e.g. if demo has extra data)
            demoEntries.forEach(e => {
              if (!assignedIds.includes(e.crewId)) {
                allCrewIds.add(e.crewId);
                const member = (crew || []).find(m => m.id === e.crewId);
                crewDetails.push({
                  id: e.crewId, name: member?.name || e.crewId, role: member?.role || '',
                  clockIn: e.clockIn || '—', status: e.status || 'Scheduled',
                  hoursLogged: e.hoursLogged || 0, payRate: e.payRate || 0,
                });
              }
            });
            const crewHours = crewDetails.reduce((s, c) => s + c.hoursLogged, 0);
            const totalHours = crewHours > 0 ? crewHours : (job.hours || 0);
            const totalLabor = crewHours > 0 ? crewDetails.reduce((s, c) => s + c.hoursLogged * c.payRate, 0) : totalHours * 30;
            return { job, crewDetails, totalHours, totalLabor, completedCount, totalSteps: steps.length, nextStep };
          });
          const grandHours = jobDetails.reduce((s, d) => s + d.totalHours, 0);
          const grandLabor = jobDetails.reduce((s, d) => s + d.totalLabor, 0);
          const grandValue = dayJobs.reduce((s, j) => s + (j.value || 0), 0);
          const statusColor = (s) => ({ 'On Site': '#22c55e', 'Traveling': '#3b82f6', 'Break': '#f59e0b', 'Clocked Out': '#D1D5DB', 'Scheduled': '#D1D5DB' }[s] || '#8B95A1');
          const pill = (text, bg, fg) => ({ display: 'inline-block', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 8, background: bg, color: fg, marginLeft: 4 });
          const sectionLabel = { fontSize: 11, fontWeight: 700, color: '#D1D5DB', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 16, marginBottom: 8 };
          const card = { background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '12px 14px', marginBottom: 8 };

          return (
            <div>
              {backBtn}
              {/* Day Totals */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 4 }}>
                <div style={{ background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#E8722A' }}>{grandHours.toFixed(1)}</div>
                  <div style={{ fontSize: 10, color: '#D1D5DB', fontWeight: 600 }}>Total Hours</div>
                </div>
                <div style={{ background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#3b82f6' }}>{allCrewIds.size}</div>
                  <div style={{ fontSize: 10, color: '#D1D5DB', fontWeight: 600 }}>Crews Deployed</div>
                </div>
                <div style={{ background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#22c55e' }}>${grandValue.toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: '#D1D5DB', fontWeight: 600 }}>Active Job Value</div>
                </div>
              </div>

              {dayJobs.length === 0 && (
                <div style={{ textAlign: 'center', padding: '28px 0', color: '#D1D5DB', fontSize: 13 }}>No jobs scheduled for this date.</div>
              )}

              {/* Jobs Scheduled */}
              {jobDetails.length > 0 && (
                <>
                  <div style={sectionLabel}>Jobs Scheduled ({jobDetails.length})</div>
                  {jobDetails.map(({ job, crewDetails: jcrew, totalHours: jHours, totalLabor: jLabor, completedCount, totalSteps, nextStep }) => {
                    const tc = getTradeColor(job.trade);
                    const estMargin = job.value > 0 ? Math.round((1 - jLabor / job.value) * 100) : null;
                    return (
                      <div key={job.id} style={card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.customer}</div>
                            <div style={{ fontSize: 11, color: '#D1D5DB', marginTop: 1 }}>{job.address || ''}</div>
                          </div>
                          <span style={{ ...pill('', tc + '22', tc), marginLeft: 8 }}>{job.trade}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#D1D5DB', marginBottom: 8, flexWrap: 'wrap' }}>
                          <span>${(job.value || 0).toLocaleString()}</span>
                          <span>{jHours.toFixed(1)} hrs today</span>
                          <span>${jLabor.toFixed(0)} labor</span>
                          {estMargin !== null && <span style={{ color: estMargin > 40 ? '#22c55e' : estMargin > 20 ? '#f59e0b' : '#ef4444' }}>{estMargin}% est. margin</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#D1D5DB', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ width: `${(completedCount / totalSteps) * 100}%`, height: '100%', background: tc, borderRadius: 2 }} />
                          </div>
                          <span style={{ fontSize: 10, color: '#D1D5DB', flexShrink: 0 }}>{completedCount}/{totalSteps}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#D1D5DB' }}>Current: <span style={{ color: '#D1D5DB' }}>{nextStep.label}</span></div>

                        {/* Crew on this job */}
                        {jcrew.length > 0 && (
                          <div style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8 }}>
                            {jcrew.map(c => (
                              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 }}>
                                <span style={{ color: '#D1D5DB', fontWeight: 600, minWidth: 100 }}>{c.name}</span>
                                <span style={{ color: '#D1D5DB', fontSize: 10, minWidth: 60 }}>{c.role}</span>
                                <span style={pill(c.status, statusColor(c.status) + '22', statusColor(c.status))}>{c.status}</span>
                                <span style={{ color: '#D1D5DB', fontSize: 10, marginLeft: 'auto' }}>{c.clockIn} · {c.hoursLogged.toFixed(1)}h</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {jcrew.length === 0 && (
                          <div style={{ fontSize: 11, color: '#D1D5DB', fontStyle: 'italic', marginTop: 6 }}>No crew assigned</div>
                        )}
                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: 6, marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8 }}>
                          <button onClick={() => { onClose(); setTimeout(() => { const sel = (allJobs || []).find(j => String(j.id) === String(job.id)); if (sel) onScheduleExisting(date, null); }, 100); }} style={{ flex: 1, padding: '6px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#D1D5DB', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>View Scope</button>
                          <button style={{ flex: 1, padding: '6px', background: '#10b98118', border: '1px solid #10b98144', borderRadius: 6, color: '#10b981', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Message Crew</button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Crew On Duty summary */}
              {allCrewIds.size > 0 && (
                <>
                  <div style={sectionLabel}>Crew On Duty ({allCrewIds.size})</div>
                  <div style={card}>
                    {Array.from(allCrewIds).map(cid => {
                      const member = (crew || []).find(m => m.id === cid);
                      const memberJobs = jobDetails.filter(d => d.crewDetails.some(c => c.id === cid));
                      const memberEntries = memberJobs.flatMap(d => d.crewDetails.filter(c => c.id === cid));
                      const totalH = memberEntries.reduce((s, e) => s + e.hoursLogged, 0);
                      const status = memberEntries[0]?.status || 'Scheduled';
                      const clockIn = memberEntries[0]?.clockIn || '—';
                      return (
                        <div key={cid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 12 }}>
                          <div style={{ minWidth: 110 }}>
                            <div style={{ fontWeight: 600, color: '#D1D5DB' }}>{member?.name || cid}</div>
                            <div style={{ fontSize: 10, color: '#D1D5DB' }}>{member?.role || ''}</div>
                          </div>
                          <div style={{ flex: 1, fontSize: 11, color: '#D1D5DB' }}>
                            {memberJobs.map(d => d.job.customer).join(', ')}
                          </div>
                          <span style={pill(status, statusColor(status) + '22', statusColor(status))}>{status}</span>
                          <div style={{ textAlign: 'right', minWidth: 60 }}>
                            <div style={{ fontSize: 11, color: '#D1D5DB', fontWeight: 600 }}>{totalH.toFixed(1)}h</div>
                            <div style={{ fontSize: 9, color: '#D1D5DB' }}>in {clockIn}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* + Schedule Job */}
              {!showScheduleForm ? (
                <button
                  onClick={() => setShowScheduleForm(true)}
                  style={{ width: '100%', padding: '11px', marginTop: 12, marginBottom: 4, background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                >
                  + Schedule Job
                </button>
              ) : (
                <div style={{ ...card, marginTop: 12, border: '1px solid #7c3aed44' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#a78bfa', marginBottom: 10 }}>Schedule a Job for {displayDate}</div>
                  <select value={schedJobId} onChange={e => setSchedJobId(e.target.value)} style={{ width: '100%', padding: '8px 10px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 13, marginBottom: 8, outline: 'none', fontFamily: 'inherit' }}>
                    <option value="">— Select a job —</option>
                    {jobs.filter(j => !j.scheduledDate || j.status === 'Scheduled').map(j => (
                      <option key={j.id} value={String(j.id)}>{j.customer} — {j.trade} (${(j.value || 0).toLocaleString()})</option>
                    ))}
                  </select>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#D1D5DB', marginBottom: 6 }}>Assign Crew</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    {(crew || []).map(m => {
                      const sel = schedCrew.includes(m.id);
                      return (
                        <button key={m.id} onClick={() => setSchedCrew(prev => sel ? prev.filter(x => x !== m.id) : [...prev, m.id])} style={{ padding: '4px 10px', borderRadius: 14, fontSize: 11, fontWeight: 600, border: sel ? '1px solid #7c3aed' : '1px solid rgba(255,255,255,0.08)', background: sel ? 'rgba(124,58,237,0.15)' : 'transparent', color: sel ? '#a78bfa' : '#D1D5DB', cursor: 'pointer' }}>
                          {m.name.split(' ')[0]}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: '#D1D5DB', marginBottom: 3 }}>Start Time</div>
                      <select value={schedTime} onChange={e => setSchedTime(e.target.value)} style={{ width: '100%', padding: '7px 8px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 12, outline: 'none', fontFamily: 'inherit' }}>
                        {['6:00 AM','6:30 AM','7:00 AM','7:30 AM','8:00 AM','8:30 AM','9:00 AM','9:30 AM','10:00 AM','10:30 AM','11:00 AM','11:30 AM','12:00 PM','12:30 PM','1:00 PM','1:30 PM','2:00 PM','2:30 PM','3:00 PM','3:30 PM','4:00 PM','4:30 PM','5:00 PM'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: '#D1D5DB', marginBottom: 3 }}>Est. Hours</div>
                      <input type="number" min={1} max={12} value={schedHours} onChange={e => setSchedHours(Math.max(1, Math.min(12, parseInt(e.target.value) || 1)))} style={{ width: '100%', padding: '7px 8px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 12, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                  <input value={schedNotes} onChange={e => setSchedNotes(e.target.value)} placeholder="Notes (optional)" style={{ width: '100%', padding: '7px 10px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#D1D5DB', fontSize: 12, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10 }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => {
                        if (schedJobId) {
                          onScheduleExisting(date, schedJobId);
                          setShowScheduleForm(false);
                          setSchedJobId(''); setSchedCrew([]); setSchedNotes('');
                        }
                      }}
                      disabled={!schedJobId}
                      style={{ flex: 1, padding: '9px', background: schedJobId ? 'linear-gradient(135deg,#7c3aed,#6d28d9)' : 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 6, color: schedJobId ? '#fff' : '#D1D5DB', fontWeight: 700, fontSize: 12, cursor: schedJobId ? 'pointer' : 'not-allowed' }}
                    >
                      Add to Schedule
                    </button>
                    <button onClick={() => setShowScheduleForm(false)} style={{ padding: '9px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#D1D5DB', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Hours Summary */}
              {jobDetails.length > 0 && (
                <>
                  <div style={sectionLabel}>Hours Summary</div>
                  <div style={card}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '4px 12px', fontSize: 11, color: '#D1D5DB' }}>
                      <div style={{ fontWeight: 700, color: '#D1D5DB' }}>Job</div>
                      <div style={{ fontWeight: 700, color: '#D1D5DB', textAlign: 'right' }}>Hours</div>
                      <div style={{ fontWeight: 700, color: '#D1D5DB', textAlign: 'right' }}>Labor $</div>
                      <div style={{ fontWeight: 700, color: '#D1D5DB', textAlign: 'right' }}>Margin</div>
                      {jobDetails.map(d => {
                        const em = d.job.value > 0 ? Math.round((1 - d.totalLabor / d.job.value) * 100) : null;
                        return (
                          <div key={d.job.id} style={{ display: 'contents' }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingTop: 4 }}>{d.job.customer}</div>
                            <div style={{ textAlign: 'right', paddingTop: 4 }}>{d.totalHours.toFixed(1)}</div>
                            <div style={{ textAlign: 'right', paddingTop: 4 }}>${d.totalLabor.toFixed(0)}</div>
                            <div style={{ textAlign: 'right', paddingTop: 4, color: em !== null ? (em > 40 ? '#22c55e' : em > 20 ? '#f59e0b' : '#ef4444') : '#D1D5DB' }}>{em !== null ? `${em}%` : '—'}</div>
                          </div>
                        );
                      })}
                      <div style={{ gridColumn: '1 / -1', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 4 }} />
                      <div style={{ fontWeight: 700, color: '#FFFFFF', paddingTop: 4 }}>Totals</div>
                      <div style={{ textAlign: 'right', fontWeight: 700, color: '#FFFFFF', paddingTop: 4 }}>{grandHours.toFixed(1)}</div>
                      <div style={{ textAlign: 'right', fontWeight: 700, color: '#FFFFFF', paddingTop: 4 }}>${grandLabor.toFixed(0)}</div>
                      <div style={{ textAlign: 'right', fontWeight: 700, color: grandValue > 0 ? '#22c55e' : '#D1D5DB', paddingTop: 4 }}>{grandValue > 0 ? `${Math.round((1 - grandLabor / grandValue) * 100)}%` : '—'}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─── Calendar Tab ─────────────────────────────────────────────────────────────
function CalendarTab({ jobs, crew, assignments, onSchedule, onComplete, onUpdateSteps, onAssign, onUnassign, onAddCrew, currentUser, demoMessages, customChecklist, isDemo, onCreate, onUpdate, onChangeStage, onDeleteJob, userTrade }) {
  const [view, setView] = useState('month');
  const [current, setCurrent] = useState(new Date(TODAY + 'T12:00:00'));
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayActionModal, setDayActionModal] = useState(null); // null | { date }
  const [dayNotes, setDayNotes] = useState({});               // { [dateStr]: string }
  const [quickBar, setQuickBar] = useState(null);             // null | { date, job }
  const [scheduleModal, setScheduleModal] = useState(null);   // null | { defaultDate, targetJob }
  const [selectedJob, setSelectedJob] = useState(null);
  const [stageChangeJob, setStageChangeJob] = useState(null);
  const [quickCrewJob, setQuickCrewJob] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);       // null | { job, x, y }
  const [dragJobId, setDragJobId] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const isMobile = useMobile();
  const effectiveTrade = userTrade || 'Full Replacement';

  const scheduledJobs = useMemo(() => jobs.filter(j => j.scheduledDate), [jobs]);
  const unscheduledJobs = useMemo(() => jobs.filter(j => (j.explicitlyScheduled === false || !j.scheduledDate) && j.status !== 'Complete'), [jobs]);

  const conflicts = useMemo(() => {
    const set = new Set();
    crew.forEach(member => {
      const memberJobs = scheduledJobs.filter(j => (assignments[String(j.id)] || []).includes(member.id));
      for (let i = 0; i < memberJobs.length; i++) {
        for (let k = i + 1; k < memberJobs.length; k++) {
          const a = memberJobs[i]; const b = memberJobs[k];
          if (a.scheduledDate && b.scheduledDate) {
            const aEnd = jobEndDate(a); const bEnd = jobEndDate(b);
            if (a.scheduledDate <= bEnd && b.scheduledDate <= aEnd) {
              set.add(`${member.id}-${String(a.id)}`); set.add(`${member.id}-${String(b.id)}`);
            }
          }
        }
      }
    });
    return set;
  }, [crew, assignments, scheduledJobs]);

  const navBtn = { padding: '6px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#D1D5DB', cursor: 'pointer', fontSize: 18, lineHeight: 1, WebkitTapHighlightColor: 'transparent' };
  const currentStr = toDateStr(current);

  const prev = () => { const d = new Date(current); if (view === 'month') d.setMonth(d.getMonth() - 1); else if (view === 'week') d.setDate(d.getDate() - 7); else d.setDate(d.getDate() - 1); setCurrent(d); setSelectedDate(null); };
  const next = () => { const d = new Date(current); if (view === 'month') d.setMonth(d.getMonth() + 1); else if (view === 'week') d.setDate(d.getDate() + 7); else d.setDate(d.getDate() + 1); setCurrent(d); setSelectedDate(null); };

  const dayJobsFn = useCallback((dateStr) => scheduledJobs.filter(j => jobOccupiesDate(j, dateStr)), [scheduledJobs]);

  const headerLabel = useMemo(() => {
    if (view === 'month') return current.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (view === 'week') {
      const wd = buildWeekDays(currentStr);
      const s = new Date(wd[0] + 'T12:00:00'); const e = new Date(wd[6] + 'T12:00:00');
      return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return current.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }, [view, current, currentStr]);

  const monthDays = useMemo(() => view === 'month' ? buildMonthDays(current.getFullYear(), current.getMonth()) : [], [view, current]);
  const weekDays = useMemo(() => (view === 'week' || view === 'day') ? buildWeekDays(currentStr) : [], [view, currentStr]);
  const dispatchDate = view === 'day' ? currentStr : selectedDate;
  const currentMonth = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;

  const closeAll = useCallback(() => { setContextMenu(null); setDragJobId(null); setDragOverDate(null); }, []);

  const handleDayClick = useCallback((dateStr) => {
    setSelectedDate(dateStr);
    setDayActionModal({ date: dateStr });
  }, []);

  const handleJobClick = useCallback((job) => {
    closeAll();
    setSelectedJob(job);
  }, [closeAll]);

  const handleJobContextMenu = useCallback((job, e) => {
    setContextMenu({ job, x: e.clientX, y: e.clientY });
  }, []);

  const handleSaveSchedule = useCallback((jobId, date, duration) => { if (onSchedule) onSchedule(jobId, date, duration); }, [onSchedule]);

  const handleDragStart = useCallback((jobId) => { setDragJobId(jobId); }, []);
  const handleDragOver = useCallback((dateStr) => { setDragOverDate(dateStr); }, []);
  const handleDrop = useCallback((jobId, dateStr) => {
    if (onSchedule) {
      const job = jobs.find(j => String(j.id) === jobId);
      onSchedule(jobId, dateStr, job ? jobDuration(job) : 1);
    }
    setDragJobId(null); setDragOverDate(null);
  }, [onSchedule, jobs]);
  const handleDragEnd = useCallback(() => { setDragJobId(null); setDragOverDate(null); }, []);

  const handleCompleteJob = useCallback((id) => { if (onComplete) onComplete(id); setSelectedJob(null); }, [onComplete]);

  const sharedGridProps = { dayJobsFn, onDayClick: handleDayClick, selectedDate, onJobClick: handleJobClick, onJobContextMenu: !isDemo ? handleJobContextMenu : null, dragJobId, dragOverDate, onDragStart: !isDemo ? handleDragStart : null, onDragOver: !isDemo ? handleDragOver : null, onDrop: !isDemo ? handleDrop : null, onDragEnd: !isDemo ? handleDragEnd : null, dayNotes };

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#FFFFFF' }}>Dispatch Board</div>
          <div style={{ fontSize: 13, color: '#D1D5DB' }}>{scheduledJobs.length} scheduled · {unscheduledJobs.length} unscheduled</div>
        </div>
        {onCreate && (
          <button onClick={() => setQuickBar({ date: TODAY, job: null })} style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#E8722A,#ea580c)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>
            + New Job
          </button>
        )}
        {onSchedule && (
          <button onClick={() => setScheduleModal({ defaultDate: TODAY, targetJob: null })} style={{ padding: '8px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#D1D5DB', fontWeight: 600, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
            Schedule Existing
          </button>
        )}
        <div style={{ display: 'flex', gap: 4 }}>
          {['month', 'week', 'day'].map(v => (
            <button key={v} style={{ padding: '6px 12px', minHeight: 32, fontSize: 12, borderRadius: 6, border: view === v ? 'none' : '1px solid rgba(255,255,255,0.12)', background: view === v ? '#E8722A' : '#2A3140', color: view === v ? '#2A3140' : '#D1D5DB', fontWeight: view === v ? 600 : 500, cursor: 'pointer' }} onClick={() => { setView(v); setSelectedDate(null); }}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Nav row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <button style={navBtn} onClick={prev}>‹</button>
        <button style={{ ...navBtn, fontSize: 12, padding: '6px 12px' }} onClick={() => { setCurrent(new Date(TODAY + 'T12:00:00')); setSelectedDate(null); }}>Today</button>
        <button style={navBtn} onClick={next}>›</button>
        <div style={{ flex: 1, textAlign: 'center', fontSize: isMobile ? 13 : 15, fontWeight: 700, color: '#FFFFFF' }}>{headerLabel}</div>
        {dragJobId && <span style={{ fontSize: 12, color: '#E8722A', fontWeight: 600 }}>Drop to reschedule</span>}
      </div>

      {/* ── Quick Schedule Bar ── */}
      {quickBar && (
        <QuickScheduleBar
          date={quickBar.date}
          job={quickBar.job}
          userTrade={effectiveTrade}
          onSave={jobData => {
            if (jobData.id) { if (onUpdate) onUpdate(jobData); }
            else { if (onCreate) onCreate(jobData); }
          }}
          onClose={() => setQuickBar(null)}
        />
      )}

      {/* ── Unscheduled banner ── */}
      {unscheduledJobs.length > 0 && (
        <div style={{ background: 'rgba(232,114,42,0.08)', border: '1px solid rgba(232,114,42,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#FCD34D', fontWeight: 600, flexShrink: 0 }}>⚠ {unscheduledJobs.length} need scheduling:</span>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', flex: 1 }}>
            {unscheduledJobs.slice(0, 5).map(j => (
              <button key={j.id} onClick={() => setScheduleModal({ defaultDate: TODAY, targetJob: j })} style={{ fontSize: 12, color: '#D1D5DB', background: 'rgba(255,255,255,0.08)', padding: '8px 12px', minHeight: 44, borderRadius: 10, border: 'none', cursor: 'pointer' }}>
                {j.customer}
              </button>
            ))}
            {unscheduledJobs.length > 5 && <span style={{ fontSize: 11, color: '#D1D5DB' }}>+{unscheduledJobs.length - 5} more</span>}
          </div>
        </div>
      )}

      {/* ── Calendar grid ── */}
      {view === 'month' && <MonthView days={monthDays} currentMonth={currentMonth} today={TODAY} {...sharedGridProps} />}
      {view === 'week' && <WeekView days={weekDays} today={TODAY} {...sharedGridProps} />}

      {/* ── Day dispatch panel ── */}
      {dispatchDate && (
        <DayDispatch
          dateStr={dispatchDate}
          jobs={jobs}
          crew={crew}
          assignments={assignments}
          conflicts={conflicts}
          onClose={view !== 'day' ? () => setSelectedDate(null) : null}
          onJobClick={handleJobClick}
          onCreateForDate={!isDemo && onCreate ? () => setQuickBar({ date: dispatchDate, job: null }) : null}
        />
      )}

      {/* ── Unscheduled jobs list ── */}
      {unscheduledJobs.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={S.sectionLabel}>Unscheduled Jobs</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {unscheduledJobs.map(job => {
              const tc = getTradeColor(job.trade);
              return (
                <div key={job.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#2A3140', border: '1px solid rgba(255,255,255,0.08)', borderLeft: `3px solid ${tc}`, borderRadius: 8, cursor: 'pointer' }} onClick={() => handleJobClick(job)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.customer}</div>
                    <div style={{ fontSize: 11, color: '#D1D5DB' }}>{job.trade} · {job.status}</div>
                  </div>
                  {!isDemo && onSchedule && (
                    <button onClick={e => { e.stopPropagation(); setScheduleModal({ defaultDate: TODAY, targetJob: job }); }} style={{ padding: '8px 14px', minHeight: 44, background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 8, color: '#E8722A', fontWeight: 600, fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>
                      Schedule
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Day note ── */}
      {dispatchDate && dayNotes[dispatchDate] && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, padding: '10px 14px', marginTop: 8 }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>📝</span>
          <div style={{ flex: 1, fontSize: 13, color: '#c7d2fe' }}>{dayNotes[dispatchDate]}</div>
          <button onClick={() => setDayNotes(p => { const n = { ...p }; delete n[dispatchDate]; return n; })} style={{ background: 'none', border: 'none', color: '#D1D5DB', fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
        </div>
      )}

      {/* ── Today's summary panel ── */}
      <TodaySummaryPanel jobs={jobs} crew={crew} assignments={assignments} conflicts={conflicts} onJobClick={handleJobClick} />

      {/* ── Modals ── */}
      {dayActionModal && (
        <DayActionModal
          date={dayActionModal.date}
          allJobs={jobs}
          assignments={assignments}
          crew={crew}
          existingNote={dayNotes[dayActionModal.date] || ''}
          onScheduleExisting={(d, jobId) => {
            if (jobId) {
              if (onSchedule) onSchedule(jobId, d);
            } else {
              setScheduleModal({ defaultDate: d, targetJob: null });
              setDayActionModal(null);
            }
          }}
          onCreateNew={(d, formData) => {
            if (formData) {
              if (onCreate) onCreate({ description: formData.description, customer: formData.customer || formData.description, duration: formData.duration, scheduledDate: d, explicitlyScheduled: true, taskList: [], status: 'In Progress', trade: effectiveTrade });
            } else {
              setQuickBar({ date: d, job: null });
              setDayActionModal(null);
            }
          }}
          onAddNote={(d, text) => setDayNotes(prev => ({ ...prev, [d]: text }))}
          onClose={() => setDayActionModal(null)}
        />
      )}
      {scheduleModal && (
        <ScheduleJobModal
          defaultDate={scheduleModal.defaultDate}
          targetJob={scheduleModal.targetJob}
          jobs={jobs}
          onSave={handleSaveSchedule}
          onClose={() => setScheduleModal(null)}
        />
      )}
      {stageChangeJob && (
        <StageChangeModal
          job={stageChangeJob}
          onSave={stage => { if (onChangeStage) onChangeStage(String(stageChangeJob.id), stage); }}
          onClose={() => setStageChangeJob(null)}
        />
      )}
      {quickCrewJob && (
        <QuickAssignCrewPanel
          job={quickCrewJob}
          crew={crew}
          assignments={assignments}
          onAssign={onAssign || (() => {})}
          onUnassign={onUnassign || (() => {})}
          onClose={() => setQuickCrewJob(null)}
        />
      )}
      {selectedJob && (
        <JobModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          customChecklist={customChecklist}
          crew={crew}
          assignments={assignments}
          onAssign={isDemo ? null : onAssign}
          onUnassign={isDemo ? null : onUnassign}
          // Crew creation is demo-safe — parent routes to handleDemoAddCrew
          // in demo and to handleAddCrew otherwise.
          onAddCrew={onAddCrew}
          currentUser={currentUser}
          demoMessages={demoMessages ? demoMessages.filter(m => String(m.jobId) === String(selectedJob.id)) : null}
          onComplete={isDemo ? null : id => handleCompleteJob(id)}
          onUpdateSteps={isDemo ? null : onUpdateSteps}
          onUpdateSchedule={isDemo ? null : (jobId, date, dur) => { handleSaveSchedule(jobId, date, dur); setSelectedJob(null); }}
          isDemo={isDemo}
        />
      )}

      {/* ── Context menu ── */}
      {contextMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 1999 }} onClick={closeAll} />
          <CalendarContextMenu
            job={contextMenu.job}
            x={contextMenu.x}
            y={contextMenu.y}
            onView={() => { setSelectedJob(contextMenu.job); closeAll(); }}
            onReschedule={() => { setScheduleModal({ defaultDate: contextMenu.job.scheduledDate || TODAY, targetJob: contextMenu.job }); closeAll(); }}
            onAssignCrew={() => { if (!isDemo) setQuickCrewJob(contextMenu.job); closeAll(); }}
            onChangeStage={() => { if (!isDemo) setStageChangeJob(contextMenu.job); closeAll(); }}
            onRemove={() => { if (onSchedule) onSchedule(String(contextMenu.job.id), null, null); closeAll(); }}
            onComplete={() => { handleCompleteJob(String(contextMenu.job.id)); closeAll(); }}
            onDelete={() => { if (onDeleteJob) onDeleteJob(String(contextMenu.job.id)); closeAll(); }}
          />
        </>
      )}
    </div>
  );
}

// ─── Demo Dashboard ────────────────────────────────────────────────────────────
const TIER_META = {
  starter: { label: 'Starter', color: '#D1D5DB', desc: 'Basic pipeline management' },
  pro:     { label: 'Pro',     color: '#E8722A', desc: 'AI coaching + full analytics' },
  business:{ label: 'Business',color: '#6366f1', desc: 'Team management + export' },
};

function DemoDashboard({ trade, onChangeTrade, customTradeConfig }) {
  const [tier, setTier] = useState('pro');
  const [tab, setTab] = useState('pipeline');
  const [selectedLead, setSelectedLead] = useState(null);
  const isMobile = useMobile();
  const goToSignup = () => { window.location.href = '/'; };
  const tradeColor = TRADE_COLORS[trade] || '#6366f1';
  // For custom trades, remap Roofing sample data to the custom trade name
  const data = TRADE_DEMO_DATA[trade] || (() => {
    const base = TRADE_DEMO_DATA['Full Replacement'];
    return {
      leads: base.leads.map(l => ({ ...l, trade })),
      jobs: base.jobs.map(j => ({ ...j, trade })),
    };
  })();

  // Build available tabs based on tier
  const allTabs = [
    { key: 'pipeline',  label: 'Pipeline',  locked: false },
    { key: 'callbacks', label: 'Callbacks', locked: false },
    { key: 'analytics', label: 'Analytics', locked: tier === 'starter' },
    { key: 'jobs',      label: 'Jobs',      locked: tier === 'starter' },
    { key: 'photos',    label: 'Photos',    locked: false },
    { key: 'team',      label: 'Team',      locked: tier !== 'business' },
  ].filter(t => tier === 'business' || t.key !== 'team');

  // Reset to pipeline when switching to starter if on a locked tab
  const handleTierChange = (newTier) => {
    setTier(newTier);
    if (newTier === 'starter' && (tab === 'analytics' || tab === 'jobs' || tab === 'team')) {
      setTab('pipeline');
    }
    if (newTier !== 'business' && tab === 'team') {
      setTab('pipeline');
    }
  };

  const tierColor = TIER_META[tier].color;

  return (
    <div style={{
      height: '100dvh', minHeight: '100vh', background: '#2A3140',
      color: '#D1D5DB', fontFamily: "'Inter', -apple-system, sans-serif",
      display: 'flex', flexDirection: 'column', overflowX: 'hidden',
    }}>
      {/* Top demo banner */}
      <div style={{
        background: 'rgba(249,115,22,0.08)',
        borderBottom: '1px solid rgba(249,115,22,0.15)',
        padding: '9px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
        fontSize: 13, color: '#E8722A',
        position: 'sticky', top: 0, zIndex: 200,
        flexWrap: 'wrap',
      }}>
        <span>
          👋 You're viewing the <strong>{trade}</strong> demo — your data stays private when you sign up
        </span>
        <button
          onClick={goToSignup}
          style={{
            padding: '4px 14px', background: '#E8722A',
            border: 'none', borderRadius: 6, color: '#fff',
            fontWeight: 700, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          Start Free Trial
        </button>
      </div>

      {/* Tier selector bar */}
      <div style={{
        background: '#2A3140', borderBottom: '1px solid rgba(255,255,255,0.08)',
        padding: isMobile ? '8px 12px' : '10px 24px',
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap',
        overflowX: isMobile ? 'auto' : 'visible',
        scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
      }}>
        <span style={{ fontSize: 10, color: '#D1D5DB', fontWeight: 600, marginRight: 2, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', flexShrink: 0 }}>
          Tier:
        </span>
        {Object.entries(TIER_META).map(([key, meta]) => (
          <button
            key={key}
            onClick={() => handleTierChange(key)}
            style={{
              padding: isMobile ? '7px 14px' : '5px 16px', borderRadius: 20, border: 'none',
              fontWeight: 600, fontSize: isMobile ? 13 : 12, cursor: 'pointer',
              background: tier === key ? meta.color : '#2A3140',
              color: tier === key ? '#fff' : '#D1D5DB',
              transition: 'all 0.15s', flexShrink: 0,
              outline: tier === key ? `2px solid ${meta.color}44` : 'none', outlineOffset: 1,
              minHeight: isMobile ? 36 : 'auto',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {meta.label}
          </button>
        ))}
        {!isMobile && (
          <span style={{ marginLeft: 8, fontSize: 12, color: '#D1D5DB' }}>
            — {TIER_META[tier].desc}
          </span>
        )}
      </div>

      {/* Starter: lead limit warning */}
      {tier === 'starter' && (
        <div style={{
          background: 'rgba(234,179,8,0.08)', borderBottom: '1px solid rgba(234,179,8,0.2)',
          padding: isMobile ? '8px 12px' : '9px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 8,
        }}>
          <span style={{ fontSize: isMobile ? 12 : 13, color: '#eab308', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>⚠</span>
            <span>
              {isMobile
                ? <><strong>8 of 10 leads</strong> used (80%) — upgrade for unlimited</>
                : <>You've used <strong>8 of 10 leads</strong> on the Starter plan (80%). Upgrade to unlock unlimited leads.</>
              }
            </span>
          </span>
          <button
            onClick={goToSignup}
            style={{
              padding: isMobile ? '8px 14px' : '4px 14px', background: '#eab308', border: 'none',
              borderRadius: 6, color: '#2A3140', fontWeight: 700,
              fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
              minHeight: isMobile ? 36 : 'auto',
            }}
          >
            Upgrade to Pro
          </button>
        </div>
      )}

      {/* Header */}
      <header style={{
        background: '#2A3140', borderBottom: '1px solid rgba(255,255,255,0.08)',
        padding: isMobile ? '0 12px' : '0 24px',
        display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12,
        height: isMobile ? 52 : 54,
      }}>
        <button
          onClick={onChangeTrade}
          style={{
            background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 6, color: '#D1D5DB', fontSize: isMobile ? 13 : 12,
            cursor: 'pointer', padding: isMobile ? '8px 10px' : '5px 12px',
            whiteSpace: 'nowrap', minHeight: isMobile ? 38 : 'auto',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          ← {isMobile ? 'Back' : 'Change job type'}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ fontSize: isMobile ? 15 : 16, fontWeight: 700, color: tradeColor, letterSpacing: '-0.5px', whiteSpace: 'nowrap' }}>
            {TRADE_ICONS[trade] || '✨'} {isMobile ? trade : `${trade} Demo`}
          </span>
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
            background: tierColor + '20', color: tierColor,
            border: `1px solid ${tierColor}33`, letterSpacing: '0.5px', whiteSpace: 'nowrap',
          }}>
            {TIER_META[tier].label.toUpperCase()}
          </span>
        </div>

        {/* Desktop: tab buttons in header */}
        {!isMobile && (
          <div style={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
            {allTabs.map(({ key, label, locked }) => (
              <DisabledTooltip key={key} active={locked} label={`${label} — upgrade to access`}>
                <button
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: 'none',
                    cursor: locked ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500,
                    background: tab === key ? tierColor : 'transparent',
                    color: locked ? 'rgba(255,255,255,0.08)' : tab === key ? '#fff' : '#D1D5DB',
                    transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 5,
                  }}
                  onClick={() => !locked && setTab(key)}
                >
                  {locked && <span style={{ fontSize: 10 }}>🔒</span>}
                  {label}
                </button>
              </DisabledTooltip>
            ))}
          </div>
        )}

        <button
          onClick={goToSignup}
          style={{
            marginLeft: isMobile ? 'auto' : 8,
            padding: isMobile ? '8px 12px' : '6px 16px',
            background: 'linear-gradient(135deg, #E8722A, #ea580c)',
            border: 'none', borderRadius: 7,
            color: '#fff', fontWeight: 700, fontSize: isMobile ? 12 : 12,
            cursor: 'pointer', whiteSpace: 'nowrap',
            minHeight: isMobile ? 38 : 'auto',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {isMobile ? 'Sign Up' : 'Start Free Trial →'}
        </button>
      </header>

      {/* Content */}
      <main style={{
        padding: isMobile ? '16px 12px' : 24,
        flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
        width: '100%', maxWidth: 1400, margin: '0 auto', boxSizing: 'border-box',
        paddingBottom: isMobile ? 24 : 88,
      }}>
        {tab === 'pipeline' && (
          <PipelineTab
            leads={data.leads}
            onSelectLead={setSelectedLead}
            onAddLead={null}
            onEditLead={null}
            onDeleteLead={null}
            demoMode={true}
          />
        )}
        {tab === 'callbacks' && (
          <CallbacksTab leads={data.leads} onSelectLead={setSelectedLead} />
        )}
        {tab === 'analytics' && (
          <AnalyticsTab leads={data.leads} jobs={DEMO_JOBS} tier={tier} />
        )}
        {tab === 'jobs' && (
          <JobsTab jobs={DEMO_JOBS} customChecklist={customTradeConfig?.checklist} demoMessages={DEMO_MESSAGES} />
        )}
        {tab === 'photos' && (
          <PhotoLogTab tier={tier} />
        )}
        {tab === 'team' && (
          <TeamTab />
        )}
      </main>

      {/* Mobile: BottomNav replaces sticky CTA */}
      {isMobile ? (
        <BottomNav
          tab={tab}
          setTab={setTab}
          color={tierColor}
          tabs={allTabs.map(t => ({
            ...t,
            icon: NAV_TABS.find(n => n.key === t.key)?.icon || '•',
          }))}
        />
      ) : (
        /* Desktop: sticky CTA bar */
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: '#2A3140', borderTop: '1px solid rgba(255,255,255,0.08)',
          padding: '13px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 20, zIndex: 100,
          boxShadow: '0 -4px 24px rgba(0,0,0,0.2)',
        }}>
          <span style={{ fontSize: 14, color: '#D1D5DB' }}>
            Like what you see?{' '}
            <span style={{ color: '#E8722A', fontWeight: 600 }}>Start your 14-day free trial</span>
            {' '}— no credit card required
          </span>
          <button
            onClick={goToSignup}
            style={{
              padding: '8px 22px',
              background: 'linear-gradient(135deg, #E8722A, #ea580c)',
              border: 'none', borderRadius: 8,
              color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Get Started Free
          </button>
        </div>
      )}

      {selectedLead && (
        <CoachPanel
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          demoMode={true}
          tier={tier}
          onStageChange={(newStage) => setSelectedLead(prev => ({ ...prev, stage: newStage }))}
        />
      )}
    </div>
  );
}

// ─── Demo Page (router) ────────────────────────────────────────────────────────
// ─── Demo Page ────────────────────────────────────────────────────────────────
function DemoPage() {
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [customTradeConfig, setCustomTradeConfig] = useState(null);

  const handleSelect = (trade, config) => {
    setSelectedTrade(trade);
    setCustomTradeConfig(config || null);
  };

  if (!selectedTrade) {
    return <TradeSelectScreen onSelect={handleSelect} />;
  }
  return (
    <DemoDashboard
      trade={selectedTrade}
      onChangeTrade={() => { setSelectedTrade(null); setCustomTradeConfig(null); }}
      customTradeConfig={customTradeConfig}
    />
  );
}

// ─── Floating Action Button ──────────────────────────────────────────────────
function FloatingActionButton({ tab, onAction }) {
  const isMobile = useMobile();

  // FAB renders only on tabs without an existing in-page add affordance.
  // Pipeline has "+ Add Lead" in the search row; Crew has "+ Add Member"
  // in its header. Jobs has no header-level add button, so the FAB is
  // the sole add entry there.
  const config = useMemo(() => {
    switch (tab) {
      case 'jobs': return { label: 'Add Project', action: 'addJob' };
      default:     return null;
    }
  }, [tab]);

  if (!config) return null;

  return (
    <button
      onClick={() => onAction(config.action)}
      aria-label={config.label}
      style={{
        position: 'fixed', bottom: isMobile ? 80 : 28, right: isMobile ? 16 : 28,
        zIndex: 900, height: 52, padding: '0 22px',
        borderRadius: 26,
        background: 'linear-gradient(135deg, #2D5016, #3d6b1e)',
        border: 'none', color: '#fff',
        fontSize: 15, fontWeight: 700, letterSpacing: '0.2px',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 8,
        boxShadow: '0 6px 20px rgba(45,80,22,0.5), 0 2px 6px rgba(0,0,0,0.3)',
        transition: 'transform 0.12s, box-shadow 0.15s',
        WebkitTapHighlightColor: 'transparent',
      }}
      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.97)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <span style={{ fontSize: 22, lineHeight: 1, fontWeight: 400 }}>+</span>
      {config.label}
    </button>
  );
}

// ─── Universal Search (Cmd+K) ────────────────────────────────────────────────
function UniversalSearch({ leads, jobs, crew, onSelectLead, onSelectJob, setTab, onClose }) {
  useScrollLock();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const results = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    const out = [];

    // Search leads/customers
    (leads || []).forEach(l => {
      const hay = [l.name, l.contact, l.phone, l.email, l.address, l.trade].filter(Boolean).join(' ').toLowerCase();
      if (hay.includes(q)) out.push({ type: 'Lead', label: l.name, sub: l.contact ? `${l.contact} · ${l.address || ''}` : l.address || '', data: l });
    });

    // Search jobs
    (jobs || []).forEach(j => {
      const hay = [j.customer, String(j.id), j.address, j.status, j.trade].filter(Boolean).join(' ').toLowerCase();
      if (hay.includes(q)) out.push({ type: 'Job', label: j.customer, sub: `${j.trade} · ${j.status} · ${j.address || ''}`, data: j });
    });

    // Search crew
    (crew || []).forEach(c => {
      const hay = [c.name, c.role, c.phone].filter(Boolean).join(' ').toLowerCase();
      if (hay.includes(q)) out.push({ type: 'Crew', label: c.name, sub: c.role || c.phone || '', data: c });
    });

    // Search change orders across jobs
    (jobs || []).forEach(j => {
      (j.changeOrders || []).forEach(co => {
        const hay = [co.title, co.description, co.status].filter(Boolean).join(' ').toLowerCase();
        if (hay.includes(q)) out.push({ type: 'Change Order', label: co.title, sub: `${j.customer} · ${co.status} · $${co.amount}`, data: { job: j, co } });
      });
    });

    return out.slice(0, 20);
  }, [query, leads, jobs, crew]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  const handleSelect = useCallback((item) => {
    if (item.type === 'Lead') {
      if (setTab) setTab('pipeline');
      if (onSelectLead) onSelectLead(item.data);
    } else if (item.type === 'Job') {
      if (setTab) setTab('jobs');
    } else if (item.type === 'Crew') {
      if (setTab) setTab('crew');
    } else if (item.type === 'Change Order') {
      if (setTab) setTab('jobs');
    }
    onClose();
  }, [setTab, onSelectLead, onClose]);

  const handleKey = useCallback((e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(prev => Math.min(prev + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(prev => Math.max(prev - 1, 0)); }
    else if (e.key === 'Enter' && results[activeIdx]) { e.preventDefault(); handleSelect(results[activeIdx]); }
    else if (e.key === 'Escape') { onClose(); }
  }, [results, activeIdx, handleSelect, onClose]);

  const typeColor = { Lead: '#E8722A', Job: '#3b82f6', Crew: '#22c55e', 'Change Order': '#f59e0b' };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1100, paddingTop: '15dvh' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: '100%', maxWidth: 520, margin: '0 12px', background: '#1E2329', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <span style={{ fontSize: 16, marginRight: 10, color: '#D1D5DB' }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Search leads, jobs, crew, change orders…"
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#C9D1D9', fontSize: 15, fontFamily: 'inherit' }}
          />
          <kbd style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.08)', color: '#D1D5DB', border: '1px solid rgba(255,255,255,0.1)' }}>ESC</kbd>
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {query && results.length === 0 && (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#D1D5DB', fontSize: 13 }}>No results for &ldquo;{query}&rdquo;</div>
          )}
          {results.map((r, i) => (
            <div
              key={`${r.type}-${r.label}-${i}`}
              onClick={() => handleSelect(r)}
              onMouseEnter={() => setActiveIdx(i)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '14px 16px', minHeight: 44, cursor: 'pointer',
                background: i === activeIdx ? 'rgba(255,255,255,0.06)' : 'transparent',
              }}
            >
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '3px 7px', borderRadius: 4,
                background: (typeColor[r.type] || '#8B95A1') + '22', color: typeColor[r.type] || '#8B95A1',
                textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0,
              }}>
                {r.type}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#C9D1D9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</div>
                {r.sub && <div style={{ fontSize: 11, color: '#D1D5DB', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sub}</div>}
              </div>
            </div>
          ))}
        </div>
        {!query && (
          <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>
            Type to search across your entire workspace
          </div>
        )}
      </div>
    </div>
  );
}

// ─── App Root ─────────────────────────────────────────────────────────────────
export { DemoPage };

export default function App() {
  const [screen, setScreen] = useState('login'); // 'login' | 'onboarding' | 'app'
  const [session, setSession] = useState(null);
  const [tab, setTab] = useState('pipeline');
  const [demoRole, setDemoRole] = useState('owner');
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadModal, setLeadModal] = useState(null); // null | 'add' | lead-object (edit)
  const mainScrollRef = useRef(null);

  useEffect(() => {
    if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
  }, [tab]);

  // Persistent leads for real (non-demo) users
  const [userLeads, setUserLeads] = useState(() => {
    try {
      const saved = localStorage.getItem('cl_leads');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const [demoLeadOverrides, setDemoLeadOverrides] = useState({});
  const [demoJobOverrides, setDemoJobOverrides] = useState({});
  const [demoCrew, setDemoCrew] = useState(DEMO_CREW);
  const [demoNewJobs, setDemoNewJobs] = useState([]);
  // Net-new leads added during a demo session. Stored alongside (not on top of)
  // DEMO_LEADS so they show up in the pipeline + quotes lookup. Cleared on
  // Reset Demo. Never persisted — demos start fresh on reload.
  const [demoNewLeads, setDemoNewLeads] = useState([]);

  // Persist to localStorage whenever userLeads changes (non-demo only)
  useEffect(() => {
    if (session && !session.isDemo) {
      localStorage.setItem('cl_leads', JSON.stringify(userLeads));
    }
  }, [userLeads, session]);

  const [userCrew, setUserCrew] = useState(() => {
    try { const s = localStorage.getItem('cl_crew'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [userJobs, setUserJobs] = useState(() => {
    try { const s = localStorage.getItem('cl_jobs'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [assignments, setAssignments] = useState(() => {
    try { const s = localStorage.getItem('cl_assignments'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [userQuotes, setUserQuotes] = useState(() => {
    try { const s = localStorage.getItem('cl_quotes'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [userProjects, setUserProjects] = useState(() => {
    try { const s = localStorage.getItem('cl_projects'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [userScopes, setUserScopes] = useState(() => {
    try { const s = localStorage.getItem('cl_scopes'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [jobModal, setJobModal] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);

  // Cmd+K / Ctrl+K to open universal search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Close header overflow menu on outside tap or Escape
  useEffect(() => {
    if (!showHeaderMenu) return undefined;
    const onDown = (e) => {
      if (!e.target.closest || !e.target.closest('[data-header-menu]')) setShowHeaderMenu(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setShowHeaderMenu(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showHeaderMenu]);

  useEffect(() => {
    if (session && !session.isDemo) localStorage.setItem('cl_crew', JSON.stringify(userCrew));
  }, [userCrew, session]);
  useEffect(() => {
    if (session && !session.isDemo) localStorage.setItem('cl_jobs', JSON.stringify(userJobs));
  }, [userJobs, session]);
  useEffect(() => {
    if (session && !session.isDemo) localStorage.setItem('cl_assignments', JSON.stringify(assignments));
  }, [assignments, session]);
  useEffect(() => {
    // Persist regardless of demo mode so generateQuoteNumber (which reads
    // cl_quotes) sees previously-created quotes after a hard refresh and the
    // sequence picks up where it left off. Without this, the demo session's
    // userQuotes state evaporates on reload and every new quote re-collides
    // on DEMO_QUOTES's max number.
    if (session) localStorage.setItem('cl_quotes', JSON.stringify(userQuotes));
  }, [userQuotes, session]);
  useEffect(() => {
    if (session && !session.isDemo) localStorage.setItem('cl_projects', JSON.stringify(userProjects));
  }, [userProjects, session]);
  useEffect(() => {
    if (session && !session.isDemo) localStorage.setItem('cl_scopes', JSON.stringify(userScopes));
  }, [userScopes, session]);

  const handleSaveQuote = (quote) => {
    setUserQuotes(prev => {
      // userQuotes may store a draft created from a DEMO_QUOTE (Mark as Signed
      // on a demo quote). Upsert by id whether it already exists in DEMO_QUOTES
      // or only here.
      const idx = prev.findIndex(q => q.id === quote.id);
      if (idx >= 0) {
        const next = [...prev]; next[idx] = quote; return next;
      }
      return [...prev, quote];
    });
  };
  // Merge DEMO_QUOTES with userQuotes, letting userQuotes override (so a demo
  // quote that got Marked as Signed reflects the new status).
  const allQuotes = (() => {
    const byId = new Map();
    DEMO_QUOTES.forEach(q => byId.set(q.id, q));
    userQuotes.forEach(q => byId.set(q.id, q));
    return Array.from(byId.values());
  })();

  // Lead → Project conversion handler. Called from the QuoteDocumentEditor's
  // Convert button when a quote is in signed status and anchored to a lead.
  // Persists the new project + scopes + updated quote + updated lead across
  // the appropriate stores (demo overrides or user-state, depending on
  // session).
  const handleConvertLead = (lead, signedQuote) => {
    let bundle;
    try {
      bundle = convertLeadToProject(lead, signedQuote);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Convert failed:', err);
      return;
    }
    const { project, scopes, updatedQuote, updatedLead } = bundle;
    setUserProjects(prev => [...prev, project]);
    setUserScopes(prev => [...prev, ...scopes]);
    handleSaveQuote(updatedQuote);
    if (session?.isDemo) {
      setDemoLeadOverrides(prev => ({ ...prev, [String(lead.id)]: updatedLead }));
      // demoNewLeads also needs the convertedToProjectId flag if the lead was
      // a session-local one rather than a seeded DEMO_LEAD.
      setDemoNewLeads(prev => prev.map(l => l.id === lead.id ? updatedLead : l));
    } else {
      setUserLeads(prev => prev.map(l => l.id === lead.id ? updatedLead : l));
    }
    // Move the user to the Projects tab so they can see the newly-materialised
    // project + scopes immediately — this is the wow-moment of the workflow.
    setTab('jobs');
  };

  // Scope add/update — both demo and non-demo write to userScopes; the
  // existing userScopesAsJobs adapter projects them into the jobs array that
  // ProjectsTab consumes, so a new or updated scope appears on the right
  // project on next render.
  const handleAddScope = (scope) => setUserScopes(prev => [...prev, scope]);
  const handleUpdateScope = (scope) => setUserScopes(prev => {
    const idx = prev.findIndex(s => s.id === scope.id);
    if (idx >= 0) {
      const next = [...prev]; next[idx] = scope; return next;
    }
    return [...prev, scope];
  });

  const handleAddCrew = (member) => setUserCrew(prev => [member, ...prev]);
  const handleEditCrew = (member) => setUserCrew(prev => prev.map(m => m.id === member.id ? member : m));
  const handleDeleteCrew = (id) => setUserCrew(prev => prev.filter(m => m.id !== id));
  const handleDemoAddCrew = (member) => setDemoCrew(prev => [member, ...prev]);
  const handleDemoEditCrew = (member) => setDemoCrew(prev => prev.map(m => m.id === member.id ? member : m));
  const handleDemoDeleteCrew = (id) => setDemoCrew(prev => prev.filter(m => m.id !== id));
  const handleAddJob = (job) => {
    if (session?.isDemo) {
      const id = job.id || `demo-j-${Date.now()}`;
      setDemoNewJobs(prev => [{
        id,
        customer: job.customer || '',
        description: job.description || '',
        taskList: job.taskList || [],
        address: job.address || '',
        trade: job.trade || 'Full Replacement',
        value: job.value || 0,
        status: job.status || 'Scheduled',
        scheduledDate: job.scheduledDate,
        duration: job.duration || 1,
        notes: job.notes || '',
        completedSteps: [],
        explicitlyScheduled: true,
        jobType: job.jobType || 'Residential',
        currentPhase: 1,
        completedPhases: [],
      }, ...prev]);
    } else {
      setUserJobs(prev => [job, ...prev]);
    }
    setJobModal(false);
  };
  const handleCreateAndScheduleJob = (jobData) => {
    const id = `j-${Date.now()}`;
    const newJob = {
      id,
      customer: jobData.customer,
      description: jobData.description || '',
      taskList: jobData.taskList || [],
      address: jobData.address || '',
      trade: jobData.trade,
      value: jobData.value || 0,
      status: 'Scheduled',
      scheduledDate: jobData.scheduledDate,
      duration: jobData.duration || 1,
      notes: jobData.notes || '',
      completedSteps: [],
      explicitlyScheduled: true,
      jobType: jobData.jobType || 'Residential',
      currentPhase: 1,
      completedPhases: [],
    };
    setUserJobs(prev => [newJob, ...prev]);
    if (jobData.crewIds && jobData.crewIds.length) {
      setAssignments(prev => ({ ...prev, [id]: jobData.crewIds }));
    }
  };
  const handleUpdateJob = (jobData) => {
    setUserJobs(prev => prev.map(j => String(j.id) === String(jobData.id) ? { ...j, customer: jobData.customer, description: jobData.description || '', taskList: jobData.taskList || [], scheduledDate: jobData.scheduledDate, duration: jobData.duration || 1, trade: jobData.trade } : j));
    setUserLeads(prev => prev.map(l => String(l.id) === String(jobData.id) ? { ...l, name: jobData.customer, scheduledDate: jobData.scheduledDate, duration: jobData.duration || 1 } : l));
  };
  const handleChangeJobStage = (jobId, stage) => {
    const statusMap = { materials_ordered: 'Scheduled', scheduled_for_install: 'Scheduled', in_progress: 'In Progress', punch_list: 'In Progress', completed: 'Complete', invoiced: 'Complete', paid: 'Complete', lost: 'Lost' };
    setUserLeads(prev => prev.map(l => String(l.id) === String(jobId) ? { ...l, stage } : l));
    setUserJobs(prev => prev.map(j => String(j.id) === String(jobId) ? { ...j, status: statusMap[stage] || stage } : j));
  };
  const handleDeleteJob = (jobId) => {
    setUserJobs(prev => prev.filter(j => String(j.id) !== String(jobId)));
    setUserLeads(prev => prev.map(l => String(l.id) === String(jobId) ? { ...l, stage: 'lost' } : l));
    setAssignments(prev => { const n = { ...prev }; delete n[String(jobId)]; return n; });
  };
  const handleCompleteJob = (id) => {
    setUserLeads(prev => prev.map(l => l.id === id ? { ...l, stage: 'completed' } : l));
    setUserJobs(prev => prev.map(j => j.id === id ? { ...j, status: 'Complete' } : j));
  };
  const handleUpdateJobSteps = (jobId, completedSteps) => {
    setUserLeads(prev => prev.map(l => l.id === jobId ? { ...l, completedSteps } : l));
    setUserJobs(prev => prev.map(j => j.id === jobId ? { ...j, completedSteps } : j));
  };
  const handleScheduleJob = (jobId, scheduledDate, duration) => {
    setUserLeads(prev => prev.map(l => String(l.id) === String(jobId) ? { ...l, scheduledDate, duration } : l));
    setUserJobs(prev => prev.map(j => String(j.id) === String(jobId) ? { ...j, scheduledDate, duration } : j));
  };
  const handleDemoScheduleJob = (jobId, scheduledDate, duration) => {
    setDemoJobOverrides(prev => ({ ...prev, [String(jobId)]: { ...prev[String(jobId)], scheduledDate, duration: duration || 1 } }));
    setDemoNewJobs(prev => prev.map(j => String(j.id) === String(jobId) ? { ...j, scheduledDate, duration: duration || 1 } : j));
  };
  const handleDemoCreateJob = (jobData) => {
    const id = `demo-j-${Date.now()}`;
    setDemoNewJobs(prev => [{ id, customer: jobData.customer, description: jobData.description || '', taskList: [], address: '', trade: jobData.trade || 'Full Replacement', value: 0, status: 'Scheduled', scheduledDate: jobData.scheduledDate, duration: jobData.duration || 1, notes: '', completedSteps: [], explicitlyScheduled: true, jobType: jobData.jobType || 'Residential', currentPhase: 1, completedPhases: [] }, ...prev]);
  };
  const handleAssign = (jobId, crewId) => {
    setAssignments(prev => ({ ...prev, [String(jobId)]: [...(prev[String(jobId)] || []), crewId] }));
    const member = userCrew.find(m => m.id === crewId);
    if (member) {
      chatDB.add({ id: `sys-${Date.now()}-${Math.random().toString(36).slice(2,6)}`, jobId: String(jobId), senderId: 'system', senderName: 'System', text: `${member.name} assigned to this job`, timestamp: Date.now(), type: 'system' }).catch(() => {});
    }
  };
  const handleUnassign = (jobId, crewId) => {
    setAssignments(prev => ({ ...prev, [String(jobId)]: (prev[String(jobId)] || []).filter(id => id !== crewId) }));
    const member = userCrew.find(m => m.id === crewId);
    if (member) {
      chatDB.add({ id: `sys-${Date.now()}-${Math.random().toString(36).slice(2,6)}`, jobId: String(jobId), senderId: 'system', senderName: 'System', text: `${member.name} removed from this job`, timestamp: Date.now(), type: 'system' }).catch(() => {});
    }
  };

  const handleLogin = (sess) => {
    setSession(sess);
    setScreen('app');
    setTab('pipeline');
  };

  const handleOnboardingComplete = (sess) => {
    setSession(sess);
    setScreen('app');
    setTab('pipeline');
  };

  const handleAddLead = (leadData) => {
    const withId = { id: leadData.id || Date.now(), completedSteps: [], notes: '', ...leadData };
    if (session?.isDemo) {
      setDemoNewLeads(prev => [withId, ...prev]);
    } else {
      setUserLeads(prev => [withId, ...prev]);
    }
    setLeadModal(null);
    return withId;
  };

  const handleEditLead = (leadData) => {
    if (session?.isDemo) {
      // If this is a demo-session-local lead, update it in place; if it's a
      // seeded lead, layer the edit via demoLeadOverrides so the diff persists
      // through the session.
      setDemoNewLeads(prev => {
        const idx = prev.findIndex(l => l.id === leadData.id);
        if (idx < 0) return prev;
        const next = [...prev]; next[idx] = leadData; return next;
      });
      const isSeeded = DEMO_LEADS.some(l => String(l.id) === String(leadData.id));
      if (isSeeded) {
        setDemoLeadOverrides(prev => ({ ...prev, [String(leadData.id)]: leadData }));
      }
    } else {
      setUserLeads(prev => prev.map(l => l.id === leadData.id ? leadData : l));
    }
    setLeadModal(null);
  };

  const handleDeleteLead = (id) => {
    if (session?.isDemo) {
      // Only let demo users delete leads they created during the session —
      // seeded leads stay put so resetting the demo always returns to a known
      // state.
      setDemoNewLeads(prev => prev.filter(l => l.id !== id));
    } else {
      setUserLeads(prev => prev.filter(l => l.id !== id));
    }
  };

  const handleLeadStageChange = (leadId, stage) => {
    if (session?.isDemo) {
      setDemoLeadOverrides(prev => {
        const cur = prev[String(leadId)] || {};
        return { ...prev, [String(leadId)]: { ...cur, stage } };
      });
    } else {
      setUserLeads(prev => prev.map(l => String(l.id) === String(leadId) ? { ...l, stage } : l));
    }
  };

  const handleUpdateLead = (leadData) => {
    if (session?.isDemo) {
      setDemoLeadOverrides(prev => ({ ...prev, [String(leadData.id)]: leadData }));
    } else {
      setUserLeads(prev => prev.map(l => l.id === leadData.id ? { ...leadData } : l));
    }
  };

  const isMobile = useMobile();

  // Reset tab when demo role changes and current tab is not accessible
  useEffect(() => {
    if (session?.isDemo && !DEMO_ROLES[demoRole].tabs.includes(tab)) {
      setTab(DEMO_ROLES[demoRole].tabs[0] || 'pipeline');
    }
  }, [demoRole, session, tab]);

  if (screen === 'login') {
    return (
      <LoginScreen
        onLogin={handleLogin}
        onStartSignup={() => setScreen('onboarding')}
      />
    );
  }

  if (screen === 'onboarding') {
    return (
      <OnboardingFlow
        onComplete={handleOnboardingComplete}
        onBackToLogin={() => setScreen('login')}
      />
    );
  }

  const isDemo = session?.isDemo;
  const rolePerms = DEMO_ROLES[isDemo ? demoRole : 'owner'];
  const visibleTabs = isDemo ? NAV_TABS.filter(t => DEMO_ROLES[demoRole].tabs.includes(t.key)) : NAV_TABS;

  const leads = isDemo
    ? [
        ...demoNewLeads,
        ...DEMO_LEADS.map(l => demoLeadOverrides[String(l.id)] ? { ...l, ...demoLeadOverrides[String(l.id)] } : l),
      ]
    : userLeads;
  const crew = isDemo ? demoCrew : userCrew;

  // Derive jobs from pipeline leads that have reached a job-execution stage,
  // then append any standalone jobs added via "Add Job" (deduplicated by id).
  const derivedJobs = userLeads
    .filter(l => JOB_STAGES.includes(l.stage))
    .map(leadToJob);
  const derivedIds = new Set(derivedJobs.map(j => String(j.id)));
  const standaloneJobs = userJobs.filter(j => !derivedIds.has(String(j.id)));
  // Synthesise jobs from user-created scopes (e.g., scopes materialised by a
  // lead→project conversion). Each becomes a job-shaped row with the legacy
  // fields populated from the parent project so ProjectsTab can group it.
  const allProjectsCombined = [...DEMO_PROJECTS, ...userProjects];
  const userScopesAsJobs = userScopes.map(s => {
    const project = allProjectsCombined.find(p => p.id === s.projectId) || {};
    return {
      id: s.id,
      projectId: s.projectId,
      customer: project.customerName,
      address: project.address,
      trade: s.scopeType,
      value: s.value,
      status: s.status,
      scheduledDate: s.scheduledDate,
      hours: s.hours,
      completedSteps: s.completedSteps || [],
      notes: s.notes || project.notes || '',
      duration: s.duration,
      jobType: s.phaseTemplate === 'commercial-7-phase' ? 'Commercial' : 'Residential',
      currentPhase: s.currentPhase,
      completedPhases: s.completedPhases || [],
      changeOrders: s.changeOrders || [],
      sections: s.sections || [],
    };
  });
  const jobs = isDemo
    ? [...DEMO_JOBS.map(j => demoJobOverrides[String(j.id)] ? { ...j, ...demoJobOverrides[String(j.id)] } : j), ...demoNewJobs, ...userScopesAsJobs]
    : [...derivedJobs, ...standaloneJobs, ...userScopesAsJobs];
  const effectiveAssignments = isDemo ? DEMO_ASSIGNMENTS : assignments;
  const userTrade = session?.trade || 'Full Replacement';
  const companyName = session?.companyName || 'RidgeOS';
  const userCustomChecklist = session?.customTradeConfig?.checklist || null;
  const currentUser = session?.name || session?.companyName || 'You';

  // Lead create/edit/delete are demo-safe — they write to demo-session-local
  // state when isDemo and to user state otherwise. New leads created in demo
  // disappear on Reset Demo or page reload.
  const addLeadHandler = () => setLeadModal('add');
  const editLeadHandler = (lead) => setLeadModal(lead);
  const deleteLeadHandler = handleDeleteLead;

  return (
    <ToastProvider>
    <GlobalStyles />
    <div style={S.app}>
      <header style={{
        ...S.header,
        ...(isMobile ? { padding: '0 10px', height: 48, gap: 6, overflow: 'visible' } : {}),
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8, minWidth: 0, flexShrink: 1, overflow: 'hidden' }}>
          <span style={{ ...S.logo, fontSize: isMobile ? 17 : 22, flexShrink: 0 }}>RidgeOS</span>
          {isDemo && (
            <span style={{
              fontSize: isMobile ? 9 : 10, fontWeight: 600,
              padding: isMobile ? '2px 6px' : '2px 8px', borderRadius: 10,
              background: 'rgba(232,114,42,0.2)', color: '#E8722A',
              border: '1px solid rgba(232,114,42,0.3)', letterSpacing: '0.5px',
              flexShrink: 0,
            }}>
              DEMO
            </span>
          )}
          {isDemo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: DEMO_ROLES[demoRole].color, flexShrink: 0 }} />
              <select
                value={demoRole}
                onChange={e => setDemoRole(e.target.value)}
                style={{
                  background: '#2A2E36', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
                  color: DEMO_ROLES[demoRole].color, fontSize: 11, fontWeight: 700,
                  padding: isMobile ? '6px 6px' : '4px 8px',
                  cursor: 'pointer', outline: 'none',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  maxWidth: isMobile ? 120 : 'none',
                  textOverflow: 'ellipsis', minWidth: 0,
                }}
              >
                {Object.entries(DEMO_ROLES).map(([key, r]) => (
                  <option key={key} value={key}>{r.label}</option>
                ))}
              </select>
            </div>
          )}
          {!isMobile && companyName !== 'RidgeOS' && (
            <span style={S.logoSub}>{companyName}</span>
          )}
        </div>

        {/* Desktop tabs */}
        {!isMobile && (
          <div style={S.tabs}>
            {visibleTabs.map(({ key, label }) => (
              <button key={key} className={tab === key ? '' : 'ri-nav-tab'} style={S.tab(tab === key, SECTION_COLORS[key])} onClick={() => setTab(key)}>
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Desktop: search + reset + sign out as separate controls */}
        {!isMobile && (
          <>
            <button
              onClick={() => setShowSearch(true)}
              title="Search (⌘K)"
              aria-label="Search"
              style={{
                background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8, color: '#D1D5DB', cursor: 'pointer',
                fontSize: 14, padding: '5px 12px',
                display: 'flex', alignItems: 'center', gap: 6,
                flexShrink: 0, marginLeft: 'auto',
              }}
            >
              🔍<span style={{ fontSize: 11, color: '#9CA3AF' }}>⌘K</span>
            </button>
            {isDemo && (
              <button
                onClick={() => {
                  setDemoLeadOverrides({});
                  setDemoJobOverrides({});
                  setDemoNewJobs([]);
                  setDemoNewLeads([]);
                  setDemoCrew(DEMO_CREW);
                }}
                className="ri-btn ri-btn-secondary"
                style={{
                  background: 'transparent', border: '1px solid rgba(232,114,42,0.3)',
                  color: '#E8722A', cursor: 'pointer', fontSize: 12,
                  padding: '5px 12px', borderRadius: 6,
                  minHeight: 'auto', flexShrink: 0,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                Reset Demo
              </button>
            )}
            <button
              onClick={() => { setScreen('login'); setSession(null); }}
              className="ri-btn ri-btn-secondary"
              style={{
                background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
                color: '#D1D5DB', cursor: 'pointer', fontSize: 12,
                padding: '5px 12px', borderRadius: 6,
                minHeight: 'auto', flexShrink: 0,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              Sign out
            </button>
          </>
        )}

        {/* Mobile: single kebab overflow menu */}
        {isMobile && (
          <div data-header-menu style={{ position: 'relative', marginLeft: 'auto', flexShrink: 0 }}>
            <button
              onClick={() => setShowHeaderMenu(v => !v)}
              aria-label="More actions"
              aria-expanded={showHeaderMenu}
              style={{
                width: 40, height: 36, padding: 0,
                background: showHeaderMenu ? 'rgba(255,255,255,0.08)' : 'transparent',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8, color: '#FFFFFF',
                fontSize: 22, lineHeight: 1, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              ⋯
            </button>
            {showHeaderMenu && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                minWidth: 180, background: '#2A3140',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10, padding: 6,
                boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
                zIndex: 200, display: 'flex', flexDirection: 'column', gap: 2,
              }}>
                <button
                  onClick={() => { setShowHeaderMenu(false); setShowSearch(true); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', minHeight: 44,
                    background: 'transparent', border: 'none', borderRadius: 8,
                    color: '#FFFFFF', fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', textAlign: 'left',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <span style={{ fontSize: 16, width: 22 }}>🔍</span> Search
                </button>
                {isDemo && (
                  <button
                    onClick={() => {
                      setDemoLeadOverrides({});
                      setDemoJobOverrides({});
                      setDemoNewJobs([]);
                      setDemoNewLeads([]);
                      setDemoCrew(DEMO_CREW);
                      setShowHeaderMenu(false);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', minHeight: 44,
                      background: 'transparent', border: 'none', borderRadius: 8,
                      color: '#E8722A', fontSize: 14, fontWeight: 600,
                      cursor: 'pointer', textAlign: 'left',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <span style={{ fontSize: 16, width: 22 }}>↻</span> Reset Demo
                  </button>
                )}
                <button
                  onClick={() => { setShowHeaderMenu(false); setScreen('login'); setSession(null); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', minHeight: 44,
                    background: 'transparent', border: 'none', borderRadius: 8,
                    color: '#D1D5DB', fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', textAlign: 'left',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <span style={{ fontSize: 16, width: 22 }}>⏻</span> Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <main ref={mainScrollRef} style={{
        ...S.body,
        flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
        ...(isMobile ? { padding: '16px 12px', paddingBottom: 96 } : { paddingBottom: 32 }),
      }}>
        <div key={tab} className="ri-fadein">
        {tab === 'pipeline' && (
          leads.length === 0
            ? <EmptyState
                icon="📋"
                title="No leads yet"
                sub="Start building your pipeline by adding your first lead."
                btnLabel="Add your first lead"
                onAction={addLeadHandler}
              />
            : <PipelineTab
                leads={leads}
                onSelectLead={setSelectedLead}
                onAddLead={addLeadHandler}
                onEditLead={editLeadHandler}
                onDeleteLead={deleteLeadHandler}
                demoMode={isDemo}
                onStageChange={handleLeadStageChange}
                onUpdateLead={handleUpdateLead}
                currentUser={currentUser}
                rolePerms={rolePerms}
                quotes={allQuotes}
                onSaveQuote={handleSaveQuote}
                onConvertLead={handleConvertLead}
              />
        )}
        {tab === 'quotes' && (
          <QuotesTab
            quotes={allQuotes}
            leads={leads}
            jobs={jobs}
            onSaveQuote={handleSaveQuote}
            onConvertLead={handleConvertLead}
            // handleAddLead is demo-safe — writes to demoNewLeads in demo, to
            // userLeads otherwise — and returns the materialised lead.
            onAddLead={handleAddLead}
            isDemo={isDemo}
            rolePerms={rolePerms}
            currentUser={currentUser}
          />
        )}
        {tab === 'callbacks' && (
          <CallbacksTab leads={leads} onSelectLead={setSelectedLead} onUpdateLead={handleUpdateLead} rolePerms={rolePerms} />
        )}
        {tab === 'analytics' && (
          <AnalyticsTab leads={leads} jobs={jobs} rolePerms={rolePerms} />
        )}
        {tab === 'jobs' && (
          jobs.length === 0
            ? <EmptyState
                icon="🔨"
                title="No projects yet"
                sub={`Add your first ${userTrade} project to start tracking progress.`}
                btnLabel="Add your first project"
                onAction={() => setJobModal(true)}
              />
            : <ProjectsTab
                jobs={jobs}
                customChecklist={userCustomChecklist}
                crew={crew}
                assignments={effectiveAssignments}
                onAssign={isDemo ? null : handleAssign}
                onUnassign={isDemo ? null : handleUnassign}
                onAddCrew={isDemo ? handleDemoAddCrew : handleAddCrew}
                currentUser={currentUser}
                demoMessages={isDemo ? DEMO_MESSAGES : null}
                onComplete={isDemo ? null : handleCompleteJob}
                onUpdateSteps={isDemo ? null : handleUpdateJobSteps}
                onUpdateSchedule={isDemo ? null : handleScheduleJob}
                rolePerms={rolePerms}
                quotes={allQuotes}
                onSaveQuote={handleSaveQuote}
                onAddScope={handleAddScope}
                onUpdateScope={handleUpdateScope}
              />
        )}
        {tab === 'calendar' && (
          <CalendarTab
            jobs={jobs}
            crew={crew}
            assignments={effectiveAssignments}
            onSchedule={isDemo ? handleDemoScheduleJob : handleScheduleJob}
            onComplete={isDemo ? null : handleCompleteJob}
            onUpdateSteps={isDemo ? null : handleUpdateJobSteps}
            onAssign={isDemo ? null : handleAssign}
            onUnassign={isDemo ? null : handleUnassign}
            onAddCrew={isDemo ? handleDemoAddCrew : handleAddCrew}
            currentUser={currentUser}
            demoMessages={isDemo ? DEMO_MESSAGES : null}
            customChecklist={userCustomChecklist}
            isDemo={isDemo}
            onCreate={isDemo ? handleDemoCreateJob : handleCreateAndScheduleJob}
            onUpdate={isDemo ? null : handleUpdateJob}
            onChangeStage={isDemo ? null : handleChangeJobStage}
            onDeleteJob={isDemo ? null : handleDeleteJob}
            userTrade={userTrade}
          />
        )}
        {tab === 'crew' && (
          <CrewTab
            crew={crew}
            jobs={jobs}
            assignments={effectiveAssignments}
            onAddMember={isDemo ? handleDemoAddCrew : handleAddCrew}
            onEditMember={isDemo ? handleDemoEditCrew : handleEditCrew}
            onDeleteMember={isDemo ? handleDemoDeleteCrew : handleDeleteCrew}
          />
        )}
        {tab === 'chat' && (
          <ChatTab jobs={jobs} demoMessages={isDemo ? DEMO_MESSAGES : null} />
        )}
        {tab === 'photos' && (
          <GlobalPhotoLog jobs={jobs} />
        )}
        {tab === 'team' && (
          rolePerms?.seeTeamMgmt
            ? <TeamManagementTab />
            : <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🔒</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#D1D5DB' }}>Access Restricted</div>
                <div style={{ fontSize: 13, color: '#D1D5DB', marginTop: 4 }}>Team Management is only available to Owner/Admin accounts.</div>
              </div>
        )}
        </div>
      </main>

      {/* Mobile bottom navigation */}
      {isMobile && <BottomNav tab={tab} setTab={setTab} tabs={visibleTabs} />}

      {selectedLead && (
        <CoachPanel
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onStageChange={(newStage) => {
            handleLeadStageChange(selectedLead.id, newStage);
            setSelectedLead(prev => ({ ...prev, stage: newStage }));
          }}
        />
      )}

      {leadModal && (
        <AddLeadModal
          lead={leadModal === 'add' ? null : leadModal}
          defaultTrade={userTrade}
          customTrade={!TRADE_LIST.includes(userTrade) ? userTrade : null}
          onSave={leadModal === 'add' ? handleAddLead : handleEditLead}
          onClose={() => setLeadModal(null)}
        />
      )}

      {jobModal && (
        <AddJobModal onSave={handleAddJob} onClose={() => setJobModal(false)} />
      )}

      <FloatingActionButton
        tab={tab}
        onAction={(action) => {
          if (action === 'addJob') setJobModal(true);
        }}
      />

      {showSearch && (
        <UniversalSearch
          leads={leads}
          jobs={jobs}
          crew={crew}
          onSelectLead={setSelectedLead}
          setTab={setTab}
          onClose={() => setShowSearch(false)}
        />
      )}
    </div>
    </ToastProvider>
  );
}
